import { applySavedTheme, createThemeEditor } from "./themes.js";
import { tokenMetadata } from "./sessions.js";
import { SEARCH_PAGE_SIZE, SEARCH_TYPES, createSearchResults, appendSearchPage } from "./search.js";
import { notificationCursor, mergeNotificationPages } from "./notifications.js";
import { createUserPostWatcher, USER_POST_WATCHER_STORAGE_KEY } from "./user-post-watcher.js";
import {
  OAUTH_CONFIG,
  TIMELINE_PAGE_SIZE,
  BOARDS,
  SORT_MODES,
  AUTH_STORAGE_KEY,
  APP_STORAGE_KEY,
} from "./config.js";
import {
  buildCatalogThreads,
  flatCatalogThreads,
  dmCatalogThreads,
  effectiveStatus,
  filterCatalogThreads,
  normalizeMutedWords,
  sortCatalogThreads,
  resolveThreadRootId,
  extractSummary,
} from "./catalog.js";
import {
  apiFetch as apiFetchRaw,
  loadTimeline,
  loadAccountStatuses,
  loadPinnedStatuses,
  setStatusPinned,
  previewStatus,
  loadGroupTimeline,
  loadTagTimeline,
  loadFollowedGroups,
  createGroup,
  getGroupSource,
  updateGroup,
  deleteGroup,
  loadInstance,
  fetchContext,
  fetchStatus,
  searchAll,
  searchByType,
  patchCachedStatus,
  forgetCachedStatuses,
} from "./data.js";
import {
  resolveRootIdFromParentMap,
  resolveRootIdFromContext,
  buildReplyTree,
  flattenThreadChronological,
  countDescendants,
  collectDescendantIds,
  buildThreadGraph as buildThreadGraphFromStatuses,
} from "./threading.js";
import {
  indexReplyLimit,
  selectIndexReplies,
  seedIndexReplies,
  omittedLabel,
} from "./indexview.js";
import {
  LINK_LIMIT,
  POST_REF_PATTERN,
  shortPostRef,
  refForObjectUrl,
  barePostRef,
  replyMentionPrefill,
  setReplyMentionPrefill,
  quotedStatuses,
  objectUrlFor,
  normalizeObjectUrl,
  indexQuotedStatuses,
  rememberQuoted,
  lookupQuoted,
  quoteRefMarkdown,
  countObjectLinks,
  expandBarePostRefs,
  downgradeObjectLinks,
  isMitraServer,
  supportsNonPublicMitraQuotes,
  isQuotableStatus,
} from "./quotes.js";
import { reconcileKeyedChildren } from "./reconcile.js";
import { attachImageNavigation, sweepImageNavigation } from "./image-navigation.js";
import { normalizePanelPosition, resolvePanelPosition, snapPanelPosition } from "./panel-position.js";
import {
  composeBodyToHtml,
  composeBodyToPlainText,
  supportsHtmlPosts,
  supportsMarkdownPosts,
  escapeHTML,
} from "./postbody.js";
import {
  DEFAULT_EMOJI_FAVORITES,
  filterEmojiList,
  loadUnicodeEmojis,
  rankEmojiMatches,
} from "./emoji.js";
import {
  formatBytes,
  mediaSizeBytes,
  mediaDimensions,
  truncateHash,
  preferredVideoPoster,
  safeUrl,
  urlPathSansQuery,
  mediaFullUrl,
  isPdfAttachment,
  isDocumentAttachment,
  uploadFilename,
} from "./media.js";
import { galleryModal, setGalleryVideoDefaults } from "./gallery.js";
import qrcode from "./vendor/qrcode.js";
import { hashRouteForServerPath } from "./routing.js";
import { isComposeSubmitShortcut } from "./shortcuts.js";

// chan-fe app.js — minimal vanilla JS. Talks to the live Mitra
// instance via the data layer (data.js).

function boardDescription(boardName) {
  const b = BOARDS[boardName] || BOARDS.twkn;
  if (["home", "twkn", "likes", "bkmks", "dm"].includes(boardName) && state.auth.status !== "signed-in") {
    return `${b.desc} (sign in required)`;
  }
  return b.desc;
}

// A board view is either a built-in ({ kind: "board", name }) or a custom
// board backed by a Mitra group ({ kind: "board", group: acct, account }).
// `boardKey` is the identity used for nav active-state and view comparison.
function boardKey(view) {
  if (view?.kind !== "board") return null;
  return view.group ? `g/${view.group}` : view.name;
}

const state = {
  instance: null,
  view: { kind: "board", name: "twkn" },
  lastNonPostRoute: { kind: "board", name: "twkn" },
  statuses: [],
  // Catalog infinite-scroll cursor. `cursor` is the max_id for the next
  // (older) page; `hasMore` gates further loading; `loadingMore` guards
  // against concurrent fetches; `error` flags a failed "load more" (so we
  // show a retry instead of wiping the loaded catalog).
  catalog: { cursor: null, hasMore: false, loadingMore: false, error: false, linkPaging: false },
  groups: [],                  // followed Mitra groups (custom boards), Account[]
  moderatedGroups: [],         // groups the user admins (subset of the above), Account[]
  groupsSupported: null,       // does this server have a group API? null until asked
  stickiedStatuses: [],        // the viewed profile's stickied posts, Status[]
  // Index view: opId -> { descendants, total, graph, loading, error } for
  // each thread whose replies have been fetched. Populated lazily as rows
  // scroll into view; cleared when the view changes (see resetIndexView).
  indexView: { rows: new Map(), inFlight: 0 },
  sort: "bump",
  filter: "",                  // catalog text filter
  mediaOnly: false,            // catalog toggle: OPs with media attachments only
  includeReplies: false,       // profile catalog toggle: show replies & reposts as their own cards
  boardIncludeReplies: {       // built-in board toggle; defaults are per board
    home: false,
    twkn: false,
    local: true,
  },
  dmAllMessages: true,         // /dm/ scope toggle: every direct message, not just conversation previews
  search: null,                // result arrays and per-type pagination for /search/
  loading: false,
  error: null,
  auth: {
    accessToken: null,         // bearer token (live mode only)
    account: null,             // /api/v1/accounts/verify_credentials payload
    status: "signed-out",      // "signed-out" | "authorizing" | "exchanging" | "signed-in" | "reconnect" | "error"
    error: null,
  },
  ui: {
    suppressImageHover: false,
    // Bumped by every explicit scroll jump ([Top]/[Bottom] links). Pending
    // scroll-position restores (load-more, refresh) capture the current value
    // and skip restoring if it changed — otherwise a jump that lands between
    // their capture and their requestAnimationFrame gets yanked back.
    scrollJumpSeq: 0,
  },
  pendingReplyFocus: null,   // { opId, replyId, retries, retryScheduled }
  notifications: {
    items: [],               // flattened, loaded notification pages
    watchedThreads: [],      // [{ id, title }]
    hiddenThreads: [],       // [{ id, title }] — filtered out of the catalog
    threadSeen: new Map(),   // opId -> { descendantsCount, latestId, latestAt }
    threadReadAt: new Map(), // opId -> latest post created_at seen last visit (unread-line baseline)
    threadUnread: new Map(), // opId -> unread reply delta count
    threadMeta: new Map(),   // opId -> { actor, snippet, latestAt }
    knownNotifKeys: new Set(),
    readNotifKeys: new Set(),
    pendingFollowRequests: new Map(), // account id -> Account awaiting approval
    notifPages: [],          // Array<Array<notification>>
    notifNextCursorByPage: [], // page index -> max_id for next page
    notifPageIndex: 0,
    notifPaging: false,
    activeTab: "watcher",   // "watcher" | "notifications" | "hidden"
    loading: false,
    error: null,
  },
};

// ---- helpers ----------------------------------------------------------------

const $ = (sel, root = document) => root.querySelector(sel);

// Wire up "click on the backdrop closes the modal" without closing when the
// click is just the tail end of a text-selection drag that started inside the
// modal (mousedown inside -> drag -> mouseup on the backdrop). The browser
// fires `click` on the backdrop in that case because its target is the lowest
// common ancestor of the mousedown and mouseup targets, so we additionally
// require that the mousedown itself landed on the backdrop.
function addBackdropClose(backdrop, onClose) {
  let downOnBackdrop = false;
  backdrop.addEventListener("mousedown", (e) => {
    downOnBackdrop = (e.target === backdrop);
  });
  backdrop.addEventListener("click", (e) => {
    const genuine = downOnBackdrop && e.target === backdrop;
    downOnBackdrop = false;
    if (genuine) onClose();
  });
}

// ---- floating, draggable, non-blocking panel modals ----------------------
//
// makePanelModal() turns a modal overlay (compose, config, subscribe,
// board-create, emoji picker, poll composer) into a repositionable panel:
// the full-screen dimmed backdrop becomes transparent and click-through so
// the page underneath stays fully interactive, and the panel box can be
// dragged by its header. The gallery and auth modals stay genuinely modal.
// See the matching ".draggable-panel" rules in style.css.
//
// Position lives on the box as inline left/top. On each *open* the box is
// re-centered (so it can never start off-screen), but within a single open
// session it stays wherever you drag it — handy when composing a reply and
// scrolling the thread for reference at the same time.
const _panelBoxes = [];

function _centerPanelBox(box) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = box.offsetWidth;
  const left = Math.max(8, Math.round((vw - w) / 2));
  const top  = Math.max(8, Math.min(Math.round(vh * 0.08), 96));
  box.style.left = left + "px";
  box.style.top  = top + "px";
  box.classList.remove("is-dragged");
}

function _clampPanelBox(box) {
  const left = parseFloat(box.style.left);
  const top  = parseFloat(box.style.top);
  if (!Number.isFinite(left) || !Number.isFinite(top)) return;
  const w = box.offsetWidth;
  const nx = Math.max(96 - w, Math.min(left, window.innerWidth  - 96));
  const ny = Math.max(0,       Math.min(top,  window.innerHeight - 40));
  box.style.left = nx + "px";
  box.style.top  = ny + "px";
}

function makePanelModal(root) {
  if (!root) return null;
  const box    = root.firstElementChild;          // the *-box
  const handle = box && box.firstElementChild;    // the *-head bar
  if (!box || !handle) return null;

  root.classList.add("draggable-panel");
  if (!_panelBoxes.includes(box)) _panelBoxes.push(box);

  // Re-center whenever the modal becomes visible. Both the `hidden`
  // attribute and the `is-open` class are toggled on open/close, so watch
  // both; only the hidden→visible transition recenters. While it stays
  // open, the position persists (no attribute change → no recenter), so
  // e.g. replying to several posts keeps your dragged spot.
  let wasVisible = !root.hidden;
  const mo = new MutationObserver(() => {
    const visible = !root.hidden;
    if (visible && !wasVisible) _centerPanelBox(box);
    wasVisible = visible;
  });
  mo.observe(root, { attributes: true, attributeFilter: ["hidden", "class"] });

  wirePanelDrag(box, handle);
  return { box, root, recenter: () => _centerPanelBox(box) };
}

function wirePanelDrag(box, handle, { onMove, onDrop } = {}) {
  handle.addEventListener("pointerdown", (e) => {
    if (!e.isPrimary) return;
    if (e.button !== 0) return;
    // Don't hijack clicks on the close button or other header controls.
    if (e.target.closest("button, input, select, textarea, a, label")) return;
    const rect = box.getBoundingClientRect();
    _activeDrag = {
      box, handle, pointerId: e.pointerId, moved: false,
      startX: e.clientX, startY: e.clientY,
      baseLeft: rect.left, baseTop: rect.top,
      onMove, onDrop,
    };
    handle.setPointerCapture(e.pointerId);
    document.body.classList.add("is-panel-dragging");
    e.preventDefault();   // suppress text selection / caret while dragging
  });

  // Pointer capture normally lasts through pointerup, but can be lost early
  // when the browser cancels a gesture or the window changes state.
  handle.addEventListener("lostpointercapture", (e) => {
    if (_activeDrag?.pointerId === e.pointerId) _dragEnd(e);
  });
}

// A single in-flight drag at a time. Pointer capture keeps tracking the
// mouse, pen, or touch contact after it leaves the panel; document-level
// listeners finish the drag on release/cancellation, with blur as a fallback.
let _activeDrag = null;

function _dragMove(e) {
  const d = _activeDrag;
  if (!d || e.pointerId !== d.pointerId) return;
  // Pin the box to explicit coordinates on the first actual movement, so a
  // plain press on the header without movement doesn't "claim" it.
  if (!d.moved) {
    d.box.style.left = d.baseLeft + "px";
    d.box.style.top  = d.baseTop + "px";
    d.box.classList.add("is-dragged");
    d.moved = true;
  }
  const left = d.baseLeft + (e.clientX - d.startX);
  const top = d.baseTop + (e.clientY - d.startY);
  if (d.onMove) {
    d.onMove(left, top);
  } else {
    const w = d.box.offsetWidth;
    const nx = Math.max(96 - w, Math.min(left, window.innerWidth - 96));
    const ny = Math.max(0, Math.min(top, window.innerHeight - 40));
    d.box.style.left = nx + "px";
    d.box.style.top = ny + "px";
  }
  e.preventDefault();
}

function _dragEnd(e) {
  const d = _activeDrag;
  if (!d || (e?.pointerId != null && e.pointerId !== d.pointerId)) return;
  _activeDrag = null;
  if (d.handle.hasPointerCapture(d.pointerId)) {
    d.handle.releasePointerCapture(d.pointerId);
  }
  document.body.classList.remove("is-panel-dragging");
  if (d.moved) d.onDrop?.(e);
}

document.addEventListener("pointermove", _dragMove);
document.addEventListener("pointerup", _dragEnd);
document.addEventListener("pointercancel", _dragEnd);
window.addEventListener("blur", _dragEnd);


// Keep panels sensibly placed when the viewport changes: re-center panels
// the user hasn't moved, and clamp dragged ones back into reach.
window.addEventListener("resize", () => {
  for (const box of _panelBoxes) {
    const root = box.parentElement;
    if (!root || root.hidden) continue;          // hidden → recentered on next open
    if (box.classList.contains("is-dragged")) _clampPanelBox(box);
    else _centerPanelBox(box);
  }
});

function apiFetch(path, opts = {}) {
  return apiFetchRaw(path, opts, state.auth.accessToken, state.auth.apiBase);
}

// Turn a failed API response into a short, human-readable message.
// Mastodon/Mitra error bodies are JSON of the form
//   { error: "...", error_description: "..." }
// but some rejections happen in the HTTP layer before the handler runs —
// e.g. a request body bigger than the server's payload limit comes back as
// a 413 with no JSON at all — so we fall back to a friendly status hint
// rather than dumping raw JSON into the UI.
async function apiErrorMessage(response) {
  let detail = "";
  try {
    const ct = response.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const data = await response.json();
      detail = (data && (data.error_description || data.error)) || "";
    } else {
      const txt = (await response.text()).trim();
      detail = txt.length > 160 ? txt.slice(0, 157) + "\u2026" : txt;
    }
  } catch (_) {
    // Body already consumed or unparseable — fall through to status hint.
  }
  if (!detail) {
    if (response.status === 413) detail = "file is too large";
    else if (response.status === 401 || response.status === 403) detail = "not authorized";
    else if (response.status === 429) detail = "rate limited; try again shortly";
    else detail = `HTTP ${response.status}`;
  }
  return detail;
}

function getCurrentPostRootId() {
  if (state.view?.kind !== "post") return null;

  // Prefer context ancestors when available; this still works even when the
  // current view is a reply id (not the thread OP id).
  const ctxRoot = resolveRootIdFromContext(state.postView?.context);
  if (ctxRoot) return ctxRoot;

  const graph = currentThreadGraph();
  if (graph?.parentById?.size) {
    return resolveRootIdFromParentMap(state.view.id, graph.parentById) || state.postView?.context?.op?.id || state.view.id;
  }

  const ctxOpId = state.postView?.context?.op?.id;
  if (ctxOpId) return ctxOpId;

  return state.view.id || null;
}

function setPendingReplyFocus(opId, replyId) {
  if (!opId || !replyId) return;
  state.pendingReplyFocus = { opId, replyId, retries: 0, retryScheduled: false };
}

function clearPendingReplyFocus() {
  state.pendingReplyFocus = null;
}

function ensurePendingReplyFocus() {
  const pending = state.pendingReplyFocus;
  if (!pending) return;

  if (state.view?.kind !== "post" || state.view.id !== pending.opId) return;

  const target = document.getElementById("p" + pending.replyId);
  if (target) {
    clearPendingReplyFocus();
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.classList.add("is-new-reply");
    setTimeout(() => target.classList.remove("is-new-reply"), 1800);
    return;
  }

  if (pending.retries >= 6) {
    clearPendingReplyFocus();
    return;
  }
  if (pending.retryScheduled) return;

  pending.retryScheduled = true;
  setTimeout(async () => {
    if (!state.pendingReplyFocus) return;
    state.pendingReplyFocus.retryScheduled = false;
    state.pendingReplyFocus.retries += 1;

    try {
      // Re-fetch the active thread without restoring old scroll position;
      // if the reply appears we want ensurePendingReplyFocus() to own scroll.
      await loadPost(pending.opId);
    } catch {
      // Keep retry loop best-effort.
    }

    ensurePendingReplyFocus();
  }, 900);
}

// data access moved to data.js (loadTimeline/loadInstance/fetchContext)

// ---- auth: persist + restore + sign in / out --------------------------------
//
// Multiple accounts are supported. localStorage under AUTH_STORAGE_KEY holds:
//   { accounts: Session[], activeId: string|null }
// Sessions include account identity, instance, access token and optional
// refreshToken, expiresAt, oauthClient and needsReconnect fields;
// `id` is a stable per-session key (apiBase + "::" + account.id). The active
// session is what state.auth mirrors and what data.js reads for apiBase.
// A legacy single-session blob is migrated to this shape on first read.

function sessionKeyFor(account, apiBase) {
  const id = account?.id || account?.url || `${apiBase || ""}/${account?.acct || ""}`;
  return `${apiBase || ""}::${id}`;
}

function readAccountStore() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return { accounts: [], activeId: null };
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.accounts)) {
      return {
        accounts: parsed.accounts.filter((a) => a && a.accessToken && a.account),
        activeId: parsed.activeId || null,
      };
    }
    // Legacy single-session shape: { accessToken, account, instance, apiBase }.
    if (parsed && parsed.accessToken && parsed.account) {
      const session = {
        id: sessionKeyFor(parsed.account, parsed.apiBase),
        accessToken: parsed.accessToken,
        account: parsed.account,
        instance: parsed.instance || null,
        apiBase: parsed.apiBase !== undefined ? parsed.apiBase : null,
      };
      return { accounts: [session], activeId: session.id };
    }
    return { accounts: [], activeId: null };
  } catch {
    return { accounts: [], activeId: null };
  }
}

function writeAccountStore(store) {
  try { localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(store)); } catch {}
}

function getActiveSession() {
  const store = readAccountStore();
  if (!store.activeId) return null;
  return store.accounts.find((a) => a && a.id === store.activeId) || null;
}

// Load a stored session into the live state.auth mirror. Pass null to clear.
function loadSessionIntoState(session) {
  state.auth.refreshToken = session?.refreshToken || null;
  state.auth.expiresAt = session?.expiresAt || null;
  state.auth.oauthClient = session?.oauthClient || null;
  state.auth.needsReconnect = !!session?.needsReconnect;
  if (!session) {
    state.auth.accessToken = null;
    state.auth.account = null;
    state.auth.currentInstance = null;
    state.auth.apiBase = null;
    return;
  }
  state.auth.accessToken = session.accessToken || null;
  state.auth.account = session.account || null;
  state.auth.currentInstance = session.instance || null;
  state.auth.apiBase = session.apiBase !== undefined ? session.apiBase : null;
}

function resetAuthSignedOut() {
  loadSessionIntoState(null);
  state.auth.accessToken = null;
  state.auth.account = null;
  state.auth.currentInstance = null;
  state.auth.apiBase = null;
  state.auth.status = "signed-out";
  state.auth.error = null;
}

// Drop the in-memory state that is scoped to whichever account is active
// (followed groups, loaded notifications, search). Called on sign-out and on
// every account switch so the new account's data is fetched fresh. The local
// Watched/hidden threads remain shared. Notification read keys persist too,
// but include the instance and recipient so they cannot collide.
function resetAccountScopedState() {
  state.notifications.requestEpoch = (state.notifications.requestEpoch || 0) + 1;
  state.notifications.loading = false;
  state.notifications.notifPaging = false;
  state.notifications.error = null;
  state.groups = [];
  state.moderatedGroups = [];
  state.search = null;
  state.notifications.items = [];
  state.notifications.notifPages = [];
  state.notifications.notifNextCursorByPage = [];
  state.notifications.notifPageIndex = 0;
  state.notifications.pendingFollowRequests.clear();
  writeNotificationsStore();
  syncWatcherIndicator();
  renderWatcherPanel();
}

function saveAuth() {
  const account = state.auth.account;
  if (!state.auth.accessToken || !account) return;
  const apiBase = state.auth.apiBase !== undefined ? state.auth.apiBase : null;
  const session = {
    id: sessionKeyFor(account, apiBase),
    accessToken: state.auth.accessToken,
    refreshToken: state.auth.refreshToken || null,
    expiresAt: state.auth.expiresAt || null,
    oauthClient: state.auth.oauthClient || null,
    needsReconnect: !!state.auth.needsReconnect,
    account,
    instance: state.auth.currentInstance || null,
    apiBase,
  };
  const store = readAccountStore();
  const idx = store.accounts.findIndex((a) => a && a.id === session.id);
  if (idx >= 0) store.accounts[idx] = session;
  else store.accounts.push(session);
  store.activeId = session.id;
  writeAccountStore(store);
}

function restoreAuth() {
  const session = getActiveSession();
  if (!session) return false;
  loadSessionIntoState(session);
  state.auth.status = state.auth.needsReconnect ? "reconnect" : "signed-in";
  saveAuth();
  return true;
}

// What to call the current instance. The server's own title once it has been
// read; until then the host the active session signed into, which is at least
// the right server. "chan" only when there's neither.
function instanceLabel() {
  return state.instance?.title || state.auth.currentInstance || "chan";
}

// Re-read /api/v1/instance for whichever session is now in state.
//
// The instance describes the server, not the account, so it used to be read
// once at boot and never again — which quietly broke multi-account: switching
// to an account on another instance left every server-shaped decision being
// made from the previous server's answers. That is the post format, whether
// object links parse, the visibility options offered, the poll limits, and the
// name in the header. Signed into Pleroma with Mitra's instance still in hand,
// the composer would send Mitra markdown.
//
// Cleared before the fetch and left unknown if it fails: the conservative
// defaults that fall out of a missing instance are wrong in smaller ways than
// another server's answers are.
async function refreshInstanceForSession() {
  state.instance = null;
  try {
    state.instance = await loadInstance(state);
  } catch {
    /* unknown beats stale */
  }
}

// Make `session` the active account: load it into state, persist activeId,
// reset account-scoped state, then reload the view and refresh groups +
// notifications for the new account. Used by switchAccount() and by logout
// when another account remains.
async function activateSession(session) {
  loadSessionIntoState(session);
  state.auth.status = state.auth.needsReconnect ? "reconnect" : "signed-in";
  state.auth.error = null;
  resetAccountScopedState();
  await refreshInstanceForSession();
  renderAuth();
  renderBoardNav();
  renderHeader();
  refreshFollowedGroups().catch(() => {});
  await navigateToCurrentHash();
  syncNotifications().catch(() => {});
}

// Menu action: switch to a different stored account.
async function switchAccount(id) {
  const store = readAccountStore();
  const session = store.accounts.find((a) => a && a.id === id);
  if (!session || id === store.activeId) return;
  store.activeId = id;
  writeAccountStore(store);
  closeAccountMenu();
  await activateSession(session);
}

// Menu action: log out the active account. If others remain, switch to the
// first of them; otherwise sign out fully.
async function logoutActiveAccount() {
  const store = readAccountStore();
  const activeId = store.activeId;
  if (!activeId) return;
  closeAccountMenu();
  store.accounts = store.accounts.filter((a) => a && a.id !== activeId);
  if (store.accounts.length) {
    const next = store.accounts[0];
    store.activeId = next.id;
    writeAccountStore(store);
    await activateSession(next);
  } else {
    store.activeId = null;
    writeAccountStore(store);
    resetAuthSignedOut();
    resetAccountScopedState();
    renderAuth();
    renderBoardNav();
    renderHeader();
    location.hash = `#/${defaultBoardName()}/`;
  }
}

// The session this tab currently mirrors in state.auth, as a store id (or
// null when signed out). Used to tell whether another tab has moved the
// active account out from under us.
function activeSessionIdInState() {
  if (!state.auth.accessToken || !state.auth.account) return null;
  return sessionKeyFor(state.auth.account, state.auth.apiBase !== undefined ? state.auth.apiBase : null);
}

// Follow account switches (and sign-ins / sign-outs) made in another tab.
// The account store is shared localStorage, so a switch elsewhere leaves this
// tab rendering the previous user's pill — and posting as, and fetching
// notifications for, whoever the *store* now says is active. Mirror the new
// active session here so the pill, board nav and view all agree.
//
// Nothing in this path writes AUTH_STORAGE_KEY, so tabs can't ping-pong: the
// tab that made the switch is the only writer.
async function syncActiveAccountFromStorage(event) {
  if (event.key !== AUTH_STORAGE_KEY || event.storageArea !== localStorage) return;
  // Mid-OAuth in this tab: the exchange finishes with saveAuth(), which makes
  // this tab's new account active. Don't yank the flow out from under it.
  if (state.auth.status === "authorizing" || state.auth.status === "exchanging") return;

  const session = getActiveSession();
  const nextId = session?.id || null;
  if (nextId === activeSessionIdInState()) {
    if (session) {
      if (state.auth.needsReconnect && !session.needsReconnect) {
        await activateSession(session);
      } else {
        loadSessionIntoState(session);
        state.auth.status = session.needsReconnect ? "reconnect" : "signed-in";
        renderAuth();
      }
    }
    return;
  }

  if (session) {
    await activateSession(session);
    return;
  }

  // Signed out everywhere (last account logged out in the other tab).
  resetAuthSignedOut();
  resetAccountScopedState();
  renderAuth();
  renderBoardNav();
  renderHeader();
  await navigateToCurrentHash();
}

window.addEventListener("chan-auth-updated", () => {
  const session = getActiveSession();
  if (session?.id !== activeSessionIdInState()) return;
  loadSessionIntoState(session);
  if (!["authorizing", "exchanging"].includes(state.auth.status)) {
    state.auth.status = session.needsReconnect ? "reconnect" : "signed-in";
  }
  renderAuth();
});

async function verifyCredentials() {
  // Bind verification to the account that started it, even if another tab or
  // the account menu switches sessions while the request is in flight.
  const id = activeSessionIdInState();
  try {
    const r = await apiFetch("/api/v1/accounts/verify_credentials");
    if (!r.ok) return false; // shared API layer handles 401 recovery
    const account = await r.json();
    if (id !== activeSessionIdInState()) return false;
    state.auth.account = account;
    state.auth.needsReconnect = false;
    state.auth.status = "signed-in";
    state.auth.error = null;
    saveAuth();
    renderAuth();
    refreshFollowedGroups().catch(() => {});
    return true;
  } catch { return false; }
}

// Refresh the signed-in user's followed groups (the custom boards in the
// nav) and the subset they admin (which gates the edit/delete controls and
// gets its own nav section). Best-effort: on failure keep whatever list we
// already have.
async function refreshFollowedGroups() {
  if (state.auth.status !== "signed-in") {
    state.groups = [];
    state.moderatedGroups = [];
    renderBoardNav();
    return;
  }
  const [followed, moderated] = await Promise.allSettled([
    loadFollowedGroups(state, "following"),
    loadFollowedGroups(state, "moderating"),
  ]);
  if (followed.status === "fulfilled") state.groups = followed.value;
  if (moderated.status === "fulfilled") state.moderatedGroups = moderated.value;

  // Custom boards are groups, and not every server has them: Pleroma and
  // Mastodon have no group API to call. Answering at all means they're
  // supported; answering "no such endpoint" means they never will be here.
  // Any other failure is a blip and leaves the question open, so a flaky
  // network can't make the board UI disappear.
  if (followed.status === "fulfilled") {
    state.groupsSupported = true;
  } else if (followed.reason?.status === 404 || followed.reason?.status === 501) {
    state.groupsSupported = false;
  }

  renderBoardNav();
  // The admin buttons on a board view depend on the moderating list.
  syncBoardAdminButtons();
}

// Whether the signed-in user administers the group with this account id.
function isModeratedGroup(accountId) {
  return !!accountId && state.moderatedGroups.some((g) => g.id === accountId);
}

// ---- dynamic app registration ----------------------------------------------

function _loadAppRegistry() {
  try {
    const raw = localStorage.getItem(APP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function _saveAppRegistry(registry) {
  try { localStorage.setItem(APP_STORAGE_KEY, JSON.stringify(registry)); } catch {}
}

function getRegisteredApp(instance) {
  const entry = _loadAppRegistry()[instance];
  if (!entry || !entry.clientId || !entry.clientSecret) return null;
  // Reject cached apps registered under a different config than we now send
  // at authorize/token time. A stale redirect_uri or scope set makes the
  // server reject the flow, and the failure surfaces as something unrelated
  // (Pleroma, for example, shows "Invalid Username/Password"). Dropping the
  // entry here forces a clean re-registration next time.
  if (entry.redirectUri !== OAUTH_CONFIG.redirectUri ||
      entry.scopes !== OAUTH_CONFIG.scopes) return null;
  return { clientId: entry.clientId, clientSecret: entry.clientSecret };
}

async function registerApp(instance) {
  // Call POST /api/v1/apps to dynamically register chan-fe on the instance.
  // Returns { clientId, clientSecret }.
  const body = new URLSearchParams();
  body.set("client_name", "chan-fe");
  body.set("redirect_uris", OAUTH_CONFIG.redirectUri);
  body.set("scopes", OAUTH_CONFIG.scopes);
  body.set("website", "https://github.com/nak/chan-fe");
  // Use same-origin for the proxied instance, cross-origin for others
  const regBase = instance === OAUTH_CONFIG.defaultInstance ? "" : `https://${instance}`;
  const url = `${regBase}/api/v1/apps`;
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`app registration failed: HTTP ${r.status} ${text.slice(0, 120)}`);
  }
  const app = await r.json();
  return { clientId: app.client_id, clientSecret: app.client_secret };
}

function saveRegisteredApp(instance, clientId, clientSecret) {
  const registry = _loadAppRegistry();
  // Stamp the config used at registration so a later config change (scopes,
  // redirect_uri) is detected by getRegisteredApp and triggers a re-register.
  registry[instance] = {
    clientId, clientSecret,
    redirectUri: OAUTH_CONFIG.redirectUri,
    scopes: OAUTH_CONFIG.scopes,
  };
  _saveAppRegistry(registry);
}

// Drops the cached app for an instance so the next sign-in re-registers.
// Backs the "Clear instance cache" action in the auth dialog and is also
// called to self-heal after a failed token exchange.
function clearRegisteredApp(instance) {
  const registry = _loadAppRegistry();
  delete registry[instance];
  _saveAppRegistry(registry);
}

// Enable/disable the "Clear instance cache" button based on whether a cached
// app exists for the instance currently typed in the auth dialog.
function refreshClearCacheButton() {
  const btn = document.getElementById("auth-clear-cache");
  if (!btn) return;
  const inst = (document.getElementById("auth-instance")?.value || "").trim().replace(/\/$/, "");
  btn.disabled = !inst || !_loadAppRegistry()[inst];
}

let pendingAuth = {};

function beginAuth() {
  pendingAuth = {};
  state.auth.status = "authorizing";
  state.auth.error = null;
  renderAuth();
}

function addAccount() {
  closeAccountMenu();
  pendingAuth = {};
  state.auth.error = null;
  openAuthDialog();
}

function reconnectAccount(id) {
  const session = readAccountStore().accounts.find((a) => a.id === id);
  if (!session) return;
  closeAccountMenu();
  pendingAuth = { reconnectId: id };
  state.auth.error = null;
  const input = document.getElementById("auth-instance");
  if (input) {
    input.value = session.instance || OAUTH_CONFIG.defaultInstance;
    input.dispatchEvent(new Event("input"));
  }
  const code = document.getElementById("auth-code");
  if (code) code.value = "";
  openAuthDialog();
}

async function exchangeCode(code) {
  if (state.auth.status === "exchanging") return;
  const flow = pendingAuth;
  const login = { ...flow };
  state.auth.status = "exchanging";
  state.auth.error = null;
  renderAuth();
  try {
    const creds = login.appCredentials;
    if (!creds) throw new Error("no app credentials; please start sign-in again");
    const body = new URLSearchParams({
      client_id: creds.clientId, client_secret: creds.clientSecret,
      code: code.trim(), grant_type: "authorization_code",
      redirect_uri: OAUTH_CONFIG.redirectUri, scope: OAUTH_CONFIG.scopes,
    });
    const r = await fetch(`${login.apiBase || ""}/oauth/token`, { method: "POST", body });
    if (!r.ok) throw new Error(`token exchange failed: HTTP ${r.status}`);
    const tok = await r.json();
    if (!tok.access_token || typeof tok.access_token !== "string") throw new Error("Server returned no access token");
    const verified = await apiFetchRaw("/api/v1/accounts/verify_credentials", {}, tok.access_token, login.apiBase);
    if (!verified.ok) throw new Error(`verify_credentials failed: HTTP ${verified.status}`);
    const account = await verified.json();
    if (!account?.id) throw new Error("Server returned no account identity");
    if (pendingAuth !== flow) return; // dialog cancelled or replaced
    const id = sessionKeyFor(account, login.apiBase);
    if (login.reconnectId && id !== login.reconnectId) {
      throw new Error("Please authorize the account you are reconnecting");
    }
    if (login.reconnectId && !readAccountStore().accounts.some((a) => a.id === id)) {
      throw new Error("This account was removed while reconnecting");
    }
    loadSessionIntoState({
      id, account, accessToken: tok.access_token, ...tokenMetadata(tok),
      instance: login.currentInstance, apiBase: login.apiBase, oauthClient: creds,
    });
    state.auth.status = "signed-in";
    saveAuth();
    pendingAuth = {};
    closeAuthDialog();
    await activateSession(getActiveSession());
  } catch (e) {
    if (pendingAuth !== flow) return;
    loadSessionIntoState(getActiveSession());
    state.auth.status = "error";
    state.auth.error = e.message || String(e);
    renderAuth();
  }
}

function openAuthDialog()  {
  setAuthDialogTitle();
  $("#auth-dialog")?.classList.add("is-open");
  renderAuthDialogStatus();
  refreshClearCacheButton();
}
function closeAuthDialog() { $("#auth-dialog")?.classList.remove("is-open"); }

// Close the auth dialog and restore the correct auth status. Cancelling an
// add-account flow must NOT drop the existing sign-in, so if a valid session
// is still on file we restore it (undoing any partial exchange mutation);
// otherwise we drop to signed-out.
function dismissAuthDialog() {
  closeAuthDialog();
  pendingAuth = {};
  const active = getActiveSession();
  if (active) {
    loadSessionIntoState(active);
    state.auth.status = state.auth.needsReconnect ? "reconnect" : "signed-in";
  } else {
    resetAuthSignedOut();
  }
  state.auth.error = null;
  renderAuth();
  renderAuthDialogStatus();
}

// The dialog doubles as the "add account" flow when accounts already exist.
function setAuthDialogTitle() {
  const title = document.getElementById("auth-dialog-title");
  if (!title) return;
  title.textContent = pendingAuth.reconnectId ? "Reconnect account" : readAccountStore().accounts.length ? "Add account" : "Sign in";
}

// ---- account menu (multi-account switcher) ---------------------------------

// Compact label for an account in the menu: "@acct", with the instance shown
// via the row title (helps tell same-username accounts on different hosts
// apart, since Mitra's local acct omits the host).
function accountMenuLabel(account) {
  const acct = account?.acct || account?.username || "?";
  return "@" + acct;
}
function accountMenuInstance(session) {
  return session?.instance || "";
}

function renderAccountMenu() {
  const menu = document.getElementById("account-menu");
  if (!menu) return;
  menu.innerHTML = "";
  const store = readAccountStore();
  const accounts = store.accounts || [];
  if (!accounts.length) { menu.hidden = true; return; }

  const active = accounts.find((a) => a.id === store.activeId);
  const others = accounts.filter((a) => a.id !== store.activeId);

  if (active) {
    const reconnect = document.createElement("button");
    reconnect.type = "button";
    reconnect.className = "account-menu-item";
    reconnect.dataset.act = "reconnect";
    reconnect.dataset.id = active.id;
    reconnect.textContent = active.needsReconnect ? "Session expired — Reconnect" : "Reconnect account";
    menu.appendChild(reconnect);
    const item = document.createElement("button");
    item.type = "button";
    item.className = "account-menu-item is-active";
    item.dataset.act = "logout";
    item.dataset.id = active.id;
    item.innerHTML = `<i class="fa-solid fa-right-from-bracket"></i> Logout ${escapeHTML(accountMenuLabel(active.account))}`;
    item.title = accountMenuInstance(active);
    menu.appendChild(item);
  }

  if (others.length) {
    const sep = document.createElement("div");
    sep.className = "account-menu-sep";
    menu.appendChild(sep);
    for (const a of others) {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "account-menu-item";
      item.dataset.act = "switch";
      item.dataset.id = a.id;
      item.innerHTML = `<i class="fa-solid fa-arrow-right-arrow-left"></i> Switch to ${escapeHTML(accountMenuLabel(a.account))}`;
      item.title = accountMenuInstance(a);
      menu.appendChild(item);
    }
  }

  const sep = document.createElement("div");
  sep.className = "account-menu-sep";
  menu.appendChild(sep);
  const add = document.createElement("button");
  add.type = "button";
  add.className = "account-menu-item";
  add.dataset.act = "add";
  add.innerHTML = `<i class="fa-solid fa-plus"></i> Add Account`;
  menu.appendChild(add);
}

function toggleAccountMenu() {
  const menu = document.getElementById("account-menu");
  const btn = document.getElementById("account-menu-btn");
  if (!menu || !btn) return;
  const open = menu.hidden;
  if (open) renderAccountMenu();
  menu.hidden = !open;
  btn.setAttribute("aria-expanded", open ? "true" : "false");
}

function closeAccountMenu() {
  const menu = document.getElementById("account-menu");
  const btn = document.getElementById("account-menu-btn");
  if (menu) menu.hidden = true;
  if (btn) btn.setAttribute("aria-expanded", "false");
}

// ---- user settings (config modal) -----------------------------------------
//
// Settings live in localStorage under a single versioned key. The shape is
// flat: each top-level field is a setting. Today only autoRefresh is
// implemented; future fields (theme override, NSFW handling, etc.) can be
// added next to it without touching the modal code.

const SETTINGS_STORAGE_KEY = "chan_fe_settings_v1";
const NOTIF_STORAGE_KEY = "chan_fe_notifications_v1";
function notificationScope() {
  // Legacy srv:<id> keys have no owner and deliberately do not match this
  // prefix: assigning them to the active account could hide unrelated alerts.
  return JSON.stringify([state.auth.apiBase || location.origin, state.auth.account?.id || ""]);
}

function notificationRequestIsCurrent(epoch, scope, token) {
  return epoch === (state.notifications.requestEpoch || 0)
    && scope === notificationScope() && token === state.auth.accessToken;
}

function rememberNotifications(items) {
  for (const item of items) {
    state.notifications.knownNotifKeys.add(item.key);
    if (item.serverSeen) state.notifications.readNotifKeys.add(item.key);
  }
}
// How long the pointer must rest on an unread notification before it's marked
// read — long enough that sweeping past on the way elsewhere doesn't count.
const NOTIF_READ_DWELL_MS = 450;
const DEFAULT_SETTINGS = Object.freeze({
  autoRefreshThreads: true,     // silent in-place poll; new replies stream in
  autoRefreshCatalogs: false,   // re-fetches the whole page each tick (noisy)
  autoRefreshIntervalSec: 30,   // honour the >= 10s minimum the modal enforces
  blurNsfwMedia: true,
  // "catalog" (thumbnail grid) or "index" (OP plus its last few replies).
  // Persisted rather than per-view: which of the two a reader wants is a
  // standing preference, not something to re-pick on every board.
  viewMode: "catalog",
  showActivityHover: true,
  collapseLeadingMentions: true,
  copyMentionsOnReply: true,
  anonymousMode: false,
  // Video playback defaults, per place a video plays. Hover previews start
  // muted + looping (they're glanceable); the click-to-expand inline player
  // is a deliberate act, so it plays through once with sound.
  videoCatalogHoverLoop: true,
  videoCatalogHoverMuted: true,
  videoThreadHoverLoop: true,
  videoThreadHoverMuted: true,
  videoExpandedLoop: false,
  videoExpandedMuted: false,
  videoGalleryLoop: false,
  videoGalleryMuted: false,
  blurredAccounts: [],
  // Accounts whose threads are kept out of catalogs. Muting hides a user's
  // own posts, but a muted user's OP still gets bumped onto the catalog by
  // replies from people you haven't muted — this drops the card outright.
  hiddenOpAccounts: [],
  mutedWords: [],             // browser-local, case-insensitive OP text filters
  tagBoards: [],   // local-only hashtag boards: bare tag names, no leading "#"
});

// In-memory snapshot of the user's settings. Always reflects the latest
// stored value (or the defaults) so the auto-refresh timer and the modal
// share one source of truth.
let userSettings = loadSettings();

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    // Merge with defaults so newly-added fields are populated.
    const merged = { ...DEFAULT_SETTINGS, ...parsed };
    // Migrate the pre-split master auto-refresh toggle onto the per-scope
    // flags, preserving a returning user's prior opt-in (and dropping the
    // now-unused field). Threads/Catalogs are independent toggles today.
    if (
      !("autoRefreshThreads" in parsed) &&
      !("autoRefreshCatalogs" in parsed) &&
      "autoRefreshEnabled" in parsed
    ) {
      merged.autoRefreshThreads = !!parsed.autoRefreshEnabled;
      merged.autoRefreshCatalogs = !!parsed.autoRefreshEnabled;
    }
    delete merged.autoRefreshEnabled;
    merged.mutedWords = normalizeMutedWords(merged.mutedWords);
    return merged;
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveSettings(next) {
  userSettings = { ...userSettings, ...next };
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(userSettings));
  } catch {
    // localStorage may be full or disabled (private mode) — silent
    // failure. The in-memory copy is still authoritative for this tab.
  }
}

function applyThemeFromSettings() {
  document.documentElement.classList.toggle("nsfw-theme", !userSettings.blurNsfwMedia);
  applySavedTheme(!userSettings.blurNsfwMedia);
  document.documentElement.classList.toggle("anonymous-mode", !!userSettings.anonymousMode);
}

// Apply before the first async render to avoid a blue-theme flash for users
// who have opted into unblurred NSFW media.
applyThemeFromSettings();

// The gallery module can't import userSettings (circular), so hand it a live
// getter for its video defaults.
setGalleryVideoDefaults(() => ({
  loop: !!userSettings.videoGalleryLoop,
  muted: !!userSettings.videoGalleryMuted,
}));

function rerenderModerationMedia() {
  // Sensitivity changes alter the media DOM (overlay, badge and reveal
  // button), so rebuild the reconciled view instead of only updating counts.
  $("#catalog")?.replaceChildren();
  render();
}

function clampAutoRefreshInterval(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 10) return 10;
  return Math.floor(v);
}

function readNotificationsStore() {
  try {
    const raw = localStorage.getItem(NOTIF_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const watchedThreads = Array.isArray(parsed.watchedThreads)
      ? parsed.watchedThreads
          .filter((x) => x && typeof x.id === "string")
          .map((x) => ({ id: x.id, title: x.title || "" }))
      : [];

    const hiddenThreads = Array.isArray(parsed.hiddenThreads)
      ? parsed.hiddenThreads
          .filter((x) => x && typeof x.id === "string")
          .map((x) => ({ id: x.id, title: x.title || "" }))
      : [];

    // Back-compat: older shape used knownKeys/readKeys.
    const readNotifKeys = Array.isArray(parsed.readNotifKeys)
      ? parsed.readNotifKeys.filter((x) => typeof x === "string")
      : (Array.isArray(parsed.readKeys) ? parsed.readKeys.filter((x) => typeof x === "string") : []);

    const knownNotifKeys = Array.isArray(parsed.knownNotifKeys)
      ? parsed.knownNotifKeys.filter((x) => typeof x === "string")
      : (Array.isArray(parsed.knownKeys) ? parsed.knownKeys.filter((x) => typeof x === "string") : []);

    const threadSeenObj = parsed.threadSeen && typeof parsed.threadSeen === "object"
      ? parsed.threadSeen
      : {};
    const threadSeen = new Map();
    for (const [id, snap] of Object.entries(threadSeenObj)) {
      if (!id || !snap || typeof snap !== "object") continue;
      threadSeen.set(id, {
        descendantsCount: Number(snap.descendantsCount) || 0,
        latestId: snap.latestId || "",
        latestAt: snap.latestAt || "",
      });
    }

    const threadUnreadObj = parsed.threadUnread && typeof parsed.threadUnread === "object"
      ? parsed.threadUnread
      : {};
    const threadUnread = new Map();
    for (const [id, count] of Object.entries(threadUnreadObj)) {
      if (!id) continue;
      threadUnread.set(id, Math.max(0, Number(count) || 0));
    }

    const threadMetaObj = parsed.threadMeta && typeof parsed.threadMeta === "object"
      ? parsed.threadMeta
      : {};
    const threadMeta = new Map();
    for (const [id, m] of Object.entries(threadMetaObj)) {
      if (!id || !m || typeof m !== "object") continue;
      threadMeta.set(id, {
        actor: m.actor || "",
        snippet: m.snippet || "",
        latestAt: m.latestAt || "",
      });
    }

    const threadReadAtObj = parsed.threadReadAt && typeof parsed.threadReadAt === "object"
      ? parsed.threadReadAt
      : {};
    const threadReadAt = new Map();
    for (const [id, at] of Object.entries(threadReadAtObj)) {
      if (id && typeof at === "string") threadReadAt.set(id, at);
    }

    const activeTab = ["notifications", "hidden"].includes(parsed.activeTab)
      ? parsed.activeTab
      : "watcher";

    return {
      watchedThreads,
      hiddenThreads,
      readNotifKeys,
      knownNotifKeys,
      threadSeen,
      threadUnread,
      threadMeta,
      threadReadAt,
      notifPageIndex: Math.max(0, Number(parsed.notifPageIndex) || 0),
      activeTab,
    };
  } catch {
    return null;
  }
}

function writeNotificationsStore({ mergeStoredReadState = true } = {}) {
  try {
    // Read state is monotonic (there is no "mark unread" action). Merge the
    // latest stored keys before writing so a background tab with stale memory
    // cannot erase notifications that another tab has already marked read.
    // Sign-out explicitly opts out so clearing local account state still works.
    if (mergeStoredReadState) {
      const stored = readNotificationsStore();
      for (const key of stored?.readNotifKeys || []) state.notifications.readNotifKeys.add(key);
      for (const key of stored?.knownNotifKeys || []) state.notifications.knownNotifKeys.add(key);
    }

    const threadSeen = Object.fromEntries(
      Array.from(state.notifications.threadSeen.entries()).map(([id, snap]) => [id, {
        descendantsCount: Number(snap?.descendantsCount) || 0,
        latestId: snap?.latestId || "",
        latestAt: snap?.latestAt || "",
      }])
    );

    const threadUnread = Object.fromEntries(
      Array.from(state.notifications.threadUnread.entries()).map(([id, n]) => [id, Math.max(0, Number(n) || 0)])
    );

    const threadMeta = Object.fromEntries(
      Array.from(state.notifications.threadMeta.entries()).map(([id, m]) => [id, {
        actor: m?.actor || "",
        snippet: m?.snippet || "",
        latestAt: m?.latestAt || "",
      }])
    );

    const threadReadAt = Object.fromEntries(state.notifications.threadReadAt.entries());

    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify({
      watchedThreads: state.notifications.watchedThreads,
      hiddenThreads: state.notifications.hiddenThreads,
      readNotifKeys: Array.from(state.notifications.readNotifKeys),
      knownNotifKeys: Array.from(state.notifications.knownNotifKeys),
      threadSeen,
      threadUnread,
      threadMeta,
      threadReadAt,
      notifPageIndex: state.notifications.notifPageIndex,
      activeTab: state.notifications.activeTab,
    }));
  } catch {
    // localStorage may be unavailable/full — keep in-memory state.
  }
}

function restoreNotificationsStore() {
  const saved = readNotificationsStore();
  if (!saved) return;
  state.notifications.watchedThreads = saved.watchedThreads;
  state.notifications.hiddenThreads = saved.hiddenThreads || [];
  state.notifications.readNotifKeys = new Set(saved.readNotifKeys);
  state.notifications.knownNotifKeys = new Set(saved.knownNotifKeys);
  state.notifications.threadSeen = saved.threadSeen;
  state.notifications.threadUnread = saved.threadUnread;
  state.notifications.threadMeta = saved.threadMeta;
  state.notifications.threadReadAt = saved.threadReadAt || new Map();
  state.notifications.notifPageIndex = saved.notifPageIndex || 0;
  state.notifications.activeTab = saved.activeTab;
}

function syncWatcherStateFromStore(saved) {
  let changed = false;
  // Counts can increase on a poll and decrease when read. Adopt the stored
  // snapshot together with its counts so another tab's next poll doesn't
  // count those same replies again. These fields are shared across accounts.
  for (const key of ["watchedThreads", "threadSeen", "threadUnread", "threadMeta"]) {
    const current = state.notifications[key];
    const next = saved[key];
    const entries = (value) => value instanceof Map ? Array.from(value.entries()) : value;
    if (JSON.stringify(entries(current)) === JSON.stringify(entries(next))) continue;
    state.notifications[key] = next;
    changed = true;
  }
  return changed;
}

function syncNotificationStateFromStorage(event) {
  if (event.key !== NOTIF_STORAGE_KEY || event.storageArea !== localStorage || !event.newValue) return;
  const saved = readNotificationsStore();
  if (!saved) return;

  let changed = syncWatcherStateFromStore(saved);
  for (const key of saved.readNotifKeys) {
    if (state.notifications.readNotifKeys.has(key)) continue;
    state.notifications.readNotifKeys.add(key);
    changed = true;
  }
  for (const key of saved.knownNotifKeys) {
    if (!state.notifications.knownNotifKeys.has(key)) changed = true;
    state.notifications.knownNotifKeys.add(key);
  }
  if (!changed) return;

  syncWatcherIndicator();
  renderWatcherPanel();
}

function compactAgeLabel(iso) {
  if (!iso) return "[now]";
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `[${sec}s]`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `[${min}m]`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `[${hr}h]`;
  const day = Math.floor(hr / 24);
  if (day < 365) return `[${day}d]`;
  const year = Math.floor(day / 365);
  return `[${year}y]`;
}

function compactAcct(acct, maxLen = 20) {
  let raw = String(acct || "someone");
  if (raw.startsWith("@")) raw = raw.slice(1);
  if (raw.length > maxLen) raw = raw.slice(0, Math.max(1, maxLen - 1)) + "…";
  return "@" + raw;
}

function compactSnippet(text, maxLen = 34) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (s.length <= maxLen) return s;
  return s.slice(0, Math.max(1, maxLen - 1)) + "…";
}

function flattenNotificationPages() {
  return (state.notifications.notifPages || []).flat();
}

function clampNotificationPageIndex() {
  const pageCount = state.notifications.notifPages.length;
  if (pageCount <= 0) {
    state.notifications.notifPageIndex = 0;
    return;
  }
  if (state.notifications.notifPageIndex < 0) state.notifications.notifPageIndex = 0;
  if (state.notifications.notifPageIndex >= pageCount) {
    state.notifications.notifPageIndex = pageCount - 1;
  }
}

function refreshFlattenedNotifications() {
  clampNotificationPageIndex();
  state.notifications.items = flattenNotificationPages();
}

function watcherUnreadCount() {
  let n = 0;
  for (const v of state.notifications.threadUnread.values()) n += Math.max(0, Number(v) || 0);
  return n;
}

function notificationUnreadCount() {
  let n = 0;
  if (state.auth.status !== "signed-in") return n;
  const prefix = `srv:${notificationScope()}:`;
  const userPrefix = `usr:${notificationScope()}:`;
  for (const key of state.notifications.knownNotifKeys) {
    if ((key.startsWith(prefix) || key.startsWith(userPrefix))
      && !state.notifications.readNotifKeys.has(key)) n++;
  }
  return n;
}

// The one definition of "there is unread activity", shared by the toolbar eye
// and the tab favicon so the two can never disagree: pending notifications or
// watched-thread replies.
function hasUnreadActivity() {
  return watcherUnreadCount() > 0 || notificationUnreadCount() > 0;
}

// The tab favicon turns red while there's unread activity, matching the eye.
// It deliberately does NOT count unacknowledged replies in the thread being
// read: that count only clears on an explicit catch-up (clicking the new-
// replies bar or scrolling to the bottom), so a reader who scrolled partway
// down was left with a red tab while the eye sat subdued, until a route change
// reset the thread's baseline. Replies to a watched thread still redden both,
// via the watcher count. Memoised so the frequent render() calls don't thrash
// the <link> href.
let _faviconIsRed = null;
function syncFavicon() {
  const hasUnread = hasUnreadActivity();
  if (hasUnread === _faviconIsRed) return;
  _faviconIsRed = hasUnread;
  const favicon = document.getElementById("favicon");
  if (favicon) favicon.href = hasUnread ? "./favicon-red.ico" : "./favicon.ico";
}

function syncWatcherIndicator() {
  const btn = document.getElementById("toolbar-eye");
  if (!btn) return;
  btn.classList.toggle("has-unread", hasUnreadActivity());
  syncFavicon();
}

function markWatcherRead() {
  for (const key of state.notifications.threadUnread.keys()) {
    state.notifications.threadUnread.set(key, 0);
  }
  writeNotificationsStore();
}

function acknowledgeWatchedThread(context) {
  const opId = context?.op?.id;
  if (!opId || !isThreadWatched(opId)) return;
  const snap = snapshotThread(context);
  const seen = state.notifications.threadSeen.get(opId);
  const unread = state.notifications.threadUnread.get(opId) || 0;
  const caughtUp = !seen || snap.latestAt > (seen.latestAt || "")
    || (snap.latestAt === (seen.latestAt || "")
      && snap.descendantsCount >= (Number(seen.descendantsCount) || 0));
  // The watcher may have fetched replies that this thread view hasn't loaded
  // yet. Keep those unread, and never move its polling baseline backwards.
  const remaining = caughtUp ? 0 : Math.min(unread, Math.max(0,
    (Number(seen?.descendantsCount) || 0) - snap.descendantsCount));
  let changed = remaining !== unread;
  state.notifications.threadUnread.set(opId, remaining);
  if (caughtUp) {
    const next = {
      descendantsCount: snap.descendantsCount,
      latestId: snap.latestId,
      latestAt: snap.latestAt,
    };
    if (JSON.stringify(seen) !== JSON.stringify(next)) changed = true;
    state.notifications.threadSeen.set(opId, next);
  }
  if (!changed) return;
  writeNotificationsStore();
  syncWatcherIndicator();
  renderWatcherPanel();
}

function markAllNotificationsRead() {
  const prefix = `srv:${notificationScope()}:`;
  const userPrefix = `usr:${notificationScope()}:`;
  for (const key of state.notifications.knownNotifKeys) {
    if (key.startsWith(prefix) || key.startsWith(userPrefix)) state.notifications.readNotifKeys.add(key);
  }
  writeNotificationsStore();
  syncWatcherIndicator();
}

function markActiveWatcherTabRead() {
  // Notifications are no longer cleared just by opening/switching to the tab —
  // each clears on hover-dwell or click (see renderWatcherNotificationsPane).
  // Watched threads keep the mark-on-open behavior; the hidden tab has no
  // unread concept.
  if (state.notifications.activeTab === "watcher") markWatcherRead();
}

function currentThreadWatchInfo() {
  if (state.view?.kind !== "post") return null;
  const opId = getCurrentPostRootId() || state.view.id;
  if (!opId) return null;
  const opStatus = state.postView?.context?.op;
  const extracted = extractSummary(opStatus);
  const title = opStatus
    ? (extracted.summary || `${compactAcct(opStatus.account?.acct || opStatus.account?.username || "post")} - ${compactSnippet(stripHTML(extracted.strippedContent)) || postRefLabel(opStatus)}`)
    : postRefLabel(opId, "Thread");
  return { id: opId, title };
}

function isThreadWatched(opId) {
  return state.notifications.watchedThreads.some((t) => t.id === opId);
}

function toggleWatchThread(opId, title = "") {
  if (!opId) return;
  if (isThreadWatched(opId)) {
    state.notifications.watchedThreads = state.notifications.watchedThreads.filter((t) => t.id !== opId);
    state.notifications.threadUnread.delete(opId);
    state.notifications.threadMeta.delete(opId);
  } else {
    state.notifications.watchedThreads.push({ id: opId, title: title || postRefLabel(opId, "Thread") });
  }
  writeNotificationsStore();
  renderWatcherPanel();
  syncWatcherIndicator();
}

function isThreadHidden(opId) {
  return state.notifications.hiddenThreads.some((t) => t.id === opId);
}

// Hide a thread from the catalog. Persisted and surfaced in the watcher's
// "Hidden Threads" tab, where it can be restored.
function hideThread(opId, title = "") {
  if (!opId || isThreadHidden(opId)) return;
  state.notifications.hiddenThreads.push({ id: opId, title: title || postRefLabel(opId, "Thread") });
  writeNotificationsStore();
  render();                 // drop the now-hidden card from the catalog
  renderWatcherPanel();
}

function unhideThread(opId) {
  if (!opId) return;
  state.notifications.hiddenThreads = state.notifications.hiddenThreads.filter((t) => t.id !== opId);
  writeNotificationsStore();
  render();                 // the thread can reappear in the catalog
  renderWatcherPanel();
}

function watcherPanelEl() {
  return document.getElementById("watcher-panel");
}

function isWatcherPanelOpen() {
  const el = watcherPanelEl();
  return !!el && !el.hidden;
}

const WATCHER_POSITION_KEY = "chan_fe_watcher_position_v1";
let watcherPanelPosition = loadWatcherPanelPosition();

function loadWatcherPanelPosition() {
  try {
    return normalizePanelPosition(JSON.parse(localStorage.getItem(WATCHER_POSITION_KEY)));
  } catch {
    return normalizePanelPosition(null);
  }
}

function watcherPanelBounds() {
  const panel = watcherPanelEl();
  const top = Math.round((document.querySelector(".topbar")?.getBoundingClientRect().bottom || 22) + 4);
  panel.style.setProperty("--watcher-top-limit", `${top}px`);
  const rect = panel.getBoundingClientRect();
  return {
    minLeft: 4, minTop: top,
    maxLeft: Math.max(4, window.innerWidth - rect.width - 4),
    maxTop: Math.max(top, window.innerHeight - rect.height - 4),
  };
}

function positionWatcherPanel(position = watcherPanelPosition) {
  const panel = watcherPanelEl();
  if (!panel || panel.hidden) return;
  const point = resolvePanelPosition(position, watcherPanelBounds());
  panel.style.left = `${point.left}px`;
  panel.style.top = `${point.top}px`;
}

function wireWatcherPanelPosition() {
  const panel = watcherPanelEl();
  const handle = panel?.querySelector(".watcher-head");
  if (!handle) return;
  handle.title = "Drag to move or snap to a corner";
  wirePanelDrag(panel, handle, {
    onMove(left, top) {
      const position = snapPanelPosition(left, top, watcherPanelBounds());
      panel.classList.toggle("is-snapping", !!position.corner);
      positionWatcherPanel(position);
    },
    onDrop(event) {
      panel.classList.remove("is-snapping");
      if (event?.type !== "pointercancel") {
        const rect = panel.getBoundingClientRect();
        watcherPanelPosition = snapPanelPosition(rect.left, rect.top, watcherPanelBounds());
        try { localStorage.setItem(WATCHER_POSITION_KEY, JSON.stringify(watcherPanelPosition)); } catch {}
      }
      positionWatcherPanel();
    },
  });
  const reposition = () => {
    if (_activeDrag?.box !== panel) positionWatcherPanel();
  };
  window.addEventListener("resize", reposition);
  new ResizeObserver(reposition).observe(panel);
}

function setWatcherTab(tab) {
  state.notifications.activeTab = ["notifications", "hidden"].includes(tab) ? tab : "watcher";
  markActiveWatcherTabRead();
  writeNotificationsStore();
  syncWatcherIndicator();
  renderWatcherPanel();
}

function openWatcherPanel() {
  const el = watcherPanelEl();
  if (!el) return;
  el.hidden = false;
  markActiveWatcherTabRead();
  writeNotificationsStore();
  syncWatcherIndicator();
  renderWatcherPanel();
  positionWatcherPanel();
  syncNotifications().catch(() => {});
}

function closeWatcherPanel() {
  const el = watcherPanelEl();
  if (!el) return;
  if (_activeDrag?.box === el) _dragEnd({ type: "pointercancel" });
  el.hidden = true;
}

function renderWatcherThreadsPane(pane) {
  pane.innerHTML = "";
  const watched = state.notifications.watchedThreads || [];
  if (!watched.length) {
    pane.innerHTML = '<div class="empty">No watched threads.</div>';
    return;
  }

  for (const t of watched) {
    const opId = t.id;
    const unread = Math.max(0, Number(state.notifications.threadUnread.get(opId) || 0));
    const meta = state.notifications.threadMeta.get(opId) || {};
    const age = compactAgeLabel(meta.latestAt || state.notifications.threadSeen.get(opId)?.latestAt || "");
    const actor = compactAcct(meta.actor || t.title || "post");
    const snippet = compactSnippet(meta.snippet || t.title || postRefLabel(opId, "Thread"));

    const row = document.createElement("div");
    row.className = "watcher-row" + (unread > 0 ? " is-unread" : "");

    const remove = document.createElement("a");
    remove.href = "javascript:;";
    remove.className = "watcher-remove";
    remove.innerHTML = '<i class="fa-solid fa-xmark"></i>';
    remove.title = "Unwatch thread";
    remove.addEventListener("click", (e) => {
      e.preventDefault();
      toggleWatchThread(opId);
    });

    const link = document.createElement("a");
    link.href = threadHref(opId);
    link.className = "watcher-link";
    link.innerHTML = `<span class="watcher-age">${escapeHTML(age)}</span><span class="watcher-unread">(${unread})</span><span class="watcher-sum"><span class="watcher-actor">${escapeHTML(actor)}</span> - ${escapeHTML(snippet)}</span>`;
    link.addEventListener("click", () => {
      state.notifications.threadUnread.set(opId, 0);
      writeNotificationsStore();
      syncWatcherIndicator();
      if (isWatcherPanelOpen() && state.notifications.activeTab === "watcher") renderWatcherPanel();
      closeWatcherPanel();
    });

    row.appendChild(remove);
    row.appendChild(link);
    pane.appendChild(row);
  }
}

function renderWatcherHiddenPane(pane) {
  pane.innerHTML = "";
  const hidden = state.notifications.hiddenThreads || [];
  if (!hidden.length) {
    pane.innerHTML = '<div class="empty">No hidden threads.</div>';
    return;
  }

  for (const t of hidden) {
    const opId = t.id;
    const label = compactSnippet(t.title || postRefLabel(opId, "Thread"));

    const row = document.createElement("div");
    row.className = "watcher-row";

    const remove = document.createElement("a");
    remove.href = "javascript:;";
    remove.className = "watcher-remove";
    remove.innerHTML = '<i class="fa-solid fa-xmark"></i>';
    remove.title = "Unhide thread";
    remove.addEventListener("click", (e) => {
      e.preventDefault();
      unhideThread(opId);
    });

    const link = document.createElement("a");
    link.href = threadHref(opId);
    link.className = "watcher-link";
    link.innerHTML = `<span class="watcher-sum">${escapeHTML(label)}</span>`;
    link.addEventListener("click", () => closeWatcherPanel());

    row.appendChild(remove);
    row.appendChild(link);
    pane.appendChild(row);
  }
}

function normalizeReactionName(name) {
  return String(name || "").trim().replace(/^:+|:+$/g, "");
}

function resolveNotificationReactionEmojiUrl(notification, status, rawName) {
  const name = normalizeReactionName(rawName);

  const direct = [
    notification?.emoji_url,
    notification?.reaction_url,
    notification?.url,
    notification?.emoji?.url,
  ];
  for (const url of direct) {
    if (isSafeEmojiSrc(url)) return url;
  }

  const reactionLists = [
    status?.pleroma?.emoji_reactions,
    status?.reblog?.pleroma?.emoji_reactions,
  ];
  for (const list of reactionLists) {
    for (const r of (list || [])) {
      if (!r?.name) continue;
      if (normalizeReactionName(r.name) !== name) continue;
      if (isSafeEmojiSrc(r.url)) return r.url;
    }
  }

  const emojiLists = [
    status?.emojis,
    status?.account?.emojis,
    notification?.account?.emojis,
  ];
  for (const list of emojiLists) {
    for (const e of (list || [])) {
      if (!e?.shortcode) continue;
      if (normalizeReactionName(e.shortcode) !== name) continue;
      const src = emojiSrc(e);
      if (isSafeEmojiSrc(src)) return src;
    }
  }

  return "";
}

function normalizeNotificationFromServer(n, scope = notificationScope()) {
  if (!n || typeof n !== "object") return null;
  const id = String(n.id || "");
  const type = String(n.type || "").toLowerCase();
  const account = n.account || {};
  const status = n.status || null;
  // Full, untruncated handle: used both for the profile link (profileHref) and
  // the display label. Truncation is now layout-driven (CSS ellipsis), not a
  // hard JS limit, so the handle uses whatever width the row affords.
  const actorAcct = account.acct || account.username || "someone";
  const actorLabel = "@" + actorAcct;

  // The verb is everything *after* the actor — rendered as its own link to the
  // post, separate from the actor link to the profile. `verbParts` carries the
  // text/emoji segments; `verbText` is the plain-text form for the row tooltip.
  let verbText = `${type} (you)`;
  let verbParts = null;

  if (type === "favourite") {
    verbText = "liked your post";
  } else if (type === "reblog") {
    verbText = "reposted (you)";
  } else if (type === "follow") {
    verbText = "followed (you)";
  } else if (type === "follow_request") {
    verbText = "requested to follow you";
  } else if (type === "mention") {
    verbText = "replied to (you)";
  } else if (type === "status") {
    verbText = "posted";
  } else if (type === "pleroma:emoji_reaction") {
    const emojiName = normalizeReactionName(n.emoji || n.reaction || "emoji");
    const emojiUrl = resolveNotificationReactionEmojiUrl(n, status, emojiName);
    if (emojiUrl) {
      verbText = "reacted (you)";
      verbParts = [
        { type: "emoji", name: emojiName, url: emojiUrl },
        { type: "text", text: " reacted (you)" },
      ];
    } else {
      // If it's likely a unicode emoji, render as-is; otherwise shortcode form.
      const looksUnicode = /[^A-Za-z0-9_+-]/u.test(emojiName);
      verbText = looksUnicode
        ? `${emojiName} reacted (you)`
        : `:${emojiName}: reacted (you)`;
    }
  } else if (type === "move") {
    // Account moved to a new one: surface the destination handle (linked).
    const target = n.target || {};
    const targetAcct = target.acct || target.username || "";
    if (targetAcct) {
      verbText = `moved to @${targetAcct}`;
      verbParts = [
        { type: "text", text: "moved to " },
        { type: "link", text: `@${targetAcct}`, acct: targetAcct, account: target },
      ];
    } else {
      verbText = "moved";
    }
  } else if (type === "admin.sign_up") {
    verbText = "signed up";
  }

  return {
    key: `srv:${scope}:${id || `${type}:${status?.id || ""}:${n.created_at || ""}`}`,
    serverSeen: n.pleroma?.is_seen === true,
    createdAt: n.created_at || status?.created_at || "",
    account,                 // retained for profileHref + future profile hover card
    type,
    accountId: String(account.id || ""),
    actorAcct,
    actorLabel,
    verbText,
    verbParts,
    isFollow: type === "follow" || type === "follow_request",
    message: `${actorLabel} ${verbText}`, // full untruncated text, for row title
    statusId: status?.id || "",
    statusObj: status || null,
  };
}

async function fetchPendingFollowRequests() {
  if (state.auth.status !== "signed-in") return [];
  try {
    const r = await apiFetch("/api/v1/follow_requests?limit=40");
    if (!r.ok) return null;
    return r.json();
  } catch {
    return null;
  }
}

async function resolveFollowRequest(accountId, decision) {
  if (!accountId || !["authorize", "reject"].includes(decision)) return;
  const r = await apiFetch(`/api/v1/follow_requests/${encodeURIComponent(accountId)}/${decision}`, { method: "POST" });
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
  const relationship = await r.json();
  state.notifications.pendingFollowRequests.delete(accountId);
  if (state.view?.account?.id === accountId) {
    state.view.relationship = relationship;
    syncProfileFollowButton();
    syncProfileFollowsYouBadge();
    syncProfileModerationMenu();
  }
  renderWatcherPanel();
}

async function fetchServerNotificationsPage({ maxId = null, limit = 20 } = {}) {
  if (state.auth.status !== "signed-in") {
    return { items: [], nextMaxId: null };
  }

  const scope = notificationScope();
  const q = new URLSearchParams();
  q.set("limit", String(limit));
  if (maxId) q.set("max_id", String(maxId));

  const r = await apiFetch(`/api/v1/notifications?${q.toString()}`);
  if (!r.ok) throw new Error(`Notifications: HTTP ${r.status}`);

  const rows = await r.json();
  if (!Array.isArray(rows)) throw new Error("Invalid notifications response");
  const items = rows.map(row => normalizeNotificationFromServer(row, scope)).filter(Boolean);
  const nextMaxId = notificationCursor(r.headers.get("Link"), rows, limit);

  return { items, nextMaxId };
}

function snapshotThread(ctx) {
  const descendants = ctx?.descendants || [];
  let latestAt = "";
  let latestId = "";
  let latestStatus = null;
  for (const s of descendants) {
    const at = s?.created_at || "";
    const id = s?.id || "";
    if ((at || "") > (latestAt || "") || ((at || "") === (latestAt || "") && (id || "") > (latestId || ""))) {
      latestAt = at;
      latestId = id;
      latestStatus = s;
    }
  }
  return { descendantsCount: descendants.length, latestAt, latestId, latestStatus };
}

// Open the notified post, not just its thread. loadPost() already re-roots a
// permalinked reply on the thread OP and marks/scrolls to the requested post,
// so navigating to the notification's own status id lands on that reply
// instead of dumping the user at the top of the conversation.
async function navigateToNotificationThread(item) {
  const targetId = item?.statusId || "";
  if (!targetId) return;
  const href = threadHref(targetId);
  // Re-clicking the same notification leaves the hash unchanged (no
  // hashchange, no navigation), so load it directly in that case.
  if (location.hash === href) await loadPost(targetId);
  else location.hash = href;
}

// Fill `node` (the verb link/span) with the notification's verb segments. Uses
// the structured `verbParts` when present (e.g. an inline reaction emoji),
// otherwise the plain `verbText`. No hard truncation — CSS handles overflow.
function renderNotificationVerb(node, item, ctx) {
  if (!Array.isArray(item?.verbParts) || !item.verbParts.length) {
    node.textContent = item?.verbText || "";
    return;
  }

  for (const part of item.verbParts) {
    if (!part) continue;
    if (part.type === "emoji" && part.url) {
      const img = document.createElement("img");
      img.className = "watcher-inline-emoji";
      img.src = part.url;
      img.alt = `:${part.name || "emoji"}:`;
      img.title = `:${part.name || "emoji"}:`;
      img.loading = "lazy";
      node.appendChild(img);
      continue;
    }
    if (part.type === "link" && part.acct) {
      // Inline account link within the verb (e.g. a "move" notification's
      // destination). Styled like the actor link and mirrors its behaviour:
      // hover-profile preview + mark-read/navigate on click.
      const a = document.createElement("a");
      a.className = "watcher-notif-user watcher-notif-target";
      a.href = profileHref(part.acct);
      a.textContent = part.text || `@${part.acct}`;
      if (part.account) {
        a.addEventListener("mouseenter", () => profilePopup.show(part.account));
        a.addEventListener("mouseleave", () => profilePopup.hide());
      }
      if (ctx?.markRead && ctx?.row) {
        a.addEventListener("click", () => {
          ctx.markRead(item, ctx.row);
          profilePopup.hide();
          closeWatcherPanel();
        });
      }
      node.appendChild(a);
      continue;
    }
    node.appendChild(document.createTextNode(String(part.text || "")));
  }
}

function renderNotificationPager(pane) {
  const pages = state.notifications.notifPages;
  const idx = state.notifications.notifPageIndex;
  const hasPrev = idx > 0;
  const hasNextLoaded = idx < pages.length - 1;
  const hasNextFetch = !!state.notifications.notifNextCursorByPage[idx];

  if (!hasPrev && !hasNextLoaded && !hasNextFetch) return;

  const nav = document.createElement("div");
  nav.className = "watcher-pager";

  if (!hasPrev && (hasNextLoaded || hasNextFetch)) {
    const next = document.createElement("a");
    next.href = "javascript:;";
    next.textContent = "next >";
    next.addEventListener("click", (e) => {
      e.preventDefault();
      gotoNotificationPage(idx + 1).catch(() => {});
    });
    nav.appendChild(next);
  } else {
    const prev = document.createElement("a");
    prev.href = "javascript:;";
    prev.textContent = "< prev";
    if (hasPrev) {
      prev.addEventListener("click", (e) => {
        e.preventDefault();
        gotoNotificationPage(idx - 1).catch(() => {});
      });
    } else {
      prev.classList.add("is-disabled");
    }

    const sep = document.createElement("span");
    sep.textContent = " | ";

    const next = document.createElement("a");
    next.href = "javascript:;";
    next.textContent = "next >";
    if (hasNextLoaded || hasNextFetch) {
      next.addEventListener("click", (e) => {
        e.preventDefault();
        gotoNotificationPage(idx + 1).catch(() => {});
      });
    } else {
      next.classList.add("is-disabled");
    }

    nav.appendChild(prev);
    nav.appendChild(sep);
    nav.appendChild(next);
  }

  pane.appendChild(nav);
}

function renderWatcherNotificationsPane(pane) {
  pane.innerHTML = "";

  const pages = state.notifications.notifPages;
  const pageItems = pages[state.notifications.notifPageIndex] || [];

  if (!pageItems.length) {
    pane.innerHTML = '<div class="empty">No notifications.</div>';
    return;
  }

  // Marking a row read clears its unread highlight and decrements the eye
  // indicator. Called on click (before navigating) and on hover-dwell.
  const markRead = (item, row) => {
    if (!row?.isConnected || scope !== notificationScope()) return;
    if (state.notifications.readNotifKeys.has(item.key)) return;
    state.notifications.readNotifKeys.add(item.key);
    if (row) row.classList.remove("is-unread");
    writeNotificationsStore();
    syncWatcherIndicator();
  };

  // Hover-dwell: resting on a row briefly marks it read, but passing through on
  // the way to the scrollbar/pager does not (the timer is cancelled on leave).
  let dwellTimer = null;
  const scope = notificationScope();

  for (const item of pageItems) {
    const unread = !state.notifications.readNotifKeys.has(item.key);
    // Row is a container (not an anchor) so it can hold two distinct links.
    const row = document.createElement("div");
    row.className = "watcher-row watcher-row-notif" + (unread ? " is-unread" : "");
    // No `title` here: a native tooltip would pop up over the profile/post hover
    // overlays. The overlays (and the dynamic line) convey the full context now.

    if (unread) {
      row.addEventListener("mouseenter", () => {
        clearTimeout(dwellTimer);
        dwellTimer = setTimeout(() => markRead(item, row), NOTIF_READ_DWELL_MS);
      });
      row.addEventListener("mouseleave", () => clearTimeout(dwellTimer));
    }

    const age = document.createElement("span");
    age.className = "watcher-age";
    age.textContent = compactAgeLabel(item.createdAt);
    row.appendChild(age);

    const sum = document.createElement("span");
    sum.className = "watcher-sum";

    // Actor link → the actor's /user profile. For follow notifications this is
    // the follower's profile; for everything else, whoever acted on your post.
    const userLink = document.createElement("a");
    userLink.className = "watcher-notif-user";
    userLink.href = profileHref(item.actorAcct);
    userLink.textContent = item.actorLabel;
    userLink.addEventListener("click", () => {
      markRead(item, row);
      profilePopup.hide();
      closeWatcherPanel();
      // href hash performs the navigation.
    });
    // Profile hover card. The notification already carries the full account
    // object, so show it directly (no fetch needed).
    if (item.account) {
      userLink.addEventListener("mouseenter", () => profilePopup.show(item.account));
      userLink.addEventListener("mouseleave", () => profilePopup.hide());
    }
    sum.appendChild(userLink);

    // Verb element → the related post. Follow notifications (and anything with
    // no status) have no post to link to, so the verb is plain text.
    const hasPost = !item.isFollow && !!item.statusObj;
    const verb = document.createElement(hasPost ? "a" : "span");
    verb.className = "watcher-notif-verb";
    if (hasPost) {
      // Real permalink to the notified post (so middle-click/copy-link give the
      // reply, not the OP); the click handler below does the in-app navigation.
      verb.href = item.statusId ? threadHref(item.statusId) : "javascript:;";
      verb.addEventListener("mouseenter", () => previewPopup.show(item.statusObj, verb));
      verb.addEventListener("mouseleave", () => previewPopup.hide());
      verb.addEventListener("click", async (e) => {
        e.preventDefault();
        markRead(item, row);
        closeWatcherPanel();
        await navigateToNotificationThread(item);
      });
    }
    renderNotificationVerb(verb, item, { row, markRead });
    sum.appendChild(verb);

    row.appendChild(sum);

    if (item.type === "follow_request" && state.notifications.pendingFollowRequests.has(item.accountId)) {
      const actions = document.createElement("span");
      actions.className = "watcher-request-actions";
      for (const [decision, label] of [["authorize", "Accept"], ["reject", "Reject"]]) {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = label;
        button.addEventListener("click", async () => {
          actions.querySelectorAll("button").forEach((el) => { el.disabled = true; });
          markRead(item, row);
          try {
            await resolveFollowRequest(item.accountId, decision);
          } catch (e) {
            actions.querySelectorAll("button").forEach((el) => { el.disabled = false; });
            alert(`Failed to ${decision === "authorize" ? "accept" : "reject"} follow request: ${e.message}`);
          }
        });
        actions.appendChild(button);
      }
      row.appendChild(actions);
    }
    pane.appendChild(row);
  }

  renderNotificationPager(pane);

  // Escape hatch: clear everything loaded without hovering each row.
  if ((state.notifications.items || []).some((it) => !state.notifications.readNotifKeys.has(it.key))) {
    const footer = document.createElement("div");
    footer.className = "watcher-pager watcher-markread";
    const link = document.createElement("a");
    link.href = "javascript:;";
    link.textContent = "mark all read";
    link.addEventListener("click", (e) => {
      e.preventDefault();
      markAllNotificationsRead();
      renderWatcherPanel();
    });
    footer.appendChild(link);
    pane.appendChild(footer);
  }
}

function renderWatcherPanel() {
  const panel = watcherPanelEl();
  if (!panel || panel.hidden) return;

  const tabWatcher = document.getElementById("watcher-tab-watcher");
  const tabNotif = document.getElementById("watcher-tab-notifications");
  const tabHidden = document.getElementById("watcher-tab-hidden");
  const paneWatcher = document.getElementById("watcher-pane-watcher");
  const paneNotif = document.getElementById("watcher-pane-notifications");
  const paneHidden = document.getElementById("watcher-pane-hidden");
  const statusEl = document.getElementById("watcher-status");
  const toggleCurrent = document.getElementById("watcher-toggle-current");

  if (!tabWatcher || !tabNotif || !tabHidden || !paneWatcher || !paneNotif || !paneHidden || !statusEl || !toggleCurrent) return;

  const active = ["notifications", "hidden"].includes(state.notifications.activeTab)
    ? state.notifications.activeTab
    : "watcher";
  const watcherUnread = watcherUnreadCount();
  const notifUnread = notificationUnreadCount();

  tabWatcher.classList.toggle("is-active", active === "watcher");
  tabWatcher.classList.toggle("has-unread", watcherUnread > 0);
  tabWatcher.setAttribute("aria-selected", active === "watcher" ? "true" : "false");

  tabNotif.classList.toggle("is-active", active === "notifications");
  tabNotif.classList.toggle("has-unread", notifUnread > 0);
  tabNotif.setAttribute("aria-selected", active === "notifications" ? "true" : "false");

  tabHidden.classList.toggle("is-active", active === "hidden");
  tabHidden.setAttribute("aria-selected", active === "hidden" ? "true" : "false");

  paneWatcher.classList.toggle("is-active", active === "watcher");
  paneWatcher.hidden = active !== "watcher";
  paneNotif.classList.toggle("is-active", active === "notifications");
  paneNotif.hidden = active !== "notifications";
  paneHidden.classList.toggle("is-active", active === "hidden");
  paneHidden.hidden = active !== "hidden";

  statusEl.textContent = state.notifications.error || (state.notifications.loading ? "sync…" : "");

  const watchInfo = currentThreadWatchInfo();
  if (watchInfo && active === "watcher") {
    toggleCurrent.hidden = false;
    toggleCurrent.textContent = isThreadWatched(watchInfo.id) ? "Unwatch current" : "Watch current";
    toggleCurrent.dataset.threadId = watchInfo.id;
    toggleCurrent.dataset.threadTitle = watchInfo.title || "";
  } else {
    toggleCurrent.hidden = true;
    delete toggleCurrent.dataset.threadId;
    delete toggleCurrent.dataset.threadTitle;
  }

  renderWatcherThreadsPane(paneWatcher);
  renderWatcherNotificationsPane(paneNotif);
  renderWatcherHiddenPane(paneHidden);
  applyAnonymousPresentation(panel);
}

async function loadNextNotificationPage() {
  if (state.notifications.loading || state.notifications.notifPaging) return false;
  const epoch = state.notifications.requestEpoch || 0;
  const scope = notificationScope();
  const token = state.auth.accessToken;
  const idx = state.notifications.notifPages.length - 1;
  const cursor = idx >= 0 ? state.notifications.notifNextCursorByPage[idx] : null;
  if (!cursor) return false;

  state.notifications.notifPaging = true;
  state.notifications.error = null;
  try {
    const { items, nextMaxId } = await fetchServerNotificationsPage({ maxId: cursor, limit: 20 });
    if (!notificationRequestIsCurrent(epoch, scope, token)) return false;
    if (nextMaxId && nextMaxId === cursor) throw new Error("Notifications cursor did not advance");
    if (!items.length) {
      state.notifications.notifNextCursorByPage[idx] = null;
      return false;
    }

    const existing = new Set(flattenNotificationPages().map(item => item.key));
    const fresh = items.filter(item => !existing.has(item.key));
    if (fresh.length) {
      state.notifications.notifPages.push(fresh);
      state.notifications.notifNextCursorByPage.push(nextMaxId || null);
    } else {
      state.notifications.notifNextCursorByPage[idx] = nextMaxId || null;
    }

    rememberNotifications(items);

    refreshFlattenedNotifications();
    return fresh.length > 0 || !!nextMaxId;
  } catch (error) {
    if (notificationRequestIsCurrent(epoch, scope, token)) {
      state.notifications.error = error.message || String(error);
    }
    return false;
  } finally {
    if (notificationRequestIsCurrent(epoch, scope, token)) state.notifications.notifPaging = false;
  }
}

async function gotoNotificationPage(nextIndex) {
  if (nextIndex < 0) return;
  const epoch = state.notifications.requestEpoch || 0;
  const scope = notificationScope();
  const token = state.auth.accessToken;

  while (nextIndex >= state.notifications.notifPages.length) {
    const loaded = await loadNextNotificationPage();
    if (!notificationRequestIsCurrent(epoch, scope, token)) return;
    if (!loaded) break;
  }

  clampNotificationPageIndex();
  if (nextIndex < state.notifications.notifPages.length) {
    state.notifications.notifPageIndex = nextIndex;
  }
  clampNotificationPageIndex();

  writeNotificationsStore();
  syncWatcherIndicator();
  renderWatcherPanel();
}

const userPostWatcher = createUserPostWatcher({
  storage: {
    getItem: key => localStorage.getItem(key),
    setItem: (key, value) => localStorage.setItem(key, value),
  },
  getScope: () => state.auth.status === "signed-in" ? notificationScope() : null,
  getSession: () => state.auth.accessToken,
  fetchPage: async (accountId, { limit, maxId }) => {
    const params = new URLSearchParams({ limit: String(limit), exclude_replies: "true", exclude_reblogs: "true" });
    if (maxId) params.set("max_id", maxId);
    const r = await apiFetch(`/api/v1/accounts/${encodeURIComponent(accountId)}/statuses?${params}`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const rows = await r.json();
    if (!Array.isArray(rows)) throw new Error("Invalid user posts response");
    return { rows, nextMaxId: notificationCursor(r.headers.get("Link"), rows, limit) };
  },
  onChange: syncUserPostNotifications,
});

function syncUserPostNotifications() {
  syncProfileNotificationButton();
  if (state.auth.status !== "signed-in") return;
  const scope = notificationScope();
  const items = userPostWatcher.notifications().map(n => ({
    ...normalizeNotificationFromServer(n, scope), key: `usr:${scope}:${n.id}`,
  }));
  if (!items.length) return;
  const oldPages = state.notifications.notifPages;
  const tailCursor = state.notifications.notifNextCursorByPage.at(-1);
  const incoming = [...items, ...oldPages.flat()].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  const merged = mergeNotificationPages(oldPages, incoming, state.notifications.notifPageIndex);
  state.notifications.notifPages = merged.pages;
  state.notifications.notifPageIndex = merged.pageIndex;
  state.notifications.notifNextCursorByPage = merged.pages.map((_, index) =>
    index === merged.pages.length - 1 ? tailCursor || null : null);
  rememberNotifications(items);
  refreshFlattenedNotifications();
  writeNotificationsStore();
  syncWatcherIndicator();
  renderWatcherPanel();
}

async function syncNotifications() {
  if (state.auth.status !== "signed-in" || state.notifications.loading || state.notifications.notifPaging) return;
  syncUserPostNotifications();
  userPostWatcher.poll().catch(() => {});
  const epoch = state.notifications.requestEpoch || 0;
  const scope = notificationScope();
  const token = state.auth.accessToken;
  state.notifications.loading = true;
  state.notifications.error = null;
  renderWatcherPanel();

  try {
    const [firstNotifPage, pendingRequests, watchedResults] = await Promise.all([
      fetchServerNotificationsPage({ limit: 20 }),
      fetchPendingFollowRequests(),
      Promise.all((state.notifications.watchedThreads || []).map(async (w) => {
        const seenAtFetch = state.notifications.threadSeen.get(w.id);
        try {
          const ctx = await fetchContext(w.id, state);
          const snap = snapshotThread(ctx);
          return { watch: w, ctx, snap, seenAtFetch };
        } catch {
          return { watch: w, ctx: null, snap: null };
        }
      })),
    ]);
    if (!notificationRequestIsCurrent(epoch, scope, token)) return;

    const serverItems = [...firstNotifPage.items];
    let nextCursor = firstNotifPage.nextMaxId;
    const oldPages = state.notifications.notifPages;
    const oldKeys = new Set([...oldPages.flat().map(item => item.key), ...state.notifications.knownNotifKeys]
      .filter(key => key.startsWith(`srv:${scope}:`)));
    const cursors = new Set();
    // Fill a busy interval completely before merging, so more than 20 new
    // notifications between polls cannot leave a hole in the history.
    while (oldKeys.size && nextCursor && !serverItems.some(item => oldKeys.has(item.key))) {
      if (cursors.has(nextCursor)) throw new Error("Notifications cursor did not advance");
      cursors.add(nextCursor);
      const page = await fetchServerNotificationsPage({ maxId: nextCursor, limit: 20 });
      if (!notificationRequestIsCurrent(epoch, scope, token)) return;
      serverItems.push(...page.items);
      nextCursor = page.nextMaxId;
    }
    if (Array.isArray(pendingRequests)) {
      state.notifications.pendingFollowRequests = new Map(
        pendingRequests.filter((account) => account?.id).map((account) => [String(account.id), account]),
      );
    }

    for (const { watch, ctx, snap, seenAtFetch } of watchedResults) {
      if (!watch?.id || !snap || !isThreadWatched(watch.id)) continue;
      const opId = watch.id;
      const prev = state.notifications.threadSeen.get(opId) || null;
      // A thread can be opened/read while this watcher fetch is in flight.
      // An older response must not undo that newer baseline and then count
      // the already-read replies again on the next poll.
      if (prev && prev !== seenAtFetch && snap.latestAt < (prev.latestAt || "")) continue;
      state.notifications.threadSeen.set(opId, {
        descendantsCount: snap.descendantsCount,
        latestId: snap.latestId,
        latestAt: snap.latestAt,
      });

      const latestStatus = snap.latestStatus || ctx?.op || null;
      const actor = latestStatus?.account?.acct || latestStatus?.account?.username || (watch.title || "post");
      const snippet = stripHTML(latestStatus?.content || "") || watch.title || postRefLabel(opId, "Thread");
      state.notifications.threadMeta.set(opId, {
        actor,
        snippet,
        latestAt: snap.latestAt || latestStatus?.created_at || "",
      });

      if (prev && snap.descendantsCount > (Number(prev.descendantsCount) || 0)) {
        const delta = snap.descendantsCount - (Number(prev.descendantsCount) || 0);
        const cur = Math.max(0, Number(state.notifications.threadUnread.get(opId) || 0));
        state.notifications.threadUnread.set(opId, cur + delta);
      }
    }

    // Local user-post polling can add pages while the server fetch is in
    // flight. Merge the live pages so neither source discards the other.
    const pagesToMerge = state.notifications.notifPages;
    const tailCursor = pagesToMerge.some(page => page.some(item => item.key.startsWith(`srv:${scope}:`)))
      ? state.notifications.notifNextCursorByPage.at(-1) : nextCursor;
    const incoming = [...serverItems, ...pagesToMerge.flat()]
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    const merged = mergeNotificationPages(pagesToMerge, incoming, state.notifications.notifPageIndex);
    state.notifications.notifPages = merged.pages;
    state.notifications.notifPageIndex = merged.pageIndex;
    state.notifications.notifNextCursorByPage = merged.pages.map((_, index) =>
      index === merged.pages.length - 1 ? tailCursor : null);
    // No automatic "read" baseline: unknown and explicitly unseen entries
    // remain unread until the user reads them.
    rememberNotifications(serverItems);

    refreshFlattenedNotifications();

    clampNotificationPageIndex();

    if (isWatcherPanelOpen()) {
      markActiveWatcherTabRead();
    }

    writeNotificationsStore();
    syncWatcherIndicator();
    renderWatcherPanel();
  } catch (e) {
    if (notificationRequestIsCurrent(epoch, scope, token)) state.notifications.error = e?.message || String(e);
  } finally {
    if (notificationRequestIsCurrent(epoch, scope, token)) {
      state.notifications.loading = false;
      renderWatcherPanel();
    }
  }
}

let autoRefreshTimer = null;
function stopAutoRefresh() {
  if (autoRefreshTimer != null) {
    clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
  }
}
function startAutoRefresh() {
  stopAutoRefresh();
  const { autoRefreshThreads, autoRefreshCatalogs } = userSettings;
  // No scope enabled → no timer. Threads and Catalogs are independent toggles:
  // threads poll silently in place, catalogs re-fetch the whole page each tick.
  if (!autoRefreshThreads && !autoRefreshCatalogs) return;
  const ms = clampAutoRefreshInterval(userSettings.autoRefreshIntervalSec) * 1000;
  autoRefreshTimer = setInterval(() => {
    // Only fire if the user is actually on a view that supports refresh
    // (the modal is open on /about/, so skip those).
    const v = state.view;
    if (!v) return;
    if (v.kind === "post" && autoRefreshThreads) {
      pollCurrentThread().catch(() => {});
    } else if ((v.kind === "board" || v.kind === "profile") && autoRefreshCatalogs) {
      refreshCurrentView().catch(() => {});
    }
  }, ms);
}
// Re-evaluate the timer whenever settings change (or at boot). Called from
// the Save button and from init.
function applyAutoRefreshFromSettings() {
  startAutoRefresh();
}

function startNotificationPolling() {
  if (state._notifTimer) {
    clearInterval(state._notifTimer);
    state._notifTimer = null;
  }
  // Keep notifications in sync whether or not auto-refresh is enabled.
  state._notifTimer = setInterval(() => {
    syncNotifications().catch(() => {});
  }, 60 * 1000);
}

// ---- config modal wiring --------------------------------------------------

// Checkbox id → settings key for the video playback matrix. Read at both
// populate and save time so the table stays a single list to extend.
const VIDEO_SETTING_INPUTS = [
  ["#config-video-catalog-loop",  "videoCatalogHoverLoop"],
  ["#config-video-catalog-muted", "videoCatalogHoverMuted"],
  ["#config-video-hover-loop",    "videoThreadHoverLoop"],
  ["#config-video-hover-muted",   "videoThreadHoverMuted"],
  ["#config-video-expanded-loop", "videoExpandedLoop"],
  ["#config-video-expanded-muted", "videoExpandedMuted"],
  ["#config-video-gallery-loop",   "videoGalleryLoop"],
  ["#config-video-gallery-muted",  "videoGalleryMuted"],
];

let themeEditor;
function openConfigModal() {
  themeEditor?.open();
  const modal = $("#config-modal");
  if (!modal) return;
  // Populate form from the latest settings every time we open, so changes
  // made elsewhere (or in another tab via the storage event) are reflected.
  $("#config-auto-refresh-thread").checked = !!userSettings.autoRefreshThreads;
  $("#config-auto-refresh-catalog").checked = !!userSettings.autoRefreshCatalogs;
  $("#config-auto-refresh-interval").value = String(clampAutoRefreshInterval(userSettings.autoRefreshIntervalSec));
  $("#config-nsfw-blur").checked = !!userSettings.blurNsfwMedia;
  $("#config-activity-hover").checked = !!userSettings.showActivityHover;
  $("#config-collapse-mentions").checked = !!userSettings.collapseLeadingMentions;
  $("#config-copy-reply-mentions").checked = !!userSettings.copyMentionsOnReply;
  $("#config-anonymous-mode").checked = !!userSettings.anonymousMode;
  for (const [id, key] of VIDEO_SETTING_INPUTS) {
    const input = $(id);
    if (input) input.checked = !!userSettings[key];
  }
  syncAnonymousConfigControls();
  renderModerationLists();
  loadMutedAccounts().catch(() => {});
  populateProfileConfig();
  const status = $("#config-save-status");
  if (status) { status.textContent = ""; status.classList.remove("is-error"); }
  modal.classList.add("is-open");
  modal.removeAttribute("hidden");
  // Focus the first tab so keyboard nav is obvious.
  const firstTab = $("#config-tab-fechan");
  if (firstTab) firstTab.focus();
}
function closeConfigModal() {
  themeEditor?.close();
  const modal = $("#config-modal");
  if (!modal) return;
  modal.classList.remove("is-open");
  modal.setAttribute("hidden", "");
}
function isConfigModalOpen() {
  return !!$("#config-modal")?.classList.contains("is-open");
}

function selectConfigTab(tabName) {
  const modal = $("#config-modal");
  if (!modal) return;
  // Buttons
  modal.querySelectorAll(".config-tab").forEach((btn) => {
    const active = btn.dataset.tab === tabName;
    btn.classList.toggle("is-active", active);
    btn.setAttribute("aria-selected", active ? "true" : "false");
  });
  // Panels
  modal.querySelectorAll(".config-panel").forEach((panel) => {
    const active = panel.dataset.panel === tabName;
    panel.classList.toggle("is-active", active);
    if (active) panel.removeAttribute("hidden");
    else panel.setAttribute("hidden", "");
  });
  if (tabName === "blocks") loadMutedAccounts().catch(() => {});
}

function saveConfigFromForm() {
  const interval = clampAutoRefreshInterval($("#config-auto-refresh-interval")?.value);
  // Echo the clamped value back so the user sees the minimum in effect if
  // they tried to type something lower.
  const intervalInput = $("#config-auto-refresh-interval");
  if (intervalInput) intervalInput.value = String(interval);
  const anonymousMode = !!$("#config-anonymous-mode")?.checked;
  const videoSettings = {};
  for (const [id, key] of VIDEO_SETTING_INPUTS) {
    const input = $(id);
    if (input) videoSettings[key] = !!input.checked;
  }
  saveSettings({
    ...videoSettings,
    autoRefreshThreads: !!$("#config-auto-refresh-thread")?.checked,
    autoRefreshCatalogs: !!$("#config-auto-refresh-catalog")?.checked,
    autoRefreshIntervalSec: interval,
    blurNsfwMedia: !!$("#config-nsfw-blur")?.checked,
    showActivityHover: anonymousMode ? userSettings.showActivityHover : !!$("#config-activity-hover")?.checked,
    collapseLeadingMentions: anonymousMode ? userSettings.collapseLeadingMentions : !!$("#config-collapse-mentions")?.checked,
    copyMentionsOnReply: !!$("#config-copy-reply-mentions")?.checked,
    anonymousMode,
  });
  if (userSettings.anonymousMode || !userSettings.showActivityHover) activityHoverPopup.hide();
  syncReactionTooltips();
  applyThemeFromSettings();
  applyAutoRefreshFromSettings();
  rerenderModerationMedia();
  const status = $("#config-save-status");
  if (status) {
    status.textContent = "Saved.";
    status.classList.remove("is-error");
  }
}

function syncAnonymousConfigControls() {
  const anonymous = !!$("#config-anonymous-mode")?.checked;
  const activity = $("#config-activity-hover");
  const mentions = $("#config-collapse-mentions");
  if (activity) {
    activity.disabled = anonymous;
    activity.checked = anonymous ? false : !!userSettings.showActivityHover;
    activity.closest("label")?.classList.toggle("is-forced", anonymous);
  }
  if (mentions) {
    mentions.disabled = anonymous;
    mentions.checked = anonymous ? true : !!userSettings.collapseLeadingMentions;
    mentions.closest("label")?.classList.toggle("is-forced", anonymous);
  }
  document.querySelectorAll("#config-muted-list .moderation-list-remove").forEach((button) => {
    button.disabled = anonymous;
    button.title = anonymous ? "Unmute is unavailable in Anonymous mode" : "Unmute";
  });
}

function addProfileFieldRow(field = {}) {
  const host = $("#profile-fields");
  if (!host || host.children.length >= 10) return;
  const row = document.createElement("div");
  row.className = "profile-field-row";
  row.innerHTML = `
    <input type="text" class="profile-field-name" placeholder="Key" aria-label="Extra info key">
    <input type="text" class="profile-field-value" placeholder="Value" aria-label="Extra info value">
    <button type="button" class="profile-field-remove" title="Remove item" aria-label="Remove item"><i class="fa-solid fa-xmark"></i></button>`;
  row.querySelector(".profile-field-name").value = field.name || "";
  row.querySelector(".profile-field-value").value = field.value || "";
  row.querySelector(".profile-field-remove").addEventListener("click", () => {
    row.remove();
    syncProfileFieldAddButton();
  });
  host.appendChild(row);
  syncProfileFieldAddButton();
}

function syncProfileFieldAddButton() {
  const btn = $("#profile-field-add");
  if (btn) btn.disabled = ($("#profile-fields")?.children.length || 0) >= 10;
}

function populateProfileConfig() {
  const account = state.auth.account;
  const form = $("#profile-config-form");
  if (!form) return;
  const signedIn = state.auth.status === "signed-in" && !!account;
  form.querySelectorAll("input, textarea, button").forEach((el) => { el.disabled = !signedIn; });
  const status = $("#profile-save-status");
  if (status) status.textContent = signedIn ? "" : "Sign in to edit your profile.";
  if (!signedIn) return;
  $("#profile-display-name").value = account.display_name || "";
  $("#profile-edit-bio").value = account.source?.note ?? stripHTML(account.note || "");
  $("#profile-bot").checked = !!account.bot;
  $("#profile-locked").checked = !!account.locked;
  $("#profile-avatar").value = "";
  $("#profile-banner").value = "";
  const fields = account.source?.fields || account.fields || [];
  $("#profile-fields").replaceChildren();
  fields.slice(0, 10).forEach(addProfileFieldRow);
  syncProfileFieldAddButton();
}

function fileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Couldn't read ${file.name}.`));
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const comma = dataUrl.indexOf(",");
      if (comma < 0) reject(new Error(`Couldn't encode ${file.name}.`));
      else resolve({ data: dataUrl.slice(comma + 1), mediaType: file.type || "application/octet-stream" });
    };
    reader.readAsDataURL(file);
  });
}

// Mitra's own format: images inline as base64 with an explicit media type.
async function saveProfileAsJson(profile, avatarFile, bannerFile) {
  const [avatar, header] = await Promise.all([
    avatarFile ? fileAsBase64(avatarFile) : null,
    bannerFile ? fileAsBase64(bannerFile) : null,
  ]);
  const payload = { ...profile };
  if (avatar) { payload.avatar = avatar.data; payload.avatar_media_type = avatar.mediaType; }
  if (header) { payload.header = header.data; payload.header_media_type = header.mediaType; }
  return apiFetch("/api/v1/accounts/update_credentials", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// The Mastodon API's own format. Scalars go as text parts; the images go as
// real file parts (filename included — see uploadFilename). Extra fields use
// Mastodon's bracket syntax, which Pleroma parses back into a map.
async function saveProfileAsMultipart(profile, avatarFile, bannerFile) {
  const form = new FormData();
  form.append("display_name", profile.display_name);
  form.append("note", profile.note);
  form.append("bot", String(profile.bot));
  form.append("locked", String(profile.locked));
  // An absent param means "leave the fields alone", so clearing every field
  // has to send one empty pair rather than nothing. Servers drop entries with
  // a blank name, which leaves the list empty — what the user asked for.
  const fields = profile.fields_attributes.length
    ? profile.fields_attributes
    : [{ name: "", value: "" }];
  fields.forEach((field, i) => {
    form.append(`fields_attributes[${i}][name]`, field.name);
    form.append(`fields_attributes[${i}][value]`, field.value);
  });
  if (avatarFile) form.append("avatar", avatarFile, uploadFilename(avatarFile));
  if (bannerFile) form.append("header", bannerFile, uploadFilename(bannerFile));
  // No Content-Type header — the browser sets it, with the boundary.
  return apiFetch("/api/v1/accounts/update_credentials", { method: "PATCH", body: form });
}

async function saveProfileConfig(e) {
  e.preventDefault();
  if (state.auth.status !== "signed-in") return;
  const saveBtn = $("#profile-save");
  const status = $("#profile-save-status");
  saveBtn.disabled = true;
  status.textContent = "Saving…";
  status.classList.remove("is-error");
  try {
    const avatarFile = $("#profile-avatar").files?.[0] || null;
    const bannerFile = $("#profile-banner").files?.[0] || null;
    const fields_attributes = Array.from($("#profile-fields").children).map((row) => ({
      name: row.querySelector(".profile-field-name").value.trim(),
      value: row.querySelector(".profile-field-value").value.trim(),
    })).filter((field) => field.name);
    const profile = {
      display_name: $("#profile-display-name").value.trim(),
      note: $("#profile-edit-bio").value,
      bot: !!$("#profile-bot").checked,
      locked: !!$("#profile-locked").checked,
      fields_attributes,
    };
    // Two wire formats, because neither server takes the other's. Mitra reads
    // avatar/header as base64 JSON and accepts as many extra fields as its
    // validator allows; its multipart form only has slots for four. Pleroma
    // and Mastodon have no base64 form at all — a base64 avatar reaches their
    // uploader as a bare string and blows up the same way a base64 media
    // upload does. So: JSON for Mitra, multipart for everyone else.
    const r = isMitraServer(state.instance)
      ? await saveProfileAsJson(profile, avatarFile, bannerFile)
      : await saveProfileAsMultipart(profile, avatarFile, bannerFile);
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
    state.auth.account = await r.json();
    saveAuth();
    renderAuth();
    populateProfileConfig();
    applyOwnAccountUpdate(state.auth.account);
    status.textContent = "Saved.";
  } catch (err) {
    status.textContent = `Failed: ${err.message}`;
    status.classList.add("is-error");
  } finally {
    saveBtn.disabled = false;
  }
}

// After the signed-in user edits their profile, refresh every copy of their
// account object held by the loaded views, then repaint. The reconcilers sync
// avatar/banner from the fresh data (see syncPostAccountMedia), so a plain
// render() carries the change into already-rendered post blocks and cards.
function applyOwnAccountUpdate(account) {
  if (!account?.id) return;
  const patch = (s) => {
    if (!s) return;
    if (s.account?.id === account.id) s.account = account;
    if (s.reblog?.account?.id === account.id) s.reblog.account = account;
  };
  (state.statuses || []).forEach(patch);
  const ctx = state.postView?.context;
  if (ctx) {
    patch(ctx.op);
    (ctx.ancestors || []).forEach(patch);
    (ctx.descendants || []).forEach(patch);
  }
  if (state.view?.kind === "profile" && state.view.account?.id === account.id) {
    state.view.account = account;
  }
  renderHeader();
  render();
}

function wireConfigModal() {
  themeEditor = createThemeEditor($("#theme-panel"), () => !userSettings.blurNsfwMedia);
  const modal = $("#config-modal");
  if (!modal) return;
  // Tabs
  modal.querySelectorAll(".config-tab").forEach((btn) => {
    btn.addEventListener("click", () => selectConfigTab(btn.dataset.tab));
  });
  // Close
  const closeBtn = $("#config-modal-close");
  if (closeBtn) closeBtn.addEventListener("click", closeConfigModal);
  // Click outside the box dismisses (matches the compose modal's behaviour),
  // but a drag-selection that ends on the backdrop won't (see addBackdropClose).
  addBackdropClose(modal, closeConfigModal);
  // Save
  const saveBtn = $("#config-save");
  if (saveBtn) saveBtn.addEventListener("click", saveConfigFromForm);
  $("#config-anonymous-mode")?.addEventListener("change", syncAnonymousConfigControls);
  $("#profile-field-add")?.addEventListener("click", () => addProfileFieldRow());
  $("#profile-config-form")?.addEventListener("submit", saveProfileConfig);
  $("#config-muted-word-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const input = $("#config-muted-word-input");
    if (!input || !input.value.trim()) return;
    saveSettings({ mutedWords: normalizeMutedWords([...(userSettings.mutedWords || []), input.value]) });
    input.value = "";
    renderMutedWords();
    render();
    input.focus();
  });
  // Clamp the interval field as the user types (number inputs with min
  // can still be bypassed via paste or arrow keys in some browsers).
  const intervalInput = $("#config-auto-refresh-interval");
  if (intervalInput) {
    intervalInput.addEventListener("blur", () => {
      intervalInput.value = String(clampAutoRefreshInterval(intervalInput.value));
    });
  }
  // Keyboard: Escape closes. Tab/arrow keys cycle the tab buttons.
  modal.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeConfigModal();
      return;
    }
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const tabs = Array.from(modal.querySelectorAll(".config-tab"));
    const idx = tabs.indexOf(document.activeElement);
    if (idx === -1) return;
    const next = e.key === "ArrowRight"
      ? tabs[(idx + 1) % tabs.length]
      : tabs[(idx - 1 + tabs.length) % tabs.length];
    e.preventDefault();
    next.focus();
    selectConfigTab(next.dataset.tab);
  });
}

function renderAuthDialogStatus() {
  const err = document.getElementById("auth-error");
  if (!err) return;

  if (state.auth.status === "exchanging") {
    err.textContent = "Exchanging code…";
    return;
  }
  if (state.auth.status === "error" && state.auth.error) {
    err.textContent = state.auth.error;
    return;
  }
  err.textContent = "";
}

function syncStartThreadButton() {
  const btn = document.getElementById("start-thread");
  if (!btn) return;

  const inThread = state.view && state.view.kind === "post";
  const onProfile = state.view && state.view.kind === "profile";
  // A group's profile (reached via /user/<acct> or search) composes to the
  // board, same as the /g/ board view — Mitra addresses group posts with the
  // group_id status param, not by mentioning the group actor.
  const onGroup = (state.view?.kind === "board" && !!state.view.group)
    || (onProfile && !!state.view.account?.is_group);
  // Your own profile is not someone to mention — offer a plain new thread.
  const onSelfProfile = onProfile && !onGroup && isOwnProfileView(state.view);
  const canCompose = state.auth.status === "signed-in";
  btn.disabled = !canCompose;
  btn.title = !canCompose
    ? "Sign in to start a thread"
    : (inThread ? "Reply to the original post"
      : onGroup ? "Start a new thread on this board"
      : onSelfProfile ? "Start a new thread"
      : onProfile ? "Mention this user in a new thread"
      : state.view?.kind === "tag" ? `Start a new post with #${state.view.tag}`
      : "Start a new thread");

  // The button repoints by view: "Reply to OP" in a thread, "Mention User" on
  // someone else's profile (pre-fills the composer with the @handle), else
  // "Start a Thread" — including boards and group profiles, where the new
  // thread is addressed to the group, and your own profile.
  btn.textContent = inThread ? "[Reply to OP]"
    : onProfile && !onGroup && !onSelfProfile ? "[Mention User]"
    : "[Start a Thread]";
}

// True when the profile view being rendered is the signed-in user's own.
// The account object arrives from the lookup a moment after the route does,
// so fall back to the handle in the route until it resolves.
function isOwnProfileView(v) {
  if (v?.kind !== "profile") return false;
  if (state.auth.status !== "signed-in") return false;
  return isOwnAccount(v.account || { acct: v.acct });
}

// [Create Board from Tag] / [Remove Board from Tag], shown next to
// [Start a Thread] on a tag view: a toggle that adds the tag to the user's
// local tag boards or removes it again. Hidden when signed out — the boards
// bracket it would land in only renders signed-in.
function syncTagBoardButton() {
  const btn = document.getElementById("tag-board-add");
  if (!btn) return;
  const onTag = state.view?.kind === "tag";
  btn.hidden = !onTag || state.auth.status !== "signed-in";
  if (btn.hidden) return;
  const isBoard = hasTagBoard(state.view.tag);
  btn.textContent = isBoard ? "[Remove Board from Tag]" : "[Create Board from Tag]";
  btn.title = isBoard
    ? "Remove this tag board from your nav"
    : "Add this tag as a board in your nav";
}

// Follow/Unfollow toggle, shown next to [Mention User] on a profile or next to
// [Start a Thread] on a custom board (/g/<name>, whose group actor is an
// account you can follow). Hidden when signed out, viewing yourself, or
// before the account has resolved.
function followableAccountForView(v) {
  if (!v) return null;
  if (v.kind === "profile") return v.account || null;
  if (v.kind === "board" && v.group) return v.account || null;
  return null;
}

// "Follows (You)" next to the handle, when the profile you're reading follows
// you. `(You)` is the same marker a quotelink uses for a post of your own, so
// the badge reads the way the rest of the frontend talks.
//
// Driven off the relationship rather than rendered with the card, because it
// resolves a moment later — see renderProfileBio's rebuild skip.
function syncProfileFollowsYouBadge() {
  const el = document.getElementById("profile-follows-you");
  if (!el) return;
  const v = state.view;
  const account = followableAccountForView(v);
  const isSelf = account?.id && account.id === state.auth.account?.id;
  const followsYou = state.auth.status === "signed-in"
    && !!account?.id && !isSelf && !!v?.relationship?.followed_by;
  el.hidden = !followsYou;
  el.replaceChildren();
  if (followsYou) {
    // "(You)" in its own span so it can carry the quotelink red, the same
    // marker a post of your own gets.
    el.append("Follows ", Object.assign(document.createElement("span"),
      { className: "you", textContent: "(You)" }));
    el.title = `@${account.acct || account.username} follows you`;
  } else {
    el.removeAttribute("title");
  }
}

function syncProfileFollowButton() {
  const btn = document.getElementById("profile-follow");
  if (!btn) return;

  const v = state.view;
  const account = followableAccountForView(v);
  const isSelf = account?.id && account.id === state.auth.account?.id;
  if (state.auth.status !== "signed-in" || !account?.id || isSelf) {
    btn.hidden = true;
    return;
  }

  btn.hidden = false;
  const rel = v.relationship;
  if (!rel) {
    // Relationship hasn't resolved yet (or the fetch failed).
    btn.disabled = true;
    btn.textContent = "[Follow]";
    btn.title = "Loading follow state…";
    return;
  }

  btn.disabled = false;
  btn.classList.toggle("is-following", !!rel.following);
  if (rel.requested) {
    btn.textContent = "[Requested]";
    btn.title = "Follow request pending approval";
  } else if (rel.following) {
    btn.textContent = "[Unfollow]";
    btn.title = "Unfollow this user";
  } else {
    btn.textContent = "[Follow]";
    btn.title = "Follow this user";
  }
}

const userPostNotificationRequests = new Set();

function syncProfileNotificationButton() {
  const btn = document.getElementById("profile-notifications");
  if (!btn) return;
  const v = state.view;
  const account = v?.kind === "profile" ? v.account : null;
  btn.hidden = state.auth.status !== "signed-in" || !account?.id || account.is_group
    || account.id === state.auth.account?.id;
  if (btn.hidden) return;
  const enabled = userPostWatcher.has(account.id);
  btn.disabled = userPostNotificationRequests.has(`${notificationScope()}:${account.id}`);
  btn.title = `${enabled ? "Disable" : "Enable"} notifications for this user's posts`;
  btn.setAttribute("aria-label", btn.title);
  btn.setAttribute("aria-pressed", String(enabled));
  btn.innerHTML = `<i class="${enabled ? "fa-solid" : "fa-regular"} fa-bell" aria-hidden="true"></i>`;
}

async function toggleProfileNotifications() {
  // The header button persists across profiles; always resolve the live user.
  const v = state.view;
  const account = v?.kind === "profile" ? v.account : null;
  if (state.auth.status !== "signed-in" || !account?.id || account.is_group
    || account.id === state.auth.account?.id) return;
  const scope = notificationScope();
  const token = state.auth.accessToken;
  const requestKey = `${scope}:${account.id}`;
  if (userPostNotificationRequests.has(requestKey)) return;
  userPostNotificationRequests.add(requestKey);
  syncProfileNotificationButton();
  try {
    await userPostWatcher.toggle(account);
  } catch (e) {
    if (scope === notificationScope() && token === state.auth.accessToken) {
      alert(`Failed to update post notifications: ${e.message}`);
    }
  } finally {
    userPostNotificationRequests.delete(requestKey);
    syncProfileNotificationButton();
  }
}

function moderationAccountForView() {
  const account = followableAccountForView(state.view);
  return account?.id && account.id !== state.auth.account?.id ? account : null;
}

function blurredAccounts() {
  return Array.isArray(userSettings.blurredAccounts) ? userSettings.blurredAccounts : [];
}

function isAccountBlurred(accountId) {
  return blurredAccounts().some((a) => a.id === accountId);
}

function isStatusBlurred(status) {
  return isAccountBlurred(status?.account?.id) || isAccountBlurred(status?.group?.id);
}

function mediaBlurState(status, borrowedFrom = null) {
  const accountBlurred = isStatusBlurred(status) || (!!borrowedFrom && isStatusBlurred(borrowedFrom));
  const sensitive = accountBlurred || !!status?.sensitive || !!borrowedFrom?.sensitive;
  return { sensitive, blurred: accountBlurred || (sensitive && !!userSettings.blurNsfwMedia) };
}

function isMediaBlurred(element) {
  const wrap = element.closest(".nsfw-img-wrap");
  return !!wrap && !wrap.classList.contains("nsfw-revealed");
}

function addMediaBlurControls(wrap, status) {
  const blur = mediaBlurState(status);
  if (!blur.sensitive) return;
  wrap.classList.add("nsfw-img-wrap");
  wrap.classList.toggle("nsfw-revealed", !blur.blurred);
  const overlay = document.createElement("div");
  overlay.className = "nsfw-blur-overlay";
  wrap.appendChild(overlay);
  const badge = document.createElement("span");
  badge.className = "thread-badge nsfw";
  badge.textContent = "NSFW";
  wrap.appendChild(badge);
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "nsfw-toggle-btn";
  const syncToggle = () => {
    const revealed = wrap.classList.contains("nsfw-revealed");
    toggle.classList.toggle("is-revealed", revealed);
    toggle.title = revealed ? "Hide media" : "Show media";
    toggle.setAttribute("aria-label", toggle.title);
    toggle.setAttribute("aria-pressed", String(revealed));
    toggle.innerHTML = `<i class="fa-solid ${revealed ? "fa-eye" : "fa-eye-low-vision"}"></i>`;
  };
  syncToggle();
  toggle.addEventListener("click", (e) => {
    e.stopPropagation();
    const revealed = wrap.classList.toggle("nsfw-revealed");
    syncToggle();
    if (!revealed) {
      wrap.querySelectorAll("video").forEach(video => video.pause());
      document.querySelectorAll(".post-overlay-img, .post-overlay-video").forEach(preview => {
        if (!preview._ownerEl || !wrap.contains(preview._ownerEl)) return;
        if (preview.tagName === "VIDEO") preview.pause();
        preview.remove();
      });
    }
  });
  wrap.appendChild(toggle);
}

function hiddenOpAccounts() {
  return Array.isArray(userSettings.hiddenOpAccounts) ? userSettings.hiddenOpAccounts : [];
}

function isOpAccountHidden(accountId) {
  return !!accountId && hiddenOpAccounts().some((a) => a.id === accountId);
}

// Whether a catalog card should be dropped because its OP's author (or the
// group it was posted to) is on the hidden-OPs list.
function isThreadOpAccountHidden(thread) {
  const op = thread?.op;
  return isOpAccountHidden(op?.account?.id) || isOpAccountHidden(op?.group?.id);
}

function syncProfileModerationMenu() {
  const wrap = $("#profile-moderation");
  const menu = $("#profile-moderation-menu");
  const account = followableAccountForView(state.view);
  if (!wrap || !menu) return;
  const canModerate = state.auth.status === "signed-in" && !!account?.id
    && account.id !== state.auth.account?.id;
  const profileUrl = state.view.kind === "profile" && /^https?:\/\//i.test(account?.url || "")
    ? safeUrl(account.url) : "";
  if (!canModerate && !profileUrl) {
    wrap.hidden = true;
    menu.hidden = true;
    return;
  }
  wrap.hidden = false;
  const rel = state.view.relationship;
  const muted = !!rel?.muting;
  const blurred = isAccountBlurred(account.id);
  const opsHidden = isOpAccountHidden(account.id);
  const canSetReposts = !!(rel?.following || rel?.requested);
  const noun = account.is_group ? "board" : "user";
  const isRemote = String(account.acct || "").includes("@");
  const hideOpsHint = opsHidden
    ? `Show this ${noun}'s threads in catalogs again`
    : `Keep this ${noun}'s threads out of catalogs, even when someone else's reply bumps them`;
  menu.innerHTML = canModerate ? `
    ${rel?.requested_by ? '<button type="button" data-moderation="accept-request">Accept follow request</button><button type="button" data-moderation="reject-request">Reject follow request</button>' : ""}
    ${rel?.followed_by ? '<button type="button" data-moderation="remove-follower">Remove from followers</button>' : ""}
    <button type="button" data-moderation="mute">${muted ? `Unmute ${noun}` : `Mute ${noun}`}</button>
    <button type="button" data-moderation="blur">${blurred ? "Stop blurring media" : `Blur ${noun} media`}</button>
    <button type="button" data-moderation="hide-ops" title="${hideOpsHint}">${opsHidden ? `Show ${noun}'s OPs` : `Hide ${noun}'s OPs`}</button>
    <button type="button" data-moderation="reposts" ${canSetReposts ? "" : "disabled"} title="Reposts by this ${noun} in your /home/ timeline, not on their profile">${rel?.showing_reblogs === false ? "Show reposts" : "Hide reposts"}</button>
    ${isRemote ? '<button type="button" data-moderation="load-content">Load Content</button>' : ""}` : "";
  if (profileUrl) {
    const link = document.createElement("a");
    link.href = profileUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = "View profile on home server";
    menu.appendChild(link);
  }
}

function toggleProfileModerationMenu() {
  const menu = $("#profile-moderation-menu");
  const btn = $("#profile-moderation-btn");
  if (!menu || !btn) return;
  const open = menu.hidden;
  if (open) syncProfileModerationMenu();
  menu.hidden = !open;
  btn.setAttribute("aria-expanded", open ? "true" : "false");
}

async function runProfileModeration(action) {
  const account = moderationAccountForView();
  const rel = state.view?.relationship;
  if (!account) return;
  try {
    if (action === "load-content") {
      const loadBtn = $('#profile-moderation-menu [data-moderation="load-content"]');
      if (loadBtn) { loadBtn.disabled = true; loadBtn.textContent = "Requesting content…"; }
      const collections = account.is_group ? ["outbox"] : ["outbox", "featured"];
      const requests = collections.map(async (collection) => {
        const r = await apiFetch(`/api/v1/accounts/${encodeURIComponent(account.id)}/load_activities`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ collection }),
        });
        if (!r.ok) throw new Error(`${collection}: HTTP ${r.status}`);
      });
      await Promise.all(requests);
      alert(`Content loading requested for @${account.acct || account.username}. It may take a moment to appear; refresh this view shortly.`);
    } else if (action === "blur") {
      const next = blurredAccounts().filter((a) => a.id !== account.id);
      if (!isAccountBlurred(account.id)) next.push({ id: account.id, acct: account.acct || account.username, display_name: account.display_name || "" });
      saveSettings({ blurredAccounts: next });
      rerenderModerationMedia();
    } else if (action === "hide-ops") {
      const next = hiddenOpAccounts().filter((a) => a.id !== account.id);
      if (!isOpAccountHidden(account.id)) next.push({ id: account.id, acct: account.acct || account.username, display_name: account.display_name || "" });
      saveSettings({ hiddenOpAccounts: next });
      renderModerationLists();
      render();
    } else if (action === "mute") {
      const r = await apiFetch(`/api/v1/accounts/${encodeURIComponent(account.id)}/${rel?.muting ? "unmute" : "mute"}`, { method: "POST" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      if (state.view?.account?.id === account.id) state.view.relationship = await r.json();
      await loadMutedAccounts(true);
    } else if (action === "accept-request" || action === "reject-request") {
      await resolveFollowRequest(account.id, action === "accept-request" ? "authorize" : "reject");
    } else if (action === "remove-follower") {
      if (!confirm(`Remove @${account.acct || account.username} from your followers?`)) return;
      const r = await apiFetch(`/api/v1/accounts/${encodeURIComponent(account.id)}/remove_from_followers`, { method: "POST" });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
      if (state.view?.account?.id === account.id) state.view.relationship = await r.json();
    } else if (action === "reposts") {
      const r = await apiFetch(`/api/v1/accounts/${encodeURIComponent(account.id)}/follow`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reblogs: rel?.showing_reblogs === false, replies: rel?.showing_replies !== false }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      if (state.view?.account?.id === account.id) state.view.relationship = await r.json();
    }
  } catch (e) {
    alert(`Failed to update moderation setting: ${e.message}`);
  } finally {
    syncProfileModerationMenu();
  }
}

let mutedAccountsCache = [];
async function loadMutedAccounts(force = false) {
  if (state.auth.status !== "signed-in") { mutedAccountsCache = []; renderModerationLists(); return; }
  if (mutedAccountsCache.length && !force) { renderModerationLists(); return; }
  const r = await apiFetch("/api/v1/mutes?limit=80");
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  mutedAccountsCache = await r.json();
  renderModerationLists();
}

function renderAccountModerationList(host, accounts, removeTitle, onRemove) {
  if (!host) return;
  host.replaceChildren();
  if (!accounts.length) { host.innerHTML = '<div class="empty">None.</div>'; return; }
  for (const account of accounts) {
    const row = document.createElement("div");
    row.className = "moderation-list-item";
    const name = document.createElement("span");
    name.className = "moderation-list-name";
    name.textContent = account.display_name || account.acct || account.username || account.id;
    name.title = account.acct ? `@${account.acct}` : "";
    const remove = document.createElement("button");
    remove.type = "button"; remove.className = "moderation-list-remove"; remove.title = removeTitle;
    remove.innerHTML = '<i class="fa-solid fa-xmark"></i>';
    remove.addEventListener("click", () => onRemove(account, remove));
    row.append(name, remove); host.appendChild(row);
  }
}

function renderMutedWords() {
  const host = $("#config-muted-words-list");
  if (!host) return;
  host.replaceChildren();
  const words = normalizeMutedWords(userSettings.mutedWords);
  for (const word of words) {
    const row = document.createElement("div");
    row.className = "moderation-list-item";
    const name = document.createElement("span");
    name.className = "moderation-list-name";
    name.textContent = word;
    name.title = word;
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "moderation-list-remove";
    remove.title = `Unmute ${word}`;
    remove.setAttribute("aria-label", remove.title);
    remove.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';
    remove.addEventListener("click", () => {
      saveSettings({ mutedWords: normalizeMutedWords(userSettings.mutedWords).filter((w) => w !== word) });
      renderMutedWords();
      render();
    });
    row.append(name, remove);
    host.appendChild(row);
  }
}

function renderModerationLists() {
  renderMutedWords();
  renderAccountModerationList($("#config-muted-list"), mutedAccountsCache, "Unmute", async (account, btn) => {
    btn.disabled = true;
    const r = await apiFetch(`/api/v1/accounts/${encodeURIComponent(account.id)}/unmute`, { method: "POST" });
    if (!r.ok) { btn.disabled = false; alert(`Failed to unmute: HTTP ${r.status}`); return; }
    mutedAccountsCache = mutedAccountsCache.filter((a) => a.id !== account.id);
    if (state.view?.relationship?.id === account.id) state.view.relationship.muting = false;
    renderModerationLists();
  });
  renderAccountModerationList($("#config-blurred-list"), blurredAccounts(), "Stop blurring", (account) => {
    saveSettings({ blurredAccounts: blurredAccounts().filter((a) => a.id !== account.id) });
    renderModerationLists(); rerenderModerationMedia();
  });
  renderAccountModerationList($("#config-hidden-ops-list"), hiddenOpAccounts(), "Show OPs again", (account) => {
    saveSettings({ hiddenOpAccounts: hiddenOpAccounts().filter((a) => a.id !== account.id) });
    renderModerationLists(); render();
  });
  applyAnonymousPresentation($("#config-modal") || document);
  syncAnonymousConfigControls();
}

async function toggleFollow() {
  const v = state.view;
  const account = followableAccountForView(v);
  if (!account?.id) return;

  // Mastodon's unfollow endpoint also cancels an outstanding follow request.
  // Treat [Requested] as the active side of the toggle so clicking it does
  // not submit the same follow request again.
  const hadFollow = !!(v.relationship && (v.relationship.following || v.relationship.requested));
  const btn = document.getElementById("profile-follow");
  if (btn) btn.disabled = true;
  try {
    const r = await apiFetch(
      `/api/v1/accounts/${account.id}/${hadFollow ? "unfollow" : "follow"}`,
      { method: "POST" }
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const rel = await r.json();
    if (state.view?.account?.id === account.id) {
      state.view.relationship = rel;
    }
    // Following/unfollowing a group changes the followed-groups list in the
    // board nav. Add it as soon as the follow endpoint succeeds, including
    // while a remote group still reports `requested`: its board route already
    // works, and the button honestly remains [Requested] until a later
    // relationship fetch sees the Accept. Do not immediately re-fetch the
    // followed-groups collection in that state; eventually-consistent servers
    // omit pending groups and would erase the optimistic link again.
    // Only group actors affect the nav, so this is a no-op for regular users.
    if (account.is_group) {
      if (hadFollow) {
        state.groups = state.groups.filter((g) => g.id !== account.id);
      } else if (!state.groups.some((g) => g.id === account.id)) {
        state.groups.push(account);
      }
      renderBoardNav();
      if (!rel?.requested) refreshFollowedGroups().catch(() => {});
    }
  } catch (e) {
    alert("Failed to update follow: " + e.message);
  } finally {
    syncProfileFollowButton();
    syncProfileFollowsYouBadge();
  }
}

// ---- Mitra subscriptions (monero-subscription) ------------------------------
// A profile may advertise a `monero-subscription` payment option: a per-second
// price (in picoXMR, 12 decimals) on a Monero chain. Subscribing creates an
// invoice on the user's own instance (POST /api/v1/subscriptions/invoices)
// whose payment_address + amount we render as a `monero:` URI QR code, then
// poll until the invoice is paid/completed. The math mirrors mitra-web's
// subscriptions-monero.ts so the amounts we send match what the server expects.
const MONERO_DECIMALS = 12;
const SUB_SECONDS_IN_MONTH = 3600 * 24 * 30; // 2592000, matches mitra DAYS_IN_MONTH=30

function subscribeRoundBigNumber(value, precision) {
  // Round a non-negative bigint to `precision` significant figures (mitra's
  // roundBigNumber), used to derive a clean per-month price from the per-second
  // price the option advertises.
  const s = value.toString();
  const decimals = s.length;
  const divisor = 10n ** BigInt(Math.max(decimals - precision, 0));
  const remainder = value % divisor;
  const midpoint = 10n ** BigInt(Math.max(decimals - precision - 1, 0)) * 5n;
  return remainder >= midpoint ? (value / divisor + 1n) * divisor : value / divisor * divisor;
}

function subscribePricePerMonthInt(pricePerSec) {
  // picoXMR (atomic units) per month, rounded to 4 sig figs like mitra.
  return subscribeRoundBigNumber(BigInt(pricePerSec) * BigInt(SUB_SECONDS_IN_MONTH), 4);
}

function subscribeFormatXmr(picoXmr) {
  // picoXMR atomic units -> human XMR string (12 decimals, trailing zeros trimmed).
  const n = Number(BigInt(picoXmr)) / 10 ** MONERO_DECIMALS;
  let s = n.toFixed(MONERO_DECIMALS);
  s = s.indexOf(".") >= 0 ? s.replace(/0+$/, "").replace(/\.$/, "") : s;
  return s || "0";
}

function subscribePaymentAmount(pricePerSec, durationMonths) {
  // picoXMR atomic units for `durationMonths` months. mitra computes this as
  // Math.round(Number(pricePerMonthInt) * duration); values stay well within
  // Number.MAX_SAFE_INTEGER for realistic subscriptions.
  const perMonth = Number(subscribePricePerMonthInt(pricePerSec));
  return Math.round(perMonth * durationMonths);
}

function subscribeMoneroUri(address, amountXmr) {
  // https://github.com/monero-project/monero/wiki/URI-Formatting
  return amountXmr ? `monero:${address}?tx_amount=${amountXmr}` : `monero:${address}`;
}

function subscribeQrSvg(text) {
  // Vendored qrcode-generator (public/vendor/qrcode.js) imported as an ES
  // module so esbuild bundles it into dist/app.js. typeNumber 0 = auto-size
  // to the data; level M for parity.
  if (typeof qrcode !== "function") return "";
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  const cell = 6;
  const margin = 4;
  const size = (n + margin * 2) * cell;
  let rects = "";
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (qr.isDark(r, c)) {
        const x = (c + margin) * cell;
        const y = (r + margin) * cell;
        rects += `<rect x="${x}" y="${y}" width="${cell}" height="${cell}"/>`;
      }
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges"><rect width="${size}" height="${size}" fill="#fff"/><g fill="#000">${rects}</g></svg>`;
}

function subscriptionOptionForAccount(account) {
  const opts = Array.isArray(account?.payment_options) ? account.payment_options : [];
  return opts.find((o) => o && o.type === "monero-subscription" && typeof o.chain_id === "string" && typeof o.price === "number") || null;
}

// [Subscribe] toggle — shown next to [Follow] on profiles (and group boards)
// that advertise a monero-subscription option. Hidden when signed out, viewing
// yourself, or before the account has resolved.
function syncProfileSubscribeButton() {
  const btn = document.getElementById("profile-subscribe");
  if (!btn) return;
  const account = followableAccountForView(state.view);
  const isSelf = account?.id && account.id === state.auth.account?.id;
  const option = subscriptionOptionForAccount(account);
  btn.removeAttribute("data-external");
  if (state.auth.status !== "signed-in" || !account?.id || isSelf || !option) {
    btn.hidden = true;
    btn.disabled = false;
    return;
  }
  btn.hidden = false;
  btn.disabled = false;
  btn.textContent = "[Subscribe]";
  btn.title = `Subscribe to @${account.acct || account.username || account.id}`;
  if (option.object_id) {
    // Remote (FEP-0837) option: the recipient's own instance hosts the
    // subscription page, so link out instead of creating a local invoice.
    btn.setAttribute("data-external", option.object_id);
    btn.title = "Open the subscription page on the recipient's instance";
  }
}

let subscribeState = {
  account: null,          // recipient account
  option: null,           // monero-subscription payment option
  pricePerMonthInt: null, // bigint picoXMR/month
  pricePerMonth: null,    // XMR string
  amountMin: null,        // picoXMR atomic units (number)
  duration: 1,            // months
  subscription: null,     // { expires_at } from /subscriptions/find, or null
  invoice: null,          // invoice object, or null
  pollTimer: null,        // setInterval id watching an open invoice
  loading: false,
};

function isSubscribedActive(sub) {
  // A subscription is "active" if it has a future expiry (mitra's isPastDate).
  return !!(sub && sub.expires_at && new Date(sub.expires_at).getTime() > Date.now());
}

function subscribeInvoiceStatusText(invoice) {
  switch (invoice?.status) {
    case "requested": return "Payment awaiting response…";
    case "open": return `Waiting for payment (${subscribeInvoiceMinutesLeft(invoice)} min left).`;
    case "paid":
    case "forwarded":
    case "failed": return "Payment processing…";
    case "timeout": return "Payment timed out.";
    case "cancelled": return "Payment cancelled.";
    case "underpaid": return "Payment amount is too small.";
    case "completed": return "Payment completed.";
    default: return "";
  }
}

function subscribeInvoiceMinutesLeft(invoice) {
  if (!invoice?.expires_at) return 0;
  const ms = new Date(invoice.expires_at).getTime() - Date.now();
  return Math.max(0, Math.round(ms / 60000));
}

function renderSubscribeModalBody() {
  const body = document.getElementById("subscribe-modal-body");
  if (!body) return;
  const s = subscribeState;
  const acct = s.account?.acct || s.account?.username || s.account?.id || "";
  const title = document.getElementById("subscribe-modal-title");
  if (title) title.textContent = `Subscribe to @${acct}`;

  if (s.loading && !s.invoice && s.subscription === null) {
    body.innerHTML = '<div class="empty">loading…</div>';
    return;
  }

  if (s.invoice) {
    const inv = s.invoice;
    const amountXmr = subscribeFormatXmr(inv.amount);
    const uri = subscribeMoneroUri(inv.payment_address, amountXmr);
    const qr = subscribeQrSvg(uri);
    const isOpen = inv.status === "open" || inv.status === "underpaid" || inv.status === "requested";
    const done = inv.status === "completed" || inv.status === "timeout" || inv.status === "cancelled";
    body.innerHTML = `
      <div class="subscribe-invoice">
        <div class="payment-header">Please send <b>${escapeHTML(amountXmr)} XMR</b> to this address:</div>
        <a class="payment-address" href="${escapeHTML(uri)}" target="_blank" rel="noopener">${escapeHTML(inv.payment_address || "")}</a>
        ${qr ? `<div class="qr-wrapper">${qr}</div>` : ""}
        <div class="invoice-status">${escapeHTML(subscribeInvoiceStatusText(inv))}</div>
        <div class="subscribe-modal-actions">
          ${isOpen ? '<button type="button" class="secondary" id="subscribe-cancel-invoice">Cancel payment</button>' : ""}
          ${done ? '<button type="button" id="subscribe-ok">OK</button>' : ""}
        </div>
      </div>`;
    document.getElementById("subscribe-cancel-invoice")?.addEventListener("click", onSubscribeCancelInvoice);
    document.getElementById("subscribe-ok")?.addEventListener("click", onSubscribeCloseInvoice);
    return;
  }

  // Payment form (no open invoice).
  const subscribed = isSubscribedActive(s.subscription);
  const statusLine = subscribed
    ? `Subscribed until ${new Date(s.subscription.expires_at).toLocaleString()}.`
    : "You are not subscribed yet.";
  const duration = Math.max(1, Math.floor(Number(s.duration) || 0));
  const amount = subscribePaymentAmount(s.option.price, duration);
  const amountStr = subscribeFormatXmr(amount);
  const minOk = amount >= (s.amountMin || 0) && duration >= 1;
  const payDisabled = s.loading || !minOk;
  const payLabel = subscribed ? "Extend" : "Pay";
  body.innerHTML = `
    <div class="subscribe-price">${escapeHTML(s.pricePerMonth || "0")} XMR <span class="price-subtext">per month</span></div>
    <div class="subscribe-status-line">${escapeHTML(statusLine)}</div>
    <div class="subscribe-pay-row">
      <label for="subscribe-duration">Duration</label>
      <input type="number" id="subscribe-duration" min="1" max="1000" value="${duration}">
      <span>months</span>
      <span class="subscribe-pay-amount">${escapeHTML(amountStr)} XMR</span>
    </div>
    <div class="subscribe-modal-status" id="subscribe-modal-status"></div>
    <div class="subscribe-modal-actions">
      <button type="button" class="secondary" id="subscribe-cancel">Cancel</button>
      <button type="button" id="subscribe-pay" ${payDisabled ? "disabled" : ""}>${payLabel}</button>
    </div>`;
  const durInput = document.getElementById("subscribe-duration");
  if (durInput) {
    durInput.addEventListener("input", () => {
      const v = parseInt(durInput.value, 10);
      subscribeState.duration = Number.isFinite(v) && v > 0 ? Math.min(v, 1000) : 0;
      const amt = subscribePaymentAmount(subscribeState.option.price, Math.max(1, subscribeState.duration || 1));
      const amtEl = document.querySelector(".subscribe-pay-amount");
      if (amtEl) amtEl.textContent = `${subscribeFormatXmr(amt)} XMR`;
      const payBtn = document.getElementById("subscribe-pay");
      if (payBtn) payBtn.disabled = subscribeState.loading || amt < (subscribeState.amountMin || 0) || (subscribeState.duration || 0) < 1;
    });
  }
  document.getElementById("subscribe-cancel")?.addEventListener("click", closeSubscribeModal);
  document.getElementById("subscribe-pay")?.addEventListener("click", onSubscribePay);
}

function setSubscribeStatus(msg, isError) {
  const el = document.getElementById("subscribe-modal-status");
  if (!el) return;
  el.textContent = msg || "";
  el.classList.toggle("is-error", !!isError);
}

async function loadSubscribeState() {
  // Check for an existing (active) subscription so the modal can show "Extend"
  // vs. "Pay". /subscriptions/find 404s when there isn't one.
  const s = subscribeState;
  const me = state.auth.account?.id;
  const them = s.account?.id;
  if (!me || !them) { s.subscription = null; return; }
  try {
    const r = await apiFetch(`/api/v1/subscriptions/find?sender_id=${encodeURIComponent(me)}&recipient_id=${encodeURIComponent(them)}`);
    if (r.status === 404) { s.subscription = null; return; }
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    s.subscription = await r.json();
  } catch {
    s.subscription = null;
  }
}

function openSubscribeModal(account) {
  const option = subscriptionOptionForAccount(account);
  if (!option) return;
  const pricePerMonthInt = subscribePricePerMonthInt(option.price);
  subscribeState = {
    account,
    option,
    pricePerMonthInt,
    pricePerMonth: subscribeFormatXmr(pricePerMonthInt),
    amountMin: typeof option.amount_min === "number" ? option.amount_min : 1000000000,
    duration: 1,
    subscription: null,
    invoice: null,
    pollTimer: null,
    loading: true,
  };
  const dlg = document.getElementById("subscribe-modal");
  if (dlg) dlg.hidden = false;
  renderSubscribeModalBody();
  loadSubscribeState().then(() => {
    subscribeState.loading = false;
    renderSubscribeModalBody();
    document.getElementById("subscribe-duration")?.focus();
  });
}

function closeSubscribeModal() {
  if (subscribeState.pollTimer) { clearInterval(subscribeState.pollTimer); subscribeState.pollTimer = null; }
  const dlg = document.getElementById("subscribe-modal");
  if (dlg) dlg.hidden = true;
  const body = document.getElementById("subscribe-modal-body");
  if (body) body.innerHTML = '<div class="empty">loading…</div>';
  subscribeState = {
    account: null, option: null, pricePerMonthInt: null, pricePerMonth: null,
    amountMin: null, duration: 1, subscription: null, invoice: null,
    pollTimer: null, loading: false,
  };
}

async function onSubscribePay() {
  const s = subscribeState;
  const duration = Math.max(1, Math.floor(Number(s.duration) || 0));
  if (duration < 1) return;
  const amount = subscribePaymentAmount(s.option.price, duration);
  if (!amount || amount < (s.amountMin || 0)) {
    setSubscribeStatus("Amount is below the minimum.", true);
    return;
  }
  const me = state.auth.account?.id;
  const them = s.account?.id;
  if (!me || !them) return;
  s.loading = true;
  const payBtn = document.getElementById("subscribe-pay");
  if (payBtn) payBtn.disabled = true;
  setSubscribeStatus("Creating invoice…", false);
  try {
    const r = await apiFetch("/api/v1/subscriptions/invoices", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sender_id: me, recipient_id: them, chain_id: s.option.chain_id, amount }),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
    s.invoice = await r.json();
    s.loading = false;
    renderSubscribeModalBody();
    watchSubscribeInvoice();
  } catch (e) {
    s.loading = false;
    setSubscribeStatus("Failed to create invoice: " + e.message, true);
    renderSubscribeModalBody();
  }
}

function watchSubscribeInvoice() {
  if (subscribeState.pollTimer) clearInterval(subscribeState.pollTimer);
  const id = subscribeState.invoice?.id;
  if (!id) return;
  subscribeState.pollTimer = setInterval(async () => {
    if (!subscribeState.invoice || subscribeState.invoice.id !== id) {
      clearInterval(subscribeState.pollTimer); subscribeState.pollTimer = null; return;
    }
    try {
      const r = await apiFetch(`/api/v1/subscriptions/invoices/${encodeURIComponent(id)}`);
      if (!r.ok) return;
      subscribeState.invoice = await r.json();
      renderSubscribeModalBody();
      if (subscribeState.invoice.status === "completed") {
        // Stop polling; leave the "Payment completed." view (with [OK]) up so
        // the user can acknowledge it. Dismissing re-checks subscription state.
        clearInterval(subscribeState.pollTimer); subscribeState.pollTimer = null;
      }
    } catch { /* keep polling; transient network errors shouldn't kill it */ }
  }, 10000);
}

async function onSubscribeCancelInvoice() {
  const id = subscribeState.invoice?.id;
  if (!id) return;
  const btn = document.getElementById("subscribe-cancel-invoice");
  if (btn) { btn.disabled = true; btn.textContent = "Cancelling…"; }
  try {
    const r = await apiFetch(`/api/v1/subscriptions/invoices/${encodeURIComponent(id)}`, { method: "DELETE" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
  } catch (e) {
    alert("Failed to cancel invoice: " + e.message);
    if (btn) { btn.disabled = false; btn.textContent = "Cancel payment"; }
    return;
  }
  if (subscribeState.pollTimer) { clearInterval(subscribeState.pollTimer); subscribeState.pollTimer = null; }
  subscribeState.invoice = null;
  renderSubscribeModalBody();
}

async function onSubscribeCloseInvoice() {
  if (subscribeState.pollTimer) { clearInterval(subscribeState.pollTimer); subscribeState.pollTimer = null; }
  subscribeState.invoice = null;
  // Re-check subscription state so the form reflects the (possibly new) expiry
  // after a completed payment, or stays "not subscribed" after a timeout/cancel.
  subscribeState.loading = true;
  renderSubscribeModalBody();
  await loadSubscribeState();
  subscribeState.loading = false;
  renderSubscribeModalBody();
}

function wireSubscribeModal() {
  const dlg = document.getElementById("subscribe-modal");
  if (!dlg) return;
  document.getElementById("subscribe-modal-close")?.addEventListener("click", closeSubscribeModal);
  addBackdropClose(dlg, closeSubscribeModal);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !dlg.hidden) closeSubscribeModal();
  });
}

// [Edit Board] / [Delete Board] — shown on a custom board view only when the
// signed-in user moderates its group (checked against the filter=moderating
// list, which is what the server enforces on PATCH/DELETE too).
function syncBoardAdminButtons() {
  const editBtn = document.getElementById("board-edit");
  const deleteBtn = document.getElementById("board-delete");
  if (!editBtn || !deleteBtn) return;
  const v = state.view;
  const account = (v?.kind === "board" && v.group) ? v.account : null;
  const canAdmin = state.auth.status === "signed-in" && isModeratedGroup(account?.id);
  editBtn.hidden = !canAdmin;
  deleteBtn.hidden = !canAdmin;
}

async function deleteCurrentBoard() {
  const v = state.view;
  const account = (v?.kind === "board" && v.group) ? v.account : null;
  if (!account?.id || !isModeratedGroup(account.id)) return;
  const username = String(v.group).split("@")[0].toLowerCase();
  if (!confirm(`Delete /${username}/? This deletes the board's group account and cannot be undone.`)) {
    return;
  }
  const btn = document.getElementById("board-delete");
  if (btn) btn.disabled = true;
  try {
    await deleteGroup(account.id, state);
    state.groups = state.groups.filter((g) => g.id !== account.id);
    state.moderatedGroups = state.moderatedGroups.filter((g) => g.id !== account.id);
    renderBoardNav();
    refreshFollowedGroups().catch(() => {});
    location.hash = `#/${defaultBoardName()}/`;
  } catch (e) {
    alert("Failed to delete board: " + (e.message || e));
  } finally {
    if (btn) btn.disabled = false;
  }
}

// Board nav: one wrappable line of bracketed groups —
//   [home / twkn / local / likes / bkmks][followed1 / followed2][own1 / +]
// Brackets and slashes are faint separators; only the names are links. The
// middle group holds followed Mitra groups (custom boards); the last group
// holds the boards the user created (moderates) plus the [+] that opens the
// create-board dialog (both only when signed in).
function renderBoardNav() {
  const nav = document.getElementById("boards-nav");
  if (!nav) return;
  nav.innerHTML = "";

  const sep = (text) => {
    const s = document.createElement("span");
    s.className = "board-sep";
    s.textContent = text;
    return s;
  };

  const makeGroup = (entries) => {
    const g = document.createElement("span");
    g.className = "board-group";
    g.appendChild(sep("["));
    entries.forEach((entry, i) => {
      if (i > 0) {
        g.appendChild(sep(" / "));
        // Let long board lists wrap after a separator, not mid-name.
        g.appendChild(document.createElement("wbr"));
      }
      const a = document.createElement("a");
      a.className = "board-link";
      a.dataset.board = entry.key;
      a.href = entry.href;
      a.textContent = entry.label;
      if (entry.title) a.title = entry.title;
      g.appendChild(a);
    });
    g.appendChild(sep("]"));
    return g;
  };

  // Guests only ever get the local timeline from Mitra (and only when
  // `instance_timeline_public` is on, surfaced to clients as
  // allow_unauthenticated.timeline_local); home/twkn/likes/bkmks and group
  // timelines all require auth, so don't render links that can only 401.
  // The explicit `=== false` keeps local visible on servers that don't
  // report the field (e.g. Mastodon, where public timelines are public).
  let builtins = Object.keys(BOARDS);
  if (state.auth.status !== "signed-in") {
    builtins = state.instance?.allow_unauthenticated?.timeline_local === false ? [] : ["local"];
  }
  if (builtins.length) {
    nav.appendChild(makeGroup(builtins.map((name) => ({
      key: name,
      label: name,
      href: `#/${name}/`,
    }))));
  }

  const groupEntry = (g) => {
    const acct = g.acct || g.username || "";
    // Label is just the username (strip the @instance domain); the tooltip
    // carries the full user@instance handle.
    const username = String(acct).split("@")[0];
    return {
      key: `g/${acct}`,
      label: username.toLowerCase(),
      href: `#/g/${encodeURIComponent(acct)}/`,
      title: acct,
    };
  };

  // Split the custom boards: boards the user merely follows go in the middle
  // group; boards they created (moderate) share the last bracket with the [+]
  // create link — [testgroup / tricky_cannon][b / +].
  const moderatedIds = new Set(state.moderatedGroups.map((g) => g.id));
  const followedOnly = state.groups.filter((g) => !moderatedIds.has(g.id));
  if (followedOnly.length) {
    nav.appendChild(makeGroup(followedOnly.map(groupEntry)));
  }

  if (state.auth.status === "signed-in") {
    // Local tag boards share the bracket with the boards the user created:
    // [#freecad / b / +].
    const tagEntry = (tag) => ({
      key: `tag/${tag}`,
      label: `#${tag}`,
      href: `#/tag/${encodeURIComponent(tag)}/`,
      title: `Local tag board for #${tag}`,
    });
    const entries = [
      ...(userSettings.tagBoards || []).map(tagEntry),
      ...state.moderatedGroups.map(groupEntry),
    ];
    const g = document.createElement("span");
    g.className = "board-group";
    g.appendChild(sep("["));
    entries.forEach((entry, i) => {
      if (i > 0) {
        g.appendChild(sep(" / "));
        g.appendChild(document.createElement("wbr"));
      }
      const a = document.createElement("a");
      a.className = "board-link";
      a.dataset.board = entry.key;
      a.href = entry.href;
      a.textContent = entry.label;
      a.title = entry.title;
      g.appendChild(a);
    });
    g.appendChild(sep(entries.length ? " / " : " "));
    const add = document.createElement("a");
    add.className = "board-link board-add";
    add.href = "#";
    add.textContent = "+";
    add.title = "Create a board";
    g.appendChild(add);
    g.appendChild(sep(" "));
    g.appendChild(sep("]"));
    nav.appendChild(g);
  }

  // Re-apply active state (renderHeader also does this on view changes).
  const key = boardKey(state.view);
  nav.querySelectorAll(".board-link").forEach((el) => {
    el.classList.toggle("active", !!key && el.dataset.board === key);
  });
}

// ---- create/edit-board dialog -----------------------------------------------
// Opened from the [+] in the board nav (create) or the [Edit Board] button on
// a moderated board (edit). Creating a board = creating a Mitra group
// (POST /api/v1/groups); the server auto-follows the creator, so the new
// board shows up in the followed-groups nav. Editing can only change the
// description (PATCH /api/v1/groups/{id}) — the group username is fixed.

// ---- local tag boards -------------------------------------------------------
// A tag board is a UI-only bookmark of a /tag/<name>/ view: it lives in
// userSettings (localStorage), never on the server, and renders in the same
// nav bracket as the boards the user created.

function normalizeTagName(raw) {
  return String(raw || "").replace(/^#+/, "").trim().toLowerCase();
}

function isValidTagName(tag) {
  return !!tag && !/[\s#/?]/.test(tag);
}

function hasTagBoard(tag) {
  return (userSettings.tagBoards || []).includes(normalizeTagName(tag));
}

// Returns the normalized tag on success, null on an invalid name.
// Adding is idempotent — re-adding an existing tag just re-navigates.
function addTagBoard(raw) {
  const tag = normalizeTagName(raw);
  if (!isValidTagName(tag)) return null;
  if (!hasTagBoard(tag)) {
    saveSettings({ tagBoards: [...(userSettings.tagBoards || []), tag] });
    renderBoardNav();
  }
  return tag;
}

function removeTagBoard(raw) {
  const tag = normalizeTagName(raw);
  if (!hasTagBoard(tag)) return;
  saveSettings({ tagBoards: (userSettings.tagBoards || []).filter((t) => t !== tag) });
  renderBoardNav();
}

let boardEditTarget = null;   // group Account being edited, or null when creating

// Kept here rather than only in the markup because the dialog swaps it out on
// a server with no group API. Matches index.html's default text.
const BOARD_NAME_HINT =
  "Becomes the board's group username on this instance (letters, digits, underscores). " +
  "Start with # (e.g. #freecad) to add a local tag board instead.";

function openCreateBoardModal(editGroup = null) {
  if (state.auth.status !== "signed-in") {
    beginAuth();
    openAuthDialog();
    return;
  }
  const dlg = document.getElementById("board-create");
  if (!dlg) return;
  boardEditTarget = editGroup || null;

  const title = document.getElementById("board-create-title");
  const nameInput = document.getElementById("board-create-name");
  const nameHint = document.getElementById("board-create-name-hint");
  const descInput = document.getElementById("board-create-desc");
  const saveBtn = document.getElementById("board-create-save");
  const status = document.getElementById("board-create-status");
  if (status) { status.textContent = ""; status.classList.remove("is-error"); }

  const username = boardEditTarget
    ? String(boardEditTarget.acct || boardEditTarget.username || "").split("@")[0]
    : "";
  // Without a group API there's no server-side board to create, so the dialog
  // offers the one kind of board that works anywhere: a local tag board.
  const tagOnly = state.groupsSupported === false;

  if (title) {
    title.textContent = boardEditTarget
      ? `Edit /${username.toLowerCase()}/`
      : (tagOnly ? "Add a tag board" : "Create a board");
  }
  if (saveBtn) saveBtn.textContent = boardEditTarget ? "Save" : (tagOnly ? "Add" : "Create");
  if (nameInput) {
    nameInput.value = tagOnly && !boardEditTarget ? "#" : username;
    nameInput.disabled = !!boardEditTarget;
  }
  if (nameHint) {
    nameHint.hidden = !!boardEditTarget;
    nameHint.textContent = tagOnly
      ? "A tag board is a local bookmark of a #hashtag timeline. Custom boards are groups, which this server doesn't have."
      : BOARD_NAME_HINT;
  }
  // A description belongs to a group; a tag board has nowhere to put one.
  if (descInput) descInput.hidden = tagOnly && !boardEditTarget;
  const descLabel = document.querySelector('label[for="board-create-desc"]');
  if (descLabel) descLabel.hidden = tagOnly && !boardEditTarget;
  if (descInput) descInput.value = "";

  dlg.hidden = false;

  if (boardEditTarget) {
    // Prefill with the markdown source of the current description; fall back
    // to empty on failure (saving would then clear it, matching what's shown).
    const target = boardEditTarget;
    getGroupSource(target.id, state).then((src) => {
      if (boardEditTarget === target && descInput && !descInput.value) {
        descInput.value = src?.description || "";
      }
    }).catch(() => {});
    setTimeout(() => descInput?.focus(), 0);
  } else {
    setTimeout(() => nameInput?.focus(), 0);
  }
}

function closeCreateBoardModal() {
  const dlg = document.getElementById("board-create");
  if (dlg) dlg.hidden = true;
  boardEditTarget = null;
}

function wireCreateBoardModal() {
  const dlg = document.getElementById("board-create");
  if (!dlg) return;
  const form = document.getElementById("board-create-form");
  const nameInput = document.getElementById("board-create-name");
  const status = document.getElementById("board-create-status");
  const saveBtn = document.getElementById("board-create-save");

  const setStatus = (msg, isError) => {
    if (!status) return;
    status.textContent = msg || "";
    status.classList.toggle("is-error", !!isError);
  };

  document.getElementById("board-create-close")?.addEventListener("click", closeCreateBoardModal);
  document.getElementById("board-create-cancel")?.addEventListener("click", closeCreateBoardModal);
  addBackdropClose(dlg, closeCreateBoardModal);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !dlg.hidden) closeCreateBoardModal();
  });

  form?.addEventListener("submit", async (e) => {
    e.preventDefault();
    const descInput = document.getElementById("board-create-desc");
    const description = (descInput?.value || "").trim();

    if (boardEditTarget) {
      if (saveBtn) saveBtn.disabled = true;
      setStatus("Saving…", false);
      try {
        const updated = await updateGroup(boardEditTarget.id, description, state);
        closeCreateBoardModal();
        // Refresh the board header's description if we're looking at it.
        if (state.view?.kind === "board" && state.view.account?.id === updated.id) {
          state.view.account = updated;
          renderHeader();
        }
        refreshFollowedGroups().catch(() => {});
      } catch (err) {
        setStatus("Failed to save: " + (err.message || err), true);
      } finally {
        if (saveBtn) saveBtn.disabled = false;
      }
      return;
    }

    const name = (nameInput?.value || "").trim();
    if (!name) {
      setStatus("Enter a board name.", true);
      return;
    }

    // A leading "#" makes a local tag board instead of a server-side group:
    // just remember the tag and jump to its timeline. No API call involved.
    if (name.startsWith("#")) {
      const tag = addTagBoard(name);
      if (!tag) {
        setStatus("Tag names can't contain spaces, #, /, or ?.", true);
        return;
      }
      if (nameInput) nameInput.value = "";
      closeCreateBoardModal();
      location.hash = `#/tag/${encodeURIComponent(tag)}/`;
      return;
    }

    // Everything below creates a group. Say so plainly rather than firing a
    // request at an endpoint this server doesn't have.
    if (state.groupsSupported === false) {
      setStatus("This server has no custom boards — start the name with # to add a tag board.", true);
      return;
    }

    if (saveBtn) saveBtn.disabled = true;
    setStatus("Creating…", false);
    try {
      const group = await createGroup(name, description, state);
      if (nameInput) nameInput.value = "";
      if (descInput) descInput.value = "";
      closeCreateBoardModal();
      // Show the new board in the nav right away; the background refresh
      // reconciles with the server's list (including the moderating split).
      state.groups.push(group);
      state.moderatedGroups.push(group);
      renderBoardNav();
      refreshFollowedGroups().catch(() => {});
      location.hash = `#/g/${encodeURIComponent(group.acct || group.username || name)}/`;
    } catch (err) {
      setStatus("Failed to create board: " + (err.message || err), true);
    } finally {
      if (saveBtn) saveBtn.disabled = false;
    }
  });
}

function renderAuth() {
  const wrap = $("#auth-pill");
  const menuBtn = document.getElementById("account-menu-btn");
  const signinBtn = document.getElementById("account-signin");
  if (!wrap) return;
  const a = state.auth;
  wrap.innerHTML = "";

  // A signed-in account is shown whenever we hold a token + account, even
  // during an in-flight "add account" exchange — so the pill (and the
  // account menu) stay visible while authenticating a second account.
  const hasAccount = !!(a.account && a.accessToken);

  if (hasAccount) {
    // Avatar + name; account switching/log out live in the menu button now.
    const link = document.createElement("a");
    link.href = profileHref(a.account.acct || a.account.username);
    link.className = "auth-pill-link";
    link.title = `${a.needsReconnect ? "Reconnect" : "Signed in as"} @${a.account.acct || a.account.username}`;
    // Which account you're about to post as, kept legible in anonymous mode.
    link.dataset.anonKeep = "1";
    if (a.account.avatar) {
      const av = document.createElement("img");
      av.className = "auth-avatar";
      av.src = a.account.avatar;
      av.alt = "";
      av.loading = "lazy";
      link.appendChild(av);
    }
    const name = document.createElement("span");
    name.className = "auth-name";
    name.innerHTML = renderTextWithCustomEmoji(
      a.account.display_name || a.account.username,
      a.account.emojis || []
    );
    link.appendChild(name);
    wrap.appendChild(link);
  }

  if (menuBtn) menuBtn.hidden = !hasAccount;
  if (signinBtn) {
    signinBtn.hidden = hasAccount && !a.needsReconnect;
    signinBtn.textContent = a.needsReconnect ? "Reconnect" : "Sign in";
    signinBtn.title = a.needsReconnect ? "Session expired — authorize this account again" : "Sign in";
  }
  // If we're no longer signed in, dismiss any open account menu.
  if (!hasAccount) closeAccountMenu();
  renderAuthDialogStatus();
  syncStartThreadButton();
  syncWatcherIndicator();
  renderBoardNav();
  renderHeader();
}

const CUSTOM_EMOJI_RE = /:([A-Za-z0-9_+-]+):/g;

function emojiSrc(emoji) {
  return emoji?.static_url || emoji?.url || "";
}

function isSafeEmojiSrc(src) {
  return /^(https?:|\/)/i.test(src || "");
}

// A bare instance hostname (optionally host:port) — no scheme, userinfo, path,
// or stray characters. Must contain a dot and no consecutive dots.
function isValidInstanceDomain(s) {
  return /^[a-z0-9.-]+(:\d{1,5})?$/i.test(s) && s.includes(".") && !s.includes("..");
}

function buildEmojiMap(emojis) {
  const map = new Map();
  for (const e of emojis || []) {
    if (!e?.shortcode) continue;
    const src = emojiSrc(e);
    if (!src || !isSafeEmojiSrc(src)) continue;
    map.set(e.shortcode, e);
  }
  return map;
}

function renderTextWithCustomEmoji(text, emojis = []) {
  const s = text == null ? "" : String(text);
  const emojiMap = buildEmojiMap(emojis);
  if (!emojiMap.size || !s.includes(":")) return escapeHTML(s);

  CUSTOM_EMOJI_RE.lastIndex = 0;
  let out = "";
  let last = 0;
  let matched = false;
  for (let m; (m = CUSTOM_EMOJI_RE.exec(s)); ) {
    const [token, shortcode] = m;
    const emoji = emojiMap.get(shortcode);
    if (!emoji) continue;
    const src = emojiSrc(emoji);
    if (!isSafeEmojiSrc(src)) continue;

    out += escapeHTML(s.slice(last, m.index));
    out += `<img class="custom-emoji" src="${escapeHTML(src)}" alt=":${escapeHTML(shortcode)}:" title=":${escapeHTML(shortcode)}:" loading="lazy">`;
    last = m.index + token.length;
    matched = true;
  }
  if (!matched) return escapeHTML(s);
  out += escapeHTML(s.slice(last));
  return out;
}

function applyCustomEmojiToTree(root, emojis = []) {
  const emojiMap = buildEmojiMap(emojis);
  if (!emojiMap.size) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n);

  for (const node of nodes) {
    const text = node.nodeValue || "";
    if (!text.includes(":")) continue;
    const tag = node.parentElement?.tagName;
    if (tag === "CODE" || tag === "PRE") continue;

    CUSTOM_EMOJI_RE.lastIndex = 0;
    let last = 0;
    let matched = false;
    const frag = document.createDocumentFragment();

    for (let m; (m = CUSTOM_EMOJI_RE.exec(text)); ) {
      const [token, shortcode] = m;
      const emoji = emojiMap.get(shortcode);
      if (!emoji) continue;
      const src = emojiSrc(emoji);
      if (!isSafeEmojiSrc(src)) continue;

      if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));

      const img = document.createElement("img");
      img.className = "custom-emoji";
      img.src = src;
      img.alt = `:${shortcode}:`;
      img.title = `:${shortcode}:`;
      img.loading = "lazy";
      frag.appendChild(img);

      last = m.index + token.length;
      matched = true;
    }

    if (!matched) continue;
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    node.parentNode?.replaceChild(frag, node);
  }
}

// Plain unicode emoji, including the sequences that render as one glyph:
// keycaps (1️⃣), flags (regional indicator pairs), skin-tone modifiers, and
// ZWJ families. Matching the whole sequence matters — wrapping only the first
// codepoint would tear the glyph apart.
const UNICODE_EMOJI_RE = /\p{Emoji}️?⃣|\p{RI}\p{RI}|\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})*(?:‍\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})*)*/gu;
const EMOJI_PRESENTATION_RE = /^\p{Emoji_Presentation}$/u;

// Wrap unicode emoji in a span so they can be hover-magnified like custom
// emoji (a bare text codepoint has nothing to attach the transform to).
// Runs after applyCustomEmojiToTree so shortcodes are already <img>s.
function wrapUnicodeEmojiInTree(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n);

  for (const node of nodes) {
    const text = node.nodeValue || "";
    if (!text) continue;
    const tag = node.parentElement?.tagName;
    if (tag === "CODE" || tag === "PRE") continue;

    UNICODE_EMOJI_RE.lastIndex = 0;
    let last = 0;
    let matched = false;
    const frag = document.createDocumentFragment();

    for (let m; (m = UNICODE_EMOJI_RE.exec(text)); ) {
      const glyph = m[0];
      // Extended_Pictographic also covers text-mode characters like © ® ™ and
      // the dingbats used as ordinary punctuation. A lone BMP character only
      // counts when it defaults to emoji presentation (☕, ✅) — otherwise it
      // needs the explicit U+FE0F, which makes the match longer than one char.
      if (glyph.length === 1 && !EMOJI_PRESENTATION_RE.test(glyph)) continue;

      if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));

      const span = document.createElement("span");
      span.className = "uni-emoji";
      span.textContent = glyph;
      frag.appendChild(span);

      last = m.index + glyph.length;
      matched = true;
    }

    if (!matched) continue;
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    node.parentNode?.replaceChild(frag, node);
  }
}

// Strip HTML tags from status content but keep paragraph breaks.
function stripHTML(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  tmp.querySelectorAll("p, br, li").forEach((el) => {
    if (el.tagName === "BR") {
      el.replaceWith(document.createTextNode("\n"));
    } else {
      el.appendChild(document.createTextNode("\n"));
    }
  });
  return (tmp.textContent || "").trim().replace(/\n{3,}/g, "\n\n");
}

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)        return `${Math.floor(diff)}s`;
  if (diff < 3600)      return `${Math.floor(diff / 60)}m`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400*30)  return `${Math.floor(diff / 86400)}d`;
  return d.toISOString().slice(0, 10);
}

function countFmt(n) {
  if (n == null) return "0";
  if (n < 1000) return String(n);
  if (n < 10000) return (n / 1000).toFixed(1) + "k";
  return Math.floor(n / 1000) + "k";
}

// Stable hue from a string — fallback background colors.
function stringHue(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

// Build a profile URL from an account.
function profileHref(acct) {
  return `#/user/${encodeURIComponent(acct)}/`;
}

function applyAnonymousPresentation(root = document) {
  document.documentElement.classList.toggle("anonymous-mode", !!userSettings.anonymousMode);

  const restore = (el) => {
    if (el.dataset.anonHtml != null) el.innerHTML = el.dataset.anonHtml;
    if (el.dataset.anonHref != null) el.setAttribute("href", el.dataset.anonHref);
    if (el.dataset.anonTitle != null) el.setAttribute("title", el.dataset.anonTitle);
    else el.removeAttribute("title");
    delete el.dataset.anonMasked;
    delete el.dataset.anonHtml;
    delete el.dataset.anonHref;
    delete el.dataset.anonTitle;
  };

  if (!userSettings.anonymousMode) {
    root.querySelectorAll?.('[data-anon-masked="1"]').forEach(restore);
    return;
  }

  // Mastodon-compatible servers commonly mark hashtags with both `mention`
  // and `hashtag`. They are topic links, not account identities; undo masking
  // from older renders before applying the narrower selector below.
  root.querySelectorAll?.('a.mention.hashtag[data-anon-masked="1"]').forEach(restore);

  const mask = (el) => {
    if (!el || el.dataset.anonMasked === "1") return;
    // Anonymous mode hides *other people* from onlookers. Your own identity is
    // the one thing you need to keep seeing — with several accounts signed in,
    // a pill reading "Anonymous" hides which one a post would go out as, which
    // is a worse leak than the one being prevented.
    if (el.closest?.("[data-anon-keep]")) return;
    el.dataset.anonMasked = "1";
    el.dataset.anonHtml = el.innerHTML;
    if (el.hasAttribute("href")) {
      el.dataset.anonHref = el.getAttribute("href") || "";
      el.removeAttribute("href");
    }
    if (el.hasAttribute("title")) el.dataset.anonTitle = el.getAttribute("title") || "";
    el.removeAttribute("title");
    el.textContent = "Anonymous";
  };

  root.querySelectorAll?.('a[href^="#/user/"], a.mention:not(.hashtag)').forEach(mask);
  root.querySelectorAll?.([
    ".profile-display-name > span",
    ".profile-acct",
    ".activity-hover-name",
    ".activity-hover-handle",
    ".contact-name",
    ".auth-name",
    ".moderation-list-name",
    ".ppv-name",
    ".compose-autocomplete-name",
    ".compose-autocomplete-acct",
    ".compose-reply-acct",
    ".watcher-actor",
  ].join(",")).forEach(mask);
}

// Board view URL for a group actor's acct ("name" local, "name@host" remote).
function groupBoardHref(acct) {
  return `#/g/${encodeURIComponent(acct)}/`;
}

// The green BOARD badge/chip shared by catalog cards, account cards, and
// thread OPs: links a status's group (or a group account) to its board view.
function boardBadgeTitle(acct) {
  return `Posted to /${String(acct).split("@")[0].toLowerCase()}/ — @${acct}`;
}

// Build a thread URL from a status.
function threadHref(id) {
  return `#/post/${encodeURIComponent(id)}/`;
}

// Absolute chan-fe permalink for a post. Used as the target of markdown
// [>>id](url) references in multi-post replies: the link resolves anywhere
// (other frontends, middle-click), while chan-fe itself re-renders the text
// as a red quotelink (see wireThreadLinks).
function chanfePostUrl(id) {
  return new URL(threadHref(id), document.baseURI).href;
}

// Default board when no explicit hash is set: /home/ for signed-in users,
// /local/ for everyone else (the only timeline Mitra serves to guests).
function defaultBoardName() {
  return state.auth.status === "signed-in" ? "home" : "local";
}

function hashForRoute(route) {
  if (!route) return "#/twkn/";
  if (route.kind === "board") {
    if (route.group) return `#/g/${encodeURIComponent(route.group)}/`;
    return `#/${route.name || "twkn"}/`;
  }
  if (route.kind === "profile") return `#/user/${encodeURIComponent(route.acct || "")}/`;
  if (route.kind === "search")  return `#/search/${encodeURIComponent(route.q || "")}/`;
  if (route.kind === "tag")     return `#/tag/${encodeURIComponent(route.tag || "")}/`;
  if (route.kind === "about")   return "#/about/";
  return "#/twkn/";
}

function backLabelForRoute(route) {
  const hash = hashForRoute(route);
  return hash.replace(/^#/, "");
}

let suppressHoverResetHandler = null;

function suppressImageHoverUntilPointerMove() {
  state.ui.suppressImageHover = true;
  if (suppressHoverResetHandler) return;

  suppressHoverResetHandler = () => {
    state.ui.suppressImageHover = false;
    suppressHoverResetHandler = null;
  };
  document.addEventListener("pointermove", suppressHoverResetHandler, { once: true, capture: true });
}

function clearFloatingOverlays() {
  document.querySelectorAll(".post-overlay-img").forEach((el) => el.remove());
  document.querySelectorAll(".post-overlay-video").forEach((el) => {
    try { el.pause(); } catch {}
    el.remove();
  });
  const pp = document.getElementById("post-preview");
  pp?.classList.remove("is-visible");
  suppressImageHoverUntilPointerMove();
}

// Catch-all cleanup for floating overlays whose owning thumbnail has left
// the DOM. A stuck overlay always means the thumbnail's mouseleave never
// fired — which is exactly what happens when the node is removed
// programmatically (route change, in-place auto-refresh, inline-reply
// teardown) rather than by the pointer leaving it. Each overlay is tagged
// with its owner (see attach*Interactions); here a single MutationObserver
// watches for removals and drops any overlay whose owner is gone. This
// doesn't depend on mouseleave, the suppress flag, or where the removal
// originated. The querySelectorAll short-circuits when no overlay exists,
// so the steady-state cost is negligible.
function sweepOrphanOverlays() {
  sweepImageNavigation();
  const overlays = document.querySelectorAll(".post-overlay-img, .post-overlay-video");
  for (const el of overlays) {
    if (el._ownerEl && el._ownerEl.isConnected) continue;
    if (el.tagName === "VIDEO") { try { el.pause(); } catch {} }
    el.remove();
  }
  // Same backstop for the reply hover preview: if the link/verb that opened it
  // has left the DOM (e.g. navigating to a new thread), its mouseleave never
  // fired, so hide the popup here instead of letting it hang over the new view.
  if (previewPopup._ownerEl && !previewPopup._ownerEl.isConnected) {
    previewPopup.hide();
  }
  // And for the activity hover list: hide() also cancels a pending dwell
  // timer, so this covers both a visible popup and one about to open for a
  // button that no longer exists.
  if (activityHoverPopup._button && !activityHoverPopup._button.isConnected) {
    activityHoverPopup.hide();
  }
}

const orphanOverlayObserver = new MutationObserver((mutations) => {
  for (const m of mutations) {
    if (m.removedNodes.length) { sweepOrphanOverlays(); return; }
  }
});
orphanOverlayObserver.observe(document.body, { childList: true, subtree: true });

// ---- data loading -----------------------------------------------------------

// ---- catalog utilities moved to catalog.js ---------------------------------

// ---- rendering: thread card -------------------------------------------------

// Count descendants we can see in the currently loaded timeline payload.
// This is a lower bound (timeline windows may omit older replies).
function countKnownDescendants(opId, statuses) {
  if (!statuses) return 0;
  const children = new Map(); // parentId -> [childIds]
  for (const s of statuses) {
    const st = s?.reblog || s;
    if (!st?.id || !st?.in_reply_to_id) continue;
    if (!children.has(st.in_reply_to_id)) children.set(st.in_reply_to_id, []);
    children.get(st.in_reply_to_id).push(st.id);
  }

  const queue = [opId];
  const visited = new Set([opId]);
  let count = 0;
  while (queue.length) {
    const parent = queue.shift();
    for (const kid of (children.get(parent) || [])) {
      if (visited.has(kid)) continue;
      visited.add(kid);
      count++;
      queue.push(kid);
    }
  }
  return count;
}

function catalogReplyCount(s, statuses) {
  const serverTotal = Number(s?.total_replies);
  const directCount = Number(s?.replies_count);
  const knownDescendants = countKnownDescendants(s?.id, statuses);
  return Math.max(
    Number.isFinite(serverTotal) ? serverTotal : 0,
    Number.isFinite(directCount) ? directCount : 0,
    knownDescendants,
  );
}

function buildCatalogVideoThumb(media, altText = "") {
  const full = media?.url || media?.preview_url || "";
  const poster = preferredVideoPoster(media);

  if (!full) {
    const ph = document.createElement("div");
    ph.className = "thread-thumb-placeholder";
    ph.innerHTML = `<span class="label">video unavailable</span>`;
    return ph;
  }

  const video = document.createElement("video");
  video.className = "thread-thumb-video";
  video.src = full;
  video.muted = !!userSettings.videoCatalogHoverMuted;
  video.loop = !!userSettings.videoCatalogHoverLoop;
  video.playsInline = true;
  video.preload = "metadata";
  // aria-label rather than title: keep the accessible name without a native
  // tooltip popping up over the catalog card on hover.
  video.setAttribute("aria-label", altText || "video");
  if (poster) video.poster = poster;

  // Pause on mouse-out but keep the playback position, so hovering back in
  // resumes where it left off instead of restarting from the beginning.
  video.addEventListener("mouseenter", () => {
    // Read loop live: cards are reconciled in place, so one built before a
    // settings change would otherwise keep the old value. Mute is *not*
    // re-applied — the per-card mute button is a manual override that should
    // survive mousing out and back in.
    video.loop = !!userSettings.videoCatalogHoverLoop;
    playVideoWithAutoplayFallback(video);
  });
  video.addEventListener("mouseleave", () => {
    try { video.pause(); } catch {}
  });

  return video;
}

function applyCatalogWatchState(btn, watching) {
  btn.classList.toggle("is-watching", watching);
  btn.title = watching ? "Unwatch thread" : "Watch thread";
}

// Browsers block autoplay with sound until the page has been interacted with,
// so an unmuted hover preview can refuse to start. Fall back to muted playback
// rather than leaving the user staring at a frozen first frame.
function playVideoWithAutoplayFallback(video) {
  video.play().catch(() => {
    if (video.muted) return;
    video.muted = true;
    video.play().catch(() => {});
  });
}

// Mute/unmute indicator for a catalog video thumb. The icon reflects current
// state: muted shows volume-xmark, unmuted shows volume.
function applyMuteState(btn, muted) {
  btn.title = muted ? "Unmute" : "Mute";
  btn.innerHTML = muted
    ? '<i class="fa-solid fa-volume-xmark"></i>'
    : '<i class="fa-solid fa-volume"></i>';
}

// Action overlay pinned to the bottom of a catalog card's thumb. Mirrors the
// thread-view reply footer (reply / repost / like / emoji react) and adds a
// watch-thread toggle, reusing the same .post-action markup so toggleLike /
// toggleRepost operate on it unchanged. Buttons stop propagation so they don't
// trigger the card's open-thread click.
function buildCatalogThumbFooter(s, watchTitle = "") {
  const foot = document.createElement("div");
  foot.className = "thread-thumb-foot";
  foot.addEventListener("click", (e) => e.stopPropagation());

  const replyCount = Number(s.replies_count) || 0;
  const repostCount = Number(s.reblogs_count) || 0;
  const likeCount = Number(s.favourites_count) || 0;
  const isReposted = !!s.reblogged;
  const isLiked = !!s.favourited;

  const reply = document.createElement("button");
  reply.type = "button";
  reply.className = "post-action";
  reply.dataset.act = "reply";
  reply.title = "Reply";
  reply.innerHTML = `<i class="fa-solid fa-comment"></i> <span class="post-action-count">${countFmt(replyCount)}</span>`;
  reply.addEventListener("click", (e) => { e.stopPropagation(); openComposeReply(s); });
  foot.appendChild(reply);

  const repost = document.createElement("button");
  repost.type = "button";
  repost.className = "post-action";
  repost.dataset.act = "repost";
  repost.innerHTML = `<i class="fa-solid fa-repeat"></i> <span class="post-action-count">${countFmt(repostCount)}</span>`;
  applyRepostState(repost, isReposted, countFmt(repostCount), s);
  repost.addEventListener("click", (e) => { e.stopPropagation(); toggleRepost(s.id, repost); });
  foot.appendChild(repost);

  const like = document.createElement("button");
  like.type = "button";
  like.className = "post-action" + (isLiked ? " is-liked" : "");
  like.dataset.act = "like";
  like.title = isLiked ? "Unlike" : "Like";
  like.innerHTML = `<i class="${isLiked ? "fa-solid" : "fa-regular"} fa-heart"></i> <span class="post-action-count">${countFmt(likeCount)}</span>`;
  like.addEventListener("click", (e) => { e.stopPropagation(); toggleLike(s.id, like); });
  foot.appendChild(like);

  const react = document.createElement("button");
  react.type = "button";
  react.className = "post-action";
  react.dataset.act = "react";
  react.title = "Emoji react";
  react.innerHTML = `<i class="fa-solid fa-face-grin-tears"></i>`;
  react.addEventListener("click", (e) => { e.stopPropagation(); openEmojiReactPicker(s.id); });
  foot.appendChild(react);

  const watch = document.createElement("button");
  watch.type = "button";
  watch.className = "post-action";
  watch.dataset.act = "watch";
  watch.innerHTML = `<i class="fa-solid fa-eye"></i>`;
  applyCatalogWatchState(watch, isThreadWatched(s.id));
  watch.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleWatchThread(s.id, watchTitle);
    applyCatalogWatchState(watch, isThreadWatched(s.id));
  });
  foot.appendChild(watch);

  return foot;
}

function buildCatalogActivityOverlay(activities) {
  const recent = (activities || []).filter((event) => event?.at).slice(0, 3);
  if (!recent.length) return null;
  const overlay = document.createElement("div");
  overlay.className = "thread-activity";
  overlay.setAttribute("aria-label", "Latest thread activity");
  for (const event of recent) {
    const account = event.account || {};
    const handle = account.acct || account.username || "unknown";
    const line = document.createElement("div");
    line.className = "thread-activity-line";
    line.setAttribute("aria-label", `${compactAgeLabel(event.at)}: ${event.type === "repost" ? "repost by" : "reply from"} @${handle}`);
    const age = document.createElement("span");
    age.textContent = `${compactAgeLabel(event.at)}: `;
    line.appendChild(age);
    const icon = document.createElement("i");
    icon.className = `fa-solid ${event.type === "repost" ? "fa-repeat" : "fa-comment"}`;
    icon.setAttribute("aria-hidden", "true");
    line.appendChild(icon);
    line.append(" ");
    const profile = document.createElement("a");
    profile.href = profileHref(handle);
    profile.textContent = `@${handle}`;
    profile.addEventListener("click", (e) => e.stopPropagation());
    line.appendChild(profile);
    line.title = new Date(event.at).toLocaleString();
    overlay.appendChild(line);
  }
  return overlay;
}

function participantHandle(account) {
  return account?.acct || account?.username || "";
}

// The other people in a DM conversation. Only /dm/ cards carry
// `conversation_accounts` (attached by the data layer from the Conversation
// entity); everywhere else this is empty and the card renders as usual. The
// signed-in account is dropped — "who is this with" is the useful identity —
// unless it's a note-to-self, where it's the only participant there is.
function dmParticipants(status) {
  const accounts = status?.conversation_accounts;
  if (!Array.isArray(accounts) || !accounts.length) return [];
  const me = state.auth.account || {};
  const others = accounts.filter((a) => a && a.id !== me.id && participantHandle(a) !== me.acct);
  return others.length ? others : accounts.slice(0, 1);
}

// The post a card borrows its thumbnail from when the status has no media of
// its own: the first post it references that does have some.
function borrowedMediaSource(status) {
  if ((status?.media_attachments || []).length) return null;
  return quotedStatuses(status).find((q) => (q.media_attachments || []).length) || null;
}

// The attachments a catalog card presents for a status: its own, or — for a
// quote post carrying none — a quoted post's. Shared by the card renderer
// and the media-only filter so the toggle keeps every card that shows media.
function catalogCardAttachments(status) {
  const own = status?.media_attachments || [];
  if (own.length) return own;
  return borrowedMediaSource(status)?.media_attachments || [];
}

// Hover preview of a post we only know by id (the parent of a reply card).
// Served from loaded timeline data when possible, otherwise fetched once and
// held by data.js's status cache. The fetch result is dropped if the pointer
// left in the meantime, so a quick pass over the badge doesn't flash a popup.
function bindParentPreview(el, parentId) {
  let hovering = false;
  el.addEventListener("mouseenter", async () => {
    hovering = true;
    const loaded = (state.statuses || []).find((st) => st?.id === parentId);
    if (loaded) {
      previewPopup.show(loaded, el);
      return;
    }
    const fetched = await fetchStatus(parentId, state).catch(() => null);
    if (fetched && hovering) previewPopup.show(fetched, el);
  });
  el.addEventListener("mouseleave", () => {
    hovering = false;
    previewPopup.hide();
  });
}

function threadCard(item) {
  const thread = item?.op ? item : null;
  const s = thread?.op || item;
  const acc = s.account || {};
  // A quote post with no media of its own borrows the quoted post's for the
  // catalog thumbnail — otherwise the card would show an avatar banner for a
  // thread that visibly has an image in it. Own media always wins.
  const quoted = getQuotedStatus(s);
  const borrowedFrom = borrowedMediaSource(s);
  const attachments = catalogCardAttachments(s);
  const firstImage = attachments.find((a) => a.type === "image");
  const firstVideo = attachments.find((a) => a.type === "video");
  const firstDocument = attachments.find(isDocumentAttachment);
  // Locally blurred accounts use the normal sensitive-media presentation,
  // including its existing badge and reveal control. Borrowed media carries
  // the quoted post's sensitivity too — it's that post's image on the card.
  const mediaBlur = mediaBlurState(s, borrowedFrom);
  const isSensitive = mediaBlur.sensitive;
  const spoiler = (s.spoiler_text || "").trim();
  // Conservative + fast: trust server counts, but never go below replies we can
  // already see in loaded timeline data.
  const replyCount = catalogReplyCount(s, state.statuses);
  const reactCount = s.favourites_count || 0;
  const repostCount = s.reblogs_count || 0;
  const display = acc.display_name || acc.username || "anonymous";
  const displayHTML = renderTextWithCustomEmoji(display, acc.emojis || []);
  const fullHandle = acc.acct || acc.username || "";
  const extracted = extractSummary(s);
  const plain = stripHTML(extracted.strippedContent);

  const el = document.createElement("article");
  el.className = "thread-card";
  el.dataset.id = s.id;

  // ---- thumb ----
  const thumb = document.createElement("div");
  thumb.className = "thread-thumb";

  // Top-right control cluster (hover-revealed, styled like the footer overlay):
  // a hide-thread button on every card, plus the NSFW reveal toggle when sensitive.
  const cardLabel = extracted.summary || compactSnippet(plain, 60) || display;
  const thumbTop = document.createElement("div");
  thumbTop.className = "thread-thumb-top";
  thumbTop.addEventListener("click", (e) => e.stopPropagation());

  const hideBtn = document.createElement("button");
  hideBtn.type = "button";
  hideBtn.className = "post-action";
  hideBtn.title = "Hide thread";
  hideBtn.innerHTML = `<i class="fa-solid fa-circle-xmark"></i>`;
  hideBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    hideThread(s.id, cardLabel);
  });
  thumbTop.appendChild(hideBtn);

  if (firstImage) {
    const img = document.createElement("img");
    img.src = firstImage.url;
    img.alt = spoiler || display;
    img.loading = "lazy";
    thumb.appendChild(img);
  } else if (firstVideo) {
    const videoThumb = buildCatalogVideoThumb(firstVideo, spoiler || display);
    thumb.appendChild(videoThumb);
    // Mute toggle in the top cluster — the icon reflects the video's current
    // state, which starts from the configured default and can also change
    // underneath us when autoplay-with-sound is blocked, hence volumechange.
    if (videoThumb.tagName === "VIDEO") {
      const muteBtn = document.createElement("button");
      muteBtn.type = "button";
      muteBtn.className = "post-action";
      muteBtn.dataset.act = "mute";
      applyMuteState(muteBtn, videoThumb.muted);
      muteBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        videoThumb.muted = !videoThumb.muted;
      });
      videoThumb.addEventListener("volumechange", () => {
        applyMuteState(muteBtn, videoThumb.muted);
      });
      thumbTop.appendChild(muteBtn);
    }
  } else if (firstDocument) {
    const full = safeUrl(mediaFullUrl(firstDocument));
    const documentThumb = document.createElement(full ? "a" : "div");
    documentThumb.className = "thread-document-placeholder";
    if (full) {
      documentThumb.href = full;
      documentThumb.target = "_blank";
      documentThumb.rel = "noopener";
      documentThumb.addEventListener("click", (e) => e.stopPropagation());
    }
    const icon = document.createElement("i");
    icon.className = `fa-solid ${isPdfAttachment(firstDocument) ? "fa-file-pdf" : "fa-file"}`;
    documentThumb.appendChild(icon);
    const label = document.createElement("span");
    label.className = "thread-document-label";
    label.textContent = isPdfAttachment(firstDocument) ? "PDF" : "FILE";
    documentThumb.appendChild(label);
    const rawName = urlPathSansQuery(mediaFullUrl(firstDocument)).split("/").pop();
    if (rawName) {
      const name = document.createElement("span");
      name.className = "thread-document-name";
      name.textContent = truncateHash(rawName, 16);
      documentThumb.appendChild(name);
    }
    const desc = (firstDocument.description || "").trim();
    if (desc) documentThumb.title = desc;
    thumb.appendChild(documentThumb);
  } else {
    // No image → avatar+banner composite
    thumb.classList.add("is-avatar-banner");
    if (userSettings.anonymousMode) {
      thumb.classList.add("is-anonymous");
      const av = document.createElement("img");
      av.className = "thumb-avatar";
      av.src = "./images/anon_avatar.png";
      av.alt = "Anonymous";
      thumb.appendChild(av);
    } else if (acc.header) {
      const bg = document.createElement("div");
      bg.className = "banner-bg";
      bg.style.backgroundImage = `url("${acc.header}")`;
      thumb.appendChild(bg);
    } else {
      const hue = stringHue(fullHandle);
      thumb.style.background = `linear-gradient(135deg, hsl(${hue}, 35%, 22%), hsl(${(hue + 40) % 360}, 25%, 14%))`;
    }
    if (!userSettings.anonymousMode) {
      if (acc.avatar) {
        const av = document.createElement("img");
        av.className = "thumb-avatar";
        av.src = acc.avatar;
        av.alt = "";
        av.loading = "lazy";
        thumb.appendChild(av);
      } else {
        const ph = document.createElement("div");
        ph.className = "thumb-avatar thumb-avatar-fallback";
        ph.textContent = display.charAt(0).toUpperCase();
        thumb.appendChild(ph);
      }
    }
  }

  if (isSensitive) {
    if (!mediaBlur.blurred) thumb.classList.add("nsfw-revealed");
    const overlay = document.createElement("div");
    overlay.className = "nsfw-blur-overlay";
    thumb.appendChild(overlay);

    const ns = document.createElement("span");
    ns.className = "thread-badge nsfw";
    ns.textContent = "NSFW";
    thumb.appendChild(ns);

    const toggleBtn = document.createElement("button");
    toggleBtn.className = "nsfw-toggle-btn";
    toggleBtn.title = "Toggle NSFW blur";
    toggleBtn.innerHTML = '<i class="fa-solid fa-eye-low-vision"></i>';
    toggleBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const revealed = thumb.classList.toggle("nsfw-revealed");
      toggleBtn.classList.toggle("is-revealed", revealed);
      toggleBtn.innerHTML = revealed
        ? '<i class="fa-solid fa-eye"></i>'
        : '<i class="fa-solid fa-eye-low-vision"></i>';
    });
    thumbTop.appendChild(toggleBtn);
  }
  if (spoiler) {
    const sp = document.createElement("span");
    sp.className = "thread-badge spoiler";
    sp.textContent = "⚠ " + spoiler;
    thumb.appendChild(sp);
  }

  // A stickied post, on its author's profile. `pinned` is the API's word for
  // it (see data.js) and travels with the status, so this is drawn only where
  // it means something — every card on a board would otherwise claim it.
  if (s.pinned && state.view?.kind === "profile") {
    const sticky = document.createElement("span");
    sticky.className = "thread-badge sticky";
    sticky.innerHTML = '<i class="fa-solid fa-thumbtack"></i>';
    sticky.title = "Stickied to this profile";
    sticky.setAttribute("aria-label", "Stickied to this profile");
    thumb.appendChild(sticky);
  }

  // Group post (Mitra sets status.group): green BOARD badge linking to the
  // board view, so threads are attributable to their board from any catalog.
  // Redundant on the board's own view, where every card is in this group.
  const groupAcct = s.group?.acct || s.group?.username || "";
  const onOwnBoard = state.view?.kind === "board" && state.view.group === groupAcct;
  const showBoardBadge = !!groupAcct && !onOwnBoard;
  if (showBoardBadge) {
    const badge = document.createElement("a");
    // NSFW owns the top-left corner; slide the board badge below it.
    badge.className = "thread-badge board" + (isSensitive ? " below-nsfw" : "");
    badge.textContent = `/${String(groupAcct).split("@")[0].toLowerCase()}/`;
    badge.href = groupBoardHref(groupAcct);
    badge.title = boardBadgeTitle(groupAcct);
    badge.addEventListener("click", (e) => e.stopPropagation());
    thumb.appendChild(badge);
  }

  // Quote post marker. Unlike the board badge it isn't a link — hovering shows
  // the quoted post, and a click falls through to opening the thread. It sits
  // under whichever top-left badges are already there.
  let stackedBadges = (isSensitive ? 1 : 0) + (showBoardBadge ? 1 : 0);
  if (quoted) {
    const badge = document.createElement("span");
    badge.className = "thread-badge quote" + (stackedBadges ? ` below-${stackedBadges}` : "");
    badge.textContent = "QUOTE";
    // No title attribute: the native tooltip would sit on top of the preview.
    badge.addEventListener("mouseenter", () => previewPopup.show(quoted, badge));
    badge.addEventListener("mouseleave", () => previewPopup.hide());
    thumb.appendChild(badge);
    stackedBadges += 1;
  }

  // Reply marker, for the flattened boards (/local/, profiles with replies)
  // where a reply gets its own card: says the card isn't a thread OP, and
  // previews the post it answers.
  if (s.in_reply_to_id) {
    const badge = document.createElement("span");
    badge.className = "thread-badge reply" + (stackedBadges ? ` below-${stackedBadges}` : "");
    badge.textContent = "REPLY";
    bindParentPreview(badge, s.in_reply_to_id);
    thumb.appendChild(badge);
    stackedBadges += 1;
  }

  thumb.appendChild(thumbTop);
  thumb.appendChild(buildCatalogThumbFooter(s, cardLabel));

  el.appendChild(thumb);

  // ---- text info: stats line + subject + body (4chan centered) ----
  const info = document.createElement("div");
  info.className = "thread-info";

  // stats: replies / reacts / reposts
  const stats = document.createElement("div");
  stats.className = "thread-stats-text";
  stats.textContent = `${replyCount} / ${reactCount} / ${repostCount}`;
  stats.title = `${replyCount} replies, ${reactCount} likes, ${repostCount} reposts`;
  info.appendChild(stats);

  const dmAccounts = dmParticipants(s);
  if (dmAccounts.length || extracted.summary) {
    // Subject line (dark blue, bold): the extracted summary, or — on /dm/,
    // where conversations rarely carry one — who the conversation is with,
    // each handle linking to its profile.
    const subject = document.createElement("div");
    subject.className = "thread-subject";
    if (dmAccounts.length) {
      subject.classList.add("is-dm");
      subject.title = dmAccounts.map(participantHandle).map((h) => `@${h}`).join(", ")
        + (extracted.summary ? ` — ${extracted.summary}` : "");
      dmAccounts.forEach((account, i) => {
        if (i > 0) subject.appendChild(document.createTextNode(", "));
        const handle = participantHandle(account);
        const link = document.createElement("a");
        link.href = profileHref(handle);
        link.title = `@${handle}`;
        link.innerHTML = renderTextWithCustomEmoji(
          account.display_name || account.username || handle,
          account.emojis || [],
        );
        link.addEventListener("click", (e) => e.stopPropagation());
        subject.appendChild(link);
      });
      if (extracted.summary) {
        subject.appendChild(document.createTextNode(` — ${extracted.summary}`));
      }
    } else {
      subject.textContent = extracted.summary;
      subject.title = extracted.summary;
    }
    info.appendChild(subject);

    // Poster name in green below the subject.
    const name = document.createElement("div");
    name.className = "thread-poster-name";
    const a = document.createElement("a");
    a.href = profileHref(fullHandle);
    a.title = `@${fullHandle}`;
    a.innerHTML = displayHTML;
    a.addEventListener("click", (e) => {
      e.stopPropagation();
    });
    name.appendChild(a);
    info.appendChild(name);
  } else {
    // No summary: show the poster name as the subject line (green, 4chan-style).
    const subject = document.createElement("div");
    subject.className = "thread-subject is-poster";
    const a = document.createElement("a");
    a.href = profileHref(fullHandle);
    a.title = `@${fullHandle}`;
    a.innerHTML = displayHTML;
    a.addEventListener("click", (e) => {
      e.stopPropagation();        // don't open the thread
    });
    subject.appendChild(a);
    info.appendChild(subject);
  }

  // body — skip entirely if there's no text content. Formatted the same way as
  // a real post body (greentext + emoji), not stripped to raw text. Links are
  // left inert via CSS so the whole card stays a single click target.
  if (plain) {
    const body = document.createElement("div");
    body.className = "thread-body";
    renderPostContentInto(body, s);
    info.appendChild(body);
  }

  el.appendChild(info);

  const activity = buildCatalogActivityOverlay(thread?.activities || []);
  if (activity) info.appendChild(activity);

  // card click → open thread (middle/ctrl-click → new tab)
  wireCardOpen(el, threadHref(s.id));

  return el;
}

// A card is a whole-card click target, not an <a> — it contains its own links
// and buttons, which can't legally nest inside one. Emulate the parts of link
// behavior that matter: plain click navigates in place, middle-click and
// ctrl/cmd-click open the target in a new tab. Clicks originating on an inner
// control are left to that control.
function wireCardOpen(el, href, { canOpen = null } = {}) {
  // Links and buttons only. A catalog thumb <video> has no native controls —
  // it hover-plays and its mute toggle is a real button — so a click on it is
  // a click on the card, not on a control.
  const fromInnerControl = (e) => !!e.target?.closest?.("a, button, input, video[controls]");
  el.addEventListener("click", (e) => {
    if (fromInnerControl(e) || (canOpen && !canOpen())) return;
    if (e.ctrlKey || e.metaKey) {
      window.open(href, "_blank", "noopener");
      return;
    }
    location.hash = href;
  });
  // Middle-click: the mousedown default is the autoscroll cursor, so suppress
  // it here and act on auxclick (which fires only if the button is released
  // over the same element, like a real click).
  el.addEventListener("mousedown", (e) => {
    if (e.button === 1 && !fromInnerControl(e)) e.preventDefault();
  });
  el.addEventListener("auxclick", (e) => {
    if (e.button !== 1 || fromInnerControl(e) || (canOpen && !canOpen())) return;
    e.preventDefault();
    window.open(href, "_blank", "noopener");
  });
}

// A search-result account card. Mirrors a no-image thread card so it slots
// into the catalog grid beside post results: avatar+banner thumb, a followers /
// following / posts stats line, the display name as the (green) subject linking
// to the profile, and the bio as the body. Clicking the card opens the profile.
function accountCard(account) {
  const acc = account || {};
  const display = acc.display_name || acc.username || "anonymous";
  const displayHTML = renderTextWithCustomEmoji(display, acc.emojis || []);
  const fullHandle = acc.acct || acc.username || "";
  const followers = Number(acc.followers_count) || 0;
  const following = Number(acc.following_count) || 0;
  const posts = Number(acc.statuses_count) || 0;

  const el = document.createElement("article");
  el.className = "thread-card is-account";
  el.dataset.id = "acct:" + (acc.id || fullHandle);

  // ---- thumb: avatar + banner composite (same as a text-only OP) ----
  const thumb = document.createElement("div");
  thumb.className = "thread-thumb is-avatar-banner";
  if (userSettings.anonymousMode && !acc.is_group) {
    thumb.classList.add("is-anonymous");
    const av = document.createElement("img");
    av.className = "thumb-avatar";
    av.src = "./images/anon_avatar.png";
    av.alt = "Anonymous";
    thumb.appendChild(av);
  } else if (acc.header) {
    const bg = document.createElement("div");
    bg.className = "banner-bg";
    bg.style.backgroundImage = `url("${acc.header}")`;
    thumb.appendChild(bg);
  } else {
    const hue = stringHue(fullHandle);
    thumb.style.background = `linear-gradient(135deg, hsl(${hue}, 35%, 22%), hsl(${(hue + 40) % 360}, 25%, 14%))`;
  }
  if (!userSettings.anonymousMode || acc.is_group) {
    if (acc.avatar) {
      const av = document.createElement("img");
      av.className = "thumb-avatar";
      av.src = acc.avatar;
      av.alt = "";
      av.loading = "lazy";
      thumb.appendChild(av);
    } else {
      const ph = document.createElement("div");
      ph.className = "thumb-avatar thumb-avatar-fallback";
      ph.textContent = display.charAt(0).toUpperCase();
      thumb.appendChild(ph);
    }
  }
  el.appendChild(thumb);

  // ---- info: stats / subject / body ----
  const info = document.createElement("div");
  info.className = "thread-info";

  const stats = document.createElement("div");
  stats.className = "thread-stats-text";
  stats.textContent = `${followers} / ${following} / ${posts}`;
  stats.title = `${followers} followers, ${following} following, ${posts} posts`;
  info.appendChild(stats);

  // Group actors are boards — route them to the board view and badge the
  // card so remote groups found via search read as followable boards.
  const isGroup = !!acc.is_group;
  const cardHref = isGroup ? groupBoardHref(fullHandle) : profileHref(fullHandle);
  if (isGroup) {
    const badge = document.createElement("span");
    badge.className = "thread-badge board";
    badge.textContent = "BOARD";
    badge.title = boardBadgeTitle(fullHandle);
    thumb.appendChild(badge);
  }

  const subject = document.createElement("div");
  subject.className = "thread-subject is-poster";
  const a = document.createElement("a");
  a.href = cardHref;
  a.title = `@${fullHandle}`;
  a.innerHTML = displayHTML;
  a.addEventListener("click", (e) => { e.stopPropagation(); });
  subject.appendChild(a);
  info.appendChild(subject);

  const noteHtml = acc.note ? String(acc.note).trim() : "";
  if (noteHtml) {
    const body = document.createElement("div");
    body.className = "thread-body";
    body.innerHTML = renderSafeHTML(noteHtml, acc.emojis || []);
    info.appendChild(body);
  }
  el.appendChild(info);

  // card click → open the profile (or the board view for a group). Anonymous
  // mode hides identities, so the card doesn't open at all there.
  wireCardOpen(el, cardHref, { canOpen: () => isGroup || !userSettings.anonymousMode });

  return el;
}

// ---- rendering: catalog -----------------------------------------------------

// /likes/, /bkmks/ and /dm/ are personal collections: show exactly what the
// endpoint returned (including replies) in server order, rather than collapsing
// activity to OPs and re-sorting like the board catalogs do. For /dm/ that
// order is one card per conversation, most recently active first.
function isCollectionBoardView() {
  return state.view?.kind === "board" &&
    ["likes", "bkmks", "dm"].includes(state.view.name);
}

function isDmBoardView() {
  return state.view?.kind === "board" && state.view.name === "dm";
}

// /dm/'s "all messages" scope: the direct timeline rather than conversation
// previews, consolidated client-side into one card per exchange.
function isDmAllMessagesView() {
  return isDmBoardView() && !!state.dmAllMessages;
}

// ---- index view (thread rows) ---------------------------------------------
//
// The index is a second way of drawing the same set of threads the catalog
// draws: instead of a thumbnail grid it stacks each thread's OP with the tail
// of its replies, the way a board index reads. Which one is on screen is a
// standing reader preference (userSettings.viewMode), not a per-view state.

// Views the index can draw. Collection boards (/likes/, /bkmks/, /dm/) list
// individual posts rather than threads, so there's nothing to expand.
function indexViewSupported(view = state.view) {
  if (!["board", "profile", "tag"].includes(view?.kind)) return false;
  if (view.kind === "board" && !view.group && ["likes", "bkmks", "dm"].includes(view.name)) {
    return false;
  }
  return true;
}

function indexViewActive(view = state.view) {
  return userSettings.viewMode === "index" && indexViewSupported(view);
}

const REPLIES_FILTER_BOARDS = new Set(["home", "twkn", "local"]);

function hasRepliesFilter(view = state.view) {
  if (view?.kind === "profile") return true;
  return view?.kind === "board" && !view.group && REPLIES_FILTER_BOARDS.has(view.name);
}

function includesReplies(view = state.view) {
  // The index shows threads with reply tails, never individual replies as
  // OPs. Keep the flat replies/reposts preference for catalog view only.
  if (indexViewActive(view)) return false;
  if (view?.kind === "profile") return !!state.includeReplies;
  if (view?.kind === "board" && !view.group && REPLIES_FILTER_BOARDS.has(view.name)) {
    return !!state.boardIncludeReplies[view.name];
  }
  return false;
}

// Re-read the stickied collection for the profile on screen, after stickying
// or unstickying has changed it. A no-op anywhere else — doing it from a
// thread view doesn't need the list until the profile is opened, which fetches
// it anyway.
async function refreshStickiedForView() {
  const account = state.view?.kind === "profile" ? state.view.account : null;
  if (!account?.id) return;
  const stickied = await loadPinnedStatuses(account.id, state);
  if (state.view?.kind === "profile" && state.view.account?.id === account.id) {
    state.stickiedStatuses = stickied;
    render();
  }
}

// Stickied posts go to the top of their author's profile, the way a stickied
// thread sits at the top of a board. They come from their own collection, so
// one may not be in the loaded page at all (a sticky is usually old); those are
// built into cards here. Any that *is* in the page is dropped from its place
// further down rather than shown twice.
//
// The subject filter and the media-only toggle have already run, so a stickied
// post that a filter excluded stays excluded — a filter the reader applied
// outranks a sticky the author applied.
function hoistStickiedThreads(threads) {
  const stickied = state.stickiedStatuses || [];
  if (!stickied.length) return threads;

  let stickyThreads = buildCatalogThreads(stickied)
    .filter((t) => !isThreadHidden(t.op?.id));
  stickyThreads = filterCatalogThreads(stickyThreads, state.view, state.filter, stripHTML, userSettings.mutedWords);
  if (state.mediaOnly) {
    stickyThreads = stickyThreads.filter((t) => catalogCardAttachments(t.op).length > 0);
  }
  if (!stickyThreads.length) return threads;

  const hoisted = new Set(stickyThreads.map((t) => t.op?.id).filter(Boolean));
  return [...stickyThreads, ...threads.filter((t) => !hoisted.has(t.op?.id))];
}

function currentCatalogThreads() {
  // Collection boards show exactly what the endpoint returned, including
  // replies. The generic catalog thread builder intentionally collapses
  // activity to OPs, which would drop a liked/bookmarked reply — or a
  // conversation's latest DM — whose OP isn't present in the feed.
  // /dm/'s wide scope is a flat timeline of individual messages; collapse a
  // back-and-forth into the one card that starts it.
  if (isDmAllMessagesView()) return dmCatalogThreads(state.statuses);
  if (isCollectionBoardView()) return flatCatalogThreads(state.statuses);
  // Expanded replies mode renders the raw timeline one row per card. The data
  // loader skips ancestor hydration in this mode, so fetched OPs cannot leak
  // into the flat list as extra cards.
  if (hasRepliesFilter() && includesReplies()) {
    return flatCatalogThreads(state.statuses);
  }
  return buildCatalogThreads(state.statuses);
}

// ---- render reconciliation helpers -----------------------------------------
//
// render() used to nuke the catalog and rebuild from scratch on every state
// change, which blew away scroll position, expanded videos, focused reply
// composers, and the like — and caused a visible flash even on a no-op
// refresh. The helpers below let renderCatalog / renderPost instead reuse
// the existing DOM nodes for posts that haven't changed and only update
// the fields that actually can change (counts, like/repost state, reactions).

function makeEmptyEl(msg) {
  const el = document.createElement("div");
  el.className = "empty";
  el.textContent = msg;
  return el;
}

// ---- catalog (thread cards) ------------------------------------------------

// Update only the parts of a thread card that can change between refreshes.
function updateThreadCard(el, item) {
  const thread = item?.op ? item : null;
  const op = thread?.op || item;
  el._statusData = op;
  const statsEl = el.querySelector(".thread-stats-text");
  const replyCount = catalogReplyCount(op, state.statuses);
  const reactCount = op.favourites_count || 0;
  const repostCount = op.reblogs_count || 0;
  if (statsEl) {
    statsEl.textContent = `${replyCount} / ${reactCount} / ${repostCount}`;
    statsEl.title = `${replyCount} replies, ${reactCount} likes, ${repostCount} reposts`;
  }

  // Keep the thumb footer in sync with refreshed server data / watch state.
  const foot = el.querySelector(".thread-thumb-foot");
  if (foot) {
    const likeBtn = foot.querySelector('[data-act="like"]');
    if (likeBtn) applyLikeState(likeBtn, !!op.favourited, countFmt(op.favourites_count || 0));
    const repostBtn = foot.querySelector('[data-act="repost"]');
    if (repostBtn) applyRepostState(repostBtn, !!op.reblogged, countFmt(op.reblogs_count || 0), op);
    const replyCountEl = foot.querySelector('[data-act="reply"] .post-action-count');
    if (replyCountEl) replyCountEl.textContent = countFmt(replyCount);
    const watchBtn = foot.querySelector('[data-act="watch"]');
    if (watchBtn) applyCatalogWatchState(watchBtn, isThreadWatched(op.id));
  }
  el.querySelector(":scope > .thread-info > .thread-activity")?.remove();
  const activity = buildCatalogActivityOverlay(thread?.activities || []);
  if (activity) el.querySelector(":scope > .thread-info")?.appendChild(activity);

  if (!userSettings.anonymousMode) {
    syncAvatarBannerComposite(el.querySelector(".thread-thumb.is-avatar-banner"), op.account);
  }
}

function reconcileCatalog(cat, threads) {
  const existing = new Map();
  for (const child of cat.children) {
    if (child.classList?.contains("thread-card") && child.dataset.id) {
      existing.set(child.dataset.id, child);
    }
  }
  const desired = threads.map((t) => ({ key: t.op.id, item: t }));
  reconcileKeyedChildren(cat, existing, desired, threadCard, updateThreadCard);
}

// True if `acc` is the signed-in user's account. Falls back to comparing
// acct/username when the numeric id isn't present (e.g. older data).
function isOwnAccount(acc) {
  if (!acc) return false;
  const me = state.auth?.account;
  if (!me) return false;
  if (acc.id != null && me.id != null) return acc.id === me.id;
  const a = acc.acct || acc.username || "";
  const b = me.acct || me.username || "";
  return a !== "" && a === b;
}

// True if `status` is a reply to one of the signed-in user's posts in the
// current thread (i.e. the parent in the graph is authored by the user).
function isReplyingToYou(status) {
  if (!status) return false;
  if (isOwnAccount(status.account)) return false; // own posts win
  const graph = currentThreadGraph();
  if (!graph) return false;
  const parentId = status.in_reply_to_id;
  if (!parentId) return false;
  const parent = graph.statusById.get(parentId);
  return isOwnAccount(parent?.account);
}

// ---- thread view (OP + replies) -------------------------------------------

// Update only the parts of a post block that can change between refreshes:
//   * _statusData ref (so click handlers see fresh fields)
//   * reply / repost / like counts and active states
//   * the emoji reactions row (reuses existing buttons, adds/removes diff)
// Rebuild a post block's children in place when its visible content changed
// (e.g. after an edit). On an ordinary refresh the signature is unchanged, so
// this no-ops and the existing DOM survives (expanded media, etc.); only an
// actual content change triggers a rebuild. We swap the children rather than
// the node itself so the reconcile caller still re-appends the same element,
// and runtime classes on the element (is-op, highlight marks) are preserved.
function updatePostContent(el, status, ctx) {
  const sig = postContentSignature(status);
  if (el._contentSig && el._contentSig === sig) return;
  el._contentSig = sig;
  const c = ctx || { isOp: el.classList.contains("is-op"), depth: Number(el.dataset.depth) || 0 };
  const fresh = createPostBlock(status, c);
  el.replaceChildren(...fresh.childNodes);
  // The rebuild discards every inline post expanded inside this one, including
  // the quotes shown by default — so forget that they were shown, and let
  // expandQuotedPosts put them back on the edited content.
  el._autoExpandedQuotes = null;
}

function updatePostBlock(el, status, ctx) {
  el._statusData = status;
  if (ctx) el.dataset.depth = String(Math.min(ctx.depth, 5));
  // Rebuild body/subject/media first when content changed; the syncs below
  // then refresh counts/reactions/poll on the freshly rendered children.
  updatePostContent(el, status, ctx);
  // Keep the "you" indicators in sync with the current auth state — the
  // user might have signed in/out since the post was first rendered.
  // OPs are never marked (is-op wins over both).
  const isOp = el.classList.contains("is-op");
  el.classList.toggle("is-own", !isOp && isOwnAccount(status.account));
  el.classList.toggle("is-replying-to-you", !isOp && isReplyingToYou(status));
  syncThreadGraphLinks(el, status);
  syncReplyToYouIndicator(el, status);
  syncPostStickyIcon(el, status);
  syncPostOwnerActions(el, status);
  syncPostAccountMedia(el, status.account);
  updatePostActions(el, status);
  updatePostReactions(el, status);
  updatePostPoll(el, status);
}

// Add the `>>parent` line and the head's "replied by" links to a post that
// was built before its thread was known.
//
// Both are read off the thread graph, so on the index a reply drawn from the
// loaded timeline page — before its thread has been fetched — has neither.
// Neither is derived from the post's own fields, so its content signature
// doesn't change when the thread arrives and updatePostContent won't rebuild
// it. Rather than widen the signature (a rebuild would discard any inline
// posts the reader had expanded inside this one), the two blocks are added
// here once the graph can supply them.
function syncThreadGraphLinks(postEl, status) {
  const graph = currentThreadGraph();
  if (!graph || !status?.id) return;

  const head = postEl.querySelector(":scope > .post-head");
  const existingFrom = head?.querySelector(":scope > .post-head-reply-from");
  const childIds = (graph.backlinksByTarget.get(status.id) || []).join(",");
  if (head && (existingFrom?.dataset.ids || "") !== childIds) {
    // Rebuilt rather than patched, which also keeps the links current in the
    // thread view when a poll brings in a new reply to this post.
    existingFrom?.remove();
    const links = buildReplyFromLinksInline(status.id, postEl);
    if (links) head.appendChild(links);
  }

  if (postEl.querySelector(":scope > .post-reply-to")) return;
  const body = postEl.querySelector(":scope > .post-body");
  const parentId = graph.parentById.get(status.id) || null;
  // The author may have written the reference themselves, in which case
  // synthesizing one would name the same post twice.
  if (bodyReferencesPost(body, parentId)) return;
  const replyTo = buildReplyToBlock(status.id, postEl);
  if (!replyTo) return;
  // The leading-mentions toggle sits on the reply-to line when there is one,
  // and falls back into the body when there isn't. There is one now.
  const toggle = body?.querySelector(":scope > .post-mentions-toggle");
  if (toggle) replyTo.appendChild(toggle);
  if (body) postEl.insertBefore(replyTo, body);
  else postEl.appendChild(replyTo);
}

// The thumbtack that marks a stickied thread, inline after the post number —
// where a board puts it. Rendered into the head at build time and kept in
// sync here, since stickying a post doesn't change its content signature and
// so wouldn't rebuild the head on its own.
function stickyIconHTML(isSticky) {
  return isSticky
    ? ` <i class="fa-solid fa-thumbtack post-sticky-icon" title="Stickied" aria-label="Stickied"></i>`
    : "";
}

function syncPostStickyIcon(postEl, status) {
  const head = postEl.querySelector(":scope > .post-head");
  if (!head) return;
  const isSticky = postEl.classList.contains("is-op") && isStickiedOp(status?.id);
  const existing = head.querySelector(":scope > .post-sticky-icon");
  if (isSticky === !!existing) return;
  if (!isSticky) {
    existing.remove();
    return;
  }
  head.querySelector(":scope > .post-id")?.insertAdjacentHTML("afterend", stickyIconHTML(true));
}

// Rewrite the >>hash link's text to include or strip the " (You)" suffix
// based on the current auth state. The reply-to block itself is created
// once in buildReplyToBlock; this just keeps its link text accurate on
// reconciliation (e.g. if the user signed in/out since first render).
function syncReplyToYouIndicator(postEl, status) {
  const a = postEl.querySelector(":scope > .post-reply-to .reply-to-link");
  if (!a) return;
  const graph = currentThreadGraph();
  const parentId = status.in_reply_to_id || graph?.parentById.get(status.id);
  if (!parentId) return;
  const parent = graph?.statusById.get(parentId);
  const baseText = postRefLabel(parent || parentId, "Reply");
  a.textContent = isOwnAccount(parent?.account) ? `${baseText} (You)` : baseText;
}

function updatePostActions(el, status) {
  const repostBtn = el.querySelector(':scope > .post-foot [data-act="repost"]');
  if (repostBtn) applyRepostState(repostBtn, !!status.reblogged, countFmt(status.reblogs_count || 0), status);
  const likeBtn = el.querySelector(':scope > .post-foot [data-act="like"]');
  if (likeBtn) {
    const isLiked = !!status.favourited;
    const count = Number(status.favourites_count) || 0;
    const countEl = likeBtn.querySelector(".post-action-count");
    if (countEl) countEl.textContent = countFmt(count);
    likeBtn.classList.toggle("is-liked", isLiked);
    likeBtn.title = isLiked ? "Unlike" : "Like";
    const icon = likeBtn.querySelector("i");
    if (icon) icon.className = isLiked ? "fa-solid fa-heart" : "fa-regular fa-heart";
  }
  const bookmarkBtn = el.querySelector(':scope > .post-foot [data-act="bookmark"]');
  if (bookmarkBtn) applyBookmarkState(bookmarkBtn, !!status.bookmarked);
  const watchBtn = el.querySelector(':scope > .post-foot [data-act="watch"]');
  if (watchBtn) applyCatalogWatchState(watchBtn, isThreadWatched(status.id));
}

function updatePostReactions(el, status) {
  const reactions = status?.pleroma?.emoji_reactions || [];
  const existingWrap = el.querySelector(".post-reactions");

  if (reactions.length === 0) {
    if (existingWrap) existingWrap.remove();
    return;
  }

  if (!existingWrap) {
    // First time we see reactions on this post — build the wrap and insert
    // it just above the action row so the layout matches a fresh render.
    const wrap = buildEmojiReactionsBlock(status);
    if (!wrap) return;
    const foot = el.querySelector(".post-foot");
    if (foot) el.insertBefore(wrap, foot);
    else el.appendChild(wrap);
    return;
  }

  // Reconcile individual reaction buttons by name.
  const existing = new Map();
  for (const btn of existingWrap.children) {
    const name = btn.dataset.name;
    if (name) existing.set(name, btn);
  }

  const desired = [];
  for (const r of reactions) {
    if (r?.name) desired.push({ key: r.name, item: r });
  }

  reconcileKeyedChildren(
    existingWrap,
    existing,
    desired,
    (r) => buildReactionButton(r, status.id, existingWrap),
    (btn, r) => {
      const countEl = btn.querySelector(".post-reaction-count");
      if (countEl) countEl.textContent = String(r.count || 0);
      btn.classList.toggle("is-mine", !!r.me);
      btn.dataset.tooltip = `:${r.name}: · ${r.count || 0}`;
      applyReactionTooltip(btn);
    }
  );

  // If the last reaction was removed, drop the now-empty wrap.
  if (!existingWrap.children.length) existingWrap.remove();
}

// Signature of the fields that affect a post block's *rendered* body/subject/
// media — everything updatePostBlock doesn't already sync in place. When this
// changes between reconciles (e.g. the post was just edited), we rebuild the
// block's children so the new text shows up without a full page reload.
function postContentSignature(status) {
  return JSON.stringify({
    content: status?.content || "",
    title: status?.title || "",
    spoiler: status?.spoiler_text || "",
    summary: status?.summary || "",
    quote: quotedStatuses(status).map((q) => q.id).join(","),
    sensitive: !!status?.sensitive,
    blur: mediaBlurState(status),
    media: (status?.media_attachments || []).map((a) => a.id).join(","),
  });
}

function createPostBlock(status, ctx) {
  const el = threadPostBlock(status, !!ctx?.isOp, ctx?.depth || 0, ctx?.index ? { index: true } : {});
  el._contentSig = postContentSignature(status);
  return el;
}

function reconcileOp(wrapper, op) {
  // The OP is a single element that lives as a sibling of .replies-wrap
  // inside the thread wrapper. We can't use the generic
  // reconcileKeyedChildren here because it drops any child outside its keyed
  // set, which would wipe out the replies-wrap too. Instead, update
  // the existing OP in place, or swap it for a new one without touching
  // anything else inside the wrapper.
  const existing = wrapper.querySelector(":scope > .post-block.is-op");

  if (existing && existing.dataset.id === op.id) {
    updatePostBlock(existing, op, { isOp: true, depth: 0 });
    expandQuotedPosts(existing, op);
    return;
  }

  const newOp = createPostBlock(op, { isOp: true, depth: 0 });
  if (existing) {
    wrapper.replaceChild(newOp, existing);
  } else {
    // Insert before the replies-wrap if it exists, otherwise at the top.
    const repliesWrap = wrapper.querySelector(":scope > .replies-wrap");
    if (repliesWrap) wrapper.insertBefore(newOp, repliesWrap);
    else wrapper.insertBefore(newOp, wrapper.firstChild);
  }
  // After insertion: the expanded copy is wired like any other post block, and
  // wiring reads the document (linked-post marks, anonymous mode).
  expandQuotedPosts(newOp, op);
}

function showOpNotFound(wrapper, postId) {
  const existingOp = wrapper.querySelector(":scope > .post-block.is-op");
  if (existingOp) existingOp.remove();

  let note = wrapper.querySelector(":scope > .empty");
  if (!note) {
    note = document.createElement("div");
    note.className = "empty";
    // Insert before the replies-wrap (or at the end if it doesn't exist yet)
    const repliesWrap = wrapper.querySelector(":scope > .replies-wrap");
    if (repliesWrap) wrapper.insertBefore(note, repliesWrap);
    else wrapper.appendChild(note);
  }
  note.textContent = `post ${postId} not found`;
}

function reconcileReplies(repliesWrap, tree) {
  const existing = new Map();
  for (const child of repliesWrap.children) {
    if (child.classList?.contains("post-block") && child.dataset.id) {
      existing.set(child.dataset.id, child);
    }
  }

  // Flatten the tree into one chronological list so the thread reads like a
  // conversation. The >>parent link rendered inside each post block
  // (buildReplyToBlock) still surfaces who-replied-to-whom, and the CSS
  // doesn't use data-depth for indentation, so this is purely a reordering —
  // no visual info is lost by flattening.
  const all = flattenThreadChronological(tree);

  const desired = all.map((status) => ({
    key: status.id,
    item: status,
    ctx: { isOp: false, depth: 0 },
  }));

  reconcileKeyedChildren(repliesWrap, existing, desired, createPostBlock, updatePostBlock);
}

// ---- end reconciliation helpers -------------------------------------------

// The threads a catalog-style view shows, in the order it shows them: hidden
// ones dropped, the reader's filters applied, sorted, stickies hoisted. The
// catalog grid and the index differ in how a thread is drawn, not in which
// threads are drawn, so both start here.
function orderedCatalogThreads() {
  // Hidden-OP accounts are dropped from boards but not from a profile view —
  // navigating to someone's profile is a deliberate request to see their
  // posts, and it's where the toggle itself lives.
  const onProfile = state.view.kind === "profile";
  const allThreads = currentCatalogThreads()
    .filter((t) => !isThreadHidden(t.op?.id))
    .filter((t) => onProfile || !isThreadOpAccountHidden(t));
  let visibleThreads = filterCatalogThreads(allThreads, state.view, state.filter, stripHTML, userSettings.mutedWords);
  if (state.mediaOnly) {
    visibleThreads = visibleThreads.filter((thread) => catalogCardAttachments(thread.op).length > 0);
  }
  // Collection boards should preserve the server/API order (matches
  // mitra-web). The endpoints are already ordered by latest activity;
  // re-sorting by post created_at makes the list differ from those views.
  // The exception is /dm/'s all-messages scope, where cards are grouped
  // exchanges rather than rows from the response, so their order has to be
  // recomputed from each exchange's newest message.
  let orderedThreads;
  if (isDmAllMessagesView()) orderedThreads = sortCatalogThreads(visibleThreads, "bump");
  else if (isCollectionBoardView()) orderedThreads = visibleThreads;
  else orderedThreads = sortCatalogThreads(visibleThreads, state.sort);

  // After the sort, not before: a sticky is the author's ordering laid on top
  // of whichever one the reader chose, so sorting by date would otherwise drop
  // a stickied post straight back among its contemporaries.
  if (onProfile) orderedThreads = hoistStickiedThreads(orderedThreads);

  return orderedThreads;
}

// Why a catalog-style view has nothing to show — the loaded page was empty, or
// the reader's own filters emptied it.
function emptyCatalogMessage() {
  if (state.filter) return `no posts match "${escapeHTML(state.filter)}".`;
  if (state.mediaOnly) return "no media posts in the loaded catalog.";
  if (state.view.kind === "profile") return `no posts by @${escapeHTML(state.view.acct || "")}`;
  if (state.view.kind === "tag") return `no posts tagged #${escapeHTML(state.view.tag || "")}`;
  return "no top-level posts in this board.";
}

function renderCatalog(cat) {
  const asIndex = indexViewActive();
  // Only show the "loading…" placeholder on the very first render of a
  // view. On a refresh the existing cards stay on screen (and the toolbar
  // refresh icon is the loading indicator) so we can reconcile in place
  // when the new data arrives.
  const hasCards = cat.querySelector(".thread-card, .index-thread") != null;
  if (state.loading && !hasCards) {
    cat.replaceChildren(makeEmptyEl("loading…"));
    return;
  }
  if (state.error) {
    cat.replaceChildren(makeEmptyEl("error: " + state.error));
    return;
  }

  const orderedThreads = orderedCatalogThreads();
  if (!orderedThreads.length) {
    cat.replaceChildren(makeEmptyEl(emptyCatalogMessage()));
    // Keep paging when media/word filters hide the entire loaded page, so
    // older matching posts can still be reached.
    if ((state.mediaOnly || userSettings.mutedWords?.length) && !state.filter) renderCatalogSentinel(cat);
    return;
  }

  // Reconcile every loaded thread — existing cards/rows are reused (so
  // scroll/focus state survives a refresh), new ones are created. Older
  // posts are appended as the user scrolls (see renderCatalogSentinel).
  if (asIndex) reconcileIndex(cat, orderedThreads);
  else reconcileCatalog(cat, orderedThreads);

  // When the filter is active, show all matches (ignore pagination).
  if (state.filter) return;
  renderCatalogSentinel(cat);
}

// Search-results catalog. A flat list of account cards (built from the search
// API's Account objects) and post cards (reusing threadCard on the returned
// statuses), grouped in tabs that span the grid. Each result
// type paginates independently, preserving existing cards when more arrive.
function filterSearchResults(results, filterText) {
  const allAccounts = Array.isArray(results?.accounts) ? results.accounts : [];
  const allStatuses = filterCatalogThreads(
    (Array.isArray(results?.statuses) ? results.statuses : []).map((row) => ({ op: effectiveStatus(row), row })),
    { kind: "search" }, "", stripHTML, userSettings.mutedWords,
  ).map((item) => item.row);
  const allHashtags = Array.isArray(results?.hashtags) ? results.hashtags : [];
  const q = String(filterText || "").trim().toLowerCase();
  if (!q) return { accounts: allAccounts, statuses: allStatuses, hashtags: allHashtags };

  const accounts = allAccounts.filter((a) => {
    const hay = [
      a?.display_name || "",
      a?.acct || "",
      a?.username || "",
      stripHTML(a?.note || ""),
    ].join("\n").toLowerCase();
    return hay.includes(q);
  });

  const statuses = allStatuses.filter((row) => {
    const s = effectiveStatus(row) || row || {};
    const acc = s.account || {};
    const { summary } = extractSummary(s);
    const hay = [
      stripHTML(s.content || ""),
      summary || "",
      acc.display_name || "",
      acc.acct || "",
      acc.username || "",
    ].join("\n").toLowerCase();
    return hay.includes(q);
  });

  const hashtags = allHashtags.filter((tag) => {
    const hay = [tag?.name || "", tag?.url || ""].join("\n").toLowerCase();
    return hay.includes(q);
  });

  return { accounts, statuses, hashtags };
}

function renderSearch(cat) {
  const q = state.view?.q || "";
  if (state.loading && !state.search) {
    cat.replaceChildren(makeEmptyEl(`searching “${escapeHTML(q)}”…`));
    return;
  }
  if (state.error) {
    cat.replaceChildren(makeEmptyEl("error: " + state.error));
    return;
  }

  const filtered = filterSearchResults(state.search, state.filter);
  const accounts = filtered.accounts;
  const statuses = filtered.statuses;

  // Feed the compose @mention typeahead with every account visible here.
  rememberAccountsFromStatuses(statuses);
  if (accounts.length) rememberAccountsFromStatuses(accounts.map((a) => ({ account: a })));

  const labels = { accounts: "Accounts", statuses: "Posts", hashtags: "Hashtags" };
  const activeType = state.search?.activeType || "accounts";
  const desired = [];
  const updateTabs = el => {
    for (const btn of el.children) {
      const type = btn.dataset.type;
      btn.textContent = `${labels[type]} (${filtered[type].length})`;
      btn.setAttribute("aria-selected", String(type === activeType));
      btn.tabIndex = type === activeType ? 0 : -1;
    }
  };
  desired.push({ key: "tabs", item: {
    create: () => {
      const el = document.createElement("div");
      el.className = "search-tabs";
      el.setAttribute("role", "tablist");
      el.setAttribute("aria-label", "Search results");
      for (const type of SEARCH_TYPES) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.dataset.type = type;
        btn.id = `search-tab-${type}`;
        btn.setAttribute("role", "tab");
        btn.setAttribute("aria-controls", `search-panel-${type}`);
        const select = () => {
          if (state.view?.kind !== "search" || !state.search) return;
          state.search.activeType = type;
          render();
        };
        btn.addEventListener("click", select);
        btn.addEventListener("keydown", e => {
          const index = SEARCH_TYPES.indexOf(type);
          const next = e.key === "ArrowRight" ? (index + 1) % SEARCH_TYPES.length
            : e.key === "ArrowLeft" ? (index + SEARCH_TYPES.length - 1) % SEARCH_TYPES.length
            : e.key === "Home" ? 0 : e.key === "End" ? SEARCH_TYPES.length - 1 : null;
          if (next === null) return;
          e.preventDefault();
          el.children[next].click();
          el.children[next].focus();
        });
        el.appendChild(btn);
      }
      updateTabs(el);
      return el;
    }, update: updateTabs,
  } });
  for (const type of SEARCH_TYPES) {
    const updatePanel = panel => {
      panel.hidden = type !== activeType;
      renderSearchPanel(panel, type, filtered[type], labels[type]);
    };
    desired.push({ key: type, item: {
      create: () => {
        const panel = document.createElement("div");
        panel.className = "search-results-panel";
        panel.id = `search-panel-${type}`;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", `search-tab-${type}`);
        panel.tabIndex = 0;
        updatePanel(panel);
        return panel;
      }, update: updatePanel,
    } });
  }
  reconcileSearchNodes(cat, desired);
}

function reconcileSearchNodes(parent, desired) {
  const existing = new Map(Array.from(parent.children)
    .filter(el => el.dataset.searchKey)
    .map(el => [el.dataset.searchKey, el]));
  reconcileKeyedChildren(parent, existing, desired, item => item.create(), (el, item) => item.update(el));
  for (let i = 0; i < desired.length; i++) parent.children[i].dataset.searchKey = desired[i].key;
}

function renderSearchPanel(panel, type, rows, label) {
  const page = state.search?.pages[type];
  const desired = [];
  const add = (key, create, update = () => {}) => desired.push({ key, item: { create, update } });
  if (!rows.length) {
    const msg = page?.error ? `Couldn't load ${label.toLowerCase()}.`
      : state.filter ? `no ${label.toLowerCase()} match the filter.`
      : `no ${label.toLowerCase()} found.`;
    add("empty", () => makeEmptyEl(msg), el => { el.textContent = msg; });
  }
  if (type === "hashtags") {
    const updateTags = el => {
      el.replaceChildren(...rows.map(tag => {
        const name = String(tag.name || "").replace(/^#/, "").trim();
        const link = document.createElement("a");
        link.className = "search-hashtag";
        link.href = `#/tag/${encodeURIComponent(name)}/`;
        link.textContent = `#${name}`;
        return link;
      }));
    };
    add("hashtags:links", () => {
      const el = document.createElement("div");
      el.className = "search-hashtags search-hashtag-links";
      updateTags(el);
      return el;
    }, updateTags);
  } else {
    for (const row of rows) {
      add(`${type}:${row.id}`, () => type === "accounts" ? accountCard(row) : threadCard(row),
        el => { if (type === "statuses") updateThreadCard(el, row); });
    }
  }
  if (page?.hasMore) {
    const updateButton = el => {
      const btn = el.firstChild;
      btn.disabled = page.loading;
      btn.textContent = page.loading ? "Loading…"
        : page.error ? "Couldn't load more — Retry" : `Load more ${label.toLowerCase()}`;
      btn.setAttribute("aria-label", page.error ? `Retry loading more ${label.toLowerCase()}` : btn.textContent);
    };
    add(`${type}:more`, () => {
      const el = document.createElement("div");
      el.className = "search-load-more";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "catalog-load-more";
      // The section type is stable; loadMoreSearch reads live view state.
      btn.addEventListener("click", () => loadMoreSearch(type));
      el.appendChild(btn);
      updateButton(el);
      return el;
    }, updateButton);
  }
  reconcileSearchNodes(panel, desired);
}

// Bottom-of-catalog sentinel that drives infinite scroll. An
// IntersectionObserver auto-loads the next page as it nears the viewport;
// the button inside is the manual fallback (and covers the case where the
// catalog is shorter than the viewport, so the observer wouldn't fire).
let catalogObserver = null;

function clearCatalogSentinel() {
  if (catalogObserver) { catalogObserver.disconnect(); catalogObserver = null; }
  document.querySelectorAll(".catalog-sentinel").forEach((el) => el.remove());
}

// ---- index rows ------------------------------------------------------------
//
// A row is the thread as it reads: the OP, a line saying what was left out,
// then the last few replies. The replies are the expensive part — there is no
// "give me the tail of this thread" call, only /context, which returns the
// whole thread — so a row is drawn immediately from whatever replies the
// loaded timeline page happens to carry and fetches its real tail only once it
// scrolls into view. Fetched threads are kept for the life of the view, so a
// refresh or a scroll back up costs nothing.

// How many threads may be fetching at once. Scrolling fast down a long index
// would otherwise fire a full thread download per row it swept past.
const INDEX_HYDRATE_CONCURRENCY = 3;
const INDEX_HYDRATE_MARGIN = "400px 0px";

let indexRowObserver = null;
const indexHydrateQueue = [];

// Forget every fetched thread. Called when the view changes — the rows on
// screen are about to be replaced, and holding another board's threads would
// only grow without bound.
function resetIndexView() {
  // A new object also invalidates requests from the previous mode, even if
  // switching Catalog/Index left state.view itself unchanged.
  state.indexView = { rows: new Map(), inFlight: 0 };
  indexHydrateQueue.length = 0;
  clearIndexRowObserver();
  activeThreadGraph = undefined;
}

function clearIndexRowObserver() {
  indexRowObserver?.disconnect();
  indexRowObserver = null;
}

// A stickied thread on the profile that stickied it. `pinned` is only
// trustworthy inside the featured collection (see loadPinnedStatuses), so ask
// the collection rather than the status.
function isStickiedOp(opId) {
  if (state.view?.kind !== "profile" || !opId) return false;
  return (state.stickiedStatuses || []).some((s) => s?.id === opId);
}

// The replies a row shows right now, plus the thread graph they resolve their
// `>>refs` and reply-to lines against (null until the thread is fetched).
function indexRowPreview(op) {
  const limit = indexReplyLimit(isStickiedOp(op.id));
  const entry = state.indexView.rows.get(op.id);
  if (entry?.descendants) {
    return { ...selectIndexReplies(entry.descendants, op.id, limit), graph: entry.graph || null };
  }

  // Not fetched yet. The loaded page's replies stand in, but its idea of how
  // many were left out is a floor — it only ever saw the replies that bumped
  // this thread onto the page. The catalog's reply count is the better
  // estimate, and it's the number the same thread shows in the grid.
  const seeded = seedIndexReplies(state.statuses, op.id, limit);
  const known = catalogReplyCount(op, state.statuses);
  return {
    ...seeded,
    omitted: Math.max(seeded.omitted, known - seeded.replies.length),
    omittedImages: 0,   // unknowable without the thread
    graph: null,
  };
}

// True if this row's replies are worth fetching: never fetched, or bumped
// since it was (the catalog's count is what noticed the bump).
function indexRowNeedsHydration(op) {
  if (!op?.id) return false;
  const entry = state.indexView.rows.get(op.id);
  if (entry?.loading || entry?.error) return false;
  // A thread nothing has replied to has no tail to fetch. Most of a timeline
  // page is usually this, so it's the difference between a handful of
  // requests per page and one per row.
  const known = catalogReplyCount(op, state.statuses);
  if (!known) return false;
  if (!entry) return true;
  // Compared against the count at the time of the fetch, not against how many
  // replies came back. The two legitimately disagree — a reply the reader
  // can't see still counts on the server — and comparing to the fetched total
  // would re-fetch such a thread every time its row scrolled past.
  return known > entry.replyCountAtFetch;
}

// Queued with the reply count that justified the fetch, so a thread whose
// count the reader can't fully account for settles instead of re-fetching.
function queueIndexHydration(op) {
  if (!op?.id || indexHydrateQueue.some((q) => q.id === op.id)) return;
  indexHydrateQueue.push({ id: op.id, known: catalogReplyCount(op, state.statuses) });
  pumpIndexHydration();
}

function pumpIndexHydration() {
  while (state.indexView.inFlight < INDEX_HYDRATE_CONCURRENCY && indexHydrateQueue.length) {
    const next = indexHydrateQueue.shift();
    hydrateIndexRow(next.id, next.known);
  }
}

async function hydrateIndexRow(opId, knownAtQueue = 0) {
  const indexView = state.indexView;
  const rows = indexView.rows;
  const prev = rows.get(opId);
  if (prev?.loading) return;
  rows.set(opId, { ...(prev || {}), loading: true, error: false });
  indexView.inFlight += 1;
  // The view the fetch was started for. Navigating away resets the row map,
  // so a late response must not repopulate it or redraw a row that's gone.
  const view = state.view;

  try {
    const ctx = await fetchContext(opId, state);
    const descendants = ctx?.descendants || [];
    if (state.view !== view || state.indexView !== indexView) return;
    rows.set(opId, {
      descendants,
      total: descendants.length,
      replyCountAtFetch: Math.max(
        knownAtQueue,
        descendants.length,
        catalogReplyCount(ctx?.op || null, state.statuses),
      ),
      graph: buildThreadGraph({ op: ctx?.op || null, descendants }),
      loading: false,
      error: false,
    });
    refreshIndexRow(opId);
  } catch {
    // A thread that won't load keeps its seeded replies rather than an error
    // of its own — the row is still readable, and the reader can click it.
    if (state.view === view && state.indexView === indexView) {
      rows.set(opId, { ...(rows.get(opId) || {}), loading: false, error: true });
    }
  } finally {
    indexView.inFlight -= 1;
    if (state.indexView === indexView) pumpIndexHydration();
  }
}

// Redraw one row after its thread arrived. Deliberately not a full render():
// that would re-sort the whole index under a reader who is mid-scroll.
function refreshIndexRow(opId) {
  const row = document.querySelector(`#catalog > .index-thread[data-id="${CSS.escape(opId)}"]`);
  if (row?._threadItem) renderIndexRow(row, row._threadItem);
}

function indexRow(item) {
  const row = document.createElement("section");
  row.className = "index-thread";
  row.dataset.id = item.op.id;
  renderIndexRow(row, item);
  return row;
}

function updateIndexRow(row, item) {
  renderIndexRow(row, item);
}

// Build or update a row in place. The OP and each reply are the same post
// blocks the thread view renders, reconciled the same way, so a redraw keeps
// expanded videos, open menus and hover state intact.
function renderIndexRow(row, item) {
  const op = item.op;
  if (!op?.id) return;
  row._threadItem = item;
  row.dataset.id = op.id;
  row.classList.toggle("is-sticky", isStickiedOp(op.id));

  const preview = indexRowPreview(op);
  withThreadGraph(preview.graph, () => {
    const opCtx = { isOp: true, depth: 0, index: true };
    let opEl = row.querySelector(":scope > .post-block.is-op");
    if (opEl && opEl.dataset.id === op.id) {
      updatePostBlock(opEl, op, opCtx);
    } else {
      const fresh = createPostBlock(op, opCtx);
      if (opEl) row.replaceChild(fresh, opEl);
      else row.insertBefore(fresh, row.firstChild);
      opEl = fresh;
    }

    syncIndexOmitted(row, op, preview, opEl);

    let wrap = row.querySelector(":scope > .index-replies");
    if (!wrap) {
      wrap = document.createElement("div");
      wrap.className = "index-replies";
      row.appendChild(wrap);
    }
    const existing = new Map();
    for (const child of wrap.children) {
      if (child.dataset?.id) existing.set(child.dataset.id, child);
    }
    const desired = preview.replies.map((s) => ({
      key: s.id,
      item: s,
      ctx: { isOp: false, depth: 0, index: true },
    }));
    reconcileKeyedChildren(wrap, existing, desired, createPostBlock, updatePostBlock);
    wrap.hidden = desired.length === 0;
  });

  wireThreadLinks(row);
  applyAnonymousPresentation(row);
}

// The "+ 2 posts and 2 image replies omitted." line, between the OP and the
// replies. It's a link to the thread, exactly as it is on a board index.
function syncIndexOmitted(row, op, preview, opEl) {
  const label = omittedLabel(preview);
  let link = row.querySelector(":scope > .index-omitted");
  if (!label) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement("a");
    link.className = "index-omitted";
    row.insertBefore(link, opEl.nextSibling);
  }
  link.href = threadHref(op.id);
  link.textContent = `${label} Click here to view.`;
}

function reconcileIndex(cat, threads) {
  const existing = new Map();
  for (const child of cat.children) {
    if (child.classList?.contains("index-thread") && child.dataset.id) {
      existing.set(child.dataset.id, child);
    }
  }
  const desired = threads.map((t) => ({ key: t.op.id, item: t }));
  reconcileKeyedChildren(cat, existing, desired, indexRow, updateIndexRow);
  wireIndexGraphScope(cat);
  observeIndexRows(cat);
}

function observeIndexRows(cat) {
  clearIndexRowObserver();
  if (typeof IntersectionObserver !== "function") return;
  indexRowObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const op = entry.target._threadItem?.op;
      if (indexRowNeedsHydration(op)) queueIndexHydration(op);
    }
  }, { rootMargin: INDEX_HYDRATE_MARGIN });
  for (const row of cat.querySelectorAll(":scope > .index-thread")) {
    indexRowObserver.observe(row);
  }
}

// Point the active thread graph at whichever row the pointer or keyboard is
// in, before any handler inside it runs. Rendering scopes the graph itself
// (withThreadGraph), but handlers fire long after — expanding a `>>ref` in
// place, opening a reply — and they'd otherwise resolve against whatever
// thread happened to be rendered last. Outside a row this clears back to
// null, which is what the thread view wants.
function wireIndexGraphScope(cat) {
  if (cat.dataset.indexGraphScope === "1") return;
  cat.dataset.indexGraphScope = "1";
  const scope = (e) => {
    const row = e.target?.closest?.(".index-thread");
    activeThreadGraph = row ? (state.indexView.rows.get(row.dataset.id)?.graph || null) : undefined;
  };
  for (const type of ["pointerover", "pointerdown", "focusin"]) {
    cat.addEventListener(type, scope, true);
  }
}

function renderCatalogSentinel(cat) {
  clearCatalogSentinel();
  // Board timelines and profiles paginate (both by a max_id status cursor);
  // filtered views show only the already-loaded set.
  const paginates = ["board", "profile", "tag"].includes(state.view?.kind);
  if (!paginates || state.filter) return;
  if (!state.catalog.hasMore && !state.catalog.loadingMore) return;

  const sentinel = document.createElement("div");
  sentinel.className = "catalog-sentinel";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "catalog-load-more";
  if (state.catalog.loadingMore) {
    btn.disabled = true;
    btn.textContent = "Loading…";
  } else if (state.catalog.error) {
    btn.textContent = "Couldn't load more — Retry";
    btn.addEventListener("click", () => loadMoreCatalog());
  } else {
    btn.textContent = "Load more";
    btn.addEventListener("click", () => loadMoreCatalog());
  }
  sentinel.appendChild(btn);
  cat.parentElement.appendChild(sentinel);

  // Don't auto-observe after a failure — that would retry in a tight loop
  // while the sentinel stays in view. The user retries via the button.
  if (!state.catalog.error) {
    catalogObserver = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMoreCatalog();
    }, { rootMargin: "600px 0px" });
    catalogObserver.observe(sentinel);
  }
}

// Record whether there's another catalog page after the one just loaded.
//
// The server tells us directly: a Mastodon-style API sends Link rel="next"
// while more pages exist and omits it on the last one. That's exact, unlike
// the "a full page came back, so assume there's more" heuristic, which keeps
// the sentinel alive past the end of the results — the user then fights an
// infinite scroll that can never load anything.
//
// The wrinkle is that an absent header is indistinguishable from a server
// that never exposes it (it needs Access-Control-Expose-Headers cross-origin),
// and there we must not stop after page one. So the flag is learned per view:
// once this catalog has seen a rel="next", its later absence means the end;
// until then we fall back to the page-length heuristic.
function updateCatalogHasMore({ nextMaxId, pageSize, hasLinkNext }) {
  if (hasLinkNext) state.catalog.linkPaging = true;
  state.catalog.hasMore = hasLinkNext ? true
    : state.catalog.linkPaging ? false
    : (pageSize >= TIMELINE_PAGE_SIZE && !!nextMaxId);
}

// Append newly fetched statuses and update rows we already hold, so refreshes
// keep scopes and interaction counts current without duplicating cards.
//
// `pruneMissing` additionally drops what the page says is *gone*. A refresh
// re-fetches the newest page, so anything we hold from inside the range that
// page covers, but which didn't come back, has been deleted (or unliked,
// unbookmarked, muted — anything that takes a post out of this timeline).
// Without it a deleted post survived every in-app refresh and only vanished
// on a browser reload, which is what empties state.statuses. Older pages we
// hold are outside the refreshed range and are left alone; so is a load-more,
// which fetches an older page and must not prune the newer ones above it.
//
// Comparing ids is sound here: every one was issued by the instance being
// refreshed, and its own pagination is built on them ordering by age.
function mergeStatuses(incoming, { pruneMissing = false } = {}) {
  const page = (incoming || []).filter((s) => s && s.id);
  const held = new Map(state.statuses.map((s) => [s.id, s]));
  for (const s of page) {
    if (held.has(s.id)) {
      Object.assign(held.get(s.id), s);
      continue;
    }
    held.set(s.id, s);
    state.statuses.push(s);
  }
  if (!pruneMissing || !page.length) return;

  const arrived = new Set(page.map((s) => s.id));
  let oldestInPage = page[0].id;
  for (const s of page) {
    if (s.id < oldestInPage) oldestInPage = s.id;
  }
  state.statuses = state.statuses.filter(
    (s) => s.id < oldestInPage || arrived.has(s.id),
  );
}

async function loadMoreCatalog() {
  const kind = state.view?.kind;
  if (!["board", "profile", "tag"].includes(kind)) return;
  // A profile refresh briefly resets state.view (dropping account) while it
  // re-looks-up; skip until the account id is back so we don't fetch a bad URL.
  // Group boards resolve an account the same way.
  if ((kind === "profile" || state.view.group) && !state.view.account?.id) return;
  if (!state.catalog.hasMore || state.catalog.loadingMore) return;
  state.catalog.loadingMore = true;
  state.catalog.error = false;
  const btn = document.querySelector(".catalog-sentinel .catalog-load-more");
  if (btn) { btn.disabled = true; btn.textContent = "Loading…"; }

  try {
    const { statuses, nextMaxId, pageSize, hasLinkNext } = kind === "profile"
      ? await loadAccountStatuses(state.view.account.id, state, { maxId: state.catalog.cursor, includeReplies: !!state.includeReplies })
      : kind === "tag"
        ? await loadTagTimeline(state.view.tag, state, { maxId: state.catalog.cursor })
      : state.view.group
        ? await loadGroupTimeline(state.view.account.id, state, { maxId: state.catalog.cursor })
        : await loadTimeline(state.view.name, state, {
          maxId: state.catalog.cursor,
          includeReplies: includesReplies(state.view),
        });
    mergeStatuses(statuses);
    state.catalog.cursor = nextMaxId;
    updateCatalogHasMore({ nextMaxId, pageSize, hasLinkNext });
  } catch (e) {
    // Keep the loaded catalog intact (don't trip state.error, which would
    // replace it with an error screen); surface a retry in the sentinel.
    state.catalog.error = true;
  } finally {
    state.catalog.loadingMore = false;
    // Hold the absolute scroll position across the re-render.
    //
    // Pinning a specific card (what the thread poll does) is deliberately NOT
    // used here. It assumes the anchor keeps its place in the list, which is
    // true of thread replies but not of a bump-ordered catalog: a page of
    // older posts can hydrate roots and bump existing threads, re-sorting the
    // list under us. If the anchor card itself gets bumped upward, following
    // it would drag the viewport up the page — worse than the drift it fixes.
    // Nothing here can stabilise a list that re-sorts on every page; that
    // needs server-side bump order (see docs/mitra-catalog-endpoint-proposal.md).
    const scrollY = window.scrollY;
    const jumpSeq = state.ui.scrollJumpSeq;
    render();
    requestAnimationFrame(() => {
      if (state.ui.scrollJumpSeq === jumpSeq) window.scrollTo(0, scrollY);
    });
  }
}

// ---- rendering: thread view -------------------------------------------------

// Apply the "linked-to" background to the single highlighted post (a followed
// No. link, or the reply a permalink resolved to), clearing any prior mark
// first — only one post is highlighted at a time. Idempotent; safe to call
// after every reconcile.
function applyLinkedHighlight() {
  const id = state.postView?.highlightId || null;
  document
    .querySelectorAll("#catalog .post-block.is-linked")
    .forEach((el) => { if (el.dataset.id !== id) el.classList.remove("is-linked"); });
  if (!id) return;
  document
    .querySelectorAll(`#catalog .post-block[data-id="${cssEscape(id)}"]`)
    .forEach((el) => el.classList.add("is-linked"));
}

// Scroll a permalink target into view once, after the thread posts have been
// reconciled. Refreshes leave the user's current scroll position alone.
function ensureLinkedPostFocus() {
  const postView = state.postView;
  if (!postView?.focusLinkedPost || !postView.highlightId) return;

  const target = document.querySelector(
    `#catalog .post-block[data-id="${cssEscape(postView.highlightId)}"]`,
  );
  if (!target) return;

  postView.focusLinkedPost = false;

  const center = () => {
    // Ignore a delayed fallback if navigation changed before it fired.
    if (state.postView !== postView || !target.isConnected) return;
    target.scrollIntoView({ behavior: "instant", block: "center" });
  };

  // Now hold the target on screen while lazy-loaded media above it settles.
  // Post thumbnails carry no width/height, so they collapse to 0 until they
  // load; on a cold cache (incognito / first visit) the OP image and reply
  // thumbs pop in well after the centre above, growing the page and pushing
  // the target down out of view — without this the page looks like it
  // snapped back up to the middle of the thread. (A warm cache loads those
  // images before the centre, which is why it only shows up fresh.)
  //
  // The anchor is the target's document position, which reflow changes but a
  // manual scroll does not, so the reader's own scrolling is left alone.
  const wrap = document.querySelector("#catalog .thread-wrapper");
  if (!wrap || !("ResizeObserver" in window)) {
    // No observer: fall back to a couple of blind retries.
    setTimeout(center, 120);
    setTimeout(center, 1500);
    return;
  }

  const docTop = () => target.getBoundingClientRect().top + window.scrollY;
  let anchorDocTop = 0;
  const repin = () => {
    if (state.postView !== postView || !target.isConnected) return;
    const shift = docTop() - anchorDocTop;
    if (Math.abs(shift) < 1) return;
    anchorDocTop += shift;
    window.scrollBy(0, shift);
  };

  const ro = new ResizeObserver(repin);
  let stopped = false;
  let grace = null;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    ro.disconnect();
    window.removeEventListener("wheel", stop, true);
    window.removeEventListener("keydown", stop);
    window.removeEventListener("touchmove", stop);
    clearTimeout(grace);
  };
  // The reader takes priority: any deliberate scroll/input ends the re-pin.
  window.addEventListener("wheel", stop, true);
  window.addEventListener("keydown", stop);
  window.addEventListener("touchmove", stop);
  grace = setTimeout(stop, 3000);

  // Centre once the just-reconciled nodes have laid out (two frames covers a
  // browser-restored fragment and the initial paint), then capture that
  // centred position as the anchor so the RO's initial sizing callback isn't
  // read as a shift.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (stopped) return;
    center();
    anchorDocTop = docTop();
    ro.observe(wrap);
  }));
}

// Pin the thread to what the reader is looking at across a reconcile.
//
// Holding the absolute scrollY only works when nothing above the viewport
// changes height. In a thread it routinely does: a new reply sorts into the
// middle of the flattened list, and the unread line is removed and
// re-inserted further down on every poll. Either one shifts everything below
// it, so the same scrollY lands on different content and the page appears to
// jump. Instead we remember the topmost post still on screen and put it back
// at the same viewport offset afterwards.
//
// Thread views only. This relies on the anchor keeping its place in the list,
// which holds for replies (append-only, chronological) but not for a
// bump-ordered catalog, where the anchor card can itself be re-sorted — see
// the note in loadMoreCatalog.
function captureThreadScrollAnchor() {
  for (const el of document.querySelectorAll("#catalog .post-block")) {
    const rect = el.getBoundingClientRect();
    // First block whose bottom is still below the top edge — i.e. the one the
    // reader is currently on, even if it's partly scrolled off.
    if (rect.bottom > 0) return { id: el.dataset.id, top: rect.top };
  }
  return null;
}

function restoreThreadScrollAnchor(anchor) {
  if (!anchor?.id) return;
  const el = document.querySelector(`#catalog .post-block[data-id="${cssEscape(anchor.id)}"]`);
  if (!el) return;   // anchor post was deleted: nothing sane to pin to
  const delta = el.getBoundingClientRect().top - anchor.top;
  if (Math.abs(delta) < 1) return;
  window.scrollBy(0, delta);
}

// The [Index] / [Catalog] pair in a thread's nav bars. Both lead back to
// wherever the thread was opened from; the difference is how that board is
// laid out once you land, so each sets the view mode on the way out. A
// modified click is opening a new tab — leave the reader's current view
// alone, since they aren't going anywhere.
function wireBackLinks(nav) {
  for (const a of nav.querySelectorAll(".nav-back-link")) {
    a.addEventListener("click", (e) => {
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      saveSettings({ viewMode: a.dataset.viewMode === "index" ? "index" : "catalog" });
    });
  }
}

function syncBackLinks(nav, backRoute) {
  const href = hashForRoute(backRoute);
  for (const a of nav.querySelectorAll(".nav-back-link")) a.href = href;
}

function renderPost(cat) {
  // Drop the catalog's infinite-scroll sentinel when switching to a thread.
  clearCatalogSentinel();

  const ctx = state.postView?.context;
  // Same trick as renderCatalog: only show "loading thread…" if there's
  // nothing to keep on screen. A refresh of a thread we're already
  // viewing keeps the existing OP/replies visible.
  if (!ctx) {
    const hasWrapper = cat.querySelector(":scope > .thread-wrapper") != null;
    if (hasWrapper) return;
    cat.replaceChildren(makeEmptyEl("loading thread…"));
    return;
  }

  // Clear any "loading thread…" placeholder from the prior render so it
  // doesn't end up stacked above the real thread content. The back link /
  // wrapper below are created (or reused) in their own slots, so this
  // only ever removes the .empty placeholder.
  const loadingMsg = cat.querySelector(":scope > .empty");
  if (loadingMsg) loadingMsg.remove();

  state.postView.graph = buildThreadGraph(ctx);

  const backRoute = state.postView?.from || state.lastNonPostRoute || { kind: "board", name: "twkn" };

  // Top navigation bar: [Index] [Catalog] [Bottom] — 4chan style. The
  // controls' border above and this bar's border below sandwich it between
  // two lines. Index and Catalog lead to the same place; which one you take
  // is which way you want to find it laid out when you get there.
  let navTop = cat.querySelector(":scope > .navLinksTop");
  if (!navTop) {
    navTop = document.createElement("div");
    navTop.className = "navLinks navLinksTop";
    navTop.innerHTML = `
      [<a href="${hashForRoute(backRoute)}" class="nav-back-link" data-view-mode="index">Index</a>]
      [<a href="${hashForRoute(backRoute)}" class="nav-back-link" data-view-mode="catalog">Catalog</a>]
      [<a href="#bottom" class="nav-scroll-bottom">Bottom</a>]
    `;
    cat.appendChild(navTop);

    // Wire listeners once.
    wireBackLinks(navTop);
    navTop.querySelector(".nav-scroll-bottom").addEventListener("click", (e) => {
      e.preventDefault();
      state.ui.scrollJumpSeq++;
      window.scrollTo({ top: document.body.scrollHeight, behavior: "instant" });
    });
  } else {
    syncBackLinks(navTop, backRoute);
  }
  // Bottom navigation bar: [Reply to OP] centered, then [Index] [Catalog]
  // [Top] [Update] — 4chan style, placed after the last post.
  let navBot = cat.querySelector(":scope > .navLinksBot");
  if (!navBot) {
    navBot = document.createElement("div");
    navBot.className = "navLinks navLinksBot";
    navBot.id = "bottom";
    navBot.innerHTML = `
      <span class="brackets-wrap qr-link-container-bottom"><a href="javascript:;" class="qr-link-bottom">Reply to OP</a></span>
      [<a href="${hashForRoute(backRoute)}" class="nav-back-link" data-view-mode="index">Index</a>]
      [<a href="${hashForRoute(backRoute)}" class="nav-back-link" data-view-mode="catalog">Catalog</a>]
      [<a href="#top" class="nav-scroll-top">Top</a>]
      <span class="brackets-wrap"><a href="javascript:;" class="nav-update">Update</a></span>
    `;
    // Wire listeners once.
    wireBackLinks(navBot);
    navBot.querySelector(".nav-scroll-top").addEventListener("click", (e) => {
      e.preventDefault();
      state.ui.scrollJumpSeq++;
      window.scrollTo({ top: 0, behavior: "instant" });
    });
    navBot.querySelector(".nav-update").addEventListener("click", (e) => {
      e.preventDefault();
      document.getElementById("toolbar-refresh")?.click();
    });
    // Read the OP from live state at click time — this listener is wired once
    // (navBot is reused across thread navigations), so closing over the local
    // `ctx` would pin it to the first thread we ever rendered.
    navBot.querySelector(".qr-link-bottom").addEventListener("click", (e) => {
      e.preventDefault();
      const op = state.postView?.context?.op;
      if (op) openComposeReply(op);
    });
  } else {
    syncBackLinks(navBot, backRoute);
  }

  // Thread container — structural, create once.
  let wrapper = cat.querySelector(":scope > .thread-wrapper");
  if (!wrapper) {
    wrapper = document.createElement("div");
    wrapper.className = "thread-wrapper";
    cat.appendChild(wrapper);
  }

  // OP — single element, reconciled in place (counts / reactions update,
  // scroll position and any open inline-stack on it survive a refresh).
  if (ctx.op) {
    reconcileOp(wrapper, ctx.op);
  } else {
    showOpNotFound(wrapper, state.view.id);
  }

  // Replies wrap — structural, create once.
  let repliesWrap = wrapper.querySelector(":scope > .replies-wrap");
  if (!repliesWrap) {
    repliesWrap = document.createElement("div");
    repliesWrap.className = "replies-wrap";
    wrapper.appendChild(repliesWrap);
  }

  // Replies — flat list in DFS tree order, reconciled by data-id.
  // New replies get created, removed ones (rare) get dropped, existing
  // ones keep their scroll position and any expanded media.
  const tree = buildReplyTree(ctx.descendants || [], ctx.op?.id);
  reconcileReplies(repliesWrap, tree);

  // Paint the "linked-to" background on the marked post (a followed No. link
  // or the reply a permalink points at). Reconcile reuses nodes across
  // refreshes, so we re-apply from state rather than trusting the class to
  // have survived.
  applyLinkedHighlight();
  ensureLinkedPostFocus();

  // Red "unread line" before the first post newer than the last-seen baseline.
  insertUnreadLine(repliesWrap);

  // "(no replies)" placeholder — only when there really are none. The
  // reconciliation above already cleared any old children of the wrap,
  // so we just need to add a fresh placeholder if the tree is empty.
  if (tree.length === 0) {
    const note = makeEmptyEl("(no replies)");
    repliesWrap.appendChild(note);
  }

  // Wire up >>reply links, No. XXXX linking, and hover previews. These
  // functions have idempotency guards so reusing elements doesn't
  // double-attach listeners; new elements (new replies) get wired fresh.
  wireThreadLinks(cat);
  ensurePendingReplyFocus();

  // Ensure the bottom nav bar sits at the very end of the catalog container
  // (after the thread-wrapper).
  if (navBot.parentNode) navBot.remove();
  cat.appendChild(navBot);
}

function threadLatestCreatedAt(ctx) {
  let latest = ctx?.op?.created_at || "";
  for (const s of (ctx?.descendants || [])) {
    const at = s?.created_at || "";
    if (at > latest) latest = at;
  }
  return latest;
}

// Byte-level signature of a thread context, used to decide whether a
// background poll actually changed anything worth re-rendering. JSON.parse
// preserves property order, so two identical server responses stringify
// identically — and a genuine content change can never produce identical
// bytes. A match therefore never skips a real update; the only failure mode
// is a needless re-render (a server that reorders fields for unchanged
// content), which is no worse than the previous always-render behaviour.
function threadContextSignature(ctx) {
  if (!ctx) return "";
  return JSON.stringify(ctx.op || null) + "\x1e" + JSON.stringify(ctx.descendants || []);
}

// Count thread replies posted after `since` (an ISO created_at). Used by the
// "N new replies" bar: replies newer than the reader's last-acknowledged post.
function countNewReplies(ctx, since) {
  if (!since) return 0;
  let n = 0;
  for (const s of (ctx?.descendants || [])) {
    if ((s?.created_at || "") > since) n++;
  }
  return n;
}

// Insert a red "unread line" before the first reply newer than the baseline
// captured when the thread was loaded — i.e. the first post that arrived since
// the last visit/refresh. ISO timestamps compare lexicographically.
function insertUnreadLine(repliesWrap) {
  const prev = repliesWrap.querySelector(":scope > .unread-line");
  if (prev) prev.remove();

  const unreadAt = state.postView?.unreadAt || "";
  const graph = currentThreadGraph();
  if (!unreadAt || !graph) return;

  for (const child of Array.from(repliesWrap.children)) {
    if (!child.classList?.contains("post-block")) continue;
    const st = graph.statusById.get(child.dataset.id);
    if (st && (st.created_at || "") > unreadAt) {
      const hr = document.createElement("hr");
      hr.className = "unread-line";
      repliesWrap.insertBefore(hr, child);
      ensureUnreadLineFocus(hr);
      return;
    }
  }
}

// On a fresh navigation, bring the saved unread boundary into view once the
// reconciled thread has settled. Refreshes intentionally leave scroll position
// alone even though they may move the marker to the newest batch of replies.
function ensureUnreadLineFocus(line) {
  const postView = state.postView;
  if (!postView?.focusUnreadLine || !line) return;
  postView.focusUnreadLine = false;
  const focus = () => {
    if (state.postView !== postView || !line.isConnected) return;
    line.scrollIntoView({ behavior: "instant", block: "center" });
  };
  requestAnimationFrame(() => requestAnimationFrame(focus));
  setTimeout(focus, 120);
}

// The thread a post is being rendered or acted on *within*.
//
// A post block reads its thread to draw the reply-to line, the "replied by"
// links, and to resolve `>>refs` — and for the thread view there is exactly
// one thread on screen, so those reads all went to state.postView.graph. The
// index view puts many threads on one page, so the graph has to be scoped:
// each row sets its own for the duration of its render (withThreadGraph), and
// a capture-phase listener re-points it at the row a click or hover landed in
// (wireIndexGraphScope) so deferred handlers see the right thread too.
// undefined = unscoped, so the thread view's own graph answers. null = scoped
// to a thread whose posts aren't loaded — an index row that hasn't been
// hydrated yet, which must NOT fall back to whatever thread was open last.
let activeThreadGraph = undefined;

function currentThreadGraph() {
  if (activeThreadGraph !== undefined) return activeThreadGraph;
  return state.postView?.graph || null;
}

// Render `fn` with `graph` as the active thread. Restores the previous graph
// afterwards, so nesting (an index row rendered during a thread view refresh)
// can't leave the wrong thread in place. Synchronous by contract: an async fn
// would outlive the restore.
function withThreadGraph(graph, fn) {
  const prev = activeThreadGraph;
  activeThreadGraph = graph || null;
  try {
    return fn();
  } finally {
    activeThreadGraph = prev;
  }
}

function buildThreadGraph(ctx) {
  return buildThreadGraphFromStatuses(ctx, (status) => {
    // A template parses entities and nested markup without loading media or
    // attaching untrusted post HTML to the live document. Matching by URL
    // also handles references whose status ids differ between servers.
    const template = document.createElement("template");
    template.innerHTML = status.content || "";
    return Array.from(template.content.querySelectorAll("a[href]"),
      (a) => a.getAttribute("href"));
  });
}

// The `>>ref` for a post, given the post itself or just its id. A ref is
// derived from the AP object id, which a bare status id doesn't carry, so an
// id has to be resolved to the status holding it — from the loaded thread, or
// from the quote index for posts outside it. Until its object URL is available,
// there is no reference to show.
function postRef(postOrId) {
  if (postOrId && typeof postOrId === "object") return shortPostRef(postOrId);
  const id = String(postOrId || "");
  const status = currentThreadGraph()?.statusById.get(id) || lookupQuoted({ ref: id });
  return status ? shortPostRef(status) : "";
}

function postRefLabel(postOrId, fallback = "Post") {
  const ref = postRef(postOrId);
  return ref ? ">>" + ref : fallback;
}

function resolveStatusId(ref) {
  const graph = currentThreadGraph();
  if (!graph) return null;
  const needle = String(ref || "").trim();
  if (!needle) return null;
  if (graph.statusById.has(needle)) return needle;

  // A ref names a post by its object id, so ask each loaded post what its ref
  // is. The hash is memoized, so this stays a lookup per post.
  for (const [id, status] of graph.statusById) {
    if (shortPostRef(status) === needle) return id;
  }
  return null;
}

const FLOAT_PREVIEW_MARGIN = 8;
const IMAGE_PREVIEW_OFFSET_X = 200;
const IMAGE_PREVIEW_OFFSET_Y = 12;

function positionFloatingNearPointer(el, e, { offsetX = 14, offsetY = 14, margin = FLOAT_PREVIEW_MARGIN } = {}) {
  if (!el || !e) return;
  const rect = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = e.clientX + offsetX;
  let top = e.clientY + offsetY;

  if (left + rect.width > vw - margin) left = e.clientX - rect.width - 12;
  if (left < margin) left = Math.max(margin, vw - rect.width - margin);

  if (top + rect.height > vh - margin) top = vh - rect.height - margin;
  if (top < margin) top = margin;

  el.style.left = Math.round(left) + "px";
  el.style.top = Math.round(top) + "px";
}

class ActivityHoverPopup {
  constructor() {
    this.el = document.getElementById("activity-hover");
    this._cache = new Map();
    this._token = 0;
    this._timer = null;
    this._lastMouseEvent = null;
    this._button = null;

    document.addEventListener("mouseover", (e) => this._onOver(e));
    document.addEventListener("mouseout", (e) => this._onOut(e));
    document.addEventListener("mousemove", (e) => {
      this._lastMouseEvent = e;
      if (this.el?.classList.contains("is-visible")) this._position(e);
    });
  }

  _activityButton(target) {
    return target?.closest?.('.post-action[data-act="like"], .post-action[data-act="repost"], .post-reaction-btn') || null;
  }

  _count(btn) {
    const count = btn.querySelector(".post-action-count, .post-reaction-count")?.textContent || "0";
    return Number(String(count).replace(/[^0-9]/g, "")) || 0;
  }

  _onOver(e) {
    const btn = this._activityButton(e.target);
    if (!btn || btn.contains(e.relatedTarget) || userSettings.anonymousMode || !userSettings.showActivityHover || this._count(btn) <= 0) return;
    const host = btn.closest(".post-block, .thread-card");
    const status = host?._statusData;
    if (!status?.id) return;

    this.hide();
    this._button = btn;
    this._lastMouseEvent = e;
    const token = ++this._token;
    this._timer = setTimeout(async () => {
      if (token !== this._token || this._button !== btn) return;
      const kind = btn.classList.contains("post-reaction-btn") ? "reaction" : btn.dataset.act;
      const reactionName = kind === "reaction" ? btn.dataset.name : "";
      this._renderState(kind === "like" ? "Liked by" : kind === "repost" ? "Reposted by" : `Reacted with ${reactionName}`, "loading…");
      try {
        const accounts = await this._accounts(status, kind, reactionName);
        if (token !== this._token || this._button !== btn) return;
        this._renderAccounts(kind, reactionName, accounts);
      } catch {
        if (token === this._token) this._renderState("Activity", "activity list unavailable");
      }
    }, 180);
  }

  _onOut(e) {
    const btn = this._activityButton(e.target);
    if (!btn || btn.contains(e.relatedTarget)) return;
    if (this._button === btn) this.hide();
  }

  async _accounts(status, kind, reactionName) {
    const key = `${status.id}:${kind}:${reactionName}`;
    const cached = this._cache.get(key);
    if (cached && Date.now() - cached.at < 30_000) return cached.loading;
    const loading = (async () => {
      if (kind === "like" || kind === "repost") {
        const endpoint = kind === "like" ? "favourited_by" : "reblogged_by";
        const r = await apiFetch(`/api/v1/statuses/${encodeURIComponent(status.id)}/${endpoint}?limit=40`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const accounts = await r.json();
        return Array.isArray(accounts) ? accounts : [];
      }

      const embedded = (status?.pleroma?.emoji_reactions || [])
        .find((r) => r?.name === reactionName)?.accounts;
      if (Array.isArray(embedded) && embedded.some((account) => account && typeof account === "object")) {
        return embedded.filter((account) => account && typeof account === "object");
      }
      // Mitra never embeds reacting accounts in a status (accounts is always
      // []); they are only exposed by the Pleroma reactions endpoint, which
      // requires auth — logged-out hovers fall through to the error state.
      const r = await apiFetch(`/api/v1/pleroma/statuses/${encodeURIComponent(status.id)}/reactions`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const reactions = await r.json();
      const match = (Array.isArray(reactions) ? reactions : []).find((x) => x?.name === reactionName);
      return (Array.isArray(match?.accounts) ? match.accounts : [])
        .filter((account) => account && typeof account === "object")
        .slice(0, 40);
    })();
    this._cache.set(key, { at: Date.now(), loading });
    try { return await loading; }
    catch (e) { this._cache.delete(key); throw e; }
  }

  _renderAccounts(kind, reactionName, accounts) {
    const title = kind === "like" ? "Liked by" : kind === "repost" ? "Reposted by" : `Reacted with ${reactionName}`;
    this.el.replaceChildren();
    const head = document.createElement("div");
    head.className = "activity-hover-title";
    head.textContent = title;
    this.el.appendChild(head);
    if (!accounts.length) {
      const stateEl = document.createElement("div");
      stateEl.className = "activity-hover-state";
      stateEl.textContent = "account details unavailable";
      this.el.appendChild(stateEl);
    } else {
      for (const account of accounts) this.el.appendChild(this._accountRow(account));
    }
    this.el.classList.add("is-visible");
    applyAnonymousPresentation(this.el);
    if (this._lastMouseEvent) this._position(this._lastMouseEvent);
  }

  _accountRow(account) {
    const row = document.createElement("div");
    row.className = "activity-hover-account";
    const textEl = document.createElement("div");
    textEl.className = "activity-hover-account-text";
    const name = document.createElement("span");
    name.className = "activity-hover-name";
    name.textContent = account?.display_name || account?.username || account?.acct || "Unknown";
    const handle = document.createElement("span");
    handle.className = "activity-hover-handle";
    handle.textContent = `@${account?.acct || account?.username || "unknown"}`;
    textEl.append(name, handle);
    row.appendChild(textEl);
    return row;
  }

  _renderState(title, message) {
    this.el.replaceChildren();
    const head = document.createElement("div");
    head.className = "activity-hover-title";
    head.textContent = title;
    const stateEl = document.createElement("div");
    stateEl.className = "activity-hover-state";
    stateEl.textContent = message;
    this.el.append(head, stateEl);
    this.el.classList.add("is-visible");
    if (this._lastMouseEvent) this._position(this._lastMouseEvent);
  }

  _position(e) {
    positionFloatingNearPointer(this.el, e, { offsetX: 14, offsetY: 14, margin: FLOAT_PREVIEW_MARGIN });
  }

  hide() {
    if (this._timer) clearTimeout(this._timer);
    this._timer = null;
    this._button = null;
    this._token++;
    this.el?.classList.remove("is-visible");
  }
}

const activityHoverPopup = new ActivityHoverPopup();

function bindPostReferenceLink(a, hostPostEl, targetId, opts = {}) {
  // Claim the link for this handler: its text is ">>hash", which the anchor
  // pass in wireThreadLinks would otherwise also match and bind a second
  // (slot-less) click handler to — expanding the quoted post twice, once in
  // its slot and once appended under the post-foot.
  a.dataset.wiredQuoteLink = "1";
  const resolved = resolveStatusId(targetId);
  const status = resolved ? currentThreadGraph()?.statusById.get(resolved) : null;

  a.addEventListener("mouseenter", () => {
    if (status) previewPopup.show(status, a);
  });
  a.addEventListener("mouseleave", () => previewPopup.hide());

  a.addEventListener("click", (e) => {
    e.preventDefault();
    if (!resolved) {
      // Post isn't in the current view — fall back to composing a reply
      // addressed to whatever post the user clicked, by id only.
      openComposeReply({ id: targetId, account: { acct: targetId.slice(0, 8) } });
      return;
    }

    const slot = opts.slot || "default";
    const afterEl = slot === "reply-to" ? (a.closest(".post-reply-to") || null) : null;
    toggleInlinePost(hostPostEl, resolved, { slot, afterEl });
  });
}

// Expand a referenced post underneath its host, or collapse it if it's already
// showing. `opts.status` carries a post the thread graph doesn't hold — a
// quoted post, which arrives inline on the post that references it.
function toggleInlinePost(hostPostEl, targetId, opts = {}) {
  const graph = currentThreadGraph();
  const targetStatus = graph?.statusById.get(targetId) || opts.status || null;
  if (!targetStatus) {
    // Target isn't in the loaded thread — open compose addressed to the
    // referenced post by id only (no parent object available).
    openComposeReply({ id: targetId, account: { acct: targetId.slice(0, 8) } });
    return;
  }

  const slot = opts.slot || "default";
  let stack = findInlineStack(hostPostEl, targetId, slot);
  if (!stack) {
    stack = document.createElement("div");
    stack.className = "inline-stack";
    stack.dataset.slot = slot;
    if (slot === "reply-to") stack.classList.add("is-reply-to-stack");
    // A quoted post belongs to another thread: the marker styling and the
    // "go to this post in its thread" No. link both key off this class.
    if (slot === "quote") stack.classList.add("is-quote-stack");

    const quoteRef = slot === "quote" && Array.from(hostPostEl.querySelectorAll("a.quotelink[data-quote-target-id]"))
      .find((a) => a.dataset.quoteTargetId === targetId && a.closest(".post-block") === hostPostEl);
    if (slot === "quote") stack.dataset.quoteTargetId = targetId;
    const afterEl = quoteRef || opts.afterEl;
    if (afterEl && hostPostEl.contains(afterEl)) {
      afterEl.after(stack);
    } else {
      hostPostEl.appendChild(stack);
    }
  }

  const existing = Array.from(stack.children).find((n) => n.dataset?.targetId === targetId);
  if (existing) {
    existing.remove();
    if (!stack.children.length) stack.remove();
    if (slot === "quote") syncQuoteLinkStates(hostPostEl, targetId);
    return;
  }

  const wrap = document.createElement("div");
  wrap.className = "inline-post";
  wrap.dataset.targetId = targetId;

  const isTargetOp = targetId === state.postView?.context?.op?.id;
  const inlineDepth = Number(hostPostEl.dataset.inlineDepth || 0) + 1;
  const inlineBlock = threadPostBlock(targetStatus, isTargetOp, 0, { inline: true, inlineDepth });
  wrap.appendChild(inlineBlock);
  stack.appendChild(wrap);
  if (slot === "quote") limitQuoteBody(inlineBlock);
  if (slot === "quote") syncQuoteLinkStates(hostPostEl, targetId);

  wireThreadLinks(wrap);
  // Anonymous mode is a pass over rendered DOM, so anything built after the
  // view was drawn has to be run through it too. The hover preview already
  // was; expanding the same post inline was not, so clicking a reference
  // showed the name the preview had just withheld.
  applyAnonymousPresentation(wrap);
}

// Only inspect stacks belonging to this host, excluding nested quoted posts.
function findInlineStack(hostPostEl, targetId, slot) {
  return Array.from(hostPostEl.querySelectorAll(".inline-stack")).find((n) =>
    n.closest(".post-block") === hostPostEl &&
    (n.dataset.slot || "default") === slot &&
    (slot !== "quote" || n.dataset.quoteTargetId === targetId));
}

function hasInlinePost(hostPostEl, targetId, slot = "default") {
  const stack = findInlineStack(hostPostEl, targetId, slot);
  return !!stack && Array.from(stack.children).some((n) => n.dataset?.targetId === targetId);
}

// Read the live expansion state: post nodes and their listeners survive refreshes.
function syncQuoteLinkState(a, targetId) {
  a.dataset.inlineQuoteTargetId = targetId;
  const host = a.closest(".post-block");
  const expanded = !!host && hasInlinePost(host, targetId, "quote");
  if (expanded) a.title = "Hide quote post";
  else a.removeAttribute("title");
  a.setAttribute("aria-expanded", String(expanded));
  return expanded;
}

function syncQuoteLinkStates(host, targetId) {
  for (const a of host.querySelectorAll("a.quotelink")) {
    if (a.closest(".post-block") !== host) continue;
    if ((a.dataset.inlineQuoteTargetId || a.dataset.quoteTargetId) === targetId) {
      syncQuoteLinkState(a, targetId);
    }
  }
}

// Keep long quote bodies compact without clipping their header or actions.
function limitQuoteBody(post) {
  const body = post.querySelector(":scope > .post-body");
  if (!body) return;
  body.classList.add("quote-body-preview");
  const more = document.createElement("button");
  more.type = "button";
  more.className = "quote-show-more";
  more.textContent = "Show more";
  more.setAttribute("aria-expanded", "false");
  more.hidden = true;
  body.after(more);
  more.addEventListener("click", () => {
    const expanded = more.getAttribute("aria-expanded") !== "true";
    body.classList.toggle("quote-body-preview", !expanded);
    more.setAttribute("aria-expanded", String(expanded));
    more.textContent = expanded ? "Show less" : "Show more";
  });
  // Measure after insertion, when wrapping at the actual column width is known.
  requestAnimationFrame(() => {
    if (post.isConnected) more.hidden = body.scrollHeight <= body.clientHeight;
  });
}

// Show metadata-backed quotes beneath their references in Index and thread
// views. Nested quotes stay collapsed to avoid recursively expanding threads.
//
// Auto-expanded once per target, tracked on the element: reconcile runs this
// again on every poll, and a quote the reader collapsed must stay collapsed.
// The set is dropped when the block's content is rebuilt (updatePostContent),
// which is also when the expanded copy itself is discarded.
function expandQuotedPosts(hostPostEl, status) {
  if (!hostPostEl || !status) return;
  const done = hostPostEl._autoExpandedQuotes || (hostPostEl._autoExpandedQuotes = new Set());
  for (const q of quotedStatuses(status)) {
    if (!q?.id || done.has(q.id)) continue;
    done.add(q.id);
    if (hasInlinePost(hostPostEl, q.id, "quote")) continue;
    toggleInlinePost(hostPostEl, q.id, { slot: "quote", status: q });
  }
}

function buildReplyFromLinksInline(postId, hostPostEl) {
  const graph = currentThreadGraph();
  if (!graph) return null;
  const childIds = graph.backlinksByTarget.get(postId) || [];
  if (!childIds.length) return null;

  const wrap = document.createElement("span");
  wrap.className = "post-head-reply-from";
  // The set these links were built from, so a resync can tell whether the
  // thread has since gained a reply to this post.
  wrap.dataset.ids = childIds.join(",");
  for (const cid of childIds) {
    const a = document.createElement("a");
    a.className = "reply-from-link";
    a.href = "#p" + cid;
    a.textContent = postRefLabel(cid, "Reply");
    bindPostReferenceLink(a, hostPostEl, cid, { slot: "reply-from" });
    wrap.appendChild(a);
  }
  return wrap;
}

function buildReplyToBlock(postId, hostPostEl) {
  const graph = currentThreadGraph();
  if (!graph) return null;
  const parentId = graph.parentById.get(postId) || null;
  if (!parentId) return null;

  const parent = graph.statusById.get(parentId);
  const parentIsYou = isOwnAccount(parent?.account);

  const wrap = document.createElement("div");
  wrap.className = "post-reply-to";
  const a = document.createElement("a");
  a.className = "reply-to-link";
  a.href = "#p" + parentId;
  // The "(You)" suffix lives *inside* the link so it picks up the same
  // underlined red styling as the >>hash — no separate span, no extra glyph.
  a.textContent = postRefLabel(parentId, "Reply") + (parentIsYou ? " (You)" : "");
  bindPostReferenceLink(a, hostPostEl, parentId, { slot: "reply-to" });
  wrap.appendChild(a);

  return wrap;
}

// Hide leading `@user` mentions in a post body and return a toggle button
// that reveals them. Mentions are detected by `.mention` (the actual @user
// link class) and by Mitra's typical `<span class="h-card">` wrapper. The
// toggle is meant to be appended to the post's reply-to line (per the 4chan-x
// style) so the caret sits next to the red >>parent link.
//
// The walker treats a "prefixed" mention as any `.mention` (or h-card
// wrapping one) that appears before the first non-mention content at the
// start of the post. Whitespace text nodes (leading, inter-mention, and
// trailing) are KEPT inside the wrap rather than skipped, so:
//   * When the toggle is open the mentions render as "@a @b @c" with the
//     original spacing (not "@a@b@c").
//   * When the toggle is collapsed the post body doesn't pick up a stray
//     leading space from the gap between the last mention and the rest of
//     the text (e.g. "@SillyBilly @grips " hides cleanly; "@p" later in
//     the post is left alone).
//
// Where the walker actually looks depends on the body's structure:
//   * If the body's first element is a <p> (the common case where the
//     server wraps inline content in a paragraph), the walker descends
//     INTO that <p> and the resulting wrap lives inside it. The <p>'s
//     structure — including any later inline content in the same
//     paragraph — is preserved.
//   * If there's no <p> wrapper (the live Mitra server typically emits
//     a flat list of inline elements: h-cards, text, <br>, links, etc.),
//     the walker iterates the body's DIRECT children instead. The
//     resulting wrap lives at the body level, so multiple leading
//     h-cards in a row are all captured even when the server hasn't
//     grouped them in a <p>.
function extractLeadingMentions(bodyEl) {
  const first = bodyEl.firstElementChild;
  if (!first) return null;
  // Pick the parent we'll iterate and the parent the wrap will live in.
  // See the function header for the rationale.
  const parent = first.tagName === "P" ? first : bodyEl;

  // A mention node is either the link itself (`<a class="mention">`), the
  // Mastodon-style h-card around it, or a transparent span containing only
  // mentions and whitespace. Some servers add one or more empty-class spans
  // around an h-card; treating those wrappers as a unit lets us move their
  // trailing space into the collapsed prefix too. Restricting this recursion
  // to spans avoids swallowing a paragraph/container that also owns content.
  const isMentionNode = (n) => {
    if (!n || n.nodeType !== 1) return false;
    if (n.classList?.contains("mention")) return true;
    if (n.classList?.contains("h-card") && n.querySelector(".mention")) return true;
    if (n.tagName !== "SPAN") return false;
    let foundMention = false;
    for (const child of n.childNodes) {
      if (child.nodeType === 3 && !child.nodeValue.trim()) continue;
      if (!isMentionNode(child)) return false;
      foundMention = true;
    }
    return foundMention;
  };
  // A whitespace-only text node. These appear between adjacent inline
  // elements (browsers materialise inter-tag whitespace as a text node)
  // and we deliberately keep them inside the wrap — see the function
  // header for why.
  const isWhitespace = (n) => n && n.nodeType === 3 && !n.nodeValue.trim();

  const leading = [];
  let hasMention = false;
  let node = parent.firstChild;
  // Accept any whitespace text nodes that appear before the first mention
  // (e.g. the newline + indent the server emits between <p> and the
  // first inline element). Hiding them with the rest of the prefix
  // prevents a visible leading gap in the post body.
  while (isWhitespace(node)) {
    leading.push(node);
    node = node.nextSibling;
  }
  while (isMentionNode(node)) {
    hasMention = true;
    leading.push(node);
    node = node.nextSibling;
    // Keep eating whitespace so consecutive leading mentions
    // ("@a @b @c …") all land in the wrap with the original spacing,
    // regardless of whether the body wraps its content in a <p> or not.
    while (isWhitespace(node)) {
      leading.push(node);
      node = node.nextSibling;
    }
  }
  // A leading-whitespace-only prefix (no mention at all) is not a
  // mentions prefix — bail so the toggle doesn't appear for posts that
  // just happen to start with a stray space.
  if (!hasMention) return null;

  // Wrap the leading mentions in a hidden span so the rest of the post body
  // flows naturally without a leading `@user1 @user2` cluttering the layout.
  // Insert the wrap *before* moving the nodes in — once leading[0] is
  // reparented into `wrap` it's no longer a child of `parent` and the
  // insertBefore(wrap, leading[0]) call would throw NotFoundError. The
  // wrap lives in `parent` (either the first <p> or the body itself,
  // depending on structure) so it stays a sibling of the mention nodes
  // it toggles.
  const wrap = document.createElement("span");
  wrap.className = "post-mentions";
  wrap.hidden = true;
  parent.insertBefore(wrap, leading[0]);
  for (const n of leading) wrap.appendChild(n);

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "post-mentions-toggle";
  btn.title = "Toggle mentioned users";
  btn.setAttribute("aria-label", "Toggle mentioned users");
  btn.setAttribute("aria-expanded", "false");
  btn.innerHTML = '<i class="fa-solid fa-caret-right"></i>';
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    const open = wrap.hidden;
    wrap.hidden = !open;
    btn.classList.toggle("is-open", open);
    btn.setAttribute("aria-expanded", open ? "true" : "false");
  });
  return btn;
}

function attachImageInteractions(img, {
  thumb,
  full,
  description = "",
  getImages,
  onImageChange,
  useFullThumbnail = false,
  collapsedMaxWidth,
  collapsedMaxHeight,
  expandedMaxHeight,
}) {
  let overlayImg = null;
  let lastMouseEvent = null;

  const applyOverlayBounds = () => {
    if (!overlayImg) return;
    overlayImg.style.maxWidth = Math.max(160, window.innerWidth - (FLOAT_PREVIEW_MARGIN * 2)) + "px";
    overlayImg.style.maxHeight = Math.max(160, window.innerHeight - (FLOAT_PREVIEW_MARGIN * 2)) + "px";
  };

  const positionOverlay = () => {
    if (!overlayImg || !lastMouseEvent) return;
    // Vertically centre the cursor on the overlay: the cursor's Y should
    // land at the overlay's middle, not just above its top edge. We pull
    // the rect fresh here because the overlay's height is only known
    // *after* the image finishes loading — on the first call (image
    // still loading) rect.height is 0 and we fall back to a 0 offset,
    // then the load handler below re-positions once the real size is
    // available.
    const rect = overlayImg.getBoundingClientRect();
    const yOffset = rect.height > 0 ? -rect.height / 2 : 0;
    positionFloatingNearPointer(overlayImg, lastMouseEvent, {
      offsetX: IMAGE_PREVIEW_OFFSET_X,
      offsetY: yOffset,
      margin: FLOAT_PREVIEW_MARGIN,
    });
  };

  const hideOverlay = () => {
    if (overlayImg) {
      overlayImg.remove();
      overlayImg = null;
    }
  };

  // Don't pop the full-size hover preview for a still-blurred NSFW image —
  // that would defeat the blur. Checked live against the DOM so the preview
  // starts working the moment the user reveals the image. (Non-sensitive
  // images aren't wrapped, so closest() is null and this is a no-op.)
  const isBlurredNsfw = () => isMediaBlurred(img);

  const selectImage = (media) => {
    if (!media) return;
    full = mediaFullUrl(media);
    thumb = useFullThumbnail ? full : (media.preview_url || full);
    description = (media.description || "").trim();
    img.alt = description;
    if (img.classList.contains("expanded")) {
      img.src = full;
      if (description) img.title = description;
      else img.removeAttribute("title");
    }
    onImageChange(media);
  };
  const navigation = attachImageNavigation(img, {
    getImages,
    onSelect: selectImage,
    isAvailable: () => !isBlurredNsfw(),
  });

  const showOverlay = (e) => {
    lastMouseEvent = e;
    if (state.ui.suppressImageHover) return;
    if (isBlurredNsfw()) return;
    // If the overlay node was removed out from under us (e.g. by
    // clearFloatingOverlays on a re-render) our closure var still points
    // at a detached node. Treat a disconnected overlay as gone so we can
    // recreate it, instead of refusing forever on the stale reference.
    if (overlayImg && !overlayImg.isConnected) overlayImg = null;
    if (img.classList.contains("expanded") || overlayImg) return;
    overlayImg = document.createElement("img");
    overlayImg.className = "post-overlay-img";
    overlayImg._ownerEl = img;   // for sweepOrphanOverlays: dies with its thumbnail
    overlayImg.src = full;
    overlayImg.addEventListener("load", function () {
      // Ignore a late load from an overlay that was already dismissed or
      // replaced, so we don't reposition the current one off a stale event.
      if (this !== overlayImg) return;
      applyOverlayBounds();
      positionOverlay();
    });
    document.body.appendChild(overlayImg);
    applyOverlayBounds();
    positionOverlay();
  };

  img.addEventListener("mouseenter", showOverlay);
  img.addEventListener("mouseleave", hideOverlay);
  img.addEventListener("mousemove", (e) => {
    lastMouseEvent = e;
    if (overlayImg && !overlayImg.isConnected) overlayImg = null;
    if (!overlayImg) {
      if (!state.ui.suppressImageHover && !img.classList.contains("expanded")) showOverlay(e);
      return;
    }
    applyOverlayBounds();
    positionOverlay();
  });

  img.addEventListener("click", (e) => {
    e.stopPropagation();
    hideOverlay();
    selectImage(navigation.current());
    if (img.classList.contains("expanded")) {
      img.src = thumb;
      img.classList.remove("expanded");
      img.style.maxWidth = collapsedMaxWidth + "px";
      img.style.maxHeight = collapsedMaxHeight + "px";
      // Alt text is a tooltip only while expanded — on the thumbnail it
      // would pop up over the floating hover preview and hide it.
      img.removeAttribute("title");
    } else {
      img.src = full;
      img.classList.add("expanded");
      img.style.maxWidth = "100%";
      img.style.maxHeight = expandedMaxHeight + "px";
      if (description) img.title = description;
    }
    navigation.setExpanded(img.classList.contains("expanded"));
  });
}

// Keep a reused post/card node's account imagery in sync with its (possibly
// refreshed) status data. Creation code only runs once per node, so avatar and
// banner changes — e.g. the signed-in user saving a new avatar — land here on
// the next reconcile pass.
function syncAvatarBannerComposite(composite, acc) {
  if (!composite || !acc || composite.classList.contains("is-anonymous")) return;
  const av = composite.querySelector(".thumb-avatar:not(.thumb-avatar-fallback)");
  if (av && acc.avatar && av.getAttribute("src") !== acc.avatar) av.src = acc.avatar;
  const bg = composite.querySelector(".banner-bg");
  if (bg && acc.header && bg.dataset.header !== acc.header) {
    bg.dataset.header = acc.header;
    bg.style.backgroundImage = `url("${escapeHTML(acc.header)}")`;
  }
}

function syncPostAccountMedia(el, acc) {
  if (!acc || userSettings.anonymousMode) return;
  const avatar = el.querySelector(":scope > .post-head .avatar");
  if (avatar && acc.avatar && avatar.getAttribute("src") !== acc.avatar) avatar.src = acc.avatar;
  syncAvatarBannerComposite(el.querySelector(":scope > .post-media.is-avatar-banner"), acc);
}

// Lightbox for an account's profile imagery: avatar and banner as gallery
// items, starting on whichever the user clicked. No-op in anonymous mode
// (the on-screen imagery is the placeholder, not the real account's).
function openAccountImageGallery(account, start = "avatar") {
  if (userSettings.anonymousMode) return;
  const items = [];
  if (account?.avatar) items.push({ type: "image", url: account.avatar, preview_url: account.avatar_static || account.avatar });
  if (account?.header) items.push({ type: "image", url: account.header, preview_url: account.header_static || account.header });
  if (!items.length) return;
  galleryModal.open(items, start === "banner" ? items.length - 1 : 0);
}

function attachVideoInteractions(collapsed, {
  full,
  player,
  contract,
}) {
  const expanded = () => !player.hidden;

  // Floating hover-preview overlay (4chan-x style). A second muted,
  // autoplaying, looping <video> is appended to <body> while the user
  // hovers the thumbnail — the same trick we use for image previews.
  // It has no controls, no timeline, no UI chrome; just the video. The
  // user can click the thumbnail to expand the real player (with
  // controls) in place; mousing out dismisses the overlay.
  let overlayVideo = null;
  let lastMouseEvent = null;
  // Remembered playback position so re-hovering resumes instead of restarting.
  // The overlay element is recreated on each hover, so the time lives here.
  let overlayResumeTime = 0;

  const applyOverlayBounds = () => {
    if (!overlayVideo) return;
    overlayVideo.style.maxWidth = Math.max(200, window.innerWidth - (FLOAT_PREVIEW_MARGIN * 2)) + "px";
    overlayVideo.style.maxHeight = Math.max(200, window.innerHeight - (FLOAT_PREVIEW_MARGIN * 2)) + "px";
  };

  const positionOverlay = () => {
    if (!overlayVideo || !lastMouseEvent) return;
    // Same vertical-centre trick as the image overlay: pull the rect
    // fresh because the overlay's true size is only known after
    // loadedmetadata fires.
    const rect = overlayVideo.getBoundingClientRect();
    const yOffset = rect.height > 0 ? -rect.height / 2 : 0;
    positionFloatingNearPointer(overlayVideo, lastMouseEvent, {
      offsetX: IMAGE_PREVIEW_OFFSET_X,
      offsetY: yOffset,
      margin: FLOAT_PREVIEW_MARGIN,
    });
  };

  const hideOverlay = () => {
    if (!overlayVideo) return;
    try {
      overlayVideo.pause();
      overlayResumeTime = overlayVideo.currentTime || 0;
    } catch {}
    overlayVideo.remove();
    overlayVideo = null;
  };

  const showOverlay = (e) => {
    lastMouseEvent = e;
    if (isMediaBlurred(collapsed)) return;
    // See attachImageInteractions: a detached overlay (removed by
    // clearFloatingOverlays) leaves a stale closure ref; treat it as gone.
    if (overlayVideo && !overlayVideo.isConnected) overlayVideo = null;
    if (!full || expanded() || state.ui.suppressImageHover || overlayVideo) return;
    overlayVideo = document.createElement("video");
    overlayVideo.className = "post-overlay-video";
    overlayVideo._ownerEl = collapsed;   // for sweepOrphanOverlays: dies with its thumbnail
    overlayVideo.src = full;
    overlayVideo.muted = !!userSettings.videoThreadHoverMuted;
    overlayVideo.loop = !!userSettings.videoThreadHoverLoop;
    overlayVideo.autoplay = true;
    overlayVideo.playsInline = true;
    overlayVideo.preload = "metadata";
    overlayVideo.addEventListener("loadedmetadata", function () {
      // metadata can arrive after the overlay was dismissed (mouseleave
      // nulls overlayVideo) or replaced. `this` is the firing element;
      // bail unless it's still the active overlay so we don't crash on a
      // null ref or position a stale node.
      if (this !== overlayVideo) return;
      applyOverlayBounds();
      positionOverlay();
      // Resume from where the last hover left off (loop keeps it < duration).
      if (overlayResumeTime > 0 && overlayResumeTime < (this.duration || Infinity)) {
        try { this.currentTime = overlayResumeTime; } catch {}
      }
      playVideoWithAutoplayFallback(this);
    }, { once: true });
    document.body.appendChild(overlayVideo);
    applyOverlayBounds();
    positionOverlay();
  };

  const expand = () => {
    if (!full || isMediaBlurred(collapsed)) return;
    hideOverlay();
    collapsed.style.display = "none";
    player.hidden = false;
    player.style.display = "block";
    contract.hidden = false;
    contract.style.display = "inline";
    // The expand click is a user gesture, so audio is allowed here even when
    // the hover preview had to stay muted.
    player.muted = !!userSettings.videoExpandedMuted;
    player.loop = !!userSettings.videoExpandedLoop;
    player.play().catch(() => {});
  };

  const collapse = () => {
    try { player.pause(); } catch {}
    player.hidden = true;
    player.style.display = "none";
    contract.hidden = true;
    contract.style.display = "none";
    collapsed.style.display = "block";
  };

  collapsed.addEventListener("mouseenter", showOverlay);
  collapsed.addEventListener("mouseleave", hideOverlay);
  collapsed.addEventListener("mousemove", (e) => {
    lastMouseEvent = e;
    if (overlayVideo && !overlayVideo.isConnected) overlayVideo = null;
    if (!overlayVideo) {
      // First mousemove after enter (or after a position reset) — spawn
      // the overlay if it isn't already up. Mirrors attachImageInteractions.
      if (!state.ui.suppressImageHover && !expanded()) showOverlay(e);
      return;
    }
    applyOverlayBounds();
    positionOverlay();
  });
  collapsed.addEventListener("click", (e) => {
    e.stopPropagation();
    expand();
  });

  contract.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    collapse();
  });

  player.addEventListener("click", (e) => e.stopPropagation());
}

function renderAudioAttachment(media, { isOp }) {
  const full = media?.url || "";
  const rawName = urlPathSansQuery(full).split("/").pop() || "audio";
  const truncatedName = truncateHash(rawName, 16);
  const sizeStr = formatBytes(mediaSizeBytes(media));

  const desc = (media?.description || "").trim();

  const m = document.createElement("div");
  m.className = "post-media post-media-audio";

  const fileInfo = document.createElement("div");
  fileInfo.className = "file-info";
  fileInfo.appendChild(document.createTextNode("File: "));
  const fileLink = document.createElement("a");
  fileLink.className = "file-link";
  fileLink.href = safeUrl(full) || "#";
  fileLink.target = "_blank";
  fileLink.rel = "noopener";
  fileLink.textContent = truncatedName;
  fileInfo.appendChild(fileLink);
  if (sizeStr) fileInfo.appendChild(document.createTextNode(` (${sizeStr})`));
  m.appendChild(fileInfo);

  const placeholder = document.createElement("div");
  placeholder.className = "audio-placeholder " + (isOp ? "is-op" : "is-reply");
  if (desc) placeholder.title = desc;
  placeholder.innerHTML = '<i class="fa-solid fa-music"></i>';
  const mimeLabel = document.createElement("span");
  mimeLabel.className = "audio-mime-label";
  mimeLabel.textContent = media.mime_type || "audio";
  placeholder.appendChild(mimeLabel);
  m.appendChild(placeholder);

  return m;
}

function renderDocumentAttachment(media, { isOp }) {
  const full = mediaFullUrl(media);
  const link = safeUrl(full);
  const rawName = urlPathSansQuery(full).split("/").pop() || "document";
  const truncatedName = truncateHash(rawName, 16);
  const sizeStr = formatBytes(mediaSizeBytes(media));
  const desc = (media?.description || "").trim();
  const isPdf = isPdfAttachment(media);

  const m = document.createElement("div");
  m.className = "post-media post-media-document";

  const fileInfo = document.createElement("div");
  fileInfo.className = "file-info";
  fileInfo.appendChild(document.createTextNode("File: "));
  const fileLink = document.createElement("a");
  fileLink.className = "file-link";
  fileLink.href = link || "#";
  fileLink.target = "_blank";
  fileLink.rel = "noopener";
  fileLink.textContent = truncatedName;
  fileInfo.appendChild(fileLink);
  if (sizeStr) fileInfo.appendChild(document.createTextNode(` (${sizeStr})`));
  m.appendChild(fileInfo);

  const tile = document.createElement(link ? "a" : "div");
  tile.className = "document-placeholder " + (isOp ? "is-op" : "is-reply");
  if (link) {
    tile.href = link;
    tile.target = "_blank";
    tile.rel = "noopener";
  }
  if (desc) tile.title = desc;
  const icon = document.createElement("i");
  icon.className = `fa-solid ${isPdf ? "fa-file-pdf" : "fa-file"}`;
  tile.appendChild(icon);
  const label = document.createElement("span");
  label.className = "document-mime-label";
  label.textContent = isPdf ? "PDF document" : (media?.mime_type || "document");
  tile.appendChild(label);
  m.appendChild(tile);

  return m;
}

function renderVideoAttachment(media, { isOp }) {
  const full = media?.url || media?.preview_url || "";
  const poster = preferredVideoPoster(media);

  const m = document.createElement("div");
  m.className = "post-media post-media-video";

  const rawName = urlPathSansQuery(full).split("/").pop() || "video";
  const truncatedName = truncateHash(rawName, 16);
  const sizeStr = formatBytes(mediaSizeBytes(media));
  const dimStr = mediaDimensions(media);
  const desc = (media?.description || "").trim();

  const fileInfo = document.createElement("div");
  fileInfo.className = "file-info";
  fileInfo.appendChild(document.createTextNode("File: "));

  const fileLink = document.createElement("a");
  fileLink.className = "file-link";
  fileLink.href = safeUrl(full) || "#";
  fileLink.target = "_blank";
  fileLink.rel = "noopener";
  fileLink.textContent = truncatedName;
  fileInfo.appendChild(fileLink);

  const contract = document.createElement("a");
  contract.href = "#";
  contract.className = "post-video-contract";
  contract.textContent = "contract";
  contract.hidden = true;
  contract.style.display = "none";
  fileInfo.appendChild(document.createTextNode(" "));
  fileInfo.appendChild(contract);

  const infoParts = [sizeStr, dimStr].filter(Boolean);
  if (infoParts.length) {
    fileInfo.appendChild(document.createTextNode(` (${infoParts.join(", ")})`));
  }

  const collapsed = document.createElement("video");
  collapsed.className = (isOp ? "op-image" : "reply-thumb") + " post-video-collapsed-media";
  collapsed.muted = true;
  collapsed.loop = true;
  collapsed.playsInline = true;
  collapsed.preload = "metadata";
  // aria-label rather than title for the alt text: a tooltip here would pop
  // up over the floating hover preview. It moves to the expanded player.
  collapsed.title = "Expand video";
  collapsed.setAttribute("aria-label", desc || "video");
  if (full) collapsed.src = full;
  if (poster) collapsed.poster = poster;

  const player = document.createElement("video");
  player.className = "post-video-player";
  player.controls = true;
  player.playsInline = true;
  player.preload = "metadata";
  player.hidden = true;
  player.style.display = "none";
  if (desc) player.title = desc;
  if (full) player.src = full;
  if (poster) player.poster = poster;

  m.appendChild(fileInfo);

  const frame = document.createElement("div");
  frame.className = "post-video-frame";
  m.appendChild(frame);
  if (full) {
    frame.appendChild(collapsed);
    frame.appendChild(player);
    attachVideoInteractions(collapsed, {
      full,
      player,
      contract,
    });
  } else {
    const fallback = document.createElement("span");
    fallback.className = "post-video-fallback " + (isOp ? "is-op" : "is-reply");
    fallback.textContent = "video unavailable";
    frame.appendChild(fallback);
  }

  return m;
}

// Badge shown in the top-right corner of a post for non-public visibility.
// Public/unlisted posts get nothing. Only "direct" also tints the post green
// (see .post-block.is-dm); the rest are icon-only.
function visibilityBadge(visibility) {
  switch (visibility) {
    case "direct":       return { icon: "fa-envelope",     title: "Direct message" };
    case "private":      return { icon: "fa-people-group", title: "Followers only" };
    case "conversation": return { icon: "fa-user-lock",    title: "Conversation" };
    default:             return null;
  }
}

function collapseThreadPost(el, acc) {
  if (!el?.parentNode || el.style.display === "none") return;
  const stub = document.createElement("div");
  stub.className = "post-stub";
  const plainName = acc.display_name || acc.username || "Anonymous";
  stub.innerHTML = `<a class="post-restore-link" href="#"><i class="fa-regular fa-square-plus"></i> ${escapeHTML(plainName)}</a>`;
  stub.querySelector(".post-restore-link").addEventListener("click", (e) => {
    e.preventDefault(); el.style.display = ""; stub.remove();
  });
  el.parentNode.insertBefore(stub, el);
  el.style.display = "none";
}

function buildPostOwnerActions(postEl) {
  const ownerActions = document.createElement("span");
  ownerActions.className = "post-foot-owner";

  const editBtn = document.createElement("button");
  editBtn.type = "button";
  editBtn.className = "post-action";
  editBtn.title = "Edit post";
  editBtn.setAttribute("aria-label", "Edit post");
  editBtn.innerHTML = '<i class="fa-solid fa-pen-to-square"></i>';
  editBtn.addEventListener("click", async (e) => {
    e.stopPropagation();
    editBtn.disabled = true;
    try {
      await openComposeEdit(postEl._statusData);
    } finally {
      editBtn.disabled = false;
    }
  });
  ownerActions.appendChild(editBtn);

  ownerActions.appendChild(buildStickyButton(postEl));

  const deleteBtn = document.createElement("button");
  deleteBtn.type = "button";
  deleteBtn.className = "post-action post-action-delete";
  deleteBtn.title = "Delete post";
  deleteBtn.setAttribute("aria-label", "Delete post");
  deleteBtn.innerHTML = '<i class="fa-solid fa-trash-can"></i>';
  deleteBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    deletePost(postEl._statusData, deleteBtn);
  });
  ownerActions.appendChild(deleteBtn);
  return ownerActions;
}

// Sticky one of your own posts to the top of your profile. The API calls this
// pinning, and the collection it joins "featured" — every other frontend shows
// it the same way, whatever it calls it.
//
// Only public posts can be stickied: both servers refuse otherwise (mitra's
// `post.is_public()`, pleroma's `activity_is_public`). Rather than let the
// click fail, the button says so.
function buildStickyButton(postEl) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "post-action post-action-sticky";

  const paint = () => {
    const s = postEl._statusData || {};
    const stickied = !!s.pinned;
    const canSticky = s.visibility === "public";
    btn.classList.toggle("is-sticky", stickied);
    btn.disabled = !canSticky;
    btn.title = !canSticky
      ? "Only public posts can be stickied"
      : (stickied ? "Unsticky from profile" : "Sticky to profile");
    btn.setAttribute("aria-label", btn.title);
    btn.setAttribute("aria-pressed", stickied ? "true" : "false");
    btn.innerHTML = '<i class="fa-solid fa-thumbtack"></i>';
  };
  paint();

  btn.addEventListener("click", async (e) => {
    e.stopPropagation();
    const status = postEl._statusData;
    if (!status?.id || btn.disabled) return;
    const next = !status.pinned;
    btn.disabled = true;
    try {
      await setStatusPinned(status.id, next, state);
      status.pinned = next;
      patchCachedStatus(status.id, { pinned: next });
      // The profile's stickied list is now stale wherever it's on screen.
      refreshStickiedForView().catch(() => {});
    } catch (err) {
      alert(`Couldn't ${next ? "sticky" : "unsticky"} the post: ${err.message || err}`);
    } finally {
      paint();
    }
  });
  return btn;
}

function syncPostOwnerActions(postEl, status) {
  const foot = postEl.querySelector(":scope > .post-foot");
  if (!foot) return;
  const existing = foot.querySelector(":scope > .post-foot-owner");
  const shouldShow = !postEl.classList.contains("is-inline") && isOwnAccount(status.account);
  if (shouldShow && !existing) foot.prepend(buildPostOwnerActions(postEl));
  if (!shouldShow && existing) existing.remove();
}

// Copy text to the clipboard. The async Clipboard API needs a secure context,
// so fall back to a throwaway textarea + execCommand on plain http.
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the legacy path */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:-1000px;opacity:0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

function closePostLinkMenus(except) {
  document.querySelectorAll(".post-link-menu-wrap.is-open").forEach((wrap) => {
    if (wrap === except) return;
    wrap.classList.remove("is-open");
    wrap.querySelector(".post-link-btn")?.setAttribute("aria-expanded", "false");
  });
}

// The link (🔗) menu that sits between the datetime and the No. in a post head:
// out-of-app links for this post. Built per post so each item closes over the
// status it belongs to.
function buildPostLinkMenu(wrap, s) {
  const btn = wrap.querySelector(".post-link-btn");
  const menu = document.createElement("div");
  menu.className = "post-link-menu";

  // Items are anchors even though clicking copies rather than navigates: the
  // browser then shows the target URL in the status bar on hover (mitra-web
  // does the same), so you can see where a link points before copying it.
  const addItem = (label, href, onClick, opts = {}) => {
    const item = document.createElement("a");
    item.className = "post-link-menu-item" + (opts.disabled ? " is-disabled" : "");
    item.textContent = label;
    item.dataset.label = label;
    if (href && !opts.disabled) item.href = href;
    if (!opts.disabled) {
      item.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(item);
      });
    }
    menu.appendChild(item);
    return item;
  };

  // Both copy actions share the feedback: swap the label, then close.
  const copyItem = async (item, text) => {
    const ok = await copyToClipboard(text);
    item.textContent = ok ? "Copied!" : "Copy failed";
    setTimeout(() => closePostLinkMenus(), 500);
  };

  const originUrl = s.url || s.uri || "";
  addItem("Copy link to original post", originUrl, (item) => copyItem(item, originUrl),
    { disabled: !originUrl });

  const chanfeUrl = chanfePostUrl(s.id);
  // Quoting is public-facing by nature, so it's offered only on posts that are
  // themselves public (or unlisted).
  const quotable = isQuotableStatus(s);

  // The reference to paste into another post. On Mitra it's the object-link
  // microsyntax, so the post it lands in federates as a quote of this one;
  // elsewhere a plain markdown link to the post itself. Falls back to the
  // chan-fe permalink for posts with no object id to point at.
  const refMarkdown = quoteRefMarkdown(s, { microsyntax: quotable && isMitraServer(state.instance) })
    || `[${postRefLabel(s)}](${chanfeUrl})`;
  // Named for what lands on the clipboard, not for how it renders once posted:
  // the "(You)" and "(Quote)" notes a reference picks up are decided at
  // render time, per reader and per thread, so no copied text carries them.
  const refItem = addItem("Copy >>ref", objectUrlFor(s) || chanfeUrl,
    (item) => copyItem(item, refMarkdown));
  refItem.title = "Reference to this post, to paste into one you're writing";

  const quoteItem = addItem("Quote post", chanfeUrl, () => {
    closePostLinkMenus();
    openComposeQuote(s);
  }, { disabled: !quotable });
  if (!quotable) quoteItem.title = "Only public posts can be quoted";

  wrap.appendChild(menu);
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    e.preventDefault();
    const open = !wrap.classList.contains("is-open");
    closePostLinkMenus(wrap);
    wrap.classList.toggle("is-open", open);
    btn.setAttribute("aria-expanded", open ? "true" : "false");
    // Reset any "Copied!" label from a previous open.
    if (open) menu.querySelectorAll(".post-link-menu-item").forEach((item) => {
      item.textContent = item.dataset.label;
    });
  });
}

function threadPostBlock(s, isOp, depth = 0, opts = {}) {
  const isInline = !!opts.inline;
  // An index row is a post rendered outside its own thread view, alongside
  // posts from other threads. It carries no element id: `p<id>` has to stay
  // unique on the page, and on the index the same post can appear both as a
  // row's OP and inside another row.
  const isIndexRow = !!opts.index;
  const inlineDepth = Number(opts.inlineDepth || 0);

  // Extract leading <h1> as a summary (thread subject).
  const extracted = extractSummary(s);

  // OPs are never marked — the "you" indicators are for tracking your
  // involvement in the conversation, and the OP is the conversation root.
  const isOwn = !isOp && isOwnAccount(s.account);
  const repliesToYou = !isOp && isReplyingToYou(s);
  const isDM = s.visibility === "direct";

  const el = document.createElement("article");
  el.className = "post-block"
    + (isOp       ? " is-op"              : "")
    + (isInline   ? " is-inline"          : "")
    + (isOwn      ? " is-own"             : "")
    + (repliesToYou ? " is-replying-to-you" : "")
    + (isDM       ? " is-dm"              : "");
  if (!isInline && !isIndexRow) el.id = "p" + s.id;
  el.dataset.id = s.id;
  el.dataset.depth = String(Math.min(depth, 5));
  el.dataset.inlineDepth = String(inlineDepth);

  const acc = s.account || {};
  const attachments = s.media_attachments || [];
  const firstImage = attachments.find((a) => a.type === "image");
  const firstVideo = attachments.find((a) => a.type === "video");
  const firstAudio = attachments.find((a) => a.type === "audio");
  const firstDocument = attachments.find(isDocumentAttachment);
  const fullHandle = acc.acct || acc.username || "";
  const postNum = postRef(s) || "Post";

  const head = document.createElement("div");
  head.className = "post-head";
  const nameHTML = renderTextWithCustomEmoji(acc.display_name || acc.username || "Anonymous", acc.emojis || []);
  const dt = new Date(s.created_at);
  // 4chan datetime format: "MM/DD/YY(Day)HH:MM:SS"
  const pad2 = (n) => String(n).padStart(2, "0");
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dateStr = `${pad2(dt.getMonth() + 1)}/${pad2(dt.getDate())}/${String(dt.getFullYear()).slice(-2)}(${days[dt.getUTCDay()]})`;
  const timeStr = `${pad2(dt.getUTCHours())}:${pad2(dt.getUTCMinutes())}:${pad2(dt.getUTCSeconds())}`;

  // Build subject inline (4chan style: subject inside postInfo row, before the datetime, for OPs only)
  const subjectHTML = (isOp && extracted.summary)
    ? `<span class="post-subject">${escapeHTML(extracted.summary)}</span> `
    : "";

  // Group thread: green /boardname/ chip on the OP's info row linking to the
  // board, so a thread opened from /home/ or /twkn/ shows where it lives.
  const opGroupAcct = isOp ? (s.group?.acct || s.group?.username || "") : "";
  const boardChipHTML = opGroupAcct
    ? `<a class="post-board-chip" href="${groupBoardHref(opGroupAcct)}" title="${escapeHTML(boardBadgeTitle(opGroupAcct))}">/${escapeHTML(String(opGroupAcct).split("@")[0].toLowerCase())}/</a> `
    : "";

  head.innerHTML = `
    ${!userSettings.anonymousMode && acc.avatar ? `<img class="avatar" src="${escapeHTML(acc.avatar)}" alt="" loading="lazy">` : ""}
    <a class="poster-name" href="${profileHref(fullHandle)}" title="@${escapeHTML(fullHandle)}">${nameHTML}</a>
    ${boardChipHTML}${subjectHTML}<span class="post-datetime">${dateStr}${timeStr}</span>
    <span class="post-link-menu-wrap"><button type="button" class="post-link-btn" title="Post links" aria-label="Post links" aria-haspopup="true" aria-expanded="false"><i class="fa-solid fa-link"></i></button></span>
    <a class="post-no" href="#p${escapeHTML(s.id)}" title="Link to this post">No.</a><a class="post-id" href="#p${escapeHTML(s.id)}" title="Reply to this post">${escapeHTML(postNum)}</a>${stickyIconHTML(isOp && isStickiedOp(s.id))}
  `;
  const linkMenuWrap = head.querySelector(".post-link-menu-wrap");
  if (linkMenuWrap) buildPostLinkMenu(linkMenuWrap, s);

  const replyFromLinks = buildReplyFromLinksInline(s.id, el);
  if (replyFromLinks) head.appendChild(replyFromLinks);

  el.appendChild(head);

  if (!isOp && !isInline) {
    const hideBtn = document.createElement("button");
    hideBtn.className = "post-hide-btn";
    hideBtn.title = "Hide post";
    hideBtn.innerHTML = '<i class="fa-regular fa-square-minus"></i>';
    hideBtn.addEventListener("click", () => collapseThreadPost(el, acc));
    el.appendChild(hideBtn);
  }

  // The same control on an index row's OP hides the whole thread — filled
  // square rather than outlined, the way a board distinguishes the two. It
  // reuses the catalog's hidden-threads list, so a thread hidden here is
  // hidden there too and comes back from the watcher's Hidden Threads tab.
  if (isOp && isIndexRow) {
    const hideBtn = document.createElement("button");
    hideBtn.type = "button";
    hideBtn.className = "post-hide-btn";
    hideBtn.title = "Hide thread";
    hideBtn.setAttribute("aria-label", "Hide thread");
    hideBtn.innerHTML = '<i class="fa-solid fa-square-minus"></i>';
    const label = extracted.summary
      || compactSnippet(stripHTML(extracted.strippedContent || ""), 60);
    hideBtn.addEventListener("click", () => hideThread(s.id, label));
    el.appendChild(hideBtn);
  }

  const visBadge = visibilityBadge(s.visibility);
  if (visBadge) {
    el.classList.add("has-vis-badge");   // reserves head space so the badge can't overlap the No./reply-from links
    const badge = document.createElement("span");
    badge.className = "post-dm-badge";
    badge.title = visBadge.title;
    badge.innerHTML = `<i class="fa-solid ${visBadge.icon}"></i>`;
    el.appendChild(badge);
  }

  let mediaContainer = null;   // the .post-media box, for the gallery link below
  if (firstImage) {
    const m = document.createElement("div");
    m.className = "post-media";

    const thumb = firstImage.preview_url || firstImage.url || "";
    const full = firstImage.url || firstImage.preview_url || "";
    const desc = (firstImage.description || "").trim();
    const img = document.createElement("img");
    // alt (not title): keeps the accessible name without a native tooltip
    // over the thumbnail's hover preview. The tooltip comes back on expand.
    img.alt = desc;
    img.loading = "lazy";
    img.className = isOp ? "op-image" : "reply-thumb";
    img.src = isOp ? full : thumb;

    const fileInfo = document.createElement("div");
    fileInfo.className = "file-info";
    const updateFileInfo = (media) => {
      const url = mediaFullUrl(media);
      const rawName = urlPathSansQuery(url).split("/").pop() || "image";
      const infoParts = [formatBytes(mediaSizeBytes(media)), mediaDimensions(media)].filter(Boolean);
      fileInfo.innerHTML = `File: <a class="file-link" href="${escapeHTML(safeUrl(url) || "#")}" target="_blank" rel="noopener">${escapeHTML(truncateHash(rawName, 16))}</a>${
        infoParts.length ? ` (${escapeHTML(infoParts.join(", "))})` : ""
      }`;
    };
    updateFileInfo(firstImage);

    m.appendChild(fileInfo);

    const frame = document.createElement("div");
    frame.className = "post-image-frame";
    frame.appendChild(img);
    addMediaBlurControls(frame, s);
    m.appendChild(frame);

    attachImageInteractions(img, {
      thumb: isOp ? full : thumb,
      full,
      description: desc,
      // Post nodes survive reconciliation; read refreshed attachments at the
      // time of the click instead of capturing their render-time objects.
      getImages: () => (el._statusData || s).media_attachments?.filter((a) => a.type === "image") || [],
      onImageChange: updateFileInfo,
      useFullThumbnail: isOp,
      collapsedMaxWidth: isOp ? 250 : 125,
      collapsedMaxHeight: isOp ? 250 : 125,
      expandedMaxHeight: isOp ? 700 : 600,
    });

    el.appendChild(m);
    mediaContainer = m;
  } else if (firstVideo) {
    mediaContainer = renderVideoAttachment(firstVideo, { isOp });
    addMediaBlurControls(mediaContainer.querySelector(".post-video-frame"), s);
    el.appendChild(mediaContainer);
  } else if (firstAudio) {
    mediaContainer = renderAudioAttachment(firstAudio, { isOp });
    el.appendChild(mediaContainer);
  } else if (firstDocument) {
    mediaContainer = renderDocumentAttachment(firstDocument, { isOp });
    el.appendChild(mediaContainer);
  } else if (isOp && !firstImage && !firstVideo && !firstAudio && !firstDocument) {
    const m = document.createElement("div");
    m.className = "post-media is-avatar-banner";
    if (userSettings.anonymousMode) {
      m.classList.add("is-anonymous");
      const av = document.createElement("img");
      av.className = "thumb-avatar";
      av.src = "./images/anon_avatar.png";
      av.alt = "Anonymous";
      m.appendChild(av);
    } else if (acc.header) {
      const bg = document.createElement("div");
      bg.className = "banner-bg";
      bg.style.backgroundImage = `url("${escapeHTML(acc.header)}")`;
      m.appendChild(bg);
    } else {
      const hue = stringHue(fullHandle);
      m.style.background = `linear-gradient(135deg, hsl(${hue}, 35%, 22%), hsl(${(hue + 40) % 360}, 25%, 14%))`;
    }
    if (!userSettings.anonymousMode) {
      if (acc.avatar) {
        const av = document.createElement("img");
        av.className = "thumb-avatar";
        av.src = acc.avatar;
        av.alt = "";
        av.loading = "lazy";
        m.appendChild(av);
      } else {
        const ph = document.createElement("div");
        ph.className = "thumb-avatar thumb-avatar-fallback";
        ph.textContent = (acc.display_name || acc.username || "A").charAt(0).toUpperCase();
        m.appendChild(ph);
      }
    }
    // Expand the OP's profile imagery in the gallery lightbox — avatar when
    // the avatar was clicked, banner otherwise. Account is read live from
    // _statusData (not the render-time `acc`) so a profile edit that swapped
    // the account object opens the fresh images.
    m.addEventListener("click", (e) => {
      const account = el._statusData?.account;
      if (!account) return;
      e.stopPropagation();
      const onAvatar = e.target.closest?.(".thumb-avatar");
      openAccountImageGallery(account, onAvatar ? "avatar" : "banner");
    });
    addMediaBlurControls(m, s);
    el.appendChild(m);
  }

  // More than one attachment → offer a gallery, centered under the thumbnail.
  if (mediaContainer && attachments.length > 1) {
    const galleryLink = document.createElement("a");
    galleryLink.href = "#";
    galleryLink.className = "post-gallery-link";
    galleryLink.innerHTML = `<i class="fa-solid fa-images"></i> Gallery (${attachments.length})`;
    galleryLink.addEventListener("click", (e) => {
      e.preventDefault();
      galleryModal.open(attachments);
    });
    mediaContainer.appendChild(galleryLink);
  }

  const hasContent = stripHTML(extracted.strippedContent).trim().length > 0;
  if (s.spoiler_text) {
    const sp = document.createElement("div");
    sp.className = "spoiler";
    sp.textContent = "⚠ " + s.spoiler_text;
    el.appendChild(sp);
  }

  // Referenced posts (FEP-e232 object links) arrive inline on the status.
  // Registering them lets the `>>ref` links in this body — and in any other
  // post that names the same target — hover-preview and expand without a fetch.
  const quoted = indexQuotedStatuses(s);

  // The body is built before the reply-to line is placed: a post that already
  // names its parent in its own text (which is what chan-fe writes now) doesn't
  // need a synthesized line saying the same thing again. Posts from other
  // frontends carry no such reference, so they still get one.
  let body = null;
  // Some Mastodon-compatible APIs return the quoted Status in metadata but
  // omit the corresponding anchor from `content`. The metadata is enough to
  // render a real reference, so a quote-only post still needs a body.
  if (hasContent || quoted.length) {
    body = document.createElement("div");
    body.className = "post-body";
    if (hasContent) {
      body.innerHTML = renderSafeHTML(extracted.strippedContent, s.emojis || [], { mentions: s.mentions });
    }
    // Turn links to referenced posts into imageboard >>quotelinks.
    rewriteQuoteLinks(body, quoted);
    // Not every server includes those links in content. Fill any omissions
    // from the quote metadata, always on their own line at the top.
    ensureQuoteRefLines(body, quoted);
    // Turn YouTube / direct-media links into 4chan-x-style embed lines.
    embedMediaLinks(body);

    // A post whose content was only a duplicate reference now has an empty
    // body — skip it so there's no stray gap.
    const stillHasContent = body.textContent.trim().length > 0 || !!body.querySelector("img, .custom-emoji");
    if (!stillHasContent) body = null;
  }

  const parentId = currentThreadGraph()?.parentById.get(s.id) || null;
  const replyToBlock = bodyReferencesPost(body, parentId) ? null : buildReplyToBlock(s.id, el);
  if (replyToBlock) el.appendChild(replyToBlock);

  if (body) {
    el.appendChild(body);

    // Hide leading `@user` mentions by default and put a toggle next to
    // the reply-to link (or inline in the body for posts without one).
    const mentionsToggle = (userSettings.collapseLeadingMentions || userSettings.anonymousMode)
      ? extractLeadingMentions(body)
      : null;
    if (mentionsToggle) {
      if (userSettings.anonymousMode) {
        // Anonymous mode keeps the leading identity prefix collapsed without
        // exposing a control that could reveal it.
        mentionsToggle.remove();
      } else if (replyToBlock) {
        replyToBlock.appendChild(mentionsToggle);
      } else {
        const firstWrap = body.querySelector(".post-mentions");
        if (firstWrap?.parentNode) {
          firstWrap.parentNode.insertBefore(mentionsToggle, firstWrap);
        } else {
          body.insertBefore(mentionsToggle, body.firstChild);
        }
      }
    }

    // After the mentions pass, which reads the body's leading nodes — a
    // reference lifted to the front would otherwise hide the prefix it looks
    // for.
    hoistTrailingRefs(body);
  }

  const pollBlock = buildPollBlock(s);
  if (pollBlock) el.appendChild(pollBlock);

  if (firstAudio?.url) {
    const player = document.createElement("audio");
    player.controls = true;
    player.preload = "metadata";
    player.src = firstAudio.url;
    player.className = "post-audio-player";
    el.appendChild(player);
  }

  const reactionsBlock = buildEmojiReactionsBlock(s);
  if (reactionsBlock) el.appendChild(reactionsBlock);

  const foot = document.createElement("div");
  foot.className = "post-foot";
  const primaryActions = document.createElement("span");
  primaryActions.className = "post-foot-primary";

  const repostCount = Number(s.reblogs_count) || 0;
  const likeCount = Number(s.favourites_count) || 0;
  const isReposted = !!s.reblogged;
  const isLiked = !!s.favourited;
  const isBookmarked = !!s.bookmarked;

  // No reply button here: the .post-id link in the post head already opens the
  // reply composer for this post.
  const repostBtn = document.createElement("button");
  repostBtn.type = "button";
  repostBtn.className = "post-action";
  repostBtn.dataset.act = "repost";
  repostBtn.innerHTML = `<i class="fa-solid fa-repeat"></i> <span class="post-action-count">${countFmt(repostCount)}</span>`;
  applyRepostState(repostBtn, isReposted, countFmt(repostCount), s);
  repostBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleRepost(s.id, repostBtn);
  });
  primaryActions.appendChild(repostBtn);

  const likeBtn = document.createElement("button");
  likeBtn.type = "button";
  likeBtn.className = "post-action";
  likeBtn.dataset.act = "like";
  likeBtn.title = isLiked ? "Unlike" : "Like";
  likeBtn.innerHTML = `<i class="${isLiked ? "fa-solid" : "fa-regular"} fa-heart"></i> <span class="post-action-count">${countFmt(likeCount)}</span>`;
  if (isLiked) likeBtn.classList.add("is-liked");
  likeBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleLike(s.id, likeBtn);
  });
  primaryActions.appendChild(likeBtn);

  const bookmarkBtn = document.createElement("button");
  bookmarkBtn.type = "button";
  bookmarkBtn.className = "post-action";
  bookmarkBtn.dataset.act = "bookmark";
  bookmarkBtn.innerHTML = `<i class="${isBookmarked ? "fa-solid" : "fa-regular"} fa-bookmark"></i>`;
  applyBookmarkState(bookmarkBtn, isBookmarked);
  bookmarkBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleBookmark(s.id, bookmarkBtn);
  });
  primaryActions.appendChild(bookmarkBtn);

  const emojiReactBtn = document.createElement("button");
  emojiReactBtn.type = "button";
  emojiReactBtn.className = "post-action";
  emojiReactBtn.dataset.act = "react";
  emojiReactBtn.title = "Emoji react";
  emojiReactBtn.innerHTML = `<i class="fa-solid fa-face-grin-tears"></i>`;
  emojiReactBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    openEmojiReactPicker(s.id);
  });
  primaryActions.appendChild(emojiReactBtn);

  if (isOp && !isInline) {
    const watchTitle = extracted.summary
      || compactSnippet(stripHTML(extracted.strippedContent), 60)
      || postRefLabel(s);
    const watchBtn = document.createElement("button");
    watchBtn.type = "button";
    watchBtn.className = "post-action";
    watchBtn.dataset.act = "watch";
    watchBtn.innerHTML = '<i class="fa-solid fa-eye"></i>';
    applyCatalogWatchState(watchBtn, isThreadWatched(s.id));
    watchBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleWatchThread(s.id, watchTitle);
      applyCatalogWatchState(watchBtn, isThreadWatched(s.id));
    });
    primaryActions.appendChild(watchBtn);
  }

  foot.appendChild(primaryActions);
  el.appendChild(foot);

  // A board puts the OP's file first in the markup, so the post's own info
  // line starts *beside* the image rather than above it, and the float's top
  // edge is the top of the thread. The blocks above are built in reading
  // order, so the move happens here rather than threading an insertion point
  // through every attachment branch. Replies keep file-after-info, which is
  // the order a board uses for them too.
  if (isOp) {
    const media = el.querySelector(":scope > .post-media");
    if (media) el.insertBefore(media, el.firstChild);
  }

  // Keep a direct reference for preview and inline stacking.
  el._statusData = s;
  syncPostOwnerActions(el, s);

  return el;
}

// ---- reply modal — see openComposeReply() further below ----

// ---- emoji picker (unicode + instance custom emojis) ----
//
// Loads `/api/v1/custom_emojis` from the instance and renders a search dialog.
// The picker has two modes:
//   - "react": pick an emoji to apply as a pleroma custom reaction on a post
//   - "compose": pick an emoji to insert into a textarea (for future compose)
//
// We deliberately do NOT cache the custom list. It's small JSON and apiFetch
// is already cache-busted, so every fetch is current — a persistent list cache
// used to hide brand-new emojis even across reloads. The emoji *image files*
// are referenced by <img src>, so the browser's HTTP cache still dedupes
// them: known emojis aren't re-downloaded, only newly-added ones. Within a
// session the result is held in memory (EmojiPicker._emojis); the picker's
// refresh button drops it to pull in emojis added since the tab opened.
//
// Unicode emoji are searchable by their familiar shortcode/name aliases. Only
// the six configurable favorites are shown until the user starts searching.
// The full index is generated from Unicode/CLDR; emoji.js holds curated aliases.

const EMOJI_FAVORITES_STORAGE_KEY = "chan-fe:emoji-favorites:v1";
const EMOJI_PICKER_RESULT_LIMIT = 300;

function emojiKey(e) {
  return e?.type === "custom" ? `custom:${e.shortcode}` : `unicode:${e?.text}`;
}

function loadEmojiFavoriteKeys() {
  try {
    const saved = JSON.parse(localStorage.getItem(EMOJI_FAVORITES_STORAGE_KEY) || "null");
    if (Array.isArray(saved) && saved.length === 6 && saved.every((key) => typeof key === "string")) return saved;
  } catch {}
  return DEFAULT_EMOJI_FAVORITES.map((shortcode) => `unicode:${shortcode}`);
}

async function fetchCustomEmojisFromServer() {
  const tagCustom = (e) => ({ type: "custom", shortcode: e.shortcode, url: e.url });
  try {
    const r = await apiFetch("/api/v1/custom_emojis");
    if (!r.ok) return [];
    const data = await r.json();
    return data
      .filter((e) => e && e.shortcode && e.url)
      .map(tagCustom);
  } catch {
    return [];
  }
}

function loadEmojis() {
  // Returns { custom, unicode }. Fetched fresh every time (no list cache) —
  // see the comment block above.
  return (async () => {
    const [custom, unicodeList] = await Promise.all([
      fetchCustomEmojisFromServer(),
      loadUnicodeEmojis(),
    ]);
    // Tag unicode entries so the picker can distinguish them.
    const unicode = unicodeList.map((e) => ({ type: "unicode", ...e }));
    return { custom, unicode };
  })();
}

// Session-cached custom-emoji list for the compose autocomplete. The picker
// above intentionally refetches to pick up newly-added emojis, but the
// autocomplete fires on every keystroke — a per-keystroke request would be
// wasteful and laggy. One fetch per session is a fine trade for typeahead.
let _customEmojiCache = null;
let _customEmojiLoading = null;
function getCustomEmojisCached() {
  if (_customEmojiCache) return Promise.resolve(_customEmojiCache);
  if (_customEmojiLoading) return _customEmojiLoading;
  _customEmojiLoading = fetchCustomEmojisFromServer().then((list) => {
    _customEmojiCache = list;
    _customEmojiLoading = null;
    return list;
  }).catch(() => {
    _customEmojiLoading = null;
    return [];
  });
  return _customEmojiLoading;
}

class EmojiPicker {
  constructor() {
    this.el      = document.getElementById("emoji-picker");
    this.body    = document.getElementById("emoji-picker-body");
    this.search  = document.getElementById("emoji-picker-search");
    this.status  = document.getElementById("emoji-picker-status");
    this.help    = document.getElementById("emoji-picker-help");
    this.closeBtn = document.getElementById("emoji-picker-close");
    this.refreshBtn = document.getElementById("emoji-picker-refresh");
    this.title   = document.getElementById("emoji-picker-title");

    this._context = null;
    this._emojis  = null;
    this._loading = null;
    this._favoriteKeys = loadEmojiFavoriteKeys();
    this._editingFavorites = false;
    this._favoriteSlot = null;
    this._renderSearch = this._debounce(() => this._render(), 60);

    this.search.addEventListener("input", () => this._renderSearch());
    this.closeBtn.addEventListener("click", () => this.close());
    this.refreshBtn.addEventListener("click", () => this.refresh());
    addBackdropClose(this.el, () => this.close());   // backdrop click
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.el.classList.contains("is-open")) {
        this.close();
      }
    });
  }

  _debounce(fn, ms) {
    let h = null;
    return (...args) => {
      if (h) clearTimeout(h);
      h = setTimeout(() => { h = null; fn(...args); }, ms);
    };
  }

  async open(context) {
    this._context = context;
    if (context.mode === "react") {
      this.title.textContent = "React to post";
      this.help.innerHTML = "Click an emoji to add a reaction. Click an existing one to toggle yours.";
    } else if (context.mode === "compose") {
      this.title.textContent = "Insert emoji";
      this.help.innerHTML = 'Custom emojis insert as <code>:shortcode:</code>; unicode emojis insert as the character.';
    } else {
      this.title.textContent = "Emoji";
      this.help.innerHTML = "";
    }
    this.el.hidden = false;
    this.el.classList.add("is-open");
    this._editingFavorites = false;
    this._favoriteSlot = null;
    this.search.value = "";
    this.body.innerHTML = '<div class="empty">loading…</div>';
    this.status.textContent = "";
    setTimeout(() => this.search.focus(), 30);
    await this._ensureLoaded();
    this._render();
  }

  close() {
    this.el.classList.remove("is-open");
    this.el.hidden = true;
    this._context = null;
    this._editingFavorites = false;
    this._favoriteSlot = null;
  }

  // Drop the in-memory list and re-fetch, so emojis added on the instance
  // since this tab opened show up without a page reload.
  async refresh() {
    this._emojis = null;
    this._loading = null;
    this.search.value = "";
    this.body.innerHTML = '<div class="empty">loading…</div>';
    this.status.textContent = "";
    await this._ensureLoaded();
    this._render();
  }

  isOpen() {
    return this.el.classList.contains("is-open");
  }

  async _ensureLoaded() {
    if (this._emojis) return this._emojis;
    if (this._loading) return this._loading;
    this._loading = loadEmojis().then((res) => {
      this._emojis = res;
      this._loading = null;
      return res;
    }).catch((e) => {
      this._loading = null;
      this.body.innerHTML = `<div class="empty">failed to load emojis: ${escapeHTML(String(e?.message || e))}</div>`;
      throw e;
    });
    return this._loading;
  }

  _render() {
    if (!this._emojis) return;
    const q = (this.search.value || "").trim();
    const rankAndLimit = (list) => rankEmojiMatches(list, q.toLowerCase())
      .slice(0, EMOJI_PICKER_RESULT_LIMIT);
    const customMatches = filterEmojiList(this._emojis.custom, q);
    const unicodeMatches = q ? filterEmojiList(this._emojis.unicode, q) : [];
    const custom = q ? rankAndLimit(customMatches) : customMatches;
    const unicode = q ? rankAndLimit(unicodeMatches) : [];
    const favorites = this._resolveFavorites();
    const total = customMatches.length + unicodeMatches.length;
    const shown = custom.length + unicode.length;

    this.body.innerHTML = "";
    this.body.appendChild(this._renderFavorites(favorites));
    if (q && !total) {
      const empty = document.createElement("div");
      empty.className = "empty";
      empty.textContent = `no emojis match “${q}”`;
      this.body.appendChild(empty);
    } else {
      if (unicode.length) this.body.appendChild(this._renderSection("Unicode", unicode));
      if (custom.length)  this.body.appendChild(this._renderSection("Reactions", custom));
    }
    this.status.textContent = q
      ? (shown < total ? `${shown} of ${total} matches` : (total === 1 ? "1 match" : `${total} matches`))
      : "";
  }

  _resolveFavorites() {
    const available = new Map();
    for (const e of [...this._emojis.unicode, ...this._emojis.custom]) {
      available.set(emojiKey(e), e);
      // Favorites saved before glyph-based keys used the curated shortcode.
      if (e.type === "unicode") available.set(`unicode:${e.shortcode}`, e);
    }
    return this._favoriteKeys.map((key, index) =>
      available.get(key) || this._emojis.unicode.find((e) => e.shortcode === DEFAULT_EMOJI_FAVORITES[index]),
    ).filter(Boolean);
  }

  _renderFavorites(items) {
    const sec = document.createElement("div");
    sec.className = "emoji-picker-section emoji-picker-favorites";
    const grid = document.createElement("div");
    grid.className = "emoji-picker-grid";
    items.forEach((e, index) => grid.appendChild(this._renderCell(e, { favoriteIndex: index })));
    sec.appendChild(grid);

    const edit = document.createElement("button");
    edit.type = "button";
    edit.className = "emoji-picker-favorites-edit";
    edit.textContent = this._editingFavorites ? "cancel" : "edit";
    edit.addEventListener("click", () => {
      this._editingFavorites = !this._editingFavorites;
      this._favoriteSlot = null;
      this._render();
    });
    sec.appendChild(edit);
    return sec;
  }

  _renderSection(title, items) {
    const sec = document.createElement("div");
    sec.className = "emoji-picker-section";
    const h = document.createElement("div");
    h.className = "emoji-picker-section-title";
    h.textContent = title;
    sec.appendChild(h);
    const grid = document.createElement("div");
    grid.className = "emoji-picker-grid";
    for (const e of items) grid.appendChild(this._renderCell(e));
    sec.appendChild(grid);
    return sec;
  }

  _renderCell(e, { favoriteIndex = null } = {}) {
    const cell = document.createElement("button");
    cell.type = "button";
    cell.className = "emoji-picker-cell";
    if (favoriteIndex != null && this._editingFavorites) {
      cell.classList.add("is-editing");
      if (favoriteIndex === this._favoriteSlot) cell.classList.add("is-replacing");
    }
    cell.title = e.type === "custom"
      ? `:${e.shortcode}:`
      : `${e.text} (${e.terms?.[0] || ""})`;

    if (e.type === "custom") {
      const img = document.createElement("img");
      img.className = "emoji-picker-cell-img";
      img.src = e.url;
      img.alt = `:${e.shortcode}:`;
      img.loading = "lazy";
      cell.appendChild(img);
    } else {
      const span = document.createElement("span");
      span.className = "emoji-picker-cell-unicode";
      span.textContent = e.text;
      cell.appendChild(span);
    }

    const name = document.createElement("span");
    name.className = "emoji-picker-cell-name";
    name.textContent = e.type === "custom" ? `:${e.shortcode}:` : (e.shortcode || e.text);
    cell.appendChild(name);

    if (favoriteIndex != null && this._editingFavorites) {
      const prompt = document.createElement("span");
      prompt.className = "emoji-picker-cell-edit-prompt";
      prompt.textContent = "Click and search to replace";
      cell.appendChild(prompt);
    }

    cell.addEventListener("click", () => {
      if (this._editingFavorites) {
        if (favoriteIndex != null) {
          this._favoriteSlot = favoriteIndex;
          this.search.focus();
          this._render();
        } else if (this._favoriteSlot != null) {
          this._favoriteKeys[this._favoriteSlot] = emojiKey(e);
          try { localStorage.setItem(EMOJI_FAVORITES_STORAGE_KEY, JSON.stringify(this._favoriteKeys)); } catch {}
          this._editingFavorites = false;
          this._favoriteSlot = null;
          this.search.value = "";
          this._render();
        }
        return;
      }
      this._onPick(e);
    });
    return cell;
  }

  async _onPick(emoji) {
    const ctx = this._context;
    if (!ctx) return;
    if (ctx.mode === "react") {
      this.close();
      await reactWithEmoji(ctx.postId, emoji);
    } else if (ctx.mode === "compose") {
      const insert = emoji.type === "custom" ? `:${emoji.shortcode}:` : emoji.text;
      this.close();
      try { ctx.onPick?.(insert, emoji); } catch {}
    }
  }
}

const emojiPicker = new EmojiPicker();

// Open the picker. The opener is the post whose reactions we're modifying.
// `postId` is the status ID; the picker reads/writes pleroma emoji reactions
// on that post via PUT/DELETE /api/v1/pleroma/statuses/<id>/reactions/<emoji>.
async function openEmojiReactPicker(postId) {
  if (!state.auth.accessToken) {
    alert("Sign in to react to this post.");
    return;
  }
  await emojiPicker.open({ mode: "react", postId });
}

// Apply an emoji as a reaction on a post, then patch the post's reactions
// block in the DOM. `emoji` is either { type: "custom", shortcode, url } or
// { type: "unicode", text, shortcode }. Falls through to the existing
// toggle path for reactions that already have a button; adds a new button
// for reactions the user just introduced.
async function reactWithEmoji(postId, emoji) {
  const name = emoji.type === "custom" ? emoji.shortcode : emoji.text;
  const url  = emoji.type === "custom" ? emoji.url : null;

  const hostEl = document.querySelector(`.post-block[data-id="${cssEscape(postId)}"]`);

  // No in-view post block to patch (e.g. reacting from a catalog card, which is
  // a .thread-card with no reactions row) — just fire the API. Without this the
  // reaction was silently dropped from catalog view.
  if (!hostEl) {
    try {
      const r = await apiFetch(
        `/api/v1/pleroma/statuses/${encodeURIComponent(postId)}/reactions/${encodeURIComponent(name)}`,
        { method: "PUT" }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (e) {
      alert("Failed to react: " + e.message);
    }
    return;
  }

  const wrap = hostEl.querySelector(".post-reactions");
  const existingBtn = wrap
    ? Array.from(wrap.querySelectorAll(".post-reaction-btn")).find((b) => {
        const bUrl = b.dataset.url || null;
        if (emoji.type === "custom") {
          return b.dataset.name === name && bUrl === (emoji.url || null);
        }
        return b.dataset.name === name && !bUrl;
      })
    : null;

  if (existingBtn) {
    // Already a button: toggle the user's reaction through the existing
    // optimistic-API flow. toggleEmojiReaction is the source of truth.
    await toggleEmojiReaction(postId, name, url, existingBtn, wrap);
    return;
  }

  // New reaction: optimistically add a button, then call the API.
  let reactionsBlock = wrap;
  if (!reactionsBlock) {
    reactionsBlock = document.createElement("div");
    reactionsBlock.className = "post-reactions";
    hostEl.appendChild(reactionsBlock);
  }

  const btn = buildReactionButton({ name, url, count: 1, me: true }, postId, reactionsBlock);
  reactionsBlock.appendChild(btn);

  try {
    const r = await apiFetch(
      `/api/v1/pleroma/statuses/${encodeURIComponent(postId)}/reactions/${encodeURIComponent(name)}`,
      { method: "PUT" }
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const updated = await r.json();
    const updatedReactions = updated?.pleroma?.emoji_reactions || [];
    const match = updatedReactions.find((x) => x && x.name === name && (x.url || null) === (url || null));
    if (match) {
      btn.classList.toggle("is-mine", !!match.me);
      btn.querySelector(".post-reaction-count").textContent = String(match.count || 0);
    } else {
      // Server disagrees: drop our optimistic button.
      btn.remove();
      if (reactionsBlock && !reactionsBlock.children.length) reactionsBlock.remove();
    }
  } catch (e) {
    btn.remove();
    if (reactionsBlock && !reactionsBlock.children.length) reactionsBlock.remove();
    alert("Failed to react: " + e.message);
  }
}

// The native tooltip on a reaction chip says ":name: · count" — exactly what
// the activity popup says, at more length, when you hover. So the chip drops
// it whenever the popup will actually answer, and keeps it when it won't:
// anonymous mode and the "Show activity list on like, repost, react hover"
// setting both suppress the popup, and so does having nobody to list. Those
// are the same conditions activityHoverPopup checks before opening.
function activityHoverAnswers(count) {
  return !userSettings.anonymousMode && !!userSettings.showActivityHover && count > 0;
}

function applyReactionTooltip(btn) {
  if (!btn) return;
  const shown = btn.querySelector(".post-reaction-count")?.textContent || "0";
  const count = Number(String(shown).replace(/[^0-9]/g, "")) || 0;
  if (activityHoverAnswers(count)) btn.removeAttribute("title");
  else btn.title = btn.dataset.tooltip || "";
}

// Reaction chips already on screen when the setting changed.
function syncReactionTooltips(root = document) {
  root.querySelectorAll(".post-reaction-btn").forEach(applyReactionTooltip);
}

function buildReactionButton(r, postId, wrap) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "post-reaction-btn" + (r.me ? " is-mine" : "");
  btn.dataset.name = r.name;
  btn.dataset.url = r.url || "";
  btn.dataset.tooltip = `:${r.name}: · ${r.count || 0}`;

  if (r.url) {
    const img = document.createElement("img");
    img.className = "post-reaction-emoji";
    img.src = r.url;
    img.alt = `:${r.name}:`;
    img.loading = "lazy";
    btn.appendChild(img);
  } else {
    const span = document.createElement("span");
    span.className = "post-reaction-emoji-unicode";
    span.textContent = r.name;
    btn.appendChild(span);
  }

  const count = document.createElement("span");
  count.className = "post-reaction-count";
  count.textContent = String(r.count || 0);
  btn.appendChild(count);
  applyReactionTooltip(btn);

  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleEmojiReaction(postId, r.name, r.url, btn, wrap);
  });
  return btn;
}

// cssEscape: lightweight CSS.escape polyfill. CSS.escape is available in
// modern browsers; the fallback handles older runtimes / test envs.
function cssEscape(s) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(String(s));
  }
  return String(s).replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}

// Future compose hook. Callers can pass an `onPick(text, emoji)` to receive
// the inserted text when the user picks an emoji. The picker itself never
// touches any DOM outside the dialog; the caller owns the textarea.
async function openEmojiComposePicker(onPick) {
  await emojiPicker.open({ mode: "compose", onPick });
}

// ---- compose modal ----
// A small dialog for posting status updates. Supports:
//   - new thread: openComposeModal({ mode: "new" })
//   - reply to an existing post: openComposeModal({ mode: "reply", inReplyToId, parent })
//   - edit an owned post: openComposeModal({ mode: "edit", status, source })
//
// Mitra's create-post API (POST /api/v1/statuses) accepts these fields:
//   { status, content_type, language, media_ids[], in_reply_to_id, visibility,
//     sensitive, poll, quote_id }
// There's no subject line — Mitra treats Mastodon's optional `spoiler_text` as
// the "content warning" (a separate header above the body), not a thread
// subject. The NSFW toggle on the form maps to `sensitive`. We default to
// markdown and public visibility; the user has no other knobs to twist.
function composeMaxLength() {
  const limit = Number(state.instance?.configuration?.statuses?.max_characters);
  return Number.isSafeInteger(limit) && limit > 0 ? limit : 100000;
}

async function resolveReplyDestinationOpId(postResult, composeContext) {
  if (composeContext?.threadRootId) return composeContext.threadRootId;

  const currentRoot = getCurrentPostRootId();
  if (currentRoot) return currentRoot;

  const parent = composeContext?.parent;
  if (parent?.id && currentThreadGraph()?.statusById?.size) {
    const byId = currentThreadGraph().statusById;
    const target = byId.get(parent.id) || parent;
    const root = resolveThreadRootId(target, byId);
    if (root) return root;
  }

  if (postResult?.id) {
    try {
      const ctx = await fetchContext(postResult.id, state);
      const root = resolveRootIdFromContext(ctx);
      if (root) return root;
    } catch {
      // Best-effort fallback below.
    }
  }

  return composeContext?.inReplyToId || postResult?.in_reply_to_id || postResult?.id || null;
}

async function navigateAfterComposeResult(postResult, composeContext) {
  if (!postResult?.id) {
    await refreshCurrentView();
    return;
  }

  if (composeContext?.mode === "edit") {
    const rootId = composeContext.threadRootId || postResult.id;
    await loadPost(rootId, { highlightId: postResult.id === rootId ? null : postResult.id });
    return;
  }

  if (composeContext?.mode !== "reply") {
    location.hash = threadHref(postResult.id);
    return;
  }

  const opId = await resolveReplyDestinationOpId(postResult, composeContext);
  if (!opId) {
    await refreshCurrentView();
    return;
  }

  setPendingReplyFocus(opId, postResult.id);

  if (state.view?.kind === "post" && state.view.id === opId) {
    await loadPost(opId);
    ensurePendingReplyFocus();
    return;
  }

  location.hash = threadHref(opId);
}

// Post visibility scopes. Keys are the Mastodon/Mitra API visibility
// ("private" == followers-only, "conversation" == thread participants);
// icon/label drive the menu button and its dropdown. Which scopes are
// offered depends on context — see ComposeModal._visibilityOptionsFor.
const VISIBILITY_META = {
  public:       { icon: "fa-globe",        label: "Public" },
  private:      { icon: "fa-people-group", label: "Followers" },
  conversation: { icon: "fa-user-lock",    label: "Conversation" },
  direct:       { icon: "fa-envelope",     label: "Direct" },
};

// Poll length choices offered in the composer, in seconds. Filtered against
// the instance's min/max expiration when those limits are advertised.
const POLL_DURATIONS = [
  { label: "5 minutes",  value: 300 },
  { label: "30 minutes", value: 1800 },
  { label: "1 hour",     value: 3600 },
  { label: "6 hours",    value: 21600 },
  { label: "1 day",      value: 86400 },
  { label: "3 days",     value: 259200 },
  { label: "7 days",     value: 604800 },
];
const POLL_DEFAULT_DURATION = 86400;

// Poll constraints, read from the instance config when present (Mastodon
// exposes configuration.polls), otherwise sensible Mastodon defaults.
function pollLimits() {
  const p = state.instance?.configuration?.polls || {};
  return {
    maxOptions:    Number(p.max_options) || 4,
    maxChars:      Number(p.max_characters_per_option) || 50,
    minExpiration: Number(p.min_expiration) || 300,
    maxExpiration: Number(p.max_expiration) || 604800,
  };
}

// Standalone modal (like the emoji picker) for building a poll. Opened from the
// compose toolbar; on save it hands a { options[], multiple, expiresIn } object
// back to the compose modal, which owns it until the post is sent.
class PollComposer {
  constructor() {
    this.el          = document.getElementById("poll-composer");
    this.optionsList = document.getElementById("poll-composer-options");
    this.addBtn      = document.getElementById("poll-composer-add");
    this.multiple    = document.getElementById("poll-composer-multiple");
    this.duration    = document.getElementById("poll-composer-duration");
    this.statusEl    = document.getElementById("poll-composer-status");
    this.removeBtn   = document.getElementById("poll-composer-remove");
    this.cancelBtn   = document.getElementById("poll-composer-cancel");
    this.saveBtn     = document.getElementById("poll-composer-save");
    this.closeBtn    = document.getElementById("poll-composer-close");
    this._onSave     = null;

    this.closeBtn.addEventListener("click", () => this.close());
    this.cancelBtn.addEventListener("click", () => this.close());
    addBackdropClose(this.el, () => this.close());
    this.addBtn.addEventListener("click", () => { this._addOption(""); this._focusLastOption(); });
    this.saveBtn.addEventListener("click", () => this._save());
    this.removeBtn.addEventListener("click", () => this._remove());
    document.addEventListener("keydown", (e) => {
      // Close only the poll composer; stop the compose modal's own Escape
      // handler (registered later on document) from also firing and closing
      // the compose modal underneath.
      if (e.key === "Escape" && this.isOpen()) {
        e.stopImmediatePropagation();
        this.close();
      }
    });
  }

  isOpen() { return this.el.classList.contains("is-open"); }

  open({ poll = null, onSave = null } = {}) {
    this._onSave = onSave;
    this._buildDurations(poll?.expiresIn);
    this.multiple.checked = !!poll?.multiple;
    this.optionsList.replaceChildren();
    const seed = poll?.options?.length ? poll.options.slice() : ["", ""];
    for (const o of seed) this._addOption(o);
    while (this.optionsList.children.length < 2) this._addOption("");
    // "Remove poll" only makes sense when editing an already-attached poll.
    this.removeBtn.hidden = !poll;
    this._setStatus("");
    this._syncAddButton();

    this.el.hidden = false;
    this.el.classList.add("is-open");
    setTimeout(() => this.optionsList.querySelector("input")?.focus(), 30);
  }

  close() {
    this.el.classList.remove("is-open");
    this.el.hidden = true;
    this._onSave = null;
  }

  _buildDurations(selected) {
    const { minExpiration, maxExpiration } = pollLimits();
    this.duration.replaceChildren();
    for (const d of POLL_DURATIONS) {
      if (d.value < minExpiration || d.value > maxExpiration) continue;
      const opt = document.createElement("option");
      opt.value = String(d.value);
      opt.textContent = d.label;
      this.duration.appendChild(opt);
    }
    const want = String(selected || POLL_DEFAULT_DURATION);
    const has = Array.from(this.duration.options).some((o) => o.value === want);
    this.duration.value = has ? want
      : (Array.from(this.duration.options).find((o) => o.value === String(POLL_DEFAULT_DURATION))?.value
         || this.duration.options[0]?.value || "");
  }

  _addOption(value = "") {
    const { maxOptions, maxChars } = pollLimits();
    if (this.optionsList.children.length >= maxOptions) return;

    const li = document.createElement("li");
    li.className = "poll-composer-option";

    const input = document.createElement("input");
    input.type = "text";
    input.className = "poll-composer-option-input";
    input.maxLength = maxChars;
    input.value = value || "";
    input.addEventListener("input", () => this._setStatus(""));
    input.addEventListener("keydown", (e) => {
      // Enter adds/moves to the next option instead of submitting.
      if (e.key !== "Enter") return;
      e.preventDefault();
      const next = li.nextElementSibling?.querySelector("input");
      if (next) next.focus();
      else if (this.optionsList.children.length < pollLimits().maxOptions) {
        this._addOption(""); this._focusLastOption();
      }
    });

    const rm = document.createElement("button");
    rm.type = "button";
    rm.className = "poll-composer-option-remove";
    rm.innerHTML = '<i class="fa-solid fa-xmark"></i>';
    rm.title = "Remove option";
    rm.addEventListener("click", () => {
      if (this.optionsList.children.length <= 2) return;   // keep the minimum two
      li.remove();
      this._syncAddButton();
    });

    li.append(input, rm);
    this.optionsList.appendChild(li);
    this._syncAddButton();
  }

  _focusLastOption() {
    const inputs = this.optionsList.querySelectorAll(".poll-composer-option-input");
    inputs[inputs.length - 1]?.focus();
  }

  // Keep placeholders numbered, disable "add" at the cap, and disable the
  // per-row remove when only the minimum two options remain.
  _syncAddButton() {
    const inputs = this.optionsList.querySelectorAll(".poll-composer-option-input");
    inputs.forEach((inp, i) => { inp.placeholder = `Option ${i + 1}`; });
    this.addBtn.disabled = this.optionsList.children.length >= pollLimits().maxOptions;
    const lockRemove = this.optionsList.children.length <= 2;
    this.optionsList.querySelectorAll(".poll-composer-option-remove")
      .forEach((b) => { b.disabled = lockRemove; });
  }

  _save() {
    const options = Array.from(this.optionsList.querySelectorAll(".poll-composer-option-input"))
      .map((i) => i.value.trim())
      .filter(Boolean);
    if (options.length < 2) { this._setStatus("Add at least two options.", "error"); return; }
    if (new Set(options.map((o) => o.toLowerCase())).size !== options.length) {
      this._setStatus("Options must be unique.", "error");
      return;
    }
    const poll = {
      options,
      multiple: !!this.multiple.checked,
      expiresIn: Number(this.duration.value) || POLL_DEFAULT_DURATION,
    };
    const cb = this._onSave;
    this.close();
    try { cb?.(poll); } catch {}
  }

  _remove() {
    const cb = this._onSave;
    this.close();
    try { cb?.(null); } catch {}
  }

  _setStatus(text, kind = "") {
    this.statusEl.textContent = text || "";
    this.statusEl.className = "poll-composer-status" + (kind ? ` is-${kind}` : "");
  }
}

const pollComposer = new PollComposer();

// ---- compose autocomplete --------------------------------------------------
//
// Typeahead for the compose textarea. Two triggers:
//   - ":"  -> emoji shortcodes (case-insensitive). A custom emoji inserts
//             ":shortcode:"; a unicode one inserts the character.
//   - "@"  -> accounts (mentions), inserts "@user" / "@user@instance.tld"
//
// A dropdown is positioned at the caret via a mirror-div technique (we can't
// ask a <textarea> for caret pixel coords directly). The controller is
// provider-driven: each trigger registers an object describing how to search,
// render a row, and turn a pick into replacement text, so the keyboard /
// positioning / show-hide logic is shared.

// Compute the viewport-space coordinates of the caret in a textarea, plus the
// line height, by mirroring its styling into a hidden div and measuring a
// marker span placed at the caret position. Handles the textarea's scroll
// offset (a scrolled-up caret measures high in the unscrolled mirror).
function getCaretCoordinates(ta, at) {
  const rect = ta.getBoundingClientRect();
  const cs = getComputedStyle(ta);
  const div = document.createElement("div");
  const props = [
    "boxSizing", "width", "height", "overflowX", "overflowY",
    "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
    "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
    "fontStyle", "fontVariant", "fontWeight", "fontStretch", "fontSize",
    "fontSizeAdjust", "lineHeight", "fontFamily", "letterSpacing",
    "wordSpacing", "textIndent", "textTransform", "tabSize",
  ];
  for (const k of props) div.style[k] = cs[k];
  div.style.position = "fixed";
  div.style.visibility = "hidden";
  div.style.whiteSpace = "pre-wrap";
  div.style.wordWrap = "break-word";
  div.style.top = rect.top + "px";
  div.style.left = rect.left + "px";
  div.textContent = ta.value.slice(0, at);
  const marker = document.createElement("span");
  marker.textContent = ta.value.slice(at) || "\u200b";   // zero-width so it has a box
  div.appendChild(marker);
  document.body.appendChild(div);
  const m = marker.getBoundingClientRect();
  document.body.removeChild(div);
  return {
    top: m.top - ta.scrollTop,
    left: m.left - ta.scrollLeft,
    lineHeight: parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) * 1.45 || 20),
  };
}

// Find the active autocomplete token at `caret` in `text`. A token is a
// trigger char (":" or "@") at a word boundary followed by a run of body
// chars ending at the caret, with no spaces inside. Returns
// { trigger, query, start, end } or null.
//   - emoji body chars:     [\w]            (shortcode chars)
//   - mention body chars:   [\w.\-+]         (username + @host chars; no "@")
// The trigger must sit at a boundary (start of text, or after a non-body char)
// so "foo@bar.com" doesn't fire a mention mid-token, but " @user" does.
function getAutocompleteToken(text, caret) {
  if (caret <= 0) return null;
  const tries = [
    { trigger: ":", bodyRe: /[\w]/ },
    { trigger: "@", bodyRe: /[\w.\-+]/ },
  ];
  for (const { trigger, bodyRe } of tries) {
    let i = caret;
    while (i > 0 && bodyRe.test(text[i - 1])) i--;
    const tIdx = i - 1;
    if (tIdx < 0 || text[tIdx] !== trigger) continue;
    const before = tIdx > 0 ? text[tIdx - 1] : "";
    if (before !== "" && bodyRe.test(before)) continue;   // not a boundary
    return { trigger, query: text.slice(i, caret), start: tIdx, end: caret };
  }
  return null;
}

class ComposeAutocomplete {
  // textarea: the <textarea> to attach to. providers: array of
  //   { trigger, minQuery, search(query)->Promise<item[]>|item[],
  //     render(item)->HTMLElement, apply(token, item)->string }
  constructor(textarea, providers) {
    this.ta = textarea;
    this.providers = new Map(providers.map((p) => [p.trigger, p]));
    this.token = null;          // active token { trigger, query, start, end }
    this.results = [];          // current items
    this.active = -1;           // active row index, -1 = none
    this._req = 0;              // request id to drop stale search results
    this.el = this._build();
    document.body.appendChild(this.el);
    this._wire();
  }

  _build() {
    const el = document.createElement("div");
    el.className = "compose-autocomplete";
    el.setAttribute("role", "listbox");
    el.hidden = true;
    return el;
  }

  _wire() {
    this.ta.addEventListener("input", () => this._schedule());
    this.ta.addEventListener("click", () => this._schedule());
    this.ta.addEventListener("keyup", (e) => {
      // Re-evaluate on caret movement that doesn't fire "input".
      if (e.key === "ArrowLeft" || e.key === "ArrowRight" ||
          e.key === "Home" || e.key === "End") this._schedule();
    });
    this.ta.addEventListener("keydown", (e) => this._onKey(e));
    this.ta.addEventListener("blur", () => {
      // Defer so a click on a row (mousedown) can preventDefault and keep
      // focus before we tear the dropdown down.
      setTimeout(() => { if (!this.ta.matches(":focus")) this.close(); }, 120);
    });
    // Clicking a row selects it. mousedown preventDefault keeps focus in the
    // textarea so the selection can splice into it.
    this.el.addEventListener("mousedown", (e) => e.preventDefault());
    this.el.addEventListener("click", (e) => {
      const row = e.target.closest("[data-idx]");
      if (!row) return;
      this._select(Number(row.dataset.idx));
    });
  }

  // Throttle re-evaluation so fast typing doesn't fire a search per char.
  _schedule() {
    if (this._t) clearTimeout(this._t);
    this._t = setTimeout(() => this._evaluate(), 40);
  }

  _currentToken() {
    return getAutocompleteToken(this.ta.value, this.ta.selectionStart);
  }

  async _evaluate() {
    const token = this._currentToken();
    const provider = token ? this.providers.get(token.trigger) : null;
    if (!token || !provider || token.query.length < provider.minQuery) {
      this.close();
      return;
    }
    this.token = token;
    const myReq = ++this._req;
    let items = [];
    try {
      items = await provider.search(token.query, token);
    } catch { items = []; }
    // Drop stale results if a newer search (or a close) superseded us.
    if (myReq !== this._req || this.token !== token) return;
    this.results = Array.isArray(items) ? items : [];
    this.active = this.results.length ? 0 : -1;
    this._render();
  }

  _render() {
    const el = this.el;
    el.innerHTML = "";
    if (!this.results.length) { this.close(); return; }
    for (let i = 0; i < this.results.length; i++) {
      const row = this.providers.get(this.token.trigger).render(this.results[i]);
      row.classList.add("compose-autocomplete-row");
      row.classList.toggle("is-active", i === this.active);
      row.dataset.idx = String(i);
      row.setAttribute("role", "option");
      el.appendChild(row);
    }
    el.hidden = false;          // unhide before measuring so offsetHeight is real
    this._position();
  }

  _position() {
    const { top, left, lineHeight } = getCaretCoordinates(this.ta, this.ta.selectionStart);
    const margin = 4;
    const el = this.el;
    // Default: just below the caret line. If it would overflow the viewport,
    // flip to sit just above the caret line instead.
    el.style.left = Math.max(8, Math.min(left, window.innerWidth - el.offsetWidth - 8)) + "px";
    const below = top + lineHeight + margin;
    const overflows = below + el.offsetHeight > window.innerHeight - 8;
    el.style.top = (overflows
      ? Math.max(8, top - margin - el.offsetHeight)
      : below) + "px";
  }

  _onKey(e) {
    if (this.el.hidden || !this.results.length) return;
    // Let the composer own its submit shortcut even while suggestions are
    // visible; plain Enter still selects the active suggestion below.
    if (isComposeSubmitShortcut(e)) return;
    // These keys belong to the dropdown while it's open; stop them from also
    // driving the compose modal (e.g. Escape closing the whole modal) or
    // the textarea (Enter inserting a newline).
    if (e.key === "ArrowDown") {
      e.preventDefault(); e.stopPropagation();
      this.active = (this.active + 1) % this.results.length;
      this._markActive();
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); e.stopPropagation();
      this.active = (this.active - 1 + this.results.length) % this.results.length;
      this._markActive();
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (this.active >= 0) { e.preventDefault(); e.stopPropagation(); this._select(this.active); }
    } else if (e.key === "Escape") {
      e.preventDefault(); e.stopPropagation();
      this.close();
    }
  }

  _markActive() {
    this.el.querySelectorAll(".compose-autocomplete-row").forEach((r, i) => {
      r.classList.toggle("is-active", i === this.active);
    });
    const cur = this.el.querySelector(".compose-autocomplete-row.is-active");
    cur?.scrollIntoView({ block: "nearest" });
  }

  _select(idx) {
    const item = this.results[idx];
    const token = this.token;
    if (!item || !token) return;
    const provider = this.providers.get(token.trigger);
    const ta = this.ta;
    const before = ta.value.slice(0, token.start);
    const after = ta.value.slice(token.end);
    const replacement = provider.apply(token, item, after);
    ta.value = before + replacement + after;
    const pos = (before + replacement).length;
    ta.focus();
    try { ta.setSelectionRange(pos, pos); } catch {}
    this._req++;   // invalidate any in-flight search
    this.close();
    // Let the textarea react (char count, draft save, submit enable).
    ta.dispatchEvent(new Event("input", { bubbles: true }));
  }

  close() {
    this.el.hidden = true;
    this.el.innerHTML = "";
    this.results = [];
    this.active = -1;
    this.token = null;
  }

  destroy() {
    this.close();
    this.el.remove();
  }
}

// ---- emoji autocomplete provider ----
//
// Offers the instance's custom emoji and the built-in unicode list together.
// They insert differently — a custom emoji as `:shortcode:` for the server to
// render, a unicode one as the character itself — which is the same split the
// picker makes, so the rows label themselves the same way the picker's cells
// do: `:shortcode:` for custom, a bare shortcode for unicode.

// Lazily tag the generated list once. This keeps the full search index out of
// the initial production bundle while avoiding repeated work per keystroke.
let unicodeAutocompleteEmojisPromise = null;
function getUnicodeAutocompleteEmojis() {
  if (!unicodeAutocompleteEmojisPromise) {
    unicodeAutocompleteEmojisPromise = loadUnicodeEmojis()
      .then((list) => list.map((e) => ({ type: "unicode", ...e })));
  }
  return unicodeAutocompleteEmojisPromise;
}

function emojiAutocompleteRow(e) {
  const row = document.createElement("div");
  if (e.type === "custom") {
    const img = document.createElement("img");
    img.className = "compose-autocomplete-emoji";
    img.src = e.url;
    img.alt = "";
    img.loading = "lazy";
    row.appendChild(img);
  } else {
    const glyph = document.createElement("span");
    glyph.className = "compose-autocomplete-emoji compose-autocomplete-emoji-char";
    glyph.textContent = e.text;
    row.appendChild(glyph);
  }
  const name = document.createElement("span");
  name.className = "compose-autocomplete-label";
  name.textContent = e.type === "custom" ? `:${e.shortcode}:` : e.shortcode;
  row.appendChild(name);
  return row;
}

const emojiAutocompleteProvider = {
  trigger: ":",
  minQuery: 1,
  async search(query) {
    const q = query.toLowerCase();
    // filterEmojiList is the picker's own matcher, so a query finds the same
    // unicode emoji in both places — including by synonym.
    const [customList, unicodeList] = await Promise.all([
      getCustomEmojisCached(),
      getUnicodeAutocompleteEmojis(),
    ]);
    const custom = customList.filter((e) => e.shortcode.toLowerCase().includes(q));
    const unicode = filterEmojiList(unicodeList, query);
    return rankEmojiMatches([...custom, ...unicode], q).slice(0, 30);
  },
  render: emojiAutocompleteRow,
  apply(_token, e) { return e.type === "custom" ? `:${e.shortcode}:` : e.text; },
};

// ---- mention (@) autocomplete ----
//
// Candidates come from a session cache of accounts the user has "seen"
// (people in the current thread/catalog, the signed-in user, and lazily the
// following/followers lists), scored by how often they recur so frequent
// contacts rank first. When the cache is thin and the query is specific, we
// fall back to a live /api/v2/search?type=accounts and fold the results in
// (also learning them into the cache for next time). This keeps the dropdown
// biased toward people you actually interact with, rather than a raw global
// search that surfaces strangers and alts.

const MENTION_CACHE_MAX = 800;
const mentionCache = new Map();   // acct -> { account, score, lastSeen }

// Record an account seen in the app; recurring accounts accumulate score and
// surface higher in the typeahead. Best-effort and bounded.
function rememberAccount(account, weight = 1) {
  if (!account) return;
  const acct = account.acct || account.username;
  if (!acct) return;
  const e = mentionCache.get(acct);
  if (e) { e.account = account; e.score += weight; e.lastSeen = Date.now(); }
  else mentionCache.set(acct, { account, score: weight, lastSeen: Date.now() });
  if (mentionCache.size > MENTION_CACHE_MAX) evictMentionCache();
}

function evictMentionCache() {
  // Drop the lowest-score entries until back under the cap. O(n log n) but
  // only runs on overflow, which is rare.
  const sorted = [...mentionCache.entries()].sort((a, b) => a[1].score - b[1].score);
  while (mentionCache.size > MENTION_CACHE_MAX && sorted.length) {
    mentionCache.delete(sorted.shift()[0]);
  }
}

// Remember every account on a list of statuses (OPs and thread replies) plus
// the signed-in user. Called from render() so thread/catalog/profile views
// all feed the cache.
function rememberAccountsFromStatuses(statuses) {
  if (state.auth.account) rememberAccount(state.auth.account, 1);
  if (!Array.isArray(statuses)) return;
  for (const s of statuses) {
    if (s?.account) rememberAccount(s.account, 1);
    // Thread context carries ancestors + descendants; remember them too.
    const ctx = s?.context;
    if (ctx) {
      for (const a of ctx.ancestors || []) if (a?.account) rememberAccount(a.account, 1);
      for (const d of ctx.descendants || []) if (d?.account) rememberAccount(d.account, 1);
    }
  }
}

// Filter + rank the cache by a query (matches username, full acct, or display
// name, case-insensitively). Returns [{ account, score }].
function searchMentionCache(query) {
  const q = String(query || "").toLowerCase();
  const out = [];
  for (const e of mentionCache.values()) {
    const a = e.account || {};
    const uname = String(a.username || "").toLowerCase();
    const acct = String(a.acct || "").toLowerCase();
    const disp = String(a.display_name || "").toLowerCase();
    if (uname.includes(q) || acct.includes(q) || disp.includes(q)) {
      out.push({ account: a, score: e.score });
    }
  }
  out.sort((x, y) =>
    (y.score - x.score) ||
    String(x.account.acct || "").localeCompare(String(y.account.acct || "")));
  return out;
}

// Lazily pull the signed-in user's following + followers once, the first time
// a mention is triggered, so the typeahead has a decent global pool without
// waiting on thread views to populate it.
let _mentionIndexLoaded = false;
let _mentionIndexLoading = null;
function maybeLoadMentionIndex() {
  if (_mentionIndexLoaded || _mentionIndexLoading) return;
  const me = state.auth.account;
  if (!state.auth.accessToken || !me?.id) return;
  _mentionIndexLoading = (async () => {
    try {
      const [following, followers] = await Promise.all([
        apiFetch(`/api/v1/accounts/${encodeURIComponent(me.id)}/following?limit=80`)
          .then((r) => r.ok ? r.json() : []),
        apiFetch(`/api/v1/accounts/${encodeURIComponent(me.id)}/followers?limit=80`)
          .then((r) => r.ok ? r.json() : []),
      ]);
      // Following weighted above followers: you actively chose them.
      for (const a of following || []) rememberAccount(a, 3);
      for (const a of followers || []) rememberAccount(a, 2);
    } catch { /* best-effort */ }
    _mentionIndexLoaded = true;
    _mentionIndexLoading = null;
  })();
}

// Live account search via /api/v2/search. Memoised per query for 30s and
// de-duplicated while in flight, so fast typing doesn't re-issue the same
// request. Results are learned into the mention cache.
const _apiSearchCache = new Map();      // query -> { result, at }
const _apiSearchInflight = new Map();   // query -> Promise
function searchAccountsAPI(query) {
  const cached = _apiSearchCache.get(query);
  if (cached && Date.now() - cached.at < 30000) return Promise.resolve(cached.result);
  if (_apiSearchInflight.has(query)) return _apiSearchInflight.get(query);
  const p = (async () => {
    try {
      const r = await apiFetch(
        `/api/v2/search?type=accounts&q=${encodeURIComponent(query)}&limit=20`
      );
      const list = r.ok ? ((await r.json())?.accounts || []) : [];
      for (const a of list) rememberAccount(a, 1);
      _apiSearchCache.set(query, { result: list, at: Date.now() });
      return list;
    } catch {
      return [];
    } finally {
      _apiSearchInflight.delete(query);
    }
  })();
  _apiSearchInflight.set(query, p);
  return p;
}

function mentionAutocompleteRow(a) {
  const row = document.createElement("div");
  if (a.avatar) {
    const img = document.createElement("img");
    img.className = "compose-autocomplete-avatar";
    img.src = userSettings.anonymousMode ? "./images/anon_avatar.png" : a.avatar;
    img.alt = "";
    img.loading = "lazy";
    row.appendChild(img);
  }
  const text = document.createElement("span");
  text.className = "compose-autocomplete-text";
  const name = document.createElement("span");
  name.className = "compose-autocomplete-name";
  name.innerHTML = renderTextWithCustomEmoji(
    userSettings.anonymousMode ? "Anonymous" : String(a.display_name || a.username || ""),
    a.emojis || []
  );
  const acct = document.createElement("span");
  acct.className = "compose-autocomplete-acct";
  acct.textContent = userSettings.anonymousMode ? "" : "@" + (a.acct || a.username || "");
  text.appendChild(name);
  text.appendChild(acct);
  row.appendChild(text);
  return row;
}

const mentionAutocompleteProvider = {
  trigger: "@",
  minQuery: 1,
  async search(query) {
    // Kick off the one-time following/followers load if we haven't yet.
    maybeLoadMentionIndex();
    const local = searchMentionCache(query).slice(0, 10).map((x) => x.account);
    let results = local;
    // Only when the local pool is thin AND the query is specific enough do we
    // hit the server, to avoid a search request per keystroke for short
    // prefixes that already match plenty of known accounts.
    if (results.length < 5 && query.length >= 2) {
      const api = await searchAccountsAPI(query);
      const seen = new Set(results.map((a) => a.acct));
      for (const a of api) {
        if (!seen.has(a.acct)) { results.push(a); seen.add(a.acct); }
      }
      results = results.slice(0, 20);
    }
    return results;
  },
  render: mentionAutocompleteRow,
  apply(_token, account, after) {
    const acct = account.acct || account.username || "";
    // Mitra parses "@user" and "@user@instance" in the markdown body into a
    // mention link. Add a trailing space (unless one already follows) so the
    // caret lands ready to keep typing.
    const trail = /^\s/.test(after) ? "" : " ";
    return "@" + acct + trail;
  },
};

class ComposeModal {
  constructor() {
    this.el         = document.getElementById("compose-modal");
    this.form       = document.getElementById("compose-form");
    this.title      = document.getElementById("compose-modal-title");
    this.replyTo    = document.getElementById("compose-reply-to");
    this.subject    = document.getElementById("compose-subject");
    this.content    = document.getElementById("compose-content");
    this.attachList = document.getElementById("compose-attachments");
    this.status     = document.getElementById("compose-status");
    this.count      = document.getElementById("compose-count");
    this.nsfw       = document.getElementById("compose-nsfw");
    this.visibilityWrap = document.getElementById("compose-visibility");
    this.visibilityBtn  = document.getElementById("compose-visibility-btn");
    this.visibilityMenu = document.getElementById("compose-visibility-menu");
    this.attachBtn  = document.getElementById("compose-attach");
    this.fileInput  = document.getElementById("compose-file");
    this.emojiBtn   = document.getElementById("compose-emoji");
    this.pollBtn    = document.getElementById("compose-poll");
    this.pollChip   = document.getElementById("compose-poll-chip");
    this.previewEl  = document.getElementById("compose-preview");
    this.previewBtn = document.getElementById("compose-preview-btn");
    this.submitBtn  = document.getElementById("compose-submit");
    this.closeBtn   = document.getElementById("compose-modal-close");

    this._context = null;            // { mode, inReplyToId, parent }
    this._visibility = "public";     // one of the VISIBILITY_META keys
    this._attachments = [];          // [{ id, url, name, type, dataUrl, status, error }]
    this._attachDrag = null;         // in-flight chip reorder; see _wireAttachmentDrag
    this._poll = null;               // { options[], multiple, expiresIn } or null
    this._previewing = false;        // eye toggle: preview stands in for the textarea
    this._previewSeq = 0;            // drops a render that a newer toggle superseded
    this._submitting = false;
    this._drafts = new Map();        // key -> { content, nsfw, attachments }
    this._activeDraftKey = null;
    this._skipSaveOnClose = false;
    this._replyMentionsEnabled = false;
    this._replyMentionPrefix = "";

    this._wireEvents();
    // Typeahead for :emoji: and @mention in the body textarea.
    this.autocomplete = new ComposeAutocomplete(this.content, [
      emojiAutocompleteProvider,
      mentionAutocompleteProvider,
    ]);
  }

  _wireEvents() {
    this.content.addEventListener("input", () => {
      this._updateState();
      this._saveDraft();
    });
    this.replyTo.addEventListener("change", (e) => {
      if (e.target.id !== "compose-reply-mentions" || this._context?.mode !== "reply") return;
      this._applyReplyMentions(e.target.checked);
      this._updateState();
      this._saveDraft();
      if (this._previewing) this._renderPreview();
    });
    this.subject.addEventListener("input", () => {
      this._saveDraft();
    });
    this.nsfw.addEventListener("change", () => {
      this._updateState();
      this._saveDraft();
    });
    this.visibilityBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this._toggleVisibilityMenu();
    });
    // Delegated so it keeps working as the menu's rows are re-rendered per
    // context (see _renderVisibilityMenu).
    this.visibilityMenu.addEventListener("click", (e) => {
      const item = e.target.closest("[data-visibility]");
      if (!item) return;
      this._setVisibility(item.dataset.visibility);
      this._closeVisibilityMenu();
      this._saveDraft();
    });
    // Dismiss the visibility menu when clicking anywhere outside it.
    document.addEventListener("click", (e) => {
      if (!this.visibilityWrap.contains(e.target)) this._closeVisibilityMenu();
    });
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this._submit();
    });
    this.form.addEventListener("keydown", (e) => {
      if (!isComposeSubmitShortcut(e)) return;
      e.preventDefault();
      if (!this.submitBtn.disabled) this.form.requestSubmit(this.submitBtn);
    });
    this.closeBtn.addEventListener("click", () => this.close());
    addBackdropClose(this.el, () => this.close());   // backdrop click
    this.attachBtn.addEventListener("click", () => {
      if (this._uploading) return;
      this.fileInput.click();
    });
    this.fileInput.addEventListener("change", () => {
      this._handleFiles(this.fileInput.files);
      this.fileInput.value = "";    // allow re-picking the same file
    });
    this.emojiBtn.addEventListener("click", () => this._openEmojiPicker());
    this.pollBtn.addEventListener("click", () => this._openPollComposer());
    this.previewBtn.addEventListener("click", () => this._togglePreview());

    // Paste an image from the clipboard into the attachment list. This covers
    // the common "screenshot → paste into a post" flow; the same path used by
    // the file-picker button (_handleFiles) handles thumbnails, uploads, and
    // status updates.
    this.content.addEventListener("paste", (e) => {
      const clip = e.clipboardData;
      if (!clip) return;
      const items = clip.items;
      if (!items || !items.length) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind !== "file") continue;
        const file = item.getAsFile();
        if (!file) continue;
        // Browsers don't always give pasted images a name; give them one so
        // they show up sensibly in the attachment list and any future upload.
        if (!file.name) {
          const ext = (file.type && file.type.split("/")[1]) || "png";
          const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
          try {
            // File is immutable; build a new one with a name.
            const renamed = new File([file], `pasted-${stamp}.${ext}`, { type: file.type });
            files.push(renamed);
          } catch {
            files.push(file);
          }
        } else {
          files.push(file);
        }
      }
      if (!files.length) return;        // text-only paste — let the textarea handle it
      e.preventDefault();
      this._handleFiles(files);
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.el.classList.contains("is-open")) {
        if (!this.visibilityMenu.hidden) { this._closeVisibilityMenu(); return; }
        this.close();
      }
    });
  }

  _draftKeyFor(context) {
    if (context?.mode === "edit") return null;
    if (context?.mode === "quote") return "quote:" + (context.quoted?.id || "unknown");
    if (!context || context.mode !== "reply") {
      // Keep per-board new-thread drafts separate so a draft written on one
      // board can't silently post into another board's group.
      return context?.groupId ? "new:group:" + context.groupId : "new";
    }
    return "reply:" + (context.inReplyToId || "unknown");
  }

  _saveDraft() {
    if (!this._activeDraftKey) return;
    this._drafts.set(this._activeDraftKey, {
      content: this.content.value || "",
      subject: this.subject.value || "",
      nsfw: !!this.nsfw.checked,
      visibility: this._visibility,
      attachments: this._attachments.map((a) => ({ ...a })),
      poll: this._poll ? { ...this._poll, options: this._poll.options.slice() } : null,
      replyMentionsEnabled: this._replyMentionsEnabled,
      replyMentionPrefix: this._replyMentionPrefix,
    });
  }

  _loadDraft(key) {
    const draft = this._drafts.get(key);
    if (!draft) return false;
    this.content.value = draft.content || "";
    this.subject.value = draft.subject || "";
    this.nsfw.checked = !!draft.nsfw;
    this._replyMentionsEnabled = !!draft.replyMentionsEnabled;
    this._replyMentionPrefix = draft.replyMentionPrefix || "";
    this._setVisibility(draft.visibility || "public");
    this._attachments = Array.isArray(draft.attachments)
      ? draft.attachments.map((a) => ({ ...a }))
      : [];
    this._poll = draft.poll
      ? { ...draft.poll, options: (draft.poll.options || []).slice() }
      : null;
    this._renderAttachments();
    this._renderPoll();
    return true;
  }

  _clearDraft(key = this._activeDraftKey) {
    if (!key) return;
    this._drafts.delete(key);
  }

  open(context = {}) {
    this._context = { mode: "new", ...context };
    this._activeDraftKey = this._draftKeyFor(this._context);
    const isEdit = this._context.mode === "edit" && !!this._context.status;
    const isReply = this._context.mode === "reply" && !!this._context.parent;
    this._replyMentionsEnabled = false;
    this._replyMentionPrefix = "";

    this._setStatus("");
    // Always open on the editor, never on a previous session's preview.
    this._previewing = false;
    this._previewSeq++;
    this._syncPreviewChrome();

    this._closeVisibilityMenu();
    // Build the menu for this context *before* restoring/defaulting the
    // scope, so _setVisibility can clamp to the allowed set.
    this._renderVisibilityMenu(this._visibilityOptionsFor(this._context));
    const restored = !isEdit && this._loadDraft(this._activeDraftKey);
    if (isEdit) {
      const status = this._context.status;
      const source = this._context.source || {};
      this.content.value = source.text || "";
      this.subject.value = source.title || "";
      this.nsfw.checked = !!status.sensitive;
      this._setVisibility(status.visibility || "public");
      this._attachments = (status.media_attachments || []).map((att) => ({
        id: att.id,
        url: att.url || att.preview_url || "",
        dataUrl: att.preview_url || att.url || "",
        name: (att.url || att.preview_url || "attachment").split("/").pop() || "attachment",
        type: att.type?.includes("/") ? att.type : `${att.type || "application"}/*`,
        status: "uploaded",
        error: null,
      }));
      this._poll = null;
      this._renderAttachments();
      this._renderPoll();
    } else if (!restored) {
      this._attachments = [];
      this._poll = null;
      this._renderAttachments();
      this._renderPoll();
      this.content.value = "";
      this.subject.value = "";
      this.nsfw.checked = false;
      this._setVisibility(this._defaultVisibilityFor(this._context));
    }

    // Optional prefill (e.g. the @handle when mentioning a user from their
    // profile). Don't clobber a restored draft the user was mid-way through.
    if (this._context.prefill && !this.content.value) {
      this.content.value = this._context.prefill;
    }

    if (isReply && !restored) this._applyReplyMentions(userSettings.copyMentionsOnReply);
    const isQuote = this._context.mode === "quote" && !!this._context.quoted;
    if (isReply || isQuote) {
      // A quote post carries the target as `quote_id` rather than
      // `in_reply_to_id`, but the header reads the same way: who, then the
      // >>hash quotelink.
      const target = isQuote ? this._context.quoted : this._context.parent;
      const acct = target.account?.acct || target.account?.username || "?";
      // The handle gets its own span so anonymous mode can mask it (the
      // title as a whole must survive: "Reply to Anonymous").
      this.title.textContent = isQuote ? "Quoting " : "Reply to ";
      const who = document.createElement("span");
      who.className = "compose-reply-acct";
      who.textContent = `@${acct}`;
      this.title.appendChild(who);
      // The ">>hash" line, hover-previewed like the reply-to lines in the
      // thread view — but only when the body doesn't already carry the
      // reference. A fresh reply leaves the body empty and shows this line;
      // additional reply targets inserted by the user appear in the body.
      const targetId = target.id || this._context.inReplyToId;
      const targetUrl = objectUrlFor(target);
      const targetRef = postRef(target);
      const inBody = (!!targetRef && this.content.value.includes(">>" + targetRef))
        || (!!targetUrl && this.content.value.includes(targetUrl));
      this.replyTo.hidden = inBody && !isReply;
      this.replyTo.innerHTML = "";
      if (!inBody) {
        const a = document.createElement("a");
        a.className = "reply-to-link";
        a.href = `#/post/${encodeURIComponent(targetId)}/`;
        a.textContent = postRefLabel(target);
        a.addEventListener("mouseenter", () => previewPopup.show(target, a));
        a.addEventListener("mouseleave", () => previewPopup.hide());
        this.replyTo.appendChild(a);
      }
      if (isReply) {
        const label = document.createElement("label");
        label.className = "compose-reply-mentions";
        label.title = "Mention all participants in this reply";
        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.id = "compose-reply-mentions";
        checkbox.checked = this._replyMentionsEnabled;
        checkbox.disabled = !replyMentionPrefill(target, state.auth.account);
        label.append(checkbox, document.createTextNode("Mentions"));
        this.replyTo.appendChild(label);
      }
    } else if (isEdit) {
      this.title.textContent = "Edit post";
      this.replyTo.hidden = true;
      this.replyTo.innerHTML = "";
    } else {
      this.title.textContent = "New post";
      this.replyTo.hidden = true;
      this.replyTo.innerHTML = "";
    }

    // Subjects/titles only apply to top-level posts (Mitra federates them as a
    // Note `name`, and only on posts without an inReplyTo). Hide the field on
    // replies so it isn't silently dropped.
    const hasParent = isReply || (isEdit && !!this._context.status?.in_reply_to_id);
    this.subject.hidden = hasParent;
    if (isReply) this.subject.value = "";
    this.visibilityWrap.hidden = isEdit;
    this.pollBtn.hidden = isEdit;
    this.submitBtn.textContent = isEdit ? "Save changes" : "Post";

    this.el.hidden = false;
    this.el.classList.add("is-open");
    // The compose modal lives outside the header/main roots the render path
    // sweeps, so mask (or unmask) its identity spans on every open.
    applyAnonymousPresentation(this.el);
    this._updateState();
    setTimeout(() => {
      this.content.focus();
      // Drop the cursor at the end so a prefilled @handle is ready to type past.
      const len = this.content.value.length;
      this.content.setSelectionRange(len, len);
    }, 30);
  }

  close() {
    if (!this._skipSaveOnClose) this._saveDraft();
    this._skipSaveOnClose = false;
    this.el.classList.remove("is-open");
    this.el.hidden = true;
    this._context = null;
    this._activeDraftKey = null;
    this.autocomplete?.close();
  }

  isOpen() {
    return this.el.classList.contains("is-open");
  }

  _applyReplyMentions(enabled) {
    const prefill = replyMentionPrefill(this._context?.parent, state.auth.account);
    const previous = this.content.value;
    const { text, prefix } = setReplyMentionPrefill(previous, prefill, enabled, this._replyMentionPrefix);
    const start = this.content.selectionStart;
    const end = this.content.selectionEnd;
    const delta = text.length - previous.length;
    this.content.value = text;
    this.content.setSelectionRange(Math.max(0, start + delta), Math.max(0, end + delta));
    this._replyMentionsEnabled = !!enabled;
    this._replyMentionPrefix = prefix;
  }

  // Add a second-or-later reply target to an already-open compose. The
  // fediverse API only threads one reply (in_reply_to_id, bound when the modal
  // opened), so further targets are referenced in the body as a bare `>>ref`.
  //
  // The @mention is what makes the reference reach its author: neither a link
  // nor a quote notifies anyone, and the server only auto-mentions the author
  // of the post being replied to (Mitra's prepare_mentions), which covers the
  // first target and no others. chan-fe hides it next to the ref on render.
  insertReplyRef(status) {
    const acct = status?.account?.acct || "";
    // Only real accounts carry a username; bare-id placeholders (fabricated
    // elsewhere as { acct }) don't, so skip the mention there — there's no
    // real author to notify.
    this._insertPostRef(status, { mention: status?.account?.username ? `@${acct}` : "" });
  }

  // Add another quote target to an already-open compose. A post can quote
  // several others (FEP-e232), so quoting accumulates refs rather than
  // replacing the one the modal opened with.
  insertQuoteRef(status) {
    if (!isQuotableStatus(status)) {
      this._setStatus("Only public posts can be quoted.", "error");
      return;
    }
    if (countObjectLinks(this.content.value) >= LINK_LIMIT) {
      this._setStatus(`A post can quote at most ${LINK_LIMIT} others.`, "error");
      return;
    }
    this._insertPostRef(status, { objectLink: true });
  }

  // A reference to another post. Two forms, because the two gestures mean
  // different things to every server that isn't chan-fe:
  //
  // - Replying writes a bare `>>ref`, expanded to a plain markdown link at
  //   submit. It reads as a reply everywhere and notifies via its @mention.
  // - Quoting writes `[[url|>>ref]]`, Mitra's object-link microsyntax, which
  //   federates as a real quote (FEP-e232). Other frontends then present it as
  //   an embedded quote — right for "talking about this post", wrong for
  //   "replying to you", which is why replies don't use it.
  _insertPostRef(status, { mention = "", objectLink = false } = {}) {
    if (!status?.id) return;
    if (this._context?.mode === "edit") return;   // don't splice refs into an edit

    // Remember the target so _submit can give the bare ref a URL even after
    // the view it came from is gone.
    rememberQuoted(status);

    let line = objectLink
      ? quoteRefMarkdown(status, { microsyntax: this._objectLinksAllowed(status) })
      : barePostRef(status);
    if (!line) line = `[${postRefLabel(status)}](${chanfePostUrl(status.id)})`;
    if (mention) line += ` ${mention}`;

    const ta = this.content;
    const start = ta.selectionStart ?? ta.value.length;
    const atLineStart = start === 0 || ta.value.slice(0, start).endsWith("\n");
    this._insertAtCursor((atLineStart ? "" : "\n") + line + " ");
    ta.focus();
    this._updateState();
    this._saveDraft();
  }

  // Whether a reference can be written as an object link: only Mitra parses the
  // microsyntax, and it only links public posts.
  _objectLinksAllowed(target = null) {
    if (!isMitraServer(state.instance)) return false;
    return !target || isQuotableStatus(target);
  }

  _setStatus(text, kind = "") {
    this.status.textContent = text || "";
    this.status.className = "compose-status" + (kind ? ` is-${kind}` : "");
  }

  // Which scopes the menu offers, given context. Replying must not widen or
  // break the parent's audience, so the choices are constrained by the
  // parent's visibility (mirrors mitra-web):
  //   - followers-only / conversation → only Conversation or Direct. Posting
  //     a "private" reply would scope it to *our* followers, diverging from
  //     the original; "conversation" keeps it to the thread's participants.
  //   - direct → only Direct (can't broaden a DM).
  //   - public / new top-level post → Public, Followers, Direct.
  _visibilityOptionsFor(context) {
    if (context?.mode === "edit") return [context.status?.visibility || "public"];
    if (context?.mode === "reply") {
      switch (context.parent?.visibility) {
        case "private":
        case "conversation": return ["conversation", "direct"];
        case "direct":       return ["direct"];
      }
      // Replying in a group thread: only public replies get announced by the
      // group actor to board followers (FEP-1b12), so drop followers-only.
      // Direct stays available for side-channel replies to the author.
      if (context.parent?.group) return ["public", "direct"];
      return ["public", "private", "direct"];
    }
    // Mitra 5.11.0 allows public targets to be quoted to a narrower audience.
    // Older or unknown Mitra versions still require a public quoting post.
    if (context?.mode === "quote") {
      if (supportsNonPublicMitraQuotes(state.instance)) return ["public", "private", "direct"];
      return isMitraServer(state.instance) ? ["public"] : ["public", "private"];
    }
    // New thread on a board: a non-public group post would sit in the group
    // timeline but never federate to board followers, so force public.
    if (context?.groupId) return ["public"];
    return ["public", "private", "direct"];
  }

  // Default selection for a fresh compose: the first (most-open) scope the
  // context allows. For restricted replies that's Conversation or Direct;
  // otherwise Public.
  _defaultVisibilityFor(context) {
    return this._visibilityOptionsFor(context)[0];
  }

  _renderVisibilityMenu(values) {
    this.visibilityMenu.innerHTML = "";
    for (const v of values) {
      const meta = VISIBILITY_META[v];
      if (!meta) continue;
      const li = document.createElement("li");
      li.setAttribute("role", "menuitem");
      li.dataset.visibility = v;
      li.innerHTML = `<i class="fa-solid ${meta.icon}"></i> ${escapeHTML(meta.label)}`;
      this.visibilityMenu.appendChild(li);
    }
  }

  _toggleVisibilityMenu() {
    if (this.visibilityMenu.hidden) this._openVisibilityMenu();
    else this._closeVisibilityMenu();
  }

  _openVisibilityMenu() {
    this.visibilityMenu.hidden = false;
    this.visibilityBtn.setAttribute("aria-expanded", "true");
  }

  _closeVisibilityMenu() {
    this.visibilityMenu.hidden = true;
    this.visibilityBtn.setAttribute("aria-expanded", "false");
  }

  // Set the active visibility scope: swap the button icon, mark the chosen
  // menu row, and give the whole modal a green hue for "direct" — mirroring
  // how DMs render in thread view. `value` is clamped to whatever the
  // current menu offers, so a stale draft scope can't escape the context's
  // allowed set.
  _setVisibility(value) {
    const allowed = Array.from(this.visibilityMenu.querySelectorAll("[data-visibility]"))
      .map((li) => li.dataset.visibility);
    if (!allowed.includes(value)) value = allowed[0] || "public";
    const meta = VISIBILITY_META[value] || VISIBILITY_META.public;
    this._visibility = value;
    this.visibilityBtn.innerHTML = `<i class="fa-solid ${meta.icon}"></i>`;
    this.visibilityBtn.title = `Post visibility: ${meta.label}`;
    this.visibilityMenu.querySelectorAll("[data-visibility]").forEach((item) => {
      item.classList.toggle("is-selected", item.dataset.visibility === value);
    });
    this.el.classList.toggle("compose-is-direct", value === "direct");
  }

  get _uploading() {
    return this._attachments.some((a) => a.status === "uploading");
  }

  _updateState() {
    const text = this.content.value;
    const len = text.length;
    const maxLength = composeMaxLength();
    // Read the active instance each time: this composer survives account switches.
    // HTML maxLength is a signed 32-bit value; validation below also handles
    // servers advertising a larger limit.
    if (maxLength <= 2147483647) this.content.maxLength = maxLength;
    else this.content.removeAttribute("maxlength");
    this.count.textContent = String(len);
    this.count.classList.toggle("is-over", len > maxLength);
    // A post needs either text or at least one attachment — media-only posts
    // are allowed (status may be empty as long as media_ids is present).
    // Attachments only get ids at post time, so a file still waiting to go up
    // counts as media just as much as one already on the server.
    const hasText = text.trim().length > 0;
    const hasMedia = this._attachments.some((a) => a.id || a.file);
    const hasPoll = !!this._poll;
    const canSubmit =
      (hasText || hasMedia || hasPoll)
      && !this._submitting
      && !this._uploading
      && !this._attachments.some((a) => a.status === "failed" && !a.file)
      && len <= maxLength;
    this.submitBtn.disabled = !canSubmit;
  }

  _renderAttachments() {
    this._syncMediaPollExclusion();
    this.attachList.innerHTML = "";
    if (!this._attachments.length) {
      this.attachList.hidden = true;
      this.attachList.classList.remove("is-sortable");
      return;
    }
    this.attachList.hidden = false;
    const total = this._attachments.length;
    // A single attachment has nowhere to go, so it gets no drag affordance —
    // and neither does an edit. Reordering an edit can't take on Mitra: its
    // attachments are served in upload order (see _uploadPendingAttachments)
    // and the ones already on the post were uploaded long ago, so the chips
    // would move and the post would not.
    const isEdit = this._context?.mode === "edit";
    const sortable = total > 1 && !isEdit;
    this.attachList.classList.toggle("is-sortable", sortable);
    this._attachments.forEach((att, i) => {
      const li = document.createElement("li");
      li.className = "compose-attachment"
        + (att.status === "uploading" ? " is-uploading" : "")
        + (att.status === "failed"    ? " is-failed"    : "");
      // The reorder handlers read each chip's attachment back off its node,
      // so DOM order after a drag *is* the new array order.
      li._att = att;

      if (att.type && att.type.startsWith("image/") && att.dataUrl) {
        const img = document.createElement("img");
        img.className = "compose-attachment-thumb";
        img.src = att.dataUrl;
        img.alt = "";
        img.draggable = false;
        li.appendChild(img);
      } else {
        const ph = document.createElement("span");
        ph.className = "compose-attachment-thumb";
        ph.textContent = "📎";
        li.appendChild(ph);
      }

      const name = document.createElement("span");
      name.className = "compose-attachment-name";
      name.textContent = att.name + (att.status === "uploading" ? " (uploading…)" : "");
      name.title = att.name;
      li.appendChild(name);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "compose-attachment-remove";
      remove.innerHTML = '<i class="fa-solid fa-xmark"></i>';
      remove.title = "Remove attachment";
      remove.addEventListener("click", () => {
        this._attachments = this._attachments.filter((a) => a !== att);
        this._renderAttachments();
        this._updateState();
        this._saveDraft();
      });
      li.appendChild(remove);

      if (att.status === "failed") {
        const err = document.createElement("span");
        err.className = "compose-attachment-error";
        err.textContent = att.error || "upload error";
        li.appendChild(err);
      }

      if (isEdit && total > 1) {
        li.title = "Attachment order can't be changed when editing a post";
      }
      if (sortable) {
        li.tabIndex = 0;
        li.title = "Drag to reorder (or Ctrl+← / Ctrl+→)";
        li.setAttribute("aria-label", `${att.name}: attachment ${i + 1} of ${total}`);
        this._wireAttachmentDrag(li);
      }

      this.attachList.appendChild(li);
    });
  }

  // ---- attachment reordering ---------------------------------------------
  //
  // Attachments post in list order (see the media_ids body above), so the
  // chip strip is the running order of media on the finished post. Dragging
  // runs on pointer events rather than HTML5 drag-and-drop, which touch
  // devices never fire. The dragged chip stays in the DOM and is moved
  // between its siblings live; `this._attachments` is only rewritten from
  // the DOM once the gesture ends, so the node holding the pointer capture
  // survives the whole drag.
  _wireAttachmentDrag(li) {
    li.addEventListener("pointerdown", (e) => {
      if (!e.isPrimary || e.button !== 0) return;
      if (this._submitting) return;
      if (e.target.closest(".compose-attachment-remove")) return;
      this._attachDrag = {
        el: li, pointerId: e.pointerId, moved: false,
        startX: e.clientX, startY: e.clientY, ghost: null,
      };
      li.setPointerCapture(e.pointerId);
      // Suppress the text selection the press would otherwise start, then
      // take the focus preventDefault would have cost us — the chip is a
      // keyboard target too.
      e.preventDefault();
      li.focus();
    });

    li.addEventListener("pointermove", (e) => {
      const d = this._attachDrag;
      if (!d || d.el !== li || e.pointerId !== d.pointerId) return;
      if (!d.moved) {
        // A plain press (or a click on the way to the remove button) must not
        // count as a drag; wait for real movement first.
        if (Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) < 5) return;
        d.moved = true;
        d.ghost = this._makeAttachmentGhost(li);
        li.classList.add("is-dragging");
        this.attachList.classList.add("is-dragging");
      }
      d.ghost.style.transform =
        `translate(${e.clientX - d.startX}px, ${e.clientY - d.startY}px)`;
      const { others, index } = this._attachmentDropIndex(e.clientX, e.clientY);
      // `others[index]` is the chip to sit in front of; past the last one it
      // is undefined, and insertBefore(li, null) appends. Skipping the case
      // where the chip is already there keeps the strip from churning on
      // every stray pixel of movement.
      const ref = others[index] || null;
      if (ref !== li.nextSibling) this.attachList.insertBefore(li, ref);
    });

    for (const type of ["pointerup", "pointercancel", "lostpointercapture"]) {
      li.addEventListener(type, (e) => {
        const d = this._attachDrag;
        if (!d || d.el !== li || e.pointerId !== d.pointerId) return;
        this._endAttachmentDrag();
      });
    }

    // Keyboard equivalent, for anyone not driving a pointer. Plain arrows are
    // left alone (they move the caret / scroll); Ctrl+arrow moves the chip.
    li.addEventListener("keydown", (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (e.altKey || e.shiftKey || this._submitting) return;
      const dir = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0;
      if (!dir) return;
      const from = this._attachments.indexOf(li._att);
      const to = from + dir;
      if (from < 0 || to < 0 || to >= this._attachments.length) return;
      e.preventDefault();
      this._attachments.splice(to, 0, this._attachments.splice(from, 1)[0]);
      this._renderAttachments();
      this._saveDraft();
      this.attachList.children[to]?.focus();
    });
  }

  // A copy of the chip that follows the pointer, so the gap left behind shows
  // where a drop would land. Fixed-position and click-through so it never
  // shadows the chips being hit-tested underneath it.
  _makeAttachmentGhost(li) {
    const r = li.getBoundingClientRect();
    const ghost = li.cloneNode(true);
    ghost.classList.add("is-ghost");
    ghost.removeAttribute("tabindex");
    ghost.style.width = r.width + "px";
    ghost.style.height = r.height + "px";
    ghost.style.left = r.left + "px";
    ghost.style.top = r.top + "px";
    document.body.appendChild(ghost);
    return ghost;
  }

  // Where the dragged chip would land, as a position among the *other* chips:
  // the count of them the pointer has already passed in reading order. A chip
  // counts as passed when the pointer is below its row outright, or on its row
  // and right of its middle — rows are compared before columns, and the first
  // chip not yet passed stops the count.
  //
  // Keeping the two axes separate is the whole point. Folding them into one
  // weighted distance and picking the nearest chip meant a chip on the row
  // below could sit "closer" than the neighbour the pointer was actually
  // heading for, so the strip reordered itself on the first pixel of movement
  // instead of when the pointer crossed anything.
  _attachmentDropIndex(x, y) {
    const others = [...this.attachList.children]
      .filter((el) => el !== this._attachDrag?.el);
    let index = 0;
    for (const el of others) {
      const r = el.getBoundingClientRect();
      const passed = y > r.bottom ? true
                   : y < r.top    ? false
                   : x > r.left + r.width / 2;
      if (!passed) break;
      index++;
    }
    return { others, index };
  }

  _endAttachmentDrag() {
    const d = this._attachDrag;
    if (!d) return;
    this._attachDrag = null;
    d.ghost?.remove();
    d.el.classList.remove("is-dragging");
    this.attachList.classList.remove("is-dragging");
    if (!d.moved) return;
    // A re-render mid-drag (an upload finishing, say) replaces the chips and
    // drops the capture; the array is already right in that case, so only
    // read the order back when the dragged chip is still the live one.
    if (d.el.parentNode !== this.attachList) return;
    const order = [...this.attachList.children].map((el) => el._att);
    if (order.length !== this._attachments.length || order.some((a) => !a)) return;
    this._attachments = order;
    this._renderAttachments();
    this._saveDraft();
  }

  async _handleFiles(files) {
    if (!files || !files.length) return;
    for (const file of Array.from(files)) {
      const att = {
        file,                    // held until _uploadPendingAttachments runs
        name: file.name,
        type: file.type,
        size: file.size,
        dataUrl: null,
        id: null,
        url: null,
        status: "pending",
        error: null,
      };
      // Capture a data URL for the chip's thumbnail. A file we can't even
      // read locally is not one we can post, so it is dropped from the
      // upload set and the chip has to be removed before posting.
      try {
        att.dataUrl = await this._readAsDataURL(file);
      } catch (e) {
        att.file = null;
        att.status = "failed";
        att.error = "could not read file";
      }
      this._attachments.push(att);
      this._renderAttachments();
      this._updateState();
      this._saveDraft();
    }
  }

  // Uploads every attachment that is still just a local file, in list order,
  // one at a time. Both properties are load-bearing: Mitra keeps no per-post
  // attachment order — `create_post_attachments` matches media_ids with
  // `id = ANY(...)`, which is set membership, then sorts by created_at, and
  // every later read is `ORDER BY media_attachment.created_at` — so on Mitra
  // the display order simply *is* the upload order, and only serialising the
  // uploads makes that match the composer. (Mastodon and Pleroma honour the
  // media_ids order instead, and are happy either way.) Returns false if an
  // upload failed; the file is kept so posting again retries it.
  async _uploadPendingAttachments() {
    for (const att of this._attachments) {
      if (att.id || !att.file) continue;
      att.status = "uploading";
      att.error = null;
      this._renderAttachments();
      // Upload as multipart/form-data, the Mastodon API's own format. Mitra
      // also accepts a base64 JSON body ({ file, media_type }), but Pleroma
      // and Mastodon don't: the string casts fine against their `file` schema
      // and then hits an uploader that only knows real file uploads, which
      // raises and comes back as a 500. Multipart works on all three (Mitra
      // has accepted it since v2.7.0), so it's the only form we send.
      try {
        const form = new FormData();
        // The filename is load-bearing, not cosmetic: Pleroma only builds a
        // file upload out of a part whose Content-Disposition carries one.
        form.append("file", att.file, uploadFilename(att.file));
        // No Content-Type header here — the browser has to set it so the
        // multipart boundary goes with it.
        const r = await apiFetch("/api/v1/media", { method: "POST", body: form });
        if (!r.ok) {
          throw new Error(await apiErrorMessage(r));
        }
        const result = await r.json();
        att.id = result.id;
        att.url = result.url;
        att.status = "uploaded";
      } catch (e) {
        att.status = "failed";
        att.error = e.message || String(e);
        this._renderAttachments();
        return false;
      }
      this._renderAttachments();
    }
    return true;
  }

  _readAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error || new Error("read failed"));
      reader.readAsDataURL(file);
    });
  }

  _openPollComposer() {
    pollComposer.open({
      poll: this._poll,
      onSave: (poll) => {
        this._poll = poll;   // poll object, or null when removed
        this._renderPoll();
        this._updateState();
        this._saveDraft();
      },
    });
  }

  _renderPoll() {
    const chip = this.pollChip;
    chip.replaceChildren();
    if (!this._poll) {
      chip.hidden = true;
      this.pollBtn.classList.remove("is-active");
    } else {
      chip.hidden = false;
      const durLabel = POLL_DURATIONS.find((d) => d.value === this._poll.expiresIn)?.label || "custom";
      const n = this._poll.options.length;

      const edit = document.createElement("button");
      edit.type = "button";
      edit.className = "compose-poll-chip-edit";
      edit.title = "Edit poll";
      edit.innerHTML = `<i class="fa-solid fa-square-poll-vertical"></i> Poll — ${n} options`
        + `${this._poll.multiple ? " · multiple" : ""} · ${escapeHTML(durLabel)}`;
      edit.addEventListener("click", () => this._openPollComposer());

      const rm = document.createElement("button");
      rm.type = "button";
      rm.className = "compose-poll-chip-remove";
      rm.title = "Remove poll";
      rm.innerHTML = '<i class="fa-solid fa-xmark"></i>';
      rm.addEventListener("click", () => {
        this._poll = null;
        this._renderPoll();
        this._updateState();
        this._saveDraft();
      });

      chip.append(edit, rm);
      this.pollBtn.classList.add("is-active");
    }
    this._syncMediaPollExclusion();
  }

  // A status can carry media or a poll, but not both. Disable whichever button
  // is precluded by the current state, with a title explaining why.
  _syncMediaPollExclusion() {
    const hasPoll = !!this._poll;
    const hasMedia = this._attachments.length > 0;
    this.pollBtn.disabled = hasMedia;
    this.pollBtn.title = hasMedia ? "Remove attachments to add a poll" : "Add poll";
    this.attachBtn.disabled = hasPoll;
    this.attachBtn.title = hasPoll ? "Remove the poll to attach files" : "Attach file";
  }

  async _openEmojiPicker() {
    if (!emojiPicker) return;
    await emojiPicker.open({
      mode: "compose",
      onPick: (text) => {
        this._insertAtCursor(text);
        this.content.focus();
        this._updateState();
        this._saveDraft();
      },
    });
  }

  _insertAtCursor(text) {
    const ta = this.content;
    const start = ta.selectionStart ?? ta.value.length;
    const end   = ta.selectionEnd   ?? ta.value.length;
    const before = ta.value.slice(0, start);
    const after  = ta.value.slice(end);
    ta.value = before + text + after;
    const pos = start + text.length;
    try { ta.setSelectionRange(pos, pos); } catch {}
  }

  // ---- preview ----
  //
  // The eye swaps the textarea for the post as it will read once sent. Where
  // the HTML comes from depends on who renders it, and the create endpoint is
  // never involved — Pleroma's preview is `POST /statuses` with preview:true,
  // and a server that ignored the flag would publish the draft.
  //
  //  - Servers taking HTML get HTML we generate (see postbody.js), so
  //    rendering it here is exactly what they'll store.
  //  - Servers taking neither render the body as plain text, where a reference
  //    is a `>>ref` followed by its URL — and a chan-fe reader sees the ref
  //    with the URL folded away behind it, which is what the local render of
  //    the markdown shows. So the same local pass covers both.
  //  - Servers taking markdown render it themselves, in a dialect we can't
  //    reproduce — Mitra's comrak greentext and hard breaks are the whole
  //    reason the formatting bug existed — so ask the server, via its own
  //    preview endpoint. If it hasn't got one, fall back to the plain-text
  //    reading, which is what a server that ignores content_type will do.
  async _previewHtml() {
    const visibility = this._context?.mode === "edit"
      ? (this._context.status?.visibility || "public")
      : this._visibility;
    const { text } = this._composeBodySource(visibility);
    if (!text.trim()) return { html: "", emojis: [] };

    if (!supportsMarkdownPosts(state.instance)
        || (supportsHtmlPosts(state.instance) && !isMitraServer(state.instance))) {
      return { html: composeBodyToHtml(text), emojis: [] };
    }
    try {
      const { content, emojis } = await previewStatus(text, "text/markdown", state);
      return { html: content, emojis };
    } catch {
      return { html: composeBodyToHtml(text), emojis: [], approximate: true };
    }
  }

  async _togglePreview() {
    this._previewing = !this._previewing;
    ++this._previewSeq;
    this._syncPreviewChrome();
    if (!this._previewing) {
      this.content.focus();      // back to editing where they left off
      return;
    }
    await this._renderPreview();
  }

  async _renderPreview() {
    const seq = ++this._previewSeq;
    this.previewEl.textContent = "rendering…";
    let result;
    try {
      result = await this._previewHtml();
    } catch {
      result = { html: "", emojis: [], approximate: true };
    }
    // A second toggle while the request was out wins.
    if (seq !== this._previewSeq || !this._previewing) return;

    this.previewEl.replaceChildren();
    if (!result.html) {
      const empty = document.createElement("div");
      empty.className = "compose-preview-empty";
      empty.textContent = "Nothing to preview yet.";
      this.previewEl.appendChild(empty);
      return;
    }
    // The same pipeline that draws a real post body, so greentext, >>refs and
    // custom emoji look here exactly as they will in the thread.
    const body = document.createElement("div");
    body.className = "post-body";
    body.innerHTML = renderSafeHTML(result.html, result.emojis, {});
    this.previewEl.appendChild(body);
    wireThreadLinks(body);
    applyAnonymousPresentation(body);
    if (result.approximate) {
      const note = document.createElement("div");
      note.className = "compose-preview-note";
      note.textContent = "This server renders posts itself — the real post may differ.";
      this.previewEl.appendChild(note);
    }
  }

  _syncPreviewChrome() {
    const on = this._previewing;
    this.content.hidden = on;
    this.previewEl.hidden = !on;
    this.previewBtn.classList.toggle("is-active", on);
    this.previewBtn.setAttribute("aria-pressed", on ? "true" : "false");
    this.previewBtn.title = on ? "Back to editing" : "Preview post";
    this.previewBtn.innerHTML = on
      ? '<i class="fa-solid fa-eye-low-vision"></i>'
      : '<i class="fa-solid fa-eye"></i>';
  }

  // The post body as it will actually be sent, before any per-server HTML
  // conversion. Shared by _submit and the preview so the two can't drift —
  // a preview that renders different source than the post is worse than none.
  //
  //  - Bare `>>ref`s get a target. The composer holds them the way they're
  //    typed on an imageboard, but a short id means nothing off this frontend,
  //    so each one that still resolves becomes a markdown link to the post it
  //    names. Unresolvable refs stay as typed.
  //  - Object links are Mitra-only. Before 5.11.0 they also require a public
  //    quoting post; unsupported links go out as plain markdown links.
  _composeBodySource(visibility) {
    let text = expandBarePostRefs(this.content.value, (ref) => {
      const inThread = resolveStatusId(ref);
      const target = (inThread && currentThreadGraph()?.statusById.get(inThread))
        || lookupQuoted({ ref });
      return target ? objectUrlFor(target) : "";
    });
    const objectLinksOk = this._objectLinksAllowed()
      && (visibility === "public" || supportsNonPublicMitraQuotes(state.instance));
    if (!objectLinksOk) text = downgradeObjectLinks(text);
    return { text, objectLinksOk };
  }

  async _submit() {
    if (this._submitting) return;
    const text = this.content.value.trim();
    if (!text && !this._attachments.some((a) => a.id || a.file) && !this._poll) return;
    if (this.content.value.length > composeMaxLength()) {
      this._setStatus("Post is too long.", "error");
      return;
    }
    if (this._uploading) {
      this._setStatus("Wait for attachments to finish uploading.", "error");
      return;
    }
    if (this._attachments.some((a) => a.status === "failed" && !a.file)) {
      this._setStatus("Remove failed attachments before posting.", "error");
      return;
    }
    if (!state.auth.accessToken) {
      this._setStatus("Sign in to post.", "error");
      return;
    }

    // Files go up here, at post time, rather than as each one is picked, so
    // that the chip order the composer shows is the order they are uploaded
    // in. See _uploadPendingAttachments for why that is the only ordering
    // Mitra will honour. The composer locks for the duration.
    this._submitting = true;
    this.submitBtn.classList.add("is-loading");
    this._updateState();
    if (!(await this._uploadPendingAttachments())) {
      this._submitting = false;
      this.submitBtn.classList.remove("is-loading");
      this._setStatus("Attachment upload failed \u2014 post again to retry.", "error");
      this._updateState();
      this._saveDraft();
      return;
    }

    const isEdit = this._context?.mode === "edit";
    const body = {
      status: this.content.value,
      content_type: isEdit ? (this._context.source?.content_type || "text/markdown") : "text/markdown",
      sensitive: !!this.nsfw.checked,
    };
    if (isEdit) {
      body.title = this._context.status?.in_reply_to_id ? null : (this.subject.value.trim() || null);
    } else if (this._context?.inReplyToId) {
      body.in_reply_to_id = this._context.inReplyToId;
    } else {
      // Subject → `title`. Only top-level posts carry a title (the field is
      // hidden for replies), so guard on the absence of a parent.
      const title = this.subject.value.trim();
      if (title) body.title = title;
      // Address the new thread to the current custom board's group. Replies
      // never carry group_id — they belong to the thread.
      if (this._context?.groupId) body.group_id = this._context.groupId;
    }
    // Media and polls are mutually exclusive (the toolbar enforces it); a poll
    // takes precedence if somehow both are set.
    if (!isEdit) body.visibility = this._visibility;

    const visibility = isEdit ? (this._context.status?.visibility || "public") : this._visibility;
    const composed = this._composeBodySource(visibility);
    body.status = composed.text;
    const objectLinksOk = composed.objectLinksOk;

    // The quote target rides along as `quote_id` unless the body already
    // carries it as an object link (which is the richer form: it federates
    // every reference, not just the first).
    const quoteTarget = isEdit
      ? getQuotedStatus(this._context.status)
      : (this._context?.mode === "quote" ? this._context.quoted : null);
    if (quoteTarget?.id) {
      const linked = objectLinksOk && body.status.includes(objectUrlFor(quoteTarget));
      if (!linked) body.quote_id = quoteTarget.id;
    }
    // Mitra parses the markdown above the way an imageboard needs. Pleroma
    // runs plain CommonMark on it, which turns every greentext line — and any
    // `>>ref` starting one — into a blockquote and drops the line breaks, so
    // it gets HTML we render ourselves instead. Mastodon renders no dialect at
    // all and would publish the markdown's punctuation as text, so there the
    // body is flattened to the plain text it's going to be read as, and the
    // `content_type` — a Pleroma extension it ignores — goes with it. See
    // postbody.js.
    if (!isMitraServer(state.instance) && supportsHtmlPosts(state.instance)) {
      body.status = composeBodyToHtml(body.status);
      body.content_type = "text/html";
    } else if (!supportsMarkdownPosts(state.instance)) {
      body.status = composeBodyToPlainText(body.status);
      delete body.content_type;
    }

    if (!isEdit && this._poll && this._poll.options?.length >= 2) {
      body.poll = {
        options: this._poll.options,
        expires_in: this._poll.expiresIn,
        multiple: !!this._poll.multiple,
      };
    } else {
      const mediaIds = this._attachments.filter((a) => a.id).map((a) => a.id);
      if (mediaIds.length) body.media_ids = mediaIds;
    }

    try {
      const endpoint = isEdit
        ? `/api/v1/statuses/${encodeURIComponent(this._context.status.id)}`
        : "/api/v1/statuses";
      const r = await apiFetch(endpoint, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const text = await r.text();
        throw new Error(`HTTP ${r.status} ${text.slice(0, 200)}`);
      }
      const result = await r.json();
      const composeContext = this._context ? { ...this._context } : null;
      const draftKey = this._activeDraftKey;
      this._clearDraft(draftKey);
      this._skipSaveOnClose = true;
      this._setStatus(isEdit ? "Saved!" : "Posted!", "");
      this.close();
      await navigateAfterComposeResult(result, composeContext);
    } catch (e) {
      this._setStatus(`Failed to ${isEdit ? "save" : "post"}: ` + (e.message || e), "error");
    }

    this._submitting = false;
    this.submitBtn.classList.remove("is-loading");
    this._updateState();
  }
}

const composeModal = new ComposeModal();

// Public entry points used by the rest of the app.
function openComposeModal(context = {}) {
  if (state.auth.status !== "signed-in") {
    beginAuth();
    openAuthDialog();
    return;
  }
  composeModal.open(context);
}

async function openComposeEdit(status) {
  if (!status?.id || !isOwnAccount(status.account)) return;
  try {
    const r = await apiFetch(`/api/v1/statuses/${encodeURIComponent(status.id)}/source`);
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
    const source = await r.json();
    openComposeModal({
      mode: "edit",
      status,
      source,
      threadRootId: resolveThreadRootId(status, currentThreadGraph()?.statusById || new Map()) || getCurrentPostRootId(),
    });
  } catch (e) {
    alert("Failed to load post for editing: " + e.message);
  }
}

// Take deleted posts out of everything this session holds. Mitra deletes a
// post's whole reply subtree with it, so `ids` is the post plus every reply
// below it that's loaded.
//
// The session status cache is the important one: it has no TTL, so a deleted
// post left in it is handed back to the next thread-ancestor hydration and
// reappears in the catalog it was just removed from — which is why a browser
// reload was the only thing that made it go.
function forgetDeletedStatuses(ids) {
  if (!ids?.size) return;
  forgetCachedStatuses(ids);
  // A repost of a deleted post goes with it; Mitra deletes those too.
  state.statuses = state.statuses.filter(
    (row) => !ids.has(row?.id) && !ids.has(row?.reblog?.id),
  );
  // Drop any index row that held one, so it re-fetches its replies rather
  // than redrawing from a thread that no longer exists in that shape.
  for (const [opId, entry] of state.indexView.rows) {
    if (ids.has(opId) || (entry?.descendants || []).some((d) => ids.has(d?.id))) {
      state.indexView.rows.delete(opId);
    }
  }
}

async function deletePost(status, btn) {
  if (!status?.id || !isOwnAccount(status.account)) return;

  // Mitra's delete_post walks the reply tree and deletes every descendant (and
  // any repost) with the post, whoever wrote them — so say what's going, not
  // just that it's irreversible. The count covers the replies loaded in this
  // view; one that hasn't federated in yet isn't here to be counted.
  const replies = countDescendants(status.id, currentThreadGraph()?.repliesByParent);
  const question = replies
    ? `Delete this post and the ${replies} ${replies === 1 ? "reply" : "replies"} below it, including other users'? This cannot be undone.`
    : "Delete this post? This cannot be undone.";
  if (!confirm(question)) return;
  if (btn) btn.disabled = true;
  const rootId = resolveThreadRootId(status, currentThreadGraph()?.statusById || new Map())
    || getCurrentPostRootId()
    || status.id;
  try {
    const r = await apiFetch(`/api/v1/statuses/${encodeURIComponent(status.id)}`, { method: "DELETE" });
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);

    const deleted = collectDescendantIds(status.id, currentThreadGraph()?.repliesByParent);
    deleted.add(status.id);
    forgetDeletedStatuses(deleted);

    // Deleted from a catalog card or an index row: the view the reader is on
    // is still the right one, so redraw it rather than navigating. (The
    // branches below are for the thread view, where the post being deleted
    // may be the thread itself.)
    if (state.view?.kind !== "post") {
      render();
    } else if (status.id === rootId) {
      const backRoute = state.postView?.from || state.lastNonPostRoute || { kind: "board", name: defaultBoardName() };
      location.hash = hashForRoute(backRoute);
    } else {
      await loadPost(rootId);
    }
  } catch (e) {
    if (btn) btn.disabled = false;
    alert("Failed to delete post: " + e.message);
  }
}

// Open the composer as a quote post: a new top-level post referencing the
// quoted status. Unlike a reply it doesn't join the thread, so there's no
// in_reply_to_id and the subject field stays available.
//
// On Mitra the reference is prefilled into the body as an object link, so it
// reads as a >>quotelink at the top of the post the way an imageboard reply
// does; elsewhere it rides along as `quote_id` (see _submit). Quoting again
// while the composer is open adds another reference rather than starting over —
// a post can quote several others.
function openComposeQuote(status) {
  if (!status?.id) return;
  if (composeModal.isOpen()) {
    composeModal.insertQuoteRef(status);
    return;
  }
  const prefill = isMitraServer(state.instance) && isQuotableStatus(status)
    ? quoteRefMarkdown(status, { microsyntax: true }) + "\n"
    : "";
  openComposeModal({ mode: "quote", quoted: status, prefill });
}

function openComposeReply(parentStatus) {
  // Accept either a full status object or a bare { id, account } placeholder.
  // If a string slips through (legacy callers), wrap it so the modal still
  // has a valid id and the submit flow knows it's a reply (not "new").
  let parent = parentStatus;
  if (typeof parent === "string") {
    parent = { id: parent, account: { acct: parent.slice(0, 8) } };
  }
  if (!parent || !parent.id) {
    openComposeModal({ mode: "new" });
    return;
  }

  // Compose already open? The API only threads a single reply
  // (in_reply_to_id), so instead of rebinding the parent, treat this click as
  // an additional reference: drop a >>ref + @mention into the body.
  if (composeModal.isOpen()) {
    composeModal.insertReplyRef(parent);
    return;
  }

  let threadRootId = null;
  const graphById = currentThreadGraph()?.statusById;
  if (graphById?.size) {
    const target = graphById.get(parent.id) || parent;
    threadRootId = resolveThreadRootId(target, graphById) || null;
  }
  if (!threadRootId) {
    threadRootId = getCurrentPostRootId();
  }

  openComposeModal({
    mode: "reply",
    inReplyToId: parent.id,
    parent,
    threadRootId,
  });
}

// ---- polls (Mastodon-compatible: status.poll) ----
// A poll object carries { id, expired, multiple, votes_count, voted,
// own_votes[], options[{ title, votes_count }], emojis[] }. We show interactive
// options when the signed-in user can still vote, and a results view (bars +
// percentages, own choices ticked) once they've voted or the poll has closed.

// Whether the current user can cast a vote on this poll right now.
function pollIsVotable(poll) {
  return state.auth.status === "signed-in" && !poll.expired && !poll.voted;
}

function buildPollBlock(status) {
  const poll = status?.poll;
  if (!poll || !Array.isArray(poll.options) || !poll.options.length) return null;
  const wrap = document.createElement("div");
  wrap.className = "post-poll";
  renderPollInto(wrap, status);
  return wrap;
}

// Human-friendly poll deadline, e.g. "closed", "ends in 3h", "ends in 2d".
function formatPollDeadline(poll) {
  if (poll.expired) return "closed";
  if (!poll.expires_at) return "open";
  const ms = new Date(poll.expires_at).getTime() - Date.now();
  if (!Number.isFinite(ms)) return "open";
  if (ms <= 0) return "closed";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `ends in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `ends in ${hrs}h`;
  return `ends in ${Math.round(hrs / 24)}d`;
}

function renderPollInto(wrap, status) {
  const poll = status.poll;
  const votable = pollIsVotable(poll);
  wrap.dataset.mode = votable ? "vote" : "results";
  wrap.replaceChildren();

  const total = Number(poll.votes_count) || 0;
  const ownVotes = new Set(Array.isArray(poll.own_votes) ? poll.own_votes : []);
  const counts = poll.options.map((o) => Number(o.votes_count) || 0);
  const leading = total > 0 ? Math.max(...counts) : -1;

  const list = document.createElement("ul");
  list.className = "poll-options";

  poll.options.forEach((opt, idx) => {
    const titleHTML = renderTextWithCustomEmoji(opt.title ?? "", poll.emojis || []);
    const li = document.createElement("li");
    li.className = "poll-option";

    if (votable) {
      const label = document.createElement("label");
      label.className = "poll-choice";
      const input = document.createElement("input");
      input.type = poll.multiple ? "checkbox" : "radio";
      input.name = "poll-" + (poll.id || status.id);
      input.value = String(idx);
      const text = document.createElement("span");
      text.className = "poll-option-text";
      text.innerHTML = titleHTML;
      label.append(input, text);
      li.appendChild(label);
    } else {
      const pct = total > 0 ? Math.round((counts[idx] / total) * 100) : 0;
      const res = document.createElement("div");
      res.className = "poll-result"
        + (ownVotes.has(idx) ? " is-own" : "")
        + (counts[idx] === leading ? " is-leading" : "");
      const bar = document.createElement("div");
      bar.className = "poll-result-bar";
      bar.style.width = pct + "%";
      const label = document.createElement("span");
      label.className = "poll-result-label";
      label.innerHTML = (ownVotes.has(idx) ? '<i class="fa-solid fa-check poll-check"></i> ' : "") + titleHTML;
      const pctEl = document.createElement("span");
      pctEl.className = "poll-result-pct";
      pctEl.textContent = pct + "%";
      res.append(bar, label, pctEl);
      li.appendChild(res);
    }
    list.appendChild(li);
  });
  wrap.appendChild(list);

  if (votable) {
    const voteBtn = document.createElement("button");
    voteBtn.type = "button";
    voteBtn.className = "poll-vote-btn";
    voteBtn.textContent = "Vote";
    voteBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const chosen = Array.from(wrap.querySelectorAll(".poll-options input:checked"))
        .map((i) => Number(i.value));
      if (!chosen.length) return;
      submitPollVote(status, chosen, wrap, voteBtn);
    });
    wrap.appendChild(voteBtn);
  }

  const footer = document.createElement("div");
  footer.className = "poll-footer";
  const votesLabel = total === 1 ? "1 vote" : `${total} votes`;
  footer.textContent = `${votesLabel} · ${formatPollDeadline(poll)}`;
  wrap.appendChild(footer);
}

async function submitPollVote(status, choices, wrap, btn) {
  const poll = status?.poll;
  if (!poll?.id) return;
  if (state.auth.status !== "signed-in") { beginAuth(); openAuthDialog(); return; }

  if (btn) { btn.disabled = true; btn.textContent = "Voting…"; }
  try {
    const r = await apiFetch(`/api/v1/polls/${encodeURIComponent(poll.id)}/votes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ choices }),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    // Persist the server's updated poll onto the status so the results survive
    // later reconciles/re-renders, then swap the block to its results view.
    status.poll = await r.json();
    renderPollInto(wrap, status);
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = "Vote"; }
    alert("Failed to submit vote: " + e.message);
  }
}

// Keep a post's poll block in sync on reconcile. A votable block is left alone
// so a background refresh can't wipe the user's mid-vote selection; otherwise
// (results view, or the votable→closed/voted transition) it re-renders.
function updatePostPoll(el, status) {
  const existing = el.querySelector(":scope > .post-poll");
  if (!status?.poll || !Array.isArray(status.poll.options) || !status.poll.options.length) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    if (existing.dataset.mode === "vote" && pollIsVotable(status.poll)) return;
    renderPollInto(existing, status);
  } else {
    const block = buildPollBlock(status);
    if (!block) return;
    // Same slot as a fresh render: before the reactions row / action foot.
    const anchor = el.querySelector(":scope > .post-reactions")
      || el.querySelector(":scope > .post-foot");
    el.insertBefore(block, anchor || null);
  }
}

// ---- emoji reactions (pleroma.emoji_reactions) ----
// Mitra returns a list of reactions on every status; each entry has:
//   { name: "❤" | "<shortcode>", url: <image url or null>, count, me }
// Custom emoji are image-backed (url is set); unicode emoji have url=null
// and the name is the actual emoji character. Clicking a button toggles
// your reaction via PUT/DELETE /api/v1/pleroma/statuses/<id>/reactions/<name>.

function buildEmojiReactionsBlock(s) {
  const reactions = s?.pleroma?.emoji_reactions || [];
  if (!reactions.length) return null;

  const wrap = document.createElement("div");
  wrap.className = "post-reactions";

  for (const r of reactions) {
    if (!r?.name) continue;
    wrap.appendChild(buildReactionButton(r, s.id, wrap));
  }

  return wrap;
}

async function toggleEmojiReaction(statusId, name, url, btn, wrap) {
  const wasMine = btn.classList.contains("is-mine");
  const countEl = btn.querySelector(".post-reaction-count");
  const prevCount = parseInt(countEl.textContent, 10) || 0;

  // Optimistic UI update
  if (wasMine) {
    btn.classList.remove("is-mine");
    countEl.textContent = String(Math.max(0, prevCount - 1));
  } else {
    btn.classList.add("is-mine");
    countEl.textContent = String(prevCount + 1);
  }

  if (!state.auth.accessToken) {
    // Undo and prompt to sign in
    if (wasMine) {
      btn.classList.add("is-mine");
      countEl.textContent = String(prevCount);
    } else {
      btn.classList.remove("is-mine");
      countEl.textContent = String(prevCount);
    }
    alert("Sign in to react to this post.");
    return;
  }

  try {
    const r = await apiFetch(
      `/api/v1/pleroma/statuses/${encodeURIComponent(statusId)}/reactions/${encodeURIComponent(name)}`,
      { method: wasMine ? "DELETE" : "PUT" }
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const updated = await r.json();
    // Reconcile: find this reaction in the response and update the button.
    const updatedReactions = updated?.pleroma?.emoji_reactions || [];
    const match = updatedReactions.find((x) => x && x.name === name && (x.url || null) === (url || null));
    if (match) {
      btn.classList.toggle("is-mine", !!match.me);
      countEl.textContent = String(match.count || 0);
    } else {
      // Server says this reaction no longer exists for this post — drop it.
      btn.remove();
      if (wrap && !wrap.children.length) wrap.remove();
    }
  } catch (e) {
    // Revert on failure
    if (wasMine) {
      btn.classList.add("is-mine");
      countEl.textContent = String(prevCount);
    } else {
      btn.classList.remove("is-mine");
      countEl.textContent = String(prevCount);
    }
    alert("Failed to update reaction: " + e.message);
  }
}

// Write an interaction's server truth back into every copy of the status the
// app is holding: the loaded timeline (what the catalog renders), the open
// thread's context, and data.js's status cache. Liking a post in thread view
// otherwise leaves the catalog card it came from showing the pre-like heart —
// the catalog re-renders from a copy that never saw the change, and hydrated
// OPs come from a session-long cache with no TTL at all.
function propagateStatusInteraction(id, patch) {
  if (!id || !patch) return;
  const apply = (row) => {
    if (!row) return;
    if (row.id === id) Object.assign(row, patch);
    if (row.reblog?.id === id) Object.assign(row.reblog, patch);
  };
  (state.statuses || []).forEach(apply);
  const ctx = state.postView?.context;
  if (ctx) {
    apply(ctx.op);
    (ctx.ancestors || []).forEach(apply);
    (ctx.descendants || []).forEach(apply);
  }
  patchCachedStatus(id, patch);
}

// ---- repost / boost (optimistic, reconciled with server response) ----
async function toggleRepost(statusId, btn) {
  const wasReposted = btn.classList.contains("is-reposted");
  if (btn.disabled || (!wasReposted && btn.dataset.repostAllowed === "0")) return;
  const countEl = btn.querySelector(".post-action-count");

  // Optimistic UI update
  applyRepostState(btn, !wasReposted, bumpCount(countEl.textContent, wasReposted ? -1 : 1));

  try {
    // Mastodon API: reblog/unreblog are separate POST endpoints (there's no
    // DELETE /reblog route — that 404s).
    const action = wasReposted ? "unreblog" : "reblog";
    const r = await apiFetch(`/api/v1/statuses/${statusId}/${action}`, { method: "POST" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const updated = await r.json();
    const actionTarget = effectiveStatus(updated);
    // Reconcile with server truth (Mitra returns a wrapper object for reblogs,
    // where the actual target status is in `reblog`).
    applyRepostState(btn, !!actionTarget?.reblogged, countFmt(actionTarget?.reblogs_count || 0), actionTarget);
    propagateStatusInteraction(statusId, {
      reblogged: !!actionTarget?.reblogged,
      reblogs_count: Number(actionTarget?.reblogs_count) || 0,
    });
  } catch (e) {
    // Revert on failure
    applyRepostState(btn, wasReposted, countFmt(Math.max(0, (parseInt(countEl.textContent, 10) || 0) + (wasReposted ? 1 : -1))));
    alert("Failed to update repost: " + e.message);
  }
}

function applyRepostState(btn, reposted, countText, status = null) {
  if (status) {
    const visibility = status.visibility;
    btn.dataset.repostAllowed = !visibility || visibility === "public" || visibility === "unlisted" ? "1" : "0";
  }
  const countEl = btn.querySelector(".post-action-count");
  btn.classList.toggle("is-reposted", reposted);
  // Undo remains available even if the post's scope has since changed.
  btn.disabled = btn.dataset.repostAllowed === "0" && !reposted;
  btn.title = btn.disabled ? "This post's scope does not allow reposting" : reposted ? "Undo repost" : "Repost";
  countEl.textContent = countText;
}

// ---- like / favourite (optimistic, reconciled with server response) ----
async function toggleLike(statusId, btn) {
  const wasLiked = btn.classList.contains("is-liked");
  const icon = btn.querySelector("i");
  const countEl = btn.querySelector(".post-action-count");

  // Optimistic UI update
  applyLikeState(btn, !wasLiked, bumpCount(countEl.textContent, wasLiked ? -1 : 1));

  try {
    // Mastodon API: favourite/unfavourite are separate POST endpoints (there's
    // no DELETE /favourite route — that 404s).
    const action = wasLiked ? "unfavourite" : "favourite";
    const r = await apiFetch(`/api/v1/statuses/${statusId}/${action}`, { method: "POST" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const updated = await r.json();
    const actionTarget = effectiveStatus(updated);
    // Reconcile with server truth
    applyLikeState(btn, !!actionTarget?.favourited, countFmt(actionTarget?.favourites_count || 0));
    propagateStatusInteraction(statusId, {
      favourited: !!actionTarget?.favourited,
      favourites_count: Number(actionTarget?.favourites_count) || 0,
    });
  } catch (e) {
    // Revert on failure
    applyLikeState(btn, wasLiked, countFmt(Math.max(0, (parseInt(countEl.textContent, 10) || 0) + (wasLiked ? 1 : -1))));
    alert("Failed to update like: " + e.message);
  }
}

function applyLikeState(btn, liked, countText) {
  const icon = btn.querySelector("i");
  const countEl = btn.querySelector(".post-action-count");
  btn.classList.toggle("is-liked", liked);
  btn.title = liked ? "Unlike" : "Like";
  icon.className = liked ? "fa-solid fa-heart" : "fa-regular fa-heart";
  countEl.textContent = countText;
}

// ---- private bookmark (optimistic, reconciled with server response) ----
async function toggleBookmark(statusId, btn) {
  const wasBookmarked = btn.classList.contains("is-bookmarked");
  applyBookmarkState(btn, !wasBookmarked);

  try {
    const action = wasBookmarked ? "unbookmark" : "bookmark";
    const r = await apiFetch(`/api/v1/statuses/${statusId}/${action}`, { method: "POST" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const updated = effectiveStatus(await r.json());
    applyBookmarkState(btn, !!updated?.bookmarked);
  } catch (e) {
    applyBookmarkState(btn, wasBookmarked);
    alert("Failed to update bookmark: " + e.message);
  }
}

function applyBookmarkState(btn, bookmarked) {
  const icon = btn.querySelector("i");
  btn.classList.toggle("is-bookmarked", bookmarked);
  btn.title = bookmarked ? "Remove bookmark" : "Bookmark";
  if (icon) icon.className = bookmarked ? "fa-solid fa-bookmark" : "fa-regular fa-bookmark";
}

function bumpCount(text, delta) {
  const n = Math.max(0, (parseInt(text, 10) || 0) + delta);
  return countFmt(n);
}

class PostPreviewPopup {
  constructor() {
    this.el = document.getElementById("post-preview");
    this._lastMouseEvent = null;
    this._ownerEl = null;
    this._onMouseMove = this._onMouseMove.bind(this);
    document.addEventListener("mousemove", this._onMouseMove);
  }

  _onMouseMove(e) {
    this._lastMouseEvent = e;
    if (!this.el || !this.el.classList.contains("is-visible")) return;
    positionFloatingNearPointer(this.el, e, { offsetX: 18, offsetY: 14, margin: FLOAT_PREVIEW_MARGIN });
  }

  show(status, ownerEl = null) {
    if (!this.el || !status) return;
    // Remember the element that triggered the popup so the orphan sweep can
    // dismiss it if that element leaves the DOM while still "hovered" — a
    // node removed under the pointer (navigation, auto-refresh re-render)
    // never fires mouseleave, which is what leaves the popup stuck.
    this._ownerEl = ownerEl || null;
    const acc = status.account || {};
    const name = acc.display_name || acc.username || "Anonymous";
    const dt = new Date(status.created_at || Date.now());
    const ts = dt.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    this.el.innerHTML = `
      <div class="ppv-head"><span class="ppv-name">${renderTextWithCustomEmoji(name, acc.emojis || [])}</span> <span class="ppv-id">${escapeHTML(postRefLabel(status))}</span> ${escapeHTML(ts)}</div>
      <div class="ppv-body"></div>
    `;
    // Format the body like a real post (greentext + emoji) instead of raw text.
    // The popup is pointer-events:none, so any links render but stay inert.
    const body = this.el.querySelector(".ppv-body");
    renderPostContentInto(body, status);
    if (!body.textContent.trim() && !body.querySelector("img")) body.textContent = "(empty)";
    this.el.classList.add("is-visible");
    applyAnonymousPresentation(this.el);

    if (this._lastMouseEvent) {
      positionFloatingNearPointer(this.el, this._lastMouseEvent, { offsetX: 18, offsetY: 14, margin: FLOAT_PREVIEW_MARGIN });
    }
  }

  // Same popup, red error text — what a reference to a post that can't be
  // shown gets instead of a preview.
  showError(message, ownerEl = null) {
    if (!this.el) return;
    this._ownerEl = ownerEl || null;
    this.el.innerHTML = `<div class="ppv-error">${escapeHTML(message)}</div>`;
    this.el.classList.add("is-visible");
    if (this._lastMouseEvent) {
      positionFloatingNearPointer(this.el, this._lastMouseEvent, { offsetX: 18, offsetY: 14, margin: FLOAT_PREVIEW_MARGIN });
    }
  }

  hide() {
    this._ownerEl = null;
    if (!this.el) return;
    this.el.classList.remove("is-visible");
  }
}

const previewPopup = new PostPreviewPopup();

// Pointer-following hover card for links that lead to a /user profile. Mirrors
// PostPreviewPopup but renders a compact account card (avatar, green display
// name, handle, stats, truncated bio) styled like the post preview. Proof of
// concept: wired on Notification user links first (which already carry a full
// account object); `showByAcct` lazily fetches + caches for future site-wide
// use on links that don't have the account preloaded.
class ProfilePreviewPopup {
  constructor() {
    this.el = document.getElementById("profile-preview");
    this._token = 0;                 // bumped on every show/hide to drop stale fetches
    this._cache = new Map();         // acct -> account entity
    this._lastMouseEvent = null;
    this._onMouseMove = this._onMouseMove.bind(this);
    document.addEventListener("mousemove", this._onMouseMove);
  }

  _onMouseMove(e) {
    this._lastMouseEvent = e;
    if (!this.el || !this.el.classList.contains("is-visible")) return;
    positionFloatingNearPointer(this.el, e, { offsetX: 18, offsetY: 14, margin: FLOAT_PREVIEW_MARGIN });
  }

  // Render immediately from a known account object.
  show(account) {
    if (!this.el || !account) return;
    this._token++;
    this.el.innerHTML = this._cardHTML(account);
    this.el.classList.add("is-visible");
    applyAnonymousPresentation(this.el);
    if (this._lastMouseEvent) {
      positionFloatingNearPointer(this.el, this._lastMouseEvent, { offsetX: 18, offsetY: 14, margin: FLOAT_PREVIEW_MARGIN });
    }
  }

  // Resolve an account by handle (cached), then show — for links lacking a
  // preloaded account.
  async showByAcct(acct) {
    if (!acct) return;
    const cached = this._cache.get(acct);
    if (cached) { this.show(cached); return; }
    const token = ++this._token;
    try {
      const r = await apiFetch(`/api/v1/accounts/lookup?acct=${encodeURIComponent(acct)}`);
      if (!r.ok) return;
      const account = await r.json();
      this._cache.set(acct, account);
      // Only show if this hover is still the active one.
      if (token === this._token) this.show(account);
    } catch { /* leave hidden */ }
  }

  hide() {
    this._token++;
    if (!this.el) return;
    this.el.classList.remove("is-visible");
  }

  _cardHTML(account) {
    const emojis = account.emojis || [];
    const nameHTML = renderTextWithCustomEmoji(account.display_name || account.username || "", emojis);
    const acct = "@" + (account.acct || account.username || "");
    const followers = Number(account.followers_count) || 0;
    const following = Number(account.following_count) || 0;
    const subscribers = Number(account.subscribers_count) || 0;
    const posts = Number(account.statuses_count) || 0;
    const social = `${followers} / ${following} / ${subscribers}`;
    const noteHTML = account.note ? renderSafeHTML(String(account.note).trim(), emojis) : "";

    const avatar = isSafeEmojiSrc(account.avatar)
      ? `<img class="profile-avatar" src="${escapeHTML(account.avatar)}" alt="" loading="lazy">`
      : "";
    const badges =
      (account.bot ? '<i class="fa-solid fa-robot profile-badge" title="Bot account"></i>' : "") +
      (account.locked ? '<i class="fa-solid fa-lock profile-badge" title="Locked account"></i>' : "");

    return `<div class="profile-card">
      ${avatar}
      <div class="profile-info">
        <div class="profile-display-name"><span>${nameHTML}</span>${badges}</div>
        <div class="profile-acct">${escapeHTML(acct)}</div>
        <div class="profile-stats">
          <span class="profile-stat" title="${followers} followers, ${following} following, ${subscribers} subscribers">${escapeHTML(social)}</span>
          <span class="profile-stat" title="${posts} posts">${posts} posts</span>
        </div>
        ${noteHTML ? `<div class="profile-note is-collapsed">${noteHTML}</div>` : ""}
      </div>
    </div>`;
  }
}

const profilePopup = new ProfilePreviewPopup();

// Wire up quotelinks and post-no links after DOM insertion
//
// A line in a post body is "greentext" if its first non-whitespace
// character is `>`. We accept both `>foo` and `> foo` (the leading
// whitespace is consumed by the regex check; the text node itself is
// preserved verbatim so any trailing whitespace or following content
// stays intact). Lines after a <br> and lines at the very start of a
// block are both candidates.
//
// We deliberately walk every text-bearing container rather than only
// <blockquote> elements — the 4chan convention is greentext at the
// top level, and the previous blockquote-only implementation missed
// posts that arrived without a <blockquote> wrapper (which is most of
// them in practice).
const GREENTEXT_TAG_NAMES = ["P", "DIV", "BLOCKQUOTE", "LI", "H1", "H2", "H3", "H4", "H5", "H6"];
const GREENTEXT_FIRST_CHAR = /^\s*>/;   // line starts with `>` (after optional leading whitespace)

function applyGreentext(container) {
  if (!container) return;
  // Include the container itself, not just descendants: callers may pass the
  // block that directly holds the text (the catalog card body / hover preview),
  // where the leading greentext line would otherwise be missed. Passing a
  // wrapper (the thread view passes the whole catalog) still works — its own
  // leading children are elements, so it's a no-op on the container.
  const descendants = container.querySelectorAll(GREENTEXT_TAG_NAMES.join(",").toLowerCase());
  const blocks = GREENTEXT_TAG_NAMES.includes(container.tagName)
    ? [container, ...descendants]
    : descendants;
  for (const el of blocks) {
    if (el.dataset.greentextDone === "1") continue;
    el.dataset.greentextDone = "1";
    // Greentext the run of text that *starts* this block (i.e. the
    // first text node(s) before any other element). On 4chan the very
    // first line of a post can be greentext, so we have to look at
    // el.firstChild — not just text that follows a <br>.
    collectGreentextFromStart(el);
    // Then any subsequent line, delimited by a <br>. Each <br> in the
    // block is a line boundary; we check the text nodes that come
    // after it and stop as soon as we hit a non-greentext one (so
    // multi-line greentext blocks like `>a<br>>b<br>>c` all get
    // wrapped, but a regular line after them doesn't).
    el.querySelectorAll("br").forEach(collectGreentextAfter);
  }
}

function collectGreentextFromStart(el) {
  let node = el.firstChild;
  // Collapsing a leading @mention prefix leaves its hidden wrapper as the
  // first child. It is not visible line content, so continue to the first
  // visible text node when deciding whether the line starts with `>`. Some
  // servers also emit leading whitespace as a separate text node rather than
  // in the same node as the greentext; skip that structural whitespace too.
  while (node && (
    (node.nodeType === 3 && !node.nodeValue.trim())
    || (node.nodeType === 1 && node.classList?.contains("post-mentions") && node.hidden)
  )) {
    node = node.nextSibling;
  }
  // The line is greentext iff its first content text node starts with `>`.
  if (!node || node.nodeType !== 3 || !GREENTEXT_FIRST_CHAR.test(node.nodeValue || "")) return;
  wrapGreentextLine(node);
}

function collectGreentextAfter(br) {
  const parent = br.parentNode;
  if (!parent) return;
  let node = br.nextSibling;
  // Skip structural whitespace at the start of the line (some servers emit
  // it as its own text node).
  while (node && node.nodeType === 3 && !node.nodeValue.trim()) node = node.nextSibling;
  if (!node || node.nodeType !== 3 || !GREENTEXT_FIRST_CHAR.test(node.nodeValue || "")) return;
  wrapGreentextLine(node);
}

// Wrap every text node on a greentext line, across inline elements like
// custom-emoji <img>s and <a> links, until a line/block boundary. The line
// has already been confirmed greentext (its leading text node starts with
// `>`); mid-line text nodes won't start with `>`, so we green the whole run
// rather than re-testing each node. Fixes e.g. `>foo :emoji: bar` where the
// emoji <img> used to cut the green after "foo".
function wrapGreentextLine(startNode) {
  let node = startNode;
  while (node) {
    if (node.nodeType === 3) {
      node = wrapGreentextTextNode(node);
      continue;
    }
    // A <br> or a nested block element starts a new line — stop here (each
    // <br> is handled by its own collectGreentextAfter call).
    if (node.nodeName === "BR") break;
    if (node.nodeType === 1 && GREENTEXT_TAG_NAMES.includes(node.tagName)) break;
    // Inline element (img, a, span, …) — part of the same line; leave it in
    // place and keep greening the text around it.
    node = node.nextSibling;
  }
}

// Replaces `node` (a text node whose contents start with `>`) with a
// <span class="greentext"> containing the same text, and returns the
// next sibling so the caller can keep walking.
function wrapGreentextTextNode(node) {
  const span = document.createElement("span");
  span.className = "greentext";
  span.textContent = node.nodeValue || "";
  node.parentNode.insertBefore(span, node);
  node.parentNode.removeChild(node);
  return span.nextSibling;
}

// A reference that resolves in neither the thread graph nor the quote index
// still names a target on the wire — chan-fe writes every reference with one,
// so the full id sits in a chan-fe permalink and a remote post has an object
// URL that search can resolve. Looked up once, on hover or click, and cached
// both ways: a post that turns out to be gone is what an imageboard calls
// "(Dead)".
const deadRefs = new Set();

// `href` is the anchor's *original* target: wireQuotelink repoints unresolved
// links at the in-page "#p<ref>" anchor, which would otherwise erase the id or
// URL the reference was written with.
function refLookupHint(a, href = "") {
  const targetId = a.dataset.quoteTargetId || "";
  if (targetId) return { id: targetId };
  const m = String(href).match(/#\/post\/([^/?#]+)/);
  if (m) return { id: decodeURIComponent(m[1]) };
  if (/^https?:/i.test(href)) return { url: href };
  return {};
}

// { status } when it's there, { dead: true } when the lookup says it's gone,
// { unknown: true } when there was nothing to look up or the lookup itself
// failed — a short ref names no target, and a network blip is not proof a post
// was deleted.
async function lookupMissingRef(a, href = "") {
  const hint = refLookupHint(a, href);
  const key = hint.id || hint.url || "";
  if (!key) return { unknown: true };
  if (deadRefs.has(key)) return { dead: true };
  // Resolving a URL goes through search, which needs auth.
  if (hint.url && !state.auth.accessToken) return { unknown: true };

  try {
    // Resolving a URL has to go through an *untyped* search. Mitra only runs
    // its URL branch — look the object up locally, then fetch and import it
    // from the server that holds it — when no `type` is given; `type=statuses`
    // routes to a full-text search over post bodies instead, which can't match
    // a URL and never federates a fetch. Asking the typed way came back empty
    // for every reference to a post this instance hadn't already seen, which
    // then got recorded as "(Dead)" — the usual case being a ref written from
    // one of your accounts and read from another.
    const found = hint.id
      ? await fetchStatus(hint.id, state)
      : ((await searchAll(hint.url, state, { limit: 1 })).statuses[0] || null);
    if (found?.id) {
      rememberQuoted(found);
      return { status: found };
    }
  } catch {
    return { unknown: true };
  }
  deadRefs.add(key);
  return { dead: true };
}

// Bind a ">>ref" anchor: hover previews the referenced post, click expands it
// inline under the host post (and collapses it again).
//
// A reference resolves in one of two places, and which one can change between
// wiring and clicking (posts stream in), so the lookup happens at event time.
// Posts in the loaded thread come from the post graph. Posts outside it —
// quoted posts, and cross-thread references to a post that some rendered post
// quotes — come from the quote index, which holds the full status, so they
// preview and expand without a fetch as well.
function wireQuotelink(a, refToken) {
  a.dataset.wiredQuoteLink = "1";
  a.classList.add("quotelink");

  const hrefAtWire = a.getAttribute("href");
  const targetId = a.dataset.quoteTargetId || "";
  if (!targetId) a.setAttribute("href", "#p" + refToken);

  const resolve = () => {
    const metadataQuote = targetId && quotedStatuses(a.closest(".post-block")?._statusData)
      .find((q) => q.id === targetId);
    if (metadataQuote) return { id: targetId, status: metadataQuote, opts: { slot: "quote", status: metadataQuote } };
    const resolved = resolveStatusId(refToken);
    const inThread = resolved ? currentThreadGraph()?.statusById.get(resolved) : null;
    if (inThread) return { id: resolved, status: inThread, opts: targetId ? { slot: "quote", status: inThread } : {} };
    const quoted = lookupQuoted({ href: hrefAtWire, ref: targetId || refToken });
    if (quoted) return { id: quoted.id, status: quoted, opts: { slot: "quote", status: quoted } };
    return null;
  };

  // Annotate from what's known now. References that only resolve later (posts
  // still streaming in) stay unannotated rather than having a note appear
  // under the reader's eyes.
  const known = resolve();
  if (known) annotateQuotelink(a, known.id, known.status);
  if (known?.opts.slot === "quote") syncQuoteLinkState(a, known.id);

  // Whatever the lookup turns up is shared by the hover and the click, so a
  // reference is only chased down once.
  let hovering = false;
  const findTarget = async () => {
    const known = resolve();
    if (known) return { target: known };
    const found = await lookupMissingRef(a, hrefAtWire);
    if (found.status) {
      annotateQuotelink(a, found.status.id, found.status);
      return { target: { id: found.status.id, status: found.status, opts: { slot: "quote", status: found.status } } };
    }
    if (found.dead) appendQuotelinkNote(a, "(Dead)");
    return { miss: found.dead ? "Post not found — deleted or no longer readable" : "Post not loaded" };
  };

  a.addEventListener("mouseenter", async () => {
    hovering = true;
    const known = resolve();
    if (known?.opts.slot === "quote" && syncQuoteLinkState(a, known.id)) {
      previewPopup.hide();
      return;
    }
    const { target, miss } = await findTarget();
    // The pointer may have left while the lookup was in flight; a popup that
    // arrives after that is a popup nobody asked for.
    if (!hovering) return;
    if (target?.opts.slot === "quote" && syncQuoteLinkState(a, target.id)) {
      previewPopup.hide();
      return;
    }
    if (target) previewPopup.show(target.status, a);
    else previewPopup.showError(miss, a);
  });
  a.addEventListener("mouseleave", () => {
    hovering = false;
    previewPopup.hide();
  });
  a.addEventListener("click", async (e) => {
    e.preventDefault();
    hovering = false;
    const host = a.closest(".post-block");
    const { target, miss } = await findTarget();
    if (host && target) {
      previewPopup.hide();
      if (target.opts.slot === "quote") syncQuoteLinkState(a, target.id);
      toggleInlinePost(host, target.id, target.opts);
    } else {
      previewPopup.showError(miss || "Post not loaded", a);
    }
  });
}

// Turn `>>ref` written as plain text into a quotelink anchor. On an imageboard
// that's how a reference is typed, and Mitra keeps it literal (comrak's
// greentext extension means `>>abc` is text, not nested blockquotes), so the
// text arrives verbatim and this pass gives it its markup. Ordinary words
// after `>>` are greentext too: only nine-digit post numbers become links.
//
// Runs over every text-bearing block, not just <blockquote> — a bare ref
// usually sits in an ordinary paragraph. Behaviour is bound separately by
// wireThreadLinks, so this is safe on containers that are display-only (catalog
// cards, hover previews), which is what keeps a ref looking the same there.
const BARE_POST_REF_RE = new RegExp(String.raw`>>(${POST_REF_PATTERN})(?![\p{L}\p{N}_-])`, "gu");

function linkifyPostRefs(container) {
  if (!container) return;
  const blocks = GREENTEXT_TAG_NAMES.includes(container.tagName)
    ? [container, ...container.querySelectorAll(GREENTEXT_TAG_NAMES.join(",").toLowerCase())]
    : Array.from(container.querySelectorAll(GREENTEXT_TAG_NAMES.join(",").toLowerCase()));

  for (const block of blocks) {
    if (block.dataset.linkifiedRefs === "1") continue;
    block.dataset.linkifiedRefs = "1";

    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let n;
    while ((n = walker.nextNode())) nodes.push(n);

    for (const node of nodes) {
      const text = node.nodeValue || "";
      if (!text.includes(">>")) continue;
      // Skip text already inside an anchor — splitting it would nest anchors
      // and mangle the DOM.
      if (node.parentElement?.closest("a")) continue;

      const frag = document.createDocumentFragment();
      let last = 0;
      BARE_POST_REF_RE.lastIndex = 0;

      for (let m; (m = BARE_POST_REF_RE.exec(text)); ) {
        const refToken = m[1];
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));

        const a = document.createElement("a");
        a.className = "quotelink";
        a.href = "#p" + refToken;
        a.textContent = ">>" + refToken;

        frag.appendChild(a);
        last = m.index + m[0].length;
      }
      if (!last) continue;

      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      node.parentNode?.replaceChild(frag, node);
    }
  }
}

// Hide what trails a `>>ref` and says the same thing it does:
//
//  - The `@handle`. It's what actually notifies the referenced author — a
//    reference on its own notifies nobody — but on screen it's redundant next
//    to the ref, which already names the post and previews its author on hover.
//  - A link to the post the ref names. Servers that render no markdown store
//    the composer's reference as `>>ref` plus a bare URL (see postbody.js), so
//    the ref reads as a ref off this frontend too; here the URL is noise.
//
// Both stay in the content, so they federate and still show on other frontends.
// A trailing link is only absorbed when it hashes to the ref beside it, which
// is the ref naming its own target and nothing else — so a link the author
// wrote about some *other* post is left where they put it.
function absorbRefMentions(container) {
  if (!container) return;
  const isMention = (n) => n?.nodeType === 1 && (
    n.classList?.contains("mention")
    || (n.classList?.contains("h-card") && n.querySelector(".mention"))
  );
  const isWhitespace = (n) => n?.nodeType === 3 && !n.nodeValue.trim();
  const isRefUrl = (n, ref) => ref && n?.nodeType === 1 && n.tagName === "A"
    && !n.classList?.contains("quotelink")
    && refForObjectUrl(n.getAttribute("href")) === ref;

  for (const a of container.querySelectorAll("a.quotelink")) {
    const ref = anchorRefToken(a);
    const absorbed = [];
    // Whitespace only comes along if something absorbable follows it, so the
    // space before the rest of the line survives.
    let pending = [];
    for (let node = a.nextSibling; node; node = node.nextSibling) {
      if (isWhitespace(node)) { pending.push(node); continue; }
      if (!isMention(node) && !isRefUrl(node, ref)) break;
      absorbed.push(...pending, node);
      pending = [];
    }
    if (!absorbed.length) continue;

    const wrap = document.createElement("span");
    wrap.className = "post-mentions is-ref-mention";
    wrap.hidden = true;
    a.parentNode?.insertBefore(wrap, absorbed[0]);
    for (const n of absorbed) wrap.appendChild(n);
  }
}

function wireThreadLinks(container) {
  // `>>ref` typed as plain text becomes an anchor first, so the pass below
  // binds behaviour to hand-typed references and to the markdown/object links
  // chan-fe writes alike.
  linkifyPostRefs(container);

  // Bind every reference anchor — markdown [>>id](url) links from multi-post
  // replies, object links pointing at a quoted post, and the ones just
  // linkified — as chan-fe quotelinks: red, hover-previewed, click-to-expand.
  container.querySelectorAll("a").forEach((a) => {
    if (a.dataset.wiredQuoteLink === "1") return;
    const refToken = anchorRefToken(a);
    if (!refToken && !a.dataset.quoteTargetId) return;
    wireQuotelink(a, refToken);
  });

  absorbRefMentions(container);

  // Greentext any line whose first non-whitespace character is `>`,
  // whether the line sits at the start of a block (before any <br>) or
  // follows a <br>. We override the server's blockquote markdown
  // convention here: 4chan-style `>foo` / `> foo` is greentext, not a
  // nested blockquote. Runs against every text-bearing container in the
  // post body so this works regardless of whether the server wrapped
  // the content in a <p>, <div>, or left it bare.
  applyGreentext(container);

  container.querySelectorAll(".post-no").forEach((noLink) => {
    if (noLink.dataset.wiredPostNo === "1") return;
    noLink.dataset.wiredPostNo = "1";

    // A No. inside a quote block belongs to a post from *another* thread, and
    // one on the index belongs to a thread that isn't open at all. An
    // in-thread anchor would only mark the copy on screen, so point both at
    // the originating thread instead (loadPost re-roots on that thread's OP
    // and marks the post).
    const outOfThread = !!noLink.closest(".is-quote-stack") || !!noLink.closest(".index-thread");
    if (outOfThread) {
      const quotedId = noLink.closest(".post-block")?.dataset.id;
      if (quotedId) {
        const href = "#/post/" + encodeURIComponent(quotedId);
        noLink.setAttribute("href", href);
        noLink.title = "Go to this post in its thread";
        noLink.addEventListener("click", (e) => {
          if (e.ctrlKey || e.metaKey) return;  // let the browser open a new tab
          e.preventDefault();
          // Same hash → no hashchange fires, so load it directly.
          if (location.hash === href) loadPost(quotedId);
          else location.hash = href;
        });
        return;
      }
    }

    noLink.addEventListener("click", (e) => {
      e.preventDefault();
      const post = noLink.closest(".post-block");
      const postId = post?.dataset.id;
      if (!post || !postId) return;

      // Mark this post as the linked-to one, clearing any previously marked
      // post — only one is highlighted at a time. Tracking it in state (not
      // just on the node) keeps the mark through a refresh and lets the
      // permalink below reopen the thread with the same post marked.
      if (state.postView) state.postView.highlightId = postId;
      applyLinkedHighlight();

      // Keep the URL bar in sync with the in-thread anchor style (#p...). No
      // scroll — the post is right here; clicking its own No. shouldn't move
      // the page.
      const nextHash = "#p" + postId;
      if (location.hash !== nextHash) {
        history.replaceState(null, "", nextHash);
      }
    });
  });

  container.querySelectorAll(".post-id").forEach((idLink) => {
    if (idLink.dataset.wiredPostId === "1") return;
    idLink.dataset.wiredPostId = "1";
    idLink.addEventListener("click", (e) => {
      e.preventDefault();
      const host = idLink.closest(".post-block");
      const postId = host?.dataset.id || "unknown";
      const parent = host?._statusData || { id: postId };
      openComposeReply(parent);
    });
  });
  for (const post of container.querySelectorAll(".post-block:not(.is-inline)")) {
    expandQuotedPosts(post, post._statusData);
  }
}

// Shared post-content formatter: sanitized HTML + custom emoji + greentext.
// The reply view renders bodies this way (renderSafeHTML, then wireThreadLinks
// at the container level); this wraps the same functions so the catalog card
// body and the hover preview format content identically instead of dumping raw
// text. `interactive` additionally wires >>quote / No. links and their hover
// previews — only meaningful inside the thread view, where the post graph those
// links resolve against actually exists.
function renderPostContentInto(el, status, { interactive = false } = {}) {
  if (!el) return;
  const { strippedContent } = extractSummary(status);
  el.innerHTML = renderSafeHTML(strippedContent, status?.emojis || [], { mentions: status?.mentions });
  const quoted = indexQuotedStatuses(status);
  rewriteQuoteLinks(el, quoted);
  ensureQuoteRefLines(el, quoted);
  hoistTrailingRefs(el);
  if (interactive) {
    wireThreadLinks(el);
  } else {
    // Display-only: references still render as quotelinks (and swallow their
    // trailing @handle), they just aren't hover/click-bound.
    linkifyPostRefs(el);
    absorbRefMentions(el);
    applyGreentext(el);
  }
}

// ---- inline media embeds in post bodies ----
//
// 4chan-x-style embed lines for links to media: YouTube and PeerTube videos,
// and direct media files by extension (catbox, proxied instance media, etc.). A media
// link becomes:
//   <icon> [YouTube] <title> (embed)      -- fa-youtube, title via oEmbed
//   <icon> <url> (embed)                  -- fa-film (video) / fa-music (audio)
//                                            / fa-image (image)
// The title/url still links to the original; clicking (embed) inserts the
// player inline beneath the line (iframe for YouTube, <video>/<audio>/<img>
// for direct files) and toggles to (close).

const MEDIA_VIDEO_EXTS = new Set(["mp4", "webm", "ogv", "mov", "m4v", "mkv"]);
const MEDIA_AUDIO_EXTS = new Set(["mp3", "ogg", "oga", "wav", "m4a", "flac", "opus", "aac"]);
// Raster formats only. SVG is deliberately absent: it's a document format that
// can carry scripts and external references, and nothing here needs it.
const MEDIA_IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "avif", "apng", "bmp", "jfif"]);

// Classify a link URL as embeddable media. Returns { kind, ... } or null.
//   kind "youtube"  -> { id }   (watch, youtu.be, /embed/, /shorts/)
//   kind "peertube" -> { id, origin } (https://instance/w/<id>)
//   kind "video"    -> { ext, url }
//   kind "audio"    -> { ext, url }
//   kind "image"    -> { ext, url }
function classifyMediaUrl(rawUrl) {
  if (!rawUrl) return null;
  let url;
  try { url = new URL(rawUrl, location.origin); } catch { return null; }
  // Every embeddable source must ultimately resolve to HTTP(S). This keeps
  // extension-looking values on schemes such as javascript: or data: from
  // reaching media element src attributes.
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  const host = url.hostname.toLowerCase();

  // YouTube — extract the video id from the common URL shapes.
  if (host === "youtu.be" || host === "youtube.com" ||
      host === "www.youtube.com" || host === "m.youtube.com" ||
      host.endsWith(".youtube.com")) {
    let id = null;
    if (host === "youtu.be") {
      id = url.pathname.slice(1).split("/")[0];
    } else if (url.pathname === "/watch") {
      id = url.searchParams.get("v");
    } else {
      const m = url.pathname.match(/^\/(?:embed|shorts|v)\/([^/?#]+)/);
      if (m) id = m[1];
    }
    if (id && /^[A-Za-z0-9_-]{6,}$/.test(id)) return { kind: "youtube", id, url: rawUrl };
    return null;
  }

  // PeerTube's canonical short watch URL. Require HTTPS and a conservative
  // opaque id before deriving the instance's /videos/embed/ URL; arbitrary
  // post links never become frames.
  if (url.protocol === "https:" && url.origin !== location.origin) {
    const peerTubeMatch = url.pathname.match(/^\/w\/([A-Za-z0-9_-]{6,})\/?$/);
    if (peerTubeMatch) {
      return { kind: "peertube", id: peerTubeMatch[1], origin: url.origin, url: rawUrl };
    }
  }

  // Direct media by file extension on the path (ignores query strings, so
  // signed/proxied URLs like ".../file.mp4?token=…" still match).
  const m = url.pathname.toLowerCase().match(
    /\.(mp4|webm|ogv|mov|m4v|mkv|mp3|ogg|oga|wav|m4a|flac|opus|aac|jpg|jpeg|png|gif|webp|avif|apng|bmp|jfif)$/,
  );
  if (m) {
    const ext = m[1];
    const kind = MEDIA_AUDIO_EXTS.has(ext) ? "audio"
      : MEDIA_IMAGE_EXTS.has(ext) ? "image"
      : "video";
    return { kind, ext, url: rawUrl };
  }
  return null;
}

// Best-effort YouTube title via oEmbed. Probed once for reachability: if the
// endpoint is blocked (CORS / network), we stop trying and fall back to the
// video id, so we never fire a failing request per video. Results are cached
// per id for the session.
let _ytReachable = null;             // Promise<boolean> | null
const _ytTitleCache = new Map();     // id -> Promise<string|null>
function ytOembedUrl(id) {
  return "https://www.youtube.com/oembed?url=" +
    encodeURIComponent("https://www.youtube.com/watch?v=" + id) + "&format=json";
}
function ytReachable(id) {
  if (_ytReachable) return _ytReachable;
  // Any response (even a 404 for a bad video) means the endpoint is reachable;
  // a CORS/network failure throws and resolves false.
  _ytReachable = fetch(ytOembedUrl(id), { mode: "cors" })
    .then(() => true)
    .catch(() => false);
  return _ytReachable;
}
function fetchYouTubeTitle(id) {
  if (_ytTitleCache.has(id)) return _ytTitleCache.get(id);
  const p = ytReachable(id).then((ok) => {
    if (!ok) return null;
    return fetch(ytOembedUrl(id))
      .then((r) => r.ok ? r.json() : null)
      .then((j) => (j && j.title) ? String(j.title) : null)
      .catch(() => null);
  });
  _ytTitleCache.set(id, p);
  return p;
}

// Build the inline player element for an embedded media (created when the
// user clicks (embed), removed again on (close) so playback stops).
function buildMediaElement(media) {
  if (media.kind === "youtube") {
    const f = document.createElement("iframe");
    f.src = `https://www.youtube.com/embed/${encodeURIComponent(media.id)}?autoplay=1&rel=0`;
    f.allow = "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen";
    f.allowFullscreen = true;
    f.loading = "lazy";
    f.className = "media-embed-youtube";
    return f;
  }
  if (media.kind === "peertube") {
    const f = document.createElement("iframe");
    f.src = `${media.origin}/videos/embed/${encodeURIComponent(media.id)}`;
    f.title = "PeerTube video";
    f.width = "560";
    f.height = "315";
    f.allow = "fullscreen";
    f.allowFullscreen = true;
    // PeerTube needs script execution and its own origin for playback, but not
    // forms, popups, top navigation, downloads, or access to the parent page.
    // classifyMediaUrl also refuses same-origin frames, so scripts cannot pair
    // allow-same-origin with the parent's origin to escape the sandbox.
    f.setAttribute("sandbox", "allow-same-origin allow-scripts");
    f.loading = "lazy";
    f.referrerPolicy = "strict-origin-when-cross-origin";
    f.className = "media-embed-peertube";
    return f;
  }
  if (media.kind === "image") {
    const img = document.createElement("img");
    img.src = media.url;
    img.loading = "lazy";
    img.decoding = "async";
    // The link right above carries the URL; the image itself adds nothing for
    // a screen reader, so keep it out of the accessibility tree.
    img.alt = "";
    img.className = "media-embed-image";
    return img;
  }
  if (media.kind === "audio") {
    const a = document.createElement("audio");
    a.src = media.url;
    a.controls = true;
    a.preload = "metadata";
    a.className = "media-embed-audio";
    return a;
  }
  // video
  const v = document.createElement("video");
  v.src = media.url;
  v.controls = true;
  v.preload = "metadata";
  v.className = "media-embed-video";
  return v;
}

// Build the embed line for a classified media link: icon + link + toggle, with
// a hidden container that the toggle fills / empties.
function buildMediaEmbed(media) {
  const wrap = document.createElement("span");
  wrap.className = "media-embed";
  wrap.dataset.kind = media.kind;

  const line = document.createElement("span");
  line.className = "media-embed-line";

  const icon = document.createElement("i");
  icon.className = media.kind === "youtube"
    ? "fa-brands fa-youtube media-embed-icon"
    : media.kind === "peertube"
      ? "fa-solid fa-circle-play media-embed-icon"
    : media.kind === "audio"
      ? "fa-solid fa-music media-embed-icon"
    : media.kind === "image"
      ? "fa-solid fa-image media-embed-icon"
      : "fa-solid fa-film media-embed-icon";
  line.appendChild(icon);

  const link = document.createElement("a");
  link.className = "media-embed-link";
  link.href = media.url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.dataset.mediaEmbed = "1";   // guard: this link is the embed's own, not a fresh media link
  if (media.kind === "youtube") {
    link.textContent = `[YouTube] ${media.id}`;
    // Swap in the real title when oEmbed resolves; harmless if detached.
    fetchYouTubeTitle(media.id).then((title) => {
      if (title) link.textContent = `[YouTube] ${title}`;
    });
  } else if (media.kind === "peertube") {
    link.textContent = `[PeerTube] ${media.id}`;
  } else {
    link.textContent = media.url;
  }
  line.appendChild(link);

  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "media-embed-toggle";
  toggle.textContent = "(embed)";
  line.appendChild(toggle);

  wrap.appendChild(line);

  const container = document.createElement("span");
  container.className = "media-embed-container";
  container.hidden = true;
  wrap.appendChild(container);

  toggle.addEventListener("click", () => {
    if (container.hasChildNodes()) {
      container.innerHTML = "";     // drop the player → stops playback
      container.hidden = true;
      toggle.textContent = "(embed)";
      wrap.classList.remove("is-expanded");
    } else {
      container.appendChild(buildMediaElement(media));
      container.hidden = false;
      toggle.textContent = "(close)";
      wrap.classList.add("is-expanded");
    }
  });

  return wrap;
}

// Replace media links inside a post body with embed lines. Runs once at post
// creation (threadPostBlock); reconciliation keeps the element (and any
// expanded media) across refreshes.
function embedMediaLinks(bodyEl) {
  if (!bodyEl) return;
  const links = Array.from(bodyEl.querySelectorAll("a[href]"));
  for (const a of links) {
    if (a.dataset.mediaEmbed === "1") continue;
    const media = classifyMediaUrl(a.getAttribute("href") || "");
    if (!media) continue;
    a.dataset.mediaEmbed = "1";
    a.replaceWith(buildMediaEmbed(media));
  }
}

// The first post a status references, or null. Cards and badges present a
// single representative quote even when the post links several.
function getQuotedStatus(status) {
  return quotedStatuses(status)[0] || null;
}

// Eat a "RE:"/"RT:" label and any <br>/whitespace sitting immediately before a
// quote link. Servers prepend it when they append a quote to the content
// (Mitra's insert_quote, Pleroma's InlineQuotePolicy, Mastodon quote posts);
// chan-fe shows the reference as a >>quotelink, so the label is noise.
function eatQuoteLabelBefore(node) {
  let prev = node.previousSibling;
  // Only an actual label is consumed. The plain space before a link is
  // ordinary text — eating it would run two adjacent references together.
  if (!(prev?.nodeType === 3 && /R[ET]:\s*$/i.test(prev.nodeValue || ""))) return false;
  prev.nodeValue = prev.nodeValue.replace(/\s*R[ET]:\s*$/i, "");

  // Then the whitespace and line breaks the label sat on.
  while (prev) {
    if (prev.nodeType === 3) {
      if (prev.nodeValue.trim() !== "") break;
      const p = prev.previousSibling; prev.remove(); prev = p; continue;
    }
    if (prev.nodeType === 1 && prev.tagName === "BR") { const p = prev.previousSibling; prev.remove(); prev = p; continue; }
    break;
  }
  return true;
}

// Turn every link that points at a referenced post into an imageboard
// quotelink: relabel it ">>ref" and tag it with the target id so
// wireThreadLinks can bind the hover preview and inline expansion without
// resolving the URL again. Matched on the referenced post's own uri/url, so
// ordinary body links are left alone.
//
// A target named more than once keeps its first (in-body) mention and loses
// the rest — which is how the trailing "RE: <url>" line a quote post appends
// disappears when the body already references that post inline.
function rewriteQuoteLinks(containerEl, quoted) {
  if (!containerEl || !quoted?.length) return;

  const byUrl = new Map();
  for (const q of quoted) {
    for (const u of [q.uri, q.url]) {
      const key = normalizeObjectUrl(u);
      if (key) byUrl.set(key, q);
    }
  }

  const seen = new Set();
  for (const a of Array.from(containerEl.querySelectorAll("a[href]"))) {
    const target = byUrl.get(normalizeObjectUrl(a.getAttribute("href")));
    if (!target) continue;

    const parent = a.parentNode;
    // Remember that this was a server-appended RE:/RT: reference. Some
    // servers put it after the prose inside the *same* paragraph, where the
    // block-based hoister below cannot distinguish it from inline prose.
    // ensureQuoteRefLines can safely extract these known machine labels.
    if (eatQuoteLabelBefore(a)) a.dataset.serverAppendedQuote = "1";

    if (seen.has(target.id)) {
      a.remove();
      // Drop a now-empty wrapper (e.g. <p>RE: <a/></p>).
      if (parent && parent !== containerEl && parent.nodeType === 1 &&
          parent.textContent.trim() === "" && !parent.querySelector("img")) {
        parent.remove();
      }
      continue;
    }
    seen.add(target.id);

    a.textContent = postRefLabel(target);
    a.classList.add("quotelink");
    a.dataset.quoteTargetId = target.id;
    // Point at chan-fe's own permalink so a modified click opens the post here
    // rather than on the originating server; a plain click expands it in place.
    a.setAttribute("href", threadHref(target.id));
    a.removeAttribute("target");
    a.removeAttribute("rel");
    annotateQuotelink(a, target.id, target);
  }
}

// Ensure every metadata-backed quote is visible even when the server did not
// serialize a link into status.content. Known server-appended RE:/RT: links
// are extracted even when the server put them inside the prose paragraph;
// genuinely inline author-written links stay where they were. Missing and
// extracted links get dedicated first-line blocks, so quote-only posts have a
// body and machine-generated quote references consistently precede the text.
function ensureQuoteRefLines(containerEl, quoted) {
  if (!containerEl || !quoted?.length) return;

  const existing = new Map();
  for (const a of containerEl.querySelectorAll("a.quotelink[data-quote-target-id]")) {
    if (!existing.has(a.dataset.quoteTargetId)) existing.set(a.dataset.quoteTargetId, a);
  }
  const fragment = document.createDocumentFragment();

  for (const target of quoted) {
    if (!target?.id) continue;
    let a = existing.get(target.id) || null;

    // A labeled server-appended link may share a <p> with the post prose.
    // Pull just the link out; discard its wrapper only if that leaves it empty.
    if (a?.dataset.serverAppendedQuote === "1") {
      const oldParent = a.parentNode;
      a.remove();
      if (oldParent && oldParent !== containerEl && oldParent.nodeType === 1 &&
          oldParent.textContent.trim() === "" && !oldParent.querySelector("img")) {
        oldParent.remove();
      }
    } else if (a) {
      continue;
    }

    const line = document.createElement("p");
    line.className = "post-ref-line";

    if (!a) {
      a = document.createElement("a");
      a.className = "quotelink";
      a.dataset.quoteTargetId = target.id;
      a.href = threadHref(target.id);
      a.textContent = postRefLabel(target);
      annotateQuotelink(a, target.id, target);
    }

    line.appendChild(a);
    fragment.appendChild(line);
  }

  if (fragment.hasChildNodes()) {
    containerEl.insertBefore(fragment, containerEl.firstChild);
  }
}

// The ref an anchor names, ignoring a "(Quote)" note appended to its
// label — the note lives inside the link (clicking it follows the ref, as on
// 4chan), so it has to be excluded when reading the ref back out.
const POST_REF_ANCHOR_RE = new RegExp(`^>>(${POST_REF_PATTERN})$`);

function anchorRefToken(a) {
  const note = a.querySelector?.(".quotelink-note");
  const text = note
    ? Array.from(a.childNodes).filter((n) => n !== note).map((n) => n.textContent).join("")
    : (a.textContent || "");
  const m = text.trim().match(POST_REF_ANCHOR_RE);
  return m ? m[1] : "";
}

// Annotate a reference the way an imageboard does: "(You)" when it points at
// your own post, "(Quote)" for metadata-backed quotes or references outside
// the current thread. The notes go inside the link — they're part of the label, and clicking
// them follows the ref — in a span only so the ref can be read back out of it
// (see anchorRefToken).
function annotateQuotelink(a, targetId, targetStatus = null) {
  if (!a || a.dataset.refNoted === "1") return;
  a.dataset.refNoted = "1";

  const graph = currentThreadGraph();
  const target = targetStatus || (targetId ? graph?.statusById.get(targetId) : null);
  if (isOwnAccount(target?.account)) appendQuotelinkNote(a, "(You)");
  if (a.dataset.quoteTargetId || (targetId && graph && !graph.statusById.has(targetId))) appendQuotelinkNote(a, "(Quote)");
}

// Notes accumulate in one span so the ref itself stays readable (see
// anchorRefToken) and a note added later — "(Dead)", which is only known once
// a lookup has failed — joins the ones added at render.
function appendQuotelinkNote(a, text) {
  if (!a) return;
  let note = a.querySelector(":scope > .quotelink-note");
  if (!note) {
    note = document.createElement("span");
    note.className = "quotelink-note";
    a.appendChild(note);
  }
  if (note.textContent.includes(text)) return;
  note.textContent += " " + text;
}

// Whether a post body already names a post — as a `>>ref` (typed, or written
// by the composer) or as a link rewritten to point at it. Used to drop the
// synthesized reply-to line when the author's own text already carries it.
function bodyReferencesPost(bodyEl, postId) {
  if (!bodyEl || !postId) return false;
  const ref = postRef(postId);
  if (ref && (bodyEl.textContent || "").includes(">>" + ref)) return true;
  if (bodyEl.querySelector(`a.quotelink[data-quote-target-id="${CSS.escape(postId)}"]`)) return true;

  // A plain object-URL link also names the parent, even without a >>ref label.
  // Runs before wireThreadLinks replaces unresolved hrefs with `#p<ref>`, so
  // the anchors still carry the URL the author wrote.
  const parentUrl = normalizeObjectUrl(
    objectUrlFor(currentThreadGraph()?.statusById.get(postId)));
  if (!parentUrl) return false;
  return Array.from(bodyEl.querySelectorAll("a[href]")).some(
    (a) => normalizeObjectUrl(a.getAttribute("href")) === parentUrl);
}

// Lift a trailing reference to the top of the body. Servers append a quote to
// the end of the content ("RE: <url>"), which is where the quoted post used to
// be embedded — but on an imageboard the references a post answers come first,
// above the text. Only a reference that is the entire last block moves; one the
// author wrote into a sentence stays where they put it.
function hoistTrailingRefs(bodyEl) {
  if (!bodyEl) return;
  const blocks = Array.from(bodyEl.children);
  const trailing = [];
  for (let i = blocks.length - 1; i >= 0; i--) {
    const block = blocks[i];
    const a = block.querySelector?.("a.quotelink[data-quote-target-id]");
    if (!a || block.textContent.trim() !== a.textContent.trim()) break;
    trailing.unshift(block);
  }
  // Nothing to lift, or the post is nothing but references.
  if (!trailing.length || trailing.length === blocks.length) return;

  const first = bodyEl.firstChild;
  for (const block of trailing) {
    block.classList.add("post-ref-line");
    bodyEl.insertBefore(block, first);
  }
}

// Render status content as HTML, allowing only safe tags.
const ALLOWED_TAGS = new Set(["a", "br", "p", "img", "span", "em", "strong", "code", "pre", "blockquote", "ul", "ol", "li"]);
// True for links that leave chan-fe: http(s) URLs on another origin. Hash
// routes, relative paths and same-origin absolutes are in-app navigation.
function isExternalHref(href) {
  if (!href) return false;
  let u;
  try { u = new URL(href, location.href); } catch { return false; }
  if (u.protocol !== "http:" && u.protocol !== "https:") return false;
  return u.origin !== location.origin;
}

// The host this session's instance lives on, used to tell a local mention
// (acct "user") from a federated one (acct "user@host").
function localInstanceHost() {
  const uri = String(state.instance?.uri || "");
  if (!uri) return "";
  try { return new URL(uri.includes("//") ? uri : `https://${uri}`).hostname; } catch { return ""; }
}

function normalizeMentionUrl(url) {
  return String(url || "").replace(/\/+$/, "").toLowerCase();
}

// The acct a mention anchor points at, for rewriting it to an in-app profile
// route. The status's own `mentions` array is authoritative — the server hands
// us the canonical acct per URL. Content with no mentions array (bios, profile
// fields) falls back to the link's @name plus its host, which is how an acct is
// spelled anyway. Returns "" when the link isn't a mention we can resolve.
function mentionAcctForLink(a, acctByUrl) {
  const href = a.getAttribute("href") || "";
  if (!href) return "";
  const known = acctByUrl.get(normalizeMentionUrl(href));
  if (known) return known;
  if (!a.classList.contains("mention") || a.classList.contains("hashtag")) return "";
  const name = (a.textContent || "").trim().replace(/^@/, "").split(/\s/)[0];
  if (!name || !/^[A-Za-z0-9_.-]+$/.test(name)) return "";
  let host = "";
  try { host = new URL(href, location.href).hostname; } catch { return ""; }
  if (!host) return "";
  return host === localInstanceHost() ? name : `${name}@${host}`;
}

const ALLOWED_ATTRS = {
  a: new Set(["href", "rel", "class"]),
  img: new Set(["src", "alt", "class"]),
  span: new Set(["class"]),
};
function renderSafeHTML(html, emojis = [], { mentions = [] } = {}) {
  if (!html) return "";
  const acctByUrl = new Map();
  for (const m of mentions || []) {
    const acct = m?.acct || m?.username || "";
    if (!acct) continue;
    for (const u of [m.url, m.uri]) {
      if (u) acctByUrl.set(normalizeMentionUrl(u), acct);
    }
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  walk(tmp);
  applyCustomEmojiToTree(tmp, emojis);
  wrapUnicodeEmojiInTree(tmp);
  return tmp.innerHTML;
  function walk(node) {
    const kids = Array.from(node.childNodes);
    for (const k of kids) {
      if (k.nodeType === 1) {
        if (!ALLOWED_TAGS.has(k.tagName.toLowerCase())) {
          // Sanitize descendants before unwrapping an unsupported container.
          // The child list for this walk was captured before the unwrap, so
          // moving unsanitized children into the parent and continuing would
          // otherwise skip them entirely (e.g. <div><img onerror=...> or an
          // SVG wrapper containing a link).
          walk(k);
          while (k.firstChild) k.parentNode.insertBefore(k.firstChild, k);
          k.remove();
          continue;
        }
        const allowed = ALLOWED_ATTRS[k.tagName.toLowerCase()] || new Set();
        for (const a of Array.from(k.attributes)) {
          if (!allowed.has(a.name.toLowerCase())) k.removeAttribute(a.name);
        }
        if (k.tagName === "A") {
          const href = k.getAttribute("href") || "";
          // Allowlist safe schemes; drop javascript:/data:/etc. (and obfuscated
          // variants) rather than blocklisting a single scheme.
          if (href && !safeUrl(href)) k.removeAttribute("href");
          if (k.classList.contains("hashtag")) {
            const tag = (k.textContent || "").trim().replace(/^#/, "").slice(0, 200);
            if (tag) k.setAttribute("href", `#/tag/${encodeURIComponent(tag)}/`);
          } else {
            // @mentions point at the remote instance's own profile page; route
            // them to chan-fe's profile view instead of leaving the app.
            const acct = mentionAcctForLink(k, acctByUrl);
            if (acct) k.setAttribute("href", profileHref(acct));
          }
          // Anything still pointing off-site (a linked article, a remote
          // profile mention) opens in a new tab instead of navigating the SPA
          // away; in-app hash routes stay in this tab. `rel` is forced rather
          // than appended so a server-supplied value can't drop noopener.
          if (isExternalHref(k.getAttribute("href") || "")) {
            k.setAttribute("target", "_blank");
            k.setAttribute("rel", "noopener noreferrer");
          }
        }
        if (k.tagName === "IMG" && /^(https?:|\/)/i.test(k.getAttribute("src") || "")) {
          // ok
        } else if (k.tagName === "IMG") {
          k.removeAttribute("src");
        }
        walk(k);
      }
    }
  }
}

// ---- rendering: about page --------------------------------------------------

function renderAbout(cat) {
  // render() only clears the catalog on view-kind changes, so when render()
  // is re-invoked while we're already on the about page (e.g. once the
  // instance info finishes loading) we need to wipe the previous content
  // ourselves to avoid appending to the "loading instance info…" placeholder.
  const inst = state.instance;
  if (!inst) {
    cat.replaceChildren(makeEmptyEl("loading instance info…"));
    return;
  }
  // is-about is set centrally in render() (see there); no need to add it
  // here. Drop any leftover children (e.g. the loading placeholder from a
  // prior render) before we build the actual page.
  cat.replaceChildren();

  // ----- Header block -----
  const header = document.createElement("div");
  header.className = "about-header";
  const h1 = document.createElement("h1");
  h1.className = "about-title";
  h1.textContent = inst.title;
  header.appendChild(h1);
  const uri = document.createElement("div");
  uri.className = "about-uri";
  uri.textContent = `@${inst.uri}`;
  header.appendChild(uri);
  if (inst.short_description) {
    const sd = document.createElement("p");
    sd.className = "about-tagline";
    sd.textContent = inst.short_description;
    header.appendChild(sd);
  }
  cat.appendChild(header);

  // ----- About block (long description) -----
  if (inst.description) {
    const block = document.createElement("section");
    block.className = "about-block";
    block.innerHTML = `<h2>About</h2><div class="about-html">${renderSafeHTML(inst.description)}</div>`;
    cat.appendChild(block);
  }

  // ----- Stats grid -----
  const stats = inst.stats || {};
  const statCards = [
    { label: "Users",     value: countFmt(stats.user_count) },
    { label: "Statuses",  value: countFmt(stats.status_count) },
    { label: "Domains",   value: countFmt(stats.domain_count) },
  ];
  const statsBlock = document.createElement("section");
  statsBlock.className = "about-block";
  statsBlock.innerHTML = `<h2>Stats</h2>`;
  const grid = document.createElement("div");
  grid.className = "about-stats";
  for (const s of statCards) {
    const cell = document.createElement("div");
    cell.className = "about-stat";
    cell.innerHTML = `<div class="about-stat-value">${escapeHTML(s.value)}</div><div class="about-stat-label">${escapeHTML(s.label)}</div>`;
    grid.appendChild(cell);
  }
  statsBlock.appendChild(grid);
  cat.appendChild(statsBlock);

  // ----- Configuration -----
  const cfg = inst.configuration || {};
  const registrations = inst.registrations
    ? (inst.approval_required ? "Invite only" : "Open")
    : "Closed";
  const authMethods = (inst.authentication_methods || []).join(", ") || "—";
  const media = (cfg.media_attachments?.supported_mime_types || []).length + " types";
  const configRows = [
    { label: "Registrations", value: registrations },
    { label: "Version",       value: inst.version || "—" },
    { label: "Auth methods",  value: authMethods },
    { label: "Media",         value: media },
    { label: "Status limit",  value: cfg.statuses ? `${countFmt(cfg.statuses.max_characters)} chars · ${cfg.statuses.max_media_attachments} attachments` : "—" },
  ];
  if (inst.pleroma?.metadata?.features) {
    configRows.push({ label: "Features", value: inst.pleroma.metadata.features.join(", ") });
  }
  const cfgBlock = document.createElement("section");
  cfgBlock.className = "about-block";
  cfgBlock.innerHTML = `<h2>Configuration</h2>`;
  const list = document.createElement("dl");
  list.className = "about-config";
  for (const row of configRows) {
    const dt = document.createElement("dt");
    dt.textContent = row.label;
    const dd = document.createElement("dd");
    dd.textContent = row.value;
    list.appendChild(dt);
    list.appendChild(dd);
  }
  cfgBlock.appendChild(list);
  cat.appendChild(cfgBlock);

  // ----- Contact account -----
  if (inst.contact_account) {
    const c = inst.contact_account;
    const block = document.createElement("section");
    block.className = "about-block about-contact";
    block.innerHTML = `<h2>Contact</h2>`;
    const inner = document.createElement("div");
    inner.className = "contact-card";
    inner.innerHTML = `
      ${c.avatar ? `<img class="contact-avatar" src="${escapeHTML(c.avatar)}" alt="" loading="lazy">` : ""}
      <div class="contact-info">
        <div class="contact-name">${renderTextWithCustomEmoji(c.display_name || c.username || "", c.emojis || [])}</div>
        <div class="contact-handle">@${escapeHTML(c.acct || c.username || "")}</div>
        ${c.note ? `<div class="contact-note">${renderSafeHTML(c.note)}</div>` : ""}
      </div>
    `;
    block.appendChild(inner);

    // Fields (XMPP, PGP, etc.)
    if (c.fields && c.fields.length) {
      const fwrap = document.createElement("dl");
      fwrap.className = "about-config contact-fields";
      for (const f of c.fields) {
        const dt = document.createElement("dt");
        dt.textContent = f.name;
        const dd = document.createElement("dd");
        dd.innerHTML = renderSafeHTML(f.value);
        fwrap.appendChild(dt);
        fwrap.appendChild(dd);
      }
      block.appendChild(fwrap);
    }
    cat.appendChild(block);
  }
}

// ---- top-level render dispatch ---------------------------------------------

// Freshness belongs to the displayed timeline, not to UI-only re-renders or
// pagination. Keep each route's last successful fetch while navigating.
const viewRefreshTimes = new Map();
const REFRESH_AGE_INTERVAL_MS = 10000;
let refreshAgeTimer = null;

function markViewRefreshed(view) {
  if (state.view !== view) return;
  viewRefreshTimes.set(hashForRoute(view), Date.now());
  if (refreshAgeTimer !== null) clearInterval(refreshAgeTimer);
  refreshAgeTimer = setInterval(syncRefreshAge, REFRESH_AGE_INTERVAL_MS);
  syncRefreshAge();
}

function syncRefreshAge() {
  const wrap = document.getElementById("refresh-age");
  const button = document.getElementById("refresh-age-btn");
  if (!wrap || !button) return;
  wrap.hidden = !["board", "profile", "tag"].includes(state.view?.kind);
  if (wrap.hidden) return;
  button.disabled = !!state.loading;
  const refreshedAt = viewRefreshTimes.get(hashForRoute(state.view));
  let label = "Refresh";
  if (refreshedAt !== undefined) {
    const seconds = Math.max(0,
      Math.floor((Date.now() - refreshedAt) / REFRESH_AGE_INTERVAL_MS) * 10);
    const [amount, unit] = seconds < 60 ? [seconds, "second"]
      : seconds < 3600 ? [Math.floor(seconds / 60), "minute"]
      : seconds < 86400 ? [Math.floor(seconds / 3600), "hour"]
      : [Math.floor(seconds / 86400), "day"];
    label = `${amount} ${unit}${amount === 1 ? "" : "s"} ago`;
  }
  if (button.textContent !== label) button.textContent = label;
}

// Reflect state.loading on the toolbar refresh button so its icon can
// spin while a fetch is in flight. Called from render() so manual
// refreshes and the auto-refresh timer are both covered.
//
// The class is held for at least one full rotation of the spin animation
// even if the fetch finishes faster, so quick loads still give visible
// feedback that a refresh happened.
let refreshSpinStartedAt = 0;
let refreshSpinClearTimer = null;
const REFRESH_SPIN_MIN_MS = 1000; // matches the CSS animation duration

function syncRefreshButton() {
  const btn = document.getElementById("toolbar-refresh");
  if (!btn) return;

  if (state.loading) {
    // New fetch in flight: cancel any pending clear and restart the clock
    // so each refresh is guaranteed to show a full rotation.
    if (refreshSpinClearTimer != null) {
      clearTimeout(refreshSpinClearTimer);
      refreshSpinClearTimer = null;
    }
    refreshSpinStartedAt = performance.now();
    if (!btn.classList.contains("is-loading")) {
      btn.classList.add("is-loading");
    }
  } else if (btn.classList.contains("is-loading")) {
    // Fetch done: only schedule a clear once, and only if at least one
    // full rotation has elapsed.
    if (refreshSpinClearTimer != null) return;
    const elapsed = performance.now() - refreshSpinStartedAt;
    const remaining = Math.max(0, REFRESH_SPIN_MIN_MS - elapsed);
    refreshSpinClearTimer = setTimeout(() => {
      btn.classList.remove("is-loading");
      refreshSpinClearTimer = null;
    }, remaining);
  }
}

// Floating [Top] bar, catalog-style views only — threads have their own
// in-flow [Top] nav after the last post. Appears once the user has scrolled
// about a screen down; hidden again near the top and on other view kinds.
const SCROLL_TOP_BAR_KINDS = new Set(["board", "profile", "tag", "search"]);

function updateScrollTopBar() {
  const bar = document.getElementById("scroll-top-bar");
  if (!bar) return;
  bar.hidden = !SCROLL_TOP_BAR_KINDS.has(state.view?.kind)
    || window.scrollY < window.innerHeight;
}

// ---- "N new replies" floating bar (thread views) ---------------------------
//
// When a background poll brings in replies the reader can't see (they've
// scrolled up), a bar appears counting them; clicking jumps to the first new
// reply. While the reader is parked at the bottom, new replies flow in inline
// and the page is pinned to the newest, so no bar is shown. "Acknowledged"
// (caught-up) state lives on postView.acknowledgedAt and only advances on an
// explicit action — opening the thread, scrolling to the bottom, or clicking
// the bar — so the count accumulates across polls until dismissed.

const NEW_REPLIES_BOTTOM_THRESHOLD = 120;  // px from document bottom counts as "watching"

function threadAtBottom() {
  return (window.innerHeight + window.scrollY) >= document.body.scrollHeight - NEW_REPLIES_BOTTOM_THRESHOLD;
}

// First reply-block in DOM order newer than the acknowledged baseline.
function firstNewReplyEl() {
  const pv = state.postView;
  const since = pv?.acknowledgedAt || "";
  const graph = pv?.graph;
  const wrap = document.querySelector(".replies-wrap");
  if (!since || !graph || !wrap) return null;
  for (const child of wrap.children) {
    if (!child.classList?.contains("post-block")) continue;
    const st = graph.statusById.get(child.dataset.id);
    if (st && (st.created_at || "") > since) return child;
  }
  return null;
}

// Mark every currently-loaded reply as seen and hide the bar.
function acknowledgeNewReplies() {
  const pv = state.postView;
  if (!pv) return;
  const latest = threadLatestCreatedAt(pv.context);
  if (latest && latest > (pv.acknowledgedAt || "")) pv.acknowledgedAt = latest;
  acknowledgeWatchedThread(pv.context);
  const bar = document.getElementById("new-replies-bar");
  if (bar) bar.hidden = true;
}

function jumpToFirstNewReply() {
  const target = firstNewReplyEl();
  state.ui.scrollJumpSeq++;
  if (target) {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }
}

// Pure view update: show/refresh/hide the bar from current state. When the
// reader is parked at the bottom, silently catch up instead of teasing a bar
// for content they can already see.
function updateNewRepliesBar() {
  const bar = document.getElementById("new-replies-bar");
  if (!bar) return;
  const pv = state.postView;
  if (state.view?.kind !== "post" || !pv) { bar.hidden = true; return; }
  if (threadAtBottom()) { acknowledgeNewReplies(); return; }
  const count = countNewReplies(pv.context, pv.acknowledgedAt || "");
  if (count <= 0) { bar.hidden = true; return; }
  bar.hidden = false;
  const label = bar.querySelector(".new-replies-count");
  if (label) label.textContent = `${count} new ${count === 1 ? "reply" : "replies"}`;
}

// ---- top loading bar --------------------------------------------------------
//
// A thin, NProgress-style bar pinned to the very top of the viewport. It
// fills toward ~90% while a view load is in flight and completes to 100% the
// instant the load finishes, then fades out. Like syncRefreshButton(), it is
// driven from render() via state.loading, so first loads (the slow, no-cache
// case), manual refreshes, and the auto-refresh timer are all covered.
//
// The fill is held below 100% while loading and only reaches it on completion,
// so a slow load still reads as "making progress" rather than a frozen bar.
const LOADING_BAR_TRICKLE_MS = 400;
const LOADING_BAR_HOLD_MS    = 180;   // full bar lingers before fading
const LOADING_BAR_FADE_MS    = 320;   // matches the CSS opacity transition
let loadingBarState = "idle";         // "idle" | "loading" | "completing"
let loadingBarTimer = null;           // completion -> fade -> reset
let loadingBarTrickle = null;         // slow upward nudge while loading

function loadingBarFill() {
  return document.querySelector("#loading-bar .loading-bar-fill");
}

function stopLoadingBarTrickle() {
  if (loadingBarTrickle != null) {
    clearInterval(loadingBarTrickle);
    loadingBarTrickle = null;
  }
}

// Width is owned by JS (so it can trickle). Opacity/visibility is owned by CSS
// via the .is-loading / .is-done classes. Set the width in one place so the
// no-transition jump (the initial appearance) stays consistent with the eased
// changes.
function setLoadingBarWidth(pct, { transition = true } = {}) {
  const fill = loadingBarFill();
  if (!fill) return;
  if (!transition) {
    fill.style.transition = "none";
    // Force a reflow so the next (eased) width change animates from here
    // instead of from 0/the previous value.
    void fill.offsetWidth;
  }
  fill.style.width = pct + "%";
  if (!transition) fill.style.transition = "";
}

function startLoadingBar() {
  const bar = document.getElementById("loading-bar");
  if (!bar) return;
  if (loadingBarState === "loading") return;   // already in flight

  // Cancel any in-flight completion/fade so a rapid re-navigation restarts
  // cleanly rather than fading out mid-load.
  if (loadingBarTimer != null) {
    clearTimeout(loadingBarTimer);
    loadingBarTimer = null;
  }
  stopLoadingBarTrickle();

  loadingBarState = "loading";
  bar.classList.remove("is-done");
  bar.classList.add("is-loading");
  // Appear at ~30% instantly, then ease up toward 80%.
  setLoadingBarWidth(30, { transition: false });
  setLoadingBarWidth(80);

  // Trickle slowly toward 90% so a long first load keeps looking alive
  // (randomized nudge, capped, never reaching 100% until completion).
  loadingBarTrickle = setInterval(() => {
    const fill = loadingBarFill();
    if (!fill) return;
    const cur = parseFloat(fill.style.width) || 0;
    if (cur < 90) setLoadingBarWidth(Math.min(90, cur + Math.random() * 3 + 1));
  }, LOADING_BAR_TRICKLE_MS);
}

function completeLoadingBar() {
  const bar = document.getElementById("loading-bar");
  if (!bar || loadingBarState !== "loading") return;   // nothing to complete

  stopLoadingBarTrickle();
  loadingBarState = "completing";
  setLoadingBarWidth(100);

  // Hold the full bar briefly, fade it out, then reset for the next load.
  if (loadingBarTimer != null) clearTimeout(loadingBarTimer);
  loadingBarTimer = setTimeout(() => {
    bar.classList.add("is-done");
    loadingBarTimer = setTimeout(() => {
      bar.classList.remove("is-loading");
      bar.classList.remove("is-done");
      setLoadingBarWidth(0, { transition: false });
      loadingBarState = "idle";
      loadingBarTimer = null;
    }, LOADING_BAR_FADE_MS);
  }, LOADING_BAR_HOLD_MS);
}

// Reflect state.loading on the top bar. Called from render() alongside
// syncRefreshButton()/updateScrollTopBar(), so every loader that toggles
// state.loading around an awaited fetch is covered. (loadMoreCatalog uses
// state.catalog.loadingMore instead, so infinite-scroll appends don't flare
// the bar — only whole-view loads do.)
function syncLoadingBar() {
  if (state.loading) startLoadingBar();
  else completeLoadingBar();
}

function render() {
  const cat = $("#catalog");
  const v = state.view;
  // Feed the compose mention typeahead with every account visible in this
  // view (thread posters, catalog OPs, the profile being viewed). Best-effort.
  rememberAccountsFromStatuses(state.statuses);
  const newKind = v?.kind || "none";
  // Identity within a view kind (board name, post id, profile acct). A
  // change here means the user navigated to a *different* board/thread,
  // so we must clear; a refresh of the same view keeps the old DOM.
  // Switching a profile's replies mode also clears rows and cached graphs:
  // reused post blocks could otherwise retain links from their reply tails.
  const newId =
    v?.kind === "board"   ? boardKey(v) :
    v?.kind === "post"    ? v.id   :
    v?.kind === "profile" ? `${v.acct}:${!!v.includeReplies}` :
    v?.kind === "search"  ? v.q :
    v?.kind === "tag"     ? v.tag :
    newKind;

  if (cat.dataset.viewKind !== newKind || cat.dataset.viewId !== newId) {
    // Wiping the catalog removes the thumbnails, but the floating hover
    // overlays/previews live on <body>, so they'd be orphaned: a node
    // removed while the pointer is over it never fires mouseleave, so the
    // per-thumbnail cleanup doesn't run. Clear them here — at the exact
    // moment the DOM is replaced — so the backstop can't run too early
    // (before an awaited load re-renders) and miss an overlay created in
    // the meantime.
    clearFloatingOverlays();
    resetIndexView();
    cat.innerHTML = "";
    cat.dataset.viewKind = newKind;
    cat.dataset.viewId = newId;
  }
  // Both classes switch the catalog from its grid to a block layout. They
  // must be managed together here — leaving a stale is-about (it used to be
  // added in renderAbout but never removed) makes a board catalog render as
  // full-width stacked cards, blowing up the thumbnails.
  cat.classList.toggle("is-thread", newKind === "post");
  cat.classList.toggle("is-about", newKind === "about");
  cat.classList.toggle("is-search", newKind === "search");
  cat.classList.toggle("is-index", indexViewActive());

  // Clear the catalog sentinel; renderCatalog re-adds it for board views.
  clearCatalogSentinel();
  // Same for the index's row observer — reconcileIndex re-observes the rows
  // it renders, so anything left here is watching detached nodes.
  clearIndexRowObserver();

  syncRefreshButton();
  syncRefreshAge();
  updateScrollTopBar();
  updateNewRepliesBar();
  syncFavicon();
  syncLoadingBar();

  if (v && v.kind === "post")  { renderPost(cat); applyAnonymousPresentation(cat); return; }
  if (v && v.kind === "about") { renderAbout(cat); applyAnonymousPresentation(cat); return; }
  if (v && v.kind === "search") { renderSearch(cat); applyAnonymousPresentation(cat); return; }
  renderCatalog(cat);
  if (isWatcherPanelOpen()) renderWatcherPanel();
  applyAnonymousPresentation(cat);
}

function syncCatalogControls() {
  const sortWrap = document.querySelector(".catalog-sort");
  const sel = document.getElementById("sort");
  const controlsRight = document.querySelector(".catalog-controls-right");
  const mediaBtn = document.getElementById("media-filter");
  const repliesBtn = document.getElementById("replies-filter");
  const dmScopeBtn = document.getElementById("dm-scope-filter");
  // Sort is irrelevant on collection boards (server order) and on search
  // (server-ranked results), so hide it there.
  const noSort = isCollectionBoardView() || state.view?.kind === "search";
  const inThread = state.view?.kind === "post";
  const showMediaToggle = ["board", "profile", "tag"].includes(state.view?.kind);

  if (controlsRight) controlsRight.hidden = inThread;
  if (mediaBtn) {
    mediaBtn.hidden = !showMediaToggle;
    mediaBtn.classList.toggle("is-active", !!state.mediaOnly);
    mediaBtn.setAttribute("aria-pressed", state.mediaOnly ? "true" : "false");
    mediaBtn.title = state.mediaOnly ? "Show all posts" : "Show media posts only";
  }
  // Catalog ⇄ Index. The label names where the toggle goes, not where the
  // reader is — it reads as the destination, the way a board's [Catalog] link
  // does. Hidden where the index has nothing to draw (thread, search, about,
  // the collection boards).
  const viewToggle = document.getElementById("view-toggle");
  const viewToggleBtn = document.getElementById("view-toggle-btn");
  if (viewToggle) viewToggle.hidden = !indexViewSupported();
  if (viewToggleBtn) {
    const asIndex = indexViewActive();
    viewToggleBtn.textContent = asIndex ? "Catalog" : "Index";
    viewToggleBtn.title = asIndex
      ? "Show threads as a catalog of thumbnails"
      : "Show threads with their latest replies";
    viewToggleBtn.setAttribute("aria-label", viewToggleBtn.title);
  }
  // Profiles plus the main timeline boards can switch between one card per
  // raw status and collapsed threads in catalog view. Indexes always show
  // replies under their thread, so the toggle is hidden there.
  if (repliesBtn) {
    const show = includesReplies();
    repliesBtn.hidden = !hasRepliesFilter() || indexViewActive();
    repliesBtn.classList.toggle("is-active", show);
    repliesBtn.setAttribute("aria-pressed", show ? "true" : "false");
    repliesBtn.title = show ? "Collapse replies into threads" : "Show replies and reposts";
    repliesBtn.setAttribute("aria-label", repliesBtn.title);
  }
  // /dm/ scope: every direct message (including DMs branching off a public
  // thread, which the conversations endpoint can't see) vs. conversations only.
  if (dmScopeBtn) {
    dmScopeBtn.hidden = !isDmBoardView();
    dmScopeBtn.classList.toggle("is-active", !!state.dmAllMessages);
    dmScopeBtn.setAttribute("aria-pressed", state.dmAllMessages ? "true" : "false");
    dmScopeBtn.title = state.dmAllMessages
      ? "Show conversations only"
      : "Show every direct message";
  }

  if (sortWrap) sortWrap.hidden = noSort;
  if (sel) {
    if (noSort) {
      sel.disabled = true;
      sel.title = "This view uses server/API order";
    } else {
      sel.disabled = false;
      sel.value = state.sort;
      sel.removeAttribute("title");
    }
  }

  // Search is auth-only for now.
  const signedIn = state.auth.status === "signed-in";
  const searchForm = document.getElementById("catalog-search");
  const searchInput = document.getElementById("search-input");
  const searchBtn = document.getElementById("catalog-search-btn");
  if (searchForm) searchForm.hidden = !signedIn;
  if (searchInput) {
    searchInput.disabled = !signedIn;
    searchInput.title = signedIn ? "Search Fedi" : "Sign in to search";
  }
  if (searchBtn) {
    searchBtn.disabled = !signedIn;
    searchBtn.title = signedIn ? "Search" : "Sign in to search";
  }
}

// Render the profile bio block above the catalog for /user/ views. Pass null
// to hide it (any non-profile view, or a profile whose lookup hasn't resolved).
function renderProfileBio(account) {
  const wrap = document.getElementById("profile-bio");
  if (!wrap) return;

  if (!account) {
    wrap.hidden = true;
    wrap.innerHTML = "";
    wrap.removeAttribute("data-acct-sig");
    return;
  }

  // Skip a rebuild when nothing user-visible changed — this keeps the note /
  // fields "show more" toggles from snapping shut on the many renderHeader()
  // calls that fire for unrelated reasons. The signature folds in the counts
  // and the editable profile bits (avatar/banner/name/bio) so a genuine
  // refresh or a just-saved profile edit still repaints.
  const sig = [
    account.id || account.acct || "",
    account.followers_count, account.following_count,
    account.subscribers_count, account.statuses_count,
    account.avatar || "", account.header || "",
    account.display_name || "", account.note || "",
  ].join(":");
  if (!wrap.hidden && wrap.dataset.acctSig === sig) return;
  wrap.dataset.acctSig = sig;

  wrap.hidden = false;
  wrap.innerHTML = "";

  const card = document.createElement("div");
  card.className = "profile-card";

  if (account.avatar) {
    const av = document.createElement("img");
    av.className = "profile-avatar";
    av.src = account.avatar;
    av.alt = "";
    av.loading = "lazy";
    av.addEventListener("click", () => openAccountImageGallery(account));
    card.appendChild(av);
  }

  const info = document.createElement("div");
  info.className = "profile-info";

  // Display name + bot/locked badges.
  const nameRow = document.createElement("div");
  nameRow.className = "profile-display-name";
  const nameSpan = document.createElement("span");
  nameSpan.innerHTML = renderTextWithCustomEmoji(
    account.display_name || account.username || "",
    account.emojis || []
  );
  nameRow.appendChild(nameSpan);
  if (account.bot) {
    const i = document.createElement("i");
    i.className = "fa-solid fa-robot profile-badge";
    i.title = "Bot account";
    nameRow.appendChild(i);
  }
  if (account.locked) {
    const i = document.createElement("i");
    i.className = "fa-solid fa-lock profile-badge";
    i.title = "Locked account — approves followers manually";
    nameRow.appendChild(i);
  }
  info.appendChild(nameRow);

  // Full acct (includes username, plus host for remote users), and whether
  // they follow you. The badge is filled in by syncProfileFollowsYouBadge
  // rather than here: the relationship resolves after this card is drawn, and
  // the signature check above would skip the rebuild that would show it.
  const acctEl = document.createElement("div");
  acctEl.className = "profile-acct";
  acctEl.textContent = "@" + (account.acct || account.username || "");
  const followsYou = document.createElement("span");
  followsYou.className = "profile-follows-you";
  followsYou.id = "profile-follows-you";
  followsYou.hidden = true;
  acctEl.appendChild(followsYou);
  info.appendChild(acctEl);

  // Stats: followers / following / subscribers, then total posts. Meaning lives
  // in the tooltip, mirroring the catalog card's "replies / reacts / reposts".
  const stats = document.createElement("div");
  stats.className = "profile-stats";
  const followers = Number(account.followers_count) || 0;
  const following = Number(account.following_count) || 0;
  const subscribers = Number(account.subscribers_count) || 0;
  const posts = Number(account.statuses_count) || 0;
  const social = document.createElement("span");
  social.className = "profile-stat";
  social.textContent = `${followers} / ${following} / ${subscribers}`;
  social.title = `${followers} followers, ${following} following, ${subscribers} subscribers`;
  stats.appendChild(social);
  const postStat = document.createElement("span");
  postStat.className = "profile-stat";
  postStat.textContent = `${posts} posts`;
  postStat.title = `${posts} posts`;
  stats.appendChild(postStat);
  info.appendChild(stats);

  // Everything below the stats (note + fields) lives in a collapsible section
  // revealed by a [show bio] link, keeping the default profile compact.
  const extra = document.createElement("div");
  extra.className = "profile-extra";
  extra.hidden = true;

  // Note (bio prose) — leading/trailing whitespace trimmed, then clamped to a
  // few lines with a show more/less toggle that only appears when it overflows.
  let noteEl = null;
  let noteToggle = null;
  let noteMeasured = false;
  const noteHtml = account.note ? String(account.note).trim() : "";
  if (noteHtml) {
    noteEl = document.createElement("div");
    noteEl.className = "profile-note is-collapsed";
    noteEl.innerHTML = renderSafeHTML(noteHtml, account.emojis || []);
    extra.appendChild(noteEl);

    noteToggle = document.createElement("button");
    noteToggle.type = "button";
    noteToggle.className = "profile-show-more";
    noteToggle.textContent = "[show more]";
    noteToggle.hidden = true;
    noteToggle.addEventListener("click", () => {
      const collapsed = noteEl.classList.toggle("is-collapsed");
      noteToggle.textContent = collapsed ? "[show more]" : "[show less]";
    });
    extra.appendChild(noteToggle);
  }

  // Fields (XMPP, GPG, links, …) — show the first few, collapse the rest.
  const fields = Array.isArray(account.fields) ? account.fields : [];
  if (fields.length) {
    const FIELDS_VISIBLE = 3;
    const dl = document.createElement("dl");
    dl.className = "profile-fields";
    fields.forEach((f, idx) => {
      const dt = document.createElement("dt");
      dt.innerHTML = renderTextWithCustomEmoji(f.name || "", account.emojis || []);
      const dd = document.createElement("dd");
      dd.innerHTML = renderSafeHTML(f.value || "", account.emojis || []);
      if (f.verified_at) dd.classList.add("is-verified");
      if (idx >= FIELDS_VISIBLE) {
        dt.classList.add("profile-field-extra");
        dd.classList.add("profile-field-extra");
        dt.hidden = true;
        dd.hidden = true;
      }
      dl.appendChild(dt);
      dl.appendChild(dd);
    });
    extra.appendChild(dl);

    const hidden = fields.length - FIELDS_VISIBLE;
    if (hidden > 0) {
      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "profile-show-more";
      const collapsedLabel = `[show ${hidden} more]`;
      toggle.textContent = collapsedLabel;
      let expanded = false;
      toggle.addEventListener("click", () => {
        expanded = !expanded;
        dl.querySelectorAll(".profile-field-extra").forEach((el) => { el.hidden = !expanded; });
        toggle.textContent = expanded ? "[show less]" : collapsedLabel;
      });
      extra.appendChild(toggle);
    }
  }

  if (extra.childNodes.length) {
    const bioToggle = document.createElement("button");
    bioToggle.type = "button";
    bioToggle.className = "profile-show-more profile-bio-toggle";
    bioToggle.textContent = "[show bio]";
    bioToggle.addEventListener("click", () => {
      const revealing = extra.hidden;
      extra.hidden = !extra.hidden;
      bioToggle.textContent = extra.hidden ? "[show bio]" : "[hide bio]";
      // The note's overflow can only be measured once it's actually laid out,
      // so defer it to the first time the section is revealed.
      if (revealing && noteEl && noteToggle && !noteMeasured) {
        noteMeasured = true;
        requestAnimationFrame(() => {
          if (noteEl.scrollHeight > noteEl.clientHeight + 2) noteToggle.hidden = false;
        });
      }
    });
    info.appendChild(bioToggle);
    info.appendChild(extra);
  }

  card.appendChild(info);
  wrap.appendChild(card);
}

function renderHeader() {
  const v = state.view;
  let title, desc;
  let boardAcct = null;
  const instName = instanceLabel();
  if (v.kind === "post") {
    // Once loaded, use the actual routed status (which may be a reply in a
    // re-rooted thread) to derive the post number from its object URL.
    const ctx = state.postView?.context;
    const viewedStatus = ctx?.op?.id === v.id
      ? ctx.op
      : (ctx?.descendants || []).find((status) => status?.id === v.id);
    const ref = postRef(viewedStatus || v.id);
    title = ref ? `/post/${ref}/` : "/post/";
    desc = "Thread view";
  } else if (v.kind === "profile") {
    // The board-title shows the lowercased username board-style (/name/); the
    // rest of the identity (full acct, bio, stats) lives in the bio block below.
    title = userSettings.anonymousMode
      ? "/anonymous/"
      : `/${String(v.account?.username || v.acct || "").toLowerCase()}/`;
    desc = "";
  } else if (v.kind === "about") {
    title = "About";
    desc = "Instance information";
  } else if (v.kind === "search") {
    title = "Search";
    desc = `“${v.q}”`;
  } else if (v.kind === "tag") {
    title = `#${v.tag}`;
    desc = `Posts tagged #${v.tag}`;
  } else if (v.kind === "board" && v.group) {
    // Custom board (Mitra group): board-title shows just the username
    // board-style (/name/); the full user@instance handle shows beneath it in
    // the profile-acct style, and the description comes from the group's bio.
    const username = String(v.group).split("@")[0];
    title = `/${username.toLowerCase()}/`;
    boardAcct = "@" + v.group;
    desc = state.auth.status !== "signed-in"
      ? "Custom board (sign in required)"
      : stripHTML(v.account?.note || "").trim() || "Custom board";
  } else {
    const boardName = (v.name) || defaultBoardName();
    const b = BOARDS[boardName] || BOARDS[defaultBoardName()];
    title = b.title;
    desc = boardDescription(boardName);
  }
  $("#board-title").textContent = title;
  const boardAcctEl = $("#board-acct");
  if (boardAcctEl) {
    if (boardAcct) {
      boardAcctEl.textContent = boardAcct;
      boardAcctEl.hidden = false;
    } else {
      boardAcctEl.hidden = true;
      boardAcctEl.textContent = "";
    }
  }
  $("#catalog-meta").textContent = desc;
  renderProfileBio(v.kind === "profile" ? v.account : null);
  document.title = `${instName} - ${title}`;
  document.querySelectorAll(".board-link").forEach((el) => {
    const active = (v.kind === "board" && el.dataset.board === boardKey(v))
      || (v.kind === "tag" && el.dataset.board === `tag/${v.tag}`);
    el.classList.toggle("active", !!active);
  });
  // Assigned unconditionally: skipping it while the instance was unknown is
  // how the previous server's name survived an account switch. With no
  // instance yet, the host the session signed into is the honest stand-in.
  const instTitleEl = $("#instance-title");
  if (instTitleEl) {
    instTitleEl.textContent = instanceLabel();
    instTitleEl.href = "#/about/";
  }
  // The start-thread button text/disabled state depends on both auth and view,
  // so refresh it on every renderHeader().
  syncStartThreadButton();
  syncTagBoardButton();
  syncProfileFollowButton();
  syncProfileNotificationButton();
  syncProfileFollowsYouBadge();
  syncProfileSubscribeButton();
  syncProfileModerationMenu();
  syncBoardAdminButtons();
  syncCatalogControls();
  applyAnonymousPresentation(document.querySelector("header") || document);
  applyAnonymousPresentation(document.querySelector("main") || document);
}

// ---- routing ----------------------------------------------------------------

function parseHash() {
  // Routes:
  //   #/home/, #/twkn/, #/local/, #/likes/, #/bkmks/, #/dm/  — boards
  //   #/g/<encoded acct>/                          — custom board (Mitra group)
  //   #/post/<id>/                                 — thread
  //   #/user/<encoded acct>/                       — profile (catalog of OPs)
  //   #/search/<encoded query>/                    — federated search
  //   #/tag/<encoded tag>/                         — local hashtag timeline
  //   #/about/                                     — instance about page
  //   #p<id>                                       — in-thread anchor (stay on current thread)
  let m = location.hash.match(/^#\/(home|twkn|local|likes|bkmks|dm)\/?$/);
  if (m) return { kind: "board", name: m[1] };
  m = location.hash.match(/^#\/g\/(.+?)\/?$/);
  if (m) return { kind: "board", group: decodeURIComponent(m[1]) };
  m = location.hash.match(/^#\/post\/(.+?)\/?$/);
  if (m) return { kind: "post", id: decodeURIComponent(m[1]) };
  m = location.hash.match(/^#\/user\/(.+?)\/?$/);
  if (m) return { kind: "profile", acct: decodeURIComponent(m[1]) };
  m = location.hash.match(/^#\/search\/(.+?)\/?$/);
  if (m) return { kind: "search", q: decodeURIComponent(m[1]) };
  m = location.hash.match(/^#\/tag\/(.+?)\/?$/);
  if (m) return { kind: "tag", tag: decodeURIComponent(m[1]) };
  m = location.hash.match(/^#\/about\/?$/);
  if (m) return { kind: "about" };

  // In-thread post anchor. Keep current thread view instead of falling back to /twkn/.
  m = location.hash.match(/^#p(.+)$/);
  if (m) {
    const anchoredId = decodeURIComponent(m[1]);
    const cur = state.view;
    if (cur?.kind === "post" && cur.id) {
      return { kind: "post", id: cur.id, highlightId: anchoredId };
    }
    if (state.postView?.id) {
      return { kind: "post", id: state.postView.id, highlightId: anchoredId };
    }
    return { kind: "post", id: anchoredId };
  }

  return { kind: "board", name: defaultBoardName() };
}

async function loadBoard(board, { resetPage = true } = {}) {
  clearPendingReplyFocus();
  const view = state.view = { kind: "board", name: board };
  if (resetPage) {
    state.statuses = [];
    state.catalog = { cursor: null, hasMore: false, loadingMore: false, error: false, linkPaging: false };
  }
  state.loading = true;
  state.error = null;
  render();
  renderHeader();
  try {
    // A refresh re-fetches the newest page and merges it in (new posts
    // appear, already-loaded older pages survive); the cursor stays put so
    // "load more" continues from the oldest post we hold.
    const { statuses, nextMaxId, pageSize, hasLinkNext } = await loadTimeline(
      board,
      state,
      { includeReplies: includesReplies(state.view) },
    );
    if (resetPage) {
      state.statuses = statuses;
      state.catalog.cursor = nextMaxId;
      // /likes/ and /bkmks/ page solely via the collection's Link-header
      // cursor; when that header isn't present/readable nextMaxId is null and
      // scroll stops after the first page, same as any timeline that ran out.
      updateCatalogHasMore({ nextMaxId, pageSize, hasLinkNext });
    } else {
      // Not on the collection boards: /likes/, /bkmks/ and /dm/ are ordered by
      // when you favourited/saved/last spoke, not by post age, so the page
      // they return isn't an id range and nothing can be concluded from a
      // post being absent from it.
      mergeStatuses(statuses, { pruneMissing: !isCollectionBoardView() });
    }
    markViewRefreshed(view);
  } catch (e) {
    state.error = e.message;
    if (resetPage) state.statuses = [];
  } finally {
    state.loading = false;
    render();
  }
}

// Custom board: resolve the group account by acct, then page its group
// timeline. Mirrors loadBoard's cursor/merge behavior; like /home/ and
// /likes/, the group timeline needs auth.
async function loadGroupBoard(acct, { resetPage = true } = {}) {
  clearPendingReplyFocus();
  // Keep the resolved account across refreshes of the same board so
  // loadMoreCatalog never sees a view without an id mid-refresh.
  const prev = state.view;
  const prevAccount = (prev?.kind === "board" && prev.group === acct) ? prev.account : null;
  const view = state.view = { kind: "board", group: acct, account: prevAccount };
  if (resetPage) {
    state.statuses = [];
    state.catalog = { cursor: null, hasMore: false, loadingMore: false, error: false, linkPaging: false };
  }
  state.loading = true;
  state.error = null;
  render();
  renderHeader();
  try {
    if (state.auth.status !== "signed-in") {
      throw new Error("sign in to view this board.");
    }
    let account = prevAccount;
    if (!account?.id) {
      account = state.groups.find((g) => g.acct === acct || g.username === acct) || null;
    }
    if (!account?.id) {
      const lookup = await apiFetch(`/api/v1/accounts/lookup?acct=${encodeURIComponent(acct)}`);
      if (!lookup.ok) throw new Error(`board lookup failed: HTTP ${lookup.status}`);
      account = await lookup.json();
    }
    if (state.view?.kind === "board" && state.view.group === acct) {
      state.view.account = account;
    }
    // Follow state for the [Follow]/[Unfollow] toggle, mirroring loadProfile.
    // The group actor is an account, so the standard relationships endpoint
    // reports whether the signed-in user follows it.
    if (state.auth.status === "signed-in" && account.id) {
      try {
        const rr = await apiFetch(`/api/v1/accounts/relationships?id[]=${encodeURIComponent(account.id)}`);
        if (rr.ok && state.view?.kind === "board" && state.view.group === acct) {
          const arr = await rr.json();
          state.view.relationship = Array.isArray(arr) ? arr[0] || null : null;
        }
      } catch { /* leave relationship unset; button shows a loading state */ }
    }
    const { statuses, nextMaxId, pageSize, hasLinkNext } = await loadGroupTimeline(account.id, state);
    if (resetPage) {
      state.statuses = statuses;
      state.catalog.cursor = nextMaxId;
      updateCatalogHasMore({ nextMaxId, pageSize, hasLinkNext });
    } else {
      mergeStatuses(statuses, { pruneMissing: true });
    }
    markViewRefreshed(view);
  } catch (e) {
    state.error = e.message;
    if (resetPage) state.statuses = [];
  } finally {
    state.loading = false;
    render();
    // Re-render the header now that the group account (title/description
    // source) has resolved.
    renderHeader();
  }
}

async function loadProfile(acct, { resetPage = true } = {}) {
  clearPendingReplyFocus();
  // Look up the account by acct, then fetch their statuses.
  // acct is "user" (local) or "user@host" (federated).
  const view = state.view = { kind: "profile", acct, includeReplies: !!state.includeReplies };
  if (resetPage) {
    state.statuses = [];
    state.stickiedStatuses = [];   // belongs to the profile being left
    state.catalog = { cursor: null, hasMore: false, loadingMore: false, error: false, linkPaging: false };
  }
  state.loading = true;
  state.error = null;
  render();
  renderHeader();
  try {
    const lookup = await apiFetch(`/api/v1/accounts/lookup?acct=${encodeURIComponent(acct)}`);
    if (!lookup.ok) throw new Error(`account lookup failed: HTTP ${lookup.status}`);
    const account = await lookup.json();
    if (account?.is_group) {
      // A group actor renders as a board, not a profile. replace() keeps the
      // /user/ URL out of history so Back doesn't bounce through the redirect.
      location.replace(groupBoardHref(acct));
      return;
    }
    state.view.account = account;
    // Follow state for the [Follow]/[Unfollow] toggle — only when signed in
    // and looking at someone else's profile.
    if (state.auth.status === "signed-in" && account.id && account.id !== state.auth.account?.id) {
      try {
        const rr = await apiFetch(`/api/v1/accounts/relationships?id[]=${encodeURIComponent(account.id)}`);
        if (rr.ok && state.view.account?.id === account.id) {
          const arr = await rr.json();
          state.view.relationship = Array.isArray(arr) ? arr[0] || null : null;
        }
      } catch { /* leave relationship unset; button shows a loading state */ }
    }
    // Stickied posts come from their own collection (see loadPinnedStatuses)
    // and ride alongside the first page. Best-effort: a server without the
    // collection just leaves the profile without a sticky.
    if (resetPage) {
      loadPinnedStatuses(account.id, state)
        .then((stickied) => {
          if (state.view?.kind === "profile" && state.view.account?.id === account.id) {
            state.stickiedStatuses = stickied;
            render();
          }
        })
        .catch(() => { /* no featured collection here */ });
    }
    // First page; older posts are appended by loadMoreCatalog as the user
    // scrolls. The account-statuses endpoint pages by max_id like the boards.
    const { statuses, nextMaxId, pageSize, hasLinkNext } = await loadAccountStatuses(account.id, state, { includeReplies: !!state.includeReplies });
    if (resetPage) {
      state.statuses = statuses;
      state.catalog.cursor = nextMaxId;
      updateCatalogHasMore({ nextMaxId, pageSize, hasLinkNext });
    } else {
      mergeStatuses(statuses, { pruneMissing: true });
    }
    markViewRefreshed(view);
  } catch (e) {
    state.error = e.message;
    if (resetPage) state.statuses = [];
  } finally {
    state.loading = false;
    render();
    // Title + bio depend on the looked-up account, which only resolves after
    // the fetch above — refresh the header now that state.view.account is set.
    renderHeader();
  }
}

// Federated search view. Runs Mitra's /api/v2/search across accounts +
// statuses and hashtags and shows them in the catalog grid. Requires auth.
async function loadSearch(q) {
  clearPendingReplyFocus();
  const view = state.view = { kind: "search", q };
  const scope = searchAuthScope();
  state.loading = true;
  state.error = null;
  // null while loading so renderSearch shows the placeholder rather than a
  // stale result set from a previous query.
  state.search = null;
  // Pre-fill the search box with the active query (e.g. on a page refresh of
  // #/search/<q>/), but never clobber while the user is typing into it.
  const si = document.getElementById("search-input");
  if (si && document.activeElement !== si) si.value = q;
  render();
  renderHeader();
  try {
    if (state.auth.status !== "signed-in") {
      throw new Error("sign in to search.");
    }
    const results = await Promise.allSettled(SEARCH_TYPES.map(type =>
      searchByType(q, state, { type, limit: SEARCH_PAGE_SIZE })));
    // A later navigation may have swapped the view out from under us; only
    // commit if we're still on this search.
    if (state.view === view && searchAuthScope() === scope) {
      const search = createSearchResults(Object.fromEntries(SEARCH_TYPES.map((type, i) =>
        [type, results[i].status === "fulfilled" ? results[i].value[type] : []])));
      for (let i = 0; i < SEARCH_TYPES.length; i++) {
        if (results[i].status === "rejected") {
          Object.assign(search.pages[SEARCH_TYPES[i]], { hasMore: true, error: true });
        }
      }
      state.search = search;
    }
  } catch (e) {
    if (state.view === view && searchAuthScope() === scope) state.error = e.message;
  } finally {
    if (state.view === view && searchAuthScope() === scope) {
      state.loading = false;
      render();
    }
  }
}

function searchAuthScope() {
  return JSON.stringify([state.auth.apiBase, state.auth.accessToken, state.auth.status]);
}

async function loadMoreSearch(type) {
  const view = state.view;
  const search = state.search;
  const page = search?.pages[type];
  if (view?.kind !== "search" || !page?.hasMore || page.loading || state.loading) return;
  const scope = searchAuthScope();
  const isCurrent = () => state.view === view && state.search === search && searchAuthScope() === scope;
  page.loading = true;
  page.error = false;
  render();
  try {
    const results = await searchByType(view.q, state, {
      type, limit: SEARCH_PAGE_SIZE, offset: page.offset,
    });
    if (isCurrent()) appendSearchPage(search, type, results[type]);
  } catch {
    if (isCurrent()) page.error = true;
  } finally {
    page.loading = false;
    if (isCurrent()) render();
  }
}

async function loadTag(tag, { resetPage = true } = {}) {
  clearPendingReplyFocus();
  const normalized = String(tag || "").replace(/^#/, "").trim();
  const view = state.view = { kind: "tag", tag: normalized };
  if (resetPage) {
    state.statuses = [];
    state.catalog = { cursor: null, hasMore: false, loadingMore: false, error: false, linkPaging: false };
  }
  state.loading = true;
  state.error = null;
  render();
  renderHeader();
  try {
    if (!normalized) throw new Error("invalid hashtag.");
    const { statuses, nextMaxId, pageSize, hasLinkNext } = await loadTagTimeline(normalized, state);
    if (resetPage) {
      state.statuses = statuses;
      state.catalog.cursor = nextMaxId;
      updateCatalogHasMore({ nextMaxId, pageSize, hasLinkNext });
    } else {
      mergeStatuses(statuses, { pruneMissing: true });
    }
    markViewRefreshed(view);
  } catch (e) {
    state.error = e.message;
    if (resetPage) state.statuses = [];
  } finally {
    state.loading = false;
    render();
  }
}

async function loadPost(id, { highlightId: requestedHighlightId = null } = {}) {
  if (state.pendingReplyFocus && state.pendingReplyFocus.opId !== id) {
    clearPendingReplyFocus();
  }

  // A refresh of the thread we're already on (vs. a fresh entry/revisit).
  // Computed before reassigning state.view / dropping state.postView.
  const isRefresh = !!(state.postView && state.postView.id === id);

  const fromRoute = (state.view && state.view.kind !== "post")
    ? state.view
    : (state.postView?.from || state.lastNonPostRoute || { kind: "board", name: defaultBoardName() });

  state.view = { kind: "post", id };
  state.loading = true;
  state.error = null;
  state.statuses = [];
  // Navigating to a *different* thread: drop the previous thread's context
  // before the intermediate render() so renderPost shows a fresh "loading
  // thread…" placeholder instead of the stale OP/replies for the seconds the
  // new fetch can take on a slow network. (renderPost already shows the
  // placeholder when state.postView is null; the catalog-based loaders do the
  // equivalent by clearing state.statuses/state.search.)
  if (!isRefresh) state.postView = null;
  render();
  renderHeader();
  const refreshBaseline = isRefresh ? threadLatestCreatedAt(state.postView.context) : "";
  const prevAcknowledgedAt = isRefresh ? (state.postView?.acknowledgedAt || "") : "";
  try {
    let context = await fetchContext(id, state);
    // A permalink can point at a reply. Rather than presenting that reply as a
    // lone OP (dropping its ancestors and sibling replies), re-root on the
    // thread's actual OP and render the whole conversation, marking the
    // requested reply so it's easy to find.
    const rootId = resolveRootIdFromContext(context) || context.op?.id || id;
    let highlightId = requestedHighlightId;
    if (rootId && rootId !== id) {
      context = await fetchContext(rootId, state);
      highlightId = highlightId || id;
    }
    // Carry the linked-post mark (permalink target or a clicked No. link)
    // across an in-place refresh of the same thread.
    if (isRefresh) highlightId = highlightId || state.postView?.highlightId || null;
    state.postView = {
      id,
      from: { ...fromRoute },
      context,
      highlightId,
      focusLinkedPost: !!highlightId && (!isRefresh || !!requestedHighlightId),
      focusUnreadLine: !isRefresh && !highlightId,
    };
    // Unread line baseline:
    //  - Fresh entry/revisit: mark posts newer than what we'd seen last visit,
    //    then advance the stored baseline to the newest post now loaded.
    //  - Same-thread refresh: compare against the newest post that was present
    //    immediately before the fetch, so the line moves to each new batch.
    const ctx = state.postView.context;
    const opId = ctx?.op?.id || id;
    state.postView.unreadAt = isRefresh
      ? refreshBaseline
      : (state.notifications.threadReadAt.get(opId) || "");
    // Baseline for the floating "N new replies" bar: the newest post the
    // user has acknowledged (opened the thread, scrolled to the bottom, or
    // clicked the bar). Carried across an in-place refresh so a manual
    // refresh doesn't reset the count; a fresh open starts caught up.
    state.postView.acknowledgedAt = isRefresh
      ? (prevAcknowledgedAt || threadLatestCreatedAt(ctx))
      : threadLatestCreatedAt(ctx);
    // Either way, track the newest post now loaded so a later visit only flags
    // posts that arrive after this session.
    const latestAt = threadLatestCreatedAt(ctx);
    if (latestAt) {
      state.notifications.threadReadAt.set(opId, latestAt);
      writeNotificationsStore();
    }
    if (!isRefresh) acknowledgeWatchedThread(ctx);
  } catch (e) {
    state.error = e.message;
    state.postView = null;
  } finally {
    state.loading = false;
    render();
    // The loaded status is now available, so show its post number in the title.
    renderHeader();
  }
}

function loadAbout() {
  clearPendingReplyFocus();
  state.view = { kind: "about" };
  state.loading = false;
  state.error = null;
  state.statuses = [];
  renderHeader();
  render();
}

async function navigateToCurrentHash() {
  // Prevent stale hover overlays/previews from persisting across route changes.
  clearFloatingOverlays();

  const r = parseHash();
  if (r.kind !== "post") state.lastNonPostRoute = { ...r };
  if (r.kind === "board")        await (r.group ? loadGroupBoard(r.group) : loadBoard(r.name));
  else if (r.kind === "profile") await loadProfile(r.acct);
  else if (r.kind === "search") {
    if (state.auth.status !== "signed-in") {
      location.hash = `#/${defaultBoardName()}/`;
      return;
    }
    await loadSearch(r.q);
  }
  else if (r.kind === "tag")     await loadTag(r.tag);
  else if (r.kind === "about")   loadAbout();
  else                             await loadPost(r.id, { highlightId: r.highlightId });
}

// Re-fetch the current view's data from the API without a full page reload.
// Preserves pagination and scroll position.
// Re-fetch the current view's first page, the way the replies toggle does.
// For when a setting changed what the loader should ask the server for, so
// the payload on screen answers the wrong question.
function reloadCurrentViewFromFirstPage() {
  const v = state.view;
  if (v?.kind === "profile") loadProfile(v.acct).catch(() => {});
  else if (v?.kind === "board" && v.group) loadGroupBoard(v.group).catch(() => {});
  else if (v?.kind === "board") loadBoard(v.name).catch(() => {});
  else if (v?.kind === "tag") loadTag(v.tag).catch(() => {});
  else render();
}

async function refreshCurrentView() {
  const r = state.view;
  if (!r) return;
  const scrollY = window.scrollY;
  const jumpSeq = state.ui.scrollJumpSeq;
  if (r.kind === "board")        await (r.group
    ? loadGroupBoard(r.group, { resetPage: false })
    : loadBoard(r.name, { resetPage: false }));
  else if (r.kind === "profile") await loadProfile(r.acct, { resetPage: false });
  else if (r.kind === "search") {
    if (state.auth.status !== "signed-in") {
      location.hash = `#/${defaultBoardName()}/`;
      return;
    }
    await loadSearch(r.q);
  }
  else if (r.kind === "tag")     await loadTag(r.tag, { resetPage: false });
  else if (r.kind === "post")    await loadPost(r.id);
  else if (r.kind === "about")   loadAbout();
  // Hold the absolute scroll position across the re-render rather than pinning
  // to a card. A catalog refresh can reorder cards (bump order), and re-pinning
  // the old topmost card would scroll the page to wherever that card landed —
  // holding scrollY leaves the viewport still. (Same approach load-more uses.)
  requestAnimationFrame(() => {
    if (state.ui.scrollJumpSeq === jumpSeq) window.scrollTo(0, scrollY);
  });
}

// Background poll of the thread currently in view: fetch its context and, only
// if something actually changed, reconcile in place. Unlike
// refreshCurrentView (used for manual refreshes), this is silent — it never
// toggles state.loading, so there's no spinning refresh icon or top loading
// bar on every tick, and a transient fetch error leaves the visible thread
// untouched instead of wiping it. New/edited/removed replies still land via
// the existing in-place reconciliation (reused DOM by data-id, scroll and
// expanded media preserved), with the unread line advanced to the newest post
// present before this poll.
async function pollCurrentThread() {
  const view = state.view;
  const threadId = view?.id;
  if (view?.kind !== "post" || !threadId) return;
  const prev = state.postView;
  if (!prev || prev.id !== threadId) return;

  // Poll the *root* of the conversation, not state.view.id. When the user
  // arrived via a permalink to a reply (or a No. link), the view id is that
  // reply while the rendered context was re-rooted on the real OP by
  // loadPost. Re-fetching the reply's own context would hand us a thread
  // whose "op" is that reply — everything above it disappears and the linked
  // post is promoted to OP on the next tick.
  const rootId = resolveRootIdFromContext(prev.context) || prev.context?.op?.id || threadId;

  let context;
  try {
    context = await fetchContext(rootId, state);
  } catch {
    // A poll on a long-idle tab regularly loses the race against the server
    // closing a keep-alive connection: the request dies as ERR_CONNECTION_CLOSED
    // before it's ever sent, even though the URL is perfectly fine on a retry.
    // These GETs are idempotent, so try once more on a fresh connection rather
    // than skipping the whole tick (which also swallows any new replies).
    await new Promise((resolve) => setTimeout(resolve, 1000));
    if (state.view?.kind !== "post" || state.view?.id !== threadId) return;
    if (state.postView !== prev) return;
    try {
      context = await fetchContext(rootId, state);
    } catch {
      return;   // really down: keep the thread as-is, try again next tick
    }
  }
  // The user may have navigated away, or a manual refresh may have replaced
  // postView, while the fetch was in flight. Identity check on postView
  // covers a same-thread refresh racing this poll (don't clobber fresher data).
  if (state.view?.kind !== "post" || state.view?.id !== threadId) return;
  if (state.postView !== prev) return;

  // Nothing changed since the last poll/render — stay completely silent.
  const prevSig = prev._sig || threadContextSignature(prev.context);
  if (threadContextSignature(context) === prevSig) return;

  // New/edited/removed content: reconcile. Carry over navigation/highlight
  // state and move the unread line (mirrors loadPost's same-thread refresh).
  // A reader parked at the bottom is kept pinned to the newest replies as
  // they stream in (live-follow) and treated as already caught up, so the
  // "new replies" bar never teases content they're watching arrive.
  const wasAtBottom = threadAtBottom();
  const refreshBaseline = threadLatestCreatedAt(prev.context);
  state.postView = {
    ...prev,
    context,
    focusLinkedPost: false,   // don't re-scroll; highlight class is re-applied in renderPost
    focusUnreadLine: false,   // refresh: leave scroll position alone
    unreadAt: refreshBaseline,
    acknowledgedAt: wasAtBottom
      ? threadLatestCreatedAt(context)
      : (prev.acknowledgedAt || threadLatestCreatedAt(context)),
    _sig: threadContextSignature(context),
  };
  const opId = context?.op?.id || threadId;
  const latestAt = threadLatestCreatedAt(context);
  if (latestAt) {
    state.notifications.threadReadAt.set(opId, latestAt);
    writeNotificationsStore();
  }
  if (wasAtBottom) acknowledgeWatchedThread(context);
  // A reader parked at the bottom follows the newest replies down; everyone
  // else stays put on the post they were reading, even when the reconcile
  // inserts a reply (or moves the unread line) above the viewport.
  const anchor = wasAtBottom ? null : captureThreadScrollAnchor();
  const jumpSeq = state.ui.scrollJumpSeq;
  const polled = state.postView;
  render();
  if (wasAtBottom) {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "instant" });
    return;
  }
  restoreThreadScrollAnchor(anchor);
  // Re-pin after layout settles: a new reply's media can finish loading (and
  // claim its height) a frame or two after the node is inserted.
  requestAnimationFrame(() => {
    // Skip if the user navigated/jumped (Top, Bottom, a permalink) in between.
    if (state.postView !== polled || state.ui.scrollJumpSeq !== jumpSeq) return;
    restoreThreadScrollAnchor(anchor);
  });
}

// ---- UI events --------------------------------------------------------------

function wireControls() {
  wireWatcherPanelPosition();
  const eyeBtn = document.getElementById("toolbar-eye");
  if (eyeBtn) {
    eyeBtn.title = "Notifications / thread watcher";
    eyeBtn.addEventListener("click", () => {
      if (isWatcherPanelOpen()) closeWatcherPanel();
      else openWatcherPanel();
    });
  }

  const watcherClose = document.getElementById("watcher-close");
  if (watcherClose) watcherClose.addEventListener("click", (e) => { e.preventDefault(); closeWatcherPanel(); });

  const watcherRefresh = document.getElementById("watcher-refresh");
  if (watcherRefresh) watcherRefresh.addEventListener("click", (e) => { e.preventDefault(); syncNotifications().catch(() => {}); });

  const tabWatcher = document.getElementById("watcher-tab-watcher");
  if (tabWatcher) tabWatcher.addEventListener("click", () => setWatcherTab("watcher"));
  const tabNotif = document.getElementById("watcher-tab-notifications");
  if (tabNotif) tabNotif.addEventListener("click", () => setWatcherTab("notifications"));
  const tabHidden = document.getElementById("watcher-tab-hidden");
  if (tabHidden) tabHidden.addEventListener("click", () => setWatcherTab("hidden"));

  const watcherToggleCurrent = document.getElementById("watcher-toggle-current");
  if (watcherToggleCurrent) {
    watcherToggleCurrent.addEventListener("click", () => {
      const threadId = watcherToggleCurrent.dataset.threadId;
      const title = watcherToggleCurrent.dataset.threadTitle || "";
      if (!threadId) return;
      toggleWatchThread(threadId, title);
      syncNotifications().catch(() => {});
    });
  }

  const sel = document.getElementById("sort");
  if (sel) {
    sel.value = state.sort;
    sel.addEventListener("change", () => {
      state.sort = sel.value;
      render();
    });
  }
  const filt = document.getElementById("filter");
  if (filt) {
    filt.value = state.filter;
    filt.addEventListener("input", () => {
      state.filter = filt.value;
      render();
    });
  }
  const mediaFilter = document.getElementById("media-filter");
  if (mediaFilter) {
    mediaFilter.addEventListener("click", () => {
      state.mediaOnly = !state.mediaOnly;
      syncCatalogControls();
      render();
    });
  }
  // Catalog ⇄ Index. Both draw the same threads, so this is normally a
  // re-render — except where the mode changes what the loader asks for: a
  // board left in "show replies" mode was fetched flat, without the ancestor
  // hydration the index needs to build a row per thread, so it's re-fetched
  // from a fresh first page.
  const viewToggle = document.getElementById("view-toggle-btn");
  if (viewToggle) {
    viewToggle.addEventListener("click", () => {
      const repliesBefore = includesReplies();
      saveSettings({ viewMode: indexViewActive() ? "catalog" : "index" });
      resetIndexView();
      syncCatalogControls();
      if (includesReplies() !== repliesBefore) reloadCurrentViewFromFirstPage();
      else render();
    });
  }
  document.getElementById("refresh-age-btn")?.addEventListener("click", () => {
    if (!state.loading) document.getElementById("toolbar-refresh")?.click();
  });
  // Toggling changes the fetch for profiles and built-in boards, so reload
  // from a fresh first page rather than re-rendering the current payload.
  const repliesFilter = document.getElementById("replies-filter");
  if (repliesFilter) {
    repliesFilter.addEventListener("click", () => {
      if (state.view?.kind === "profile") {
        state.includeReplies = !state.includeReplies;
        loadProfile(state.view.acct).catch(() => {});
      } else if (
        state.view?.kind === "board"
        && !state.view.group
        && REPLIES_FILTER_BOARDS.has(state.view.name)
      ) {
        const board = state.view.name;
        state.boardIncludeReplies[board] = !state.boardIncludeReplies[board];
        loadBoard(board).catch(() => {});
      } else {
        syncCatalogControls();
        render();
      }
    });
  }
  // /dm/ scope toggle: the two scopes are different endpoints, so switching
  // re-loads the board from a fresh first page.
  const dmScopeFilter = document.getElementById("dm-scope-filter");
  if (dmScopeFilter) {
    dmScopeFilter.addEventListener("click", () => {
      state.dmAllMessages = !state.dmAllMessages;
      syncCatalogControls();
      if (isDmBoardView()) loadBoard("dm").catch(() => {});
      else render();
    });
  }
  const searchForm = document.getElementById("catalog-search");
  if (searchForm) {
    searchForm.addEventListener("submit", (e) => {
      e.preventDefault();
      if (state.auth.status !== "signed-in") return;
      const input = document.getElementById("search-input");
      const q = (input?.value || "").trim();
      if (!q) return;
      location.hash = `#/search/${encodeURIComponent(q)}/`;
    });
  }
  const btn = document.getElementById("start-thread");
  if (btn) {
    btn.addEventListener("click", () => {
      if (state.view?.kind === "post" && state.view.id) {
        // "Reply to OP" — look up the OP and open the composer in reply mode.
        const host = document.querySelector(`.post-block.is-op`);
        const parent = host?._statusData || { id: state.view.id };
        openComposeReply(parent);
      } else if (state.view?.kind === "profile") {
        if (state.view.account?.is_group) {
          // A group's profile composes to the board via group_id, exactly
          // like the /g/ board view — no @mention needed.
          openComposeModal({ mode: "new", groupId: state.view.account.id });
        } else if (isOwnProfileView(state.view)) {
          // Your own profile: "Start a Thread", not a mention of yourself.
          openComposeModal({ mode: "new" });
        } else {
          // "Mention User" — fresh thread pre-filled with the profile's @handle.
          const acct = state.view.account?.acct || state.view.acct || "";
          openComposeModal({ mode: "new", prefill: acct ? `@${acct} ` : "" });
        }
      } else if (state.view?.kind === "tag") {
        openComposeModal({ mode: "new", prefill: `#${state.view.tag} ` });
      } else {
        // "Start a Thread" — fresh post, no parent. On a custom board the
        // thread is addressed to the board's group.
        const v = state.view;
        const groupId = (v?.kind === "board" && v.group) ? v.account?.id : null;
        openComposeModal(groupId ? { mode: "new", groupId } : { mode: "new" });
      }
    });
  }
  window.addEventListener("scroll", updateScrollTopBar, { passive: true });
  window.addEventListener("scroll", updateNewRepliesBar, { passive: true });
  document.getElementById("scroll-top-link")?.addEventListener("click", (e) => {
    e.preventDefault();
    state.ui.scrollJumpSeq++;
    window.scrollTo({ top: 0, behavior: "instant" });
  });
  document.getElementById("new-replies-bar")?.addEventListener("click", (e) => {
    e.preventDefault();
    jumpToFirstNewReply();
    acknowledgeNewReplies();
  });
  const tagBoardBtn = document.getElementById("tag-board-add");
  if (tagBoardBtn) {
    tagBoardBtn.addEventListener("click", () => {
      if (state.view?.kind !== "tag") return;
      if (hasTagBoard(state.view.tag)) removeTagBoard(state.view.tag);
      else addTagBoard(state.view.tag);
      syncTagBoardButton();   // flip the toggle's label
    });
  }
  const followBtn = document.getElementById("profile-follow");
  if (followBtn) followBtn.addEventListener("click", toggleFollow);
  document.getElementById("profile-notifications")?.addEventListener("click", toggleProfileNotifications);
  const subscribeBtn = document.getElementById("profile-subscribe");
  if (subscribeBtn) subscribeBtn.addEventListener("click", () => {
    // Remote (FEP-0837) options link out to the recipient's subscription page.
    const external = subscribeBtn.getAttribute("data-external");
    if (external) { window.open(external, "_blank", "noopener"); return; }
    const account = followableAccountForView(state.view);
    if (account?.id) openSubscribeModal(account);
  });
  const moderationBtn = document.getElementById("profile-moderation-btn");
  if (moderationBtn) moderationBtn.addEventListener("click", (e) => { e.stopPropagation(); toggleProfileModerationMenu(); });
  const moderationMenu = document.getElementById("profile-moderation-menu");
  if (moderationMenu) moderationMenu.addEventListener("click", (e) => {
    const item = e.target.closest("[data-moderation]");
    if (!item || item.disabled) return;
    runProfileModeration(item.dataset.moderation);
  });
  document.addEventListener("click", (e) => {
    if (e.target.closest("#profile-moderation")) return;
    const menu = $("#profile-moderation-menu");
    const btn = $("#profile-moderation-btn");
    if (menu) menu.hidden = true;
    if (btn) btn.setAttribute("aria-expanded", "false");
  });
  const editBoardBtn = document.getElementById("board-edit");
  if (editBoardBtn) editBoardBtn.addEventListener("click", () => {
    const v = state.view;
    const account = (v?.kind === "board" && v.group) ? v.account : null;
    if (account?.id && isModeratedGroup(account.id)) openCreateBoardModal(account);
  });
  const deleteBoardBtn = document.getElementById("board-delete");
  if (deleteBoardBtn) deleteBoardBtn.addEventListener("click", deleteCurrentBoard);
  syncStartThreadButton();

  // Board nav is re-rendered by renderBoardNav() whenever the followed groups
  // change, so wire clicks by delegation on the container. Clicking the board
  // link you're already on is a no-op navigation (same hash → no hashchange),
  // so treat it as a refresh of the current board.
  const boardsNav = document.getElementById("boards-nav");
  if (boardsNav) {
    boardsNav.addEventListener("click", (e) => {
      const add = e.target.closest(".board-add");
      if (add) {
        e.preventDefault();
        openCreateBoardModal();
        return;
      }
      const link = e.target.closest(".board-link");
      const onSameBoard = (state.view?.kind === "board" && boardKey(state.view) === link?.dataset.board)
        || (state.view?.kind === "tag" && link?.dataset.board === `tag/${state.view.tag}`);
      if (link && onSameBoard) {
        e.preventDefault();
        refreshCurrentView().catch(() => {});
      }
    });
  }

  // Toolbar buttons
  const refreshBtn = document.getElementById("toolbar-refresh");
  if (refreshBtn) {
    refreshBtn.addEventListener("click", () => { refreshCurrentView(); });
  }
  const cogBtn = document.getElementById("toolbar-cog");
  if (cogBtn) {
    cogBtn.addEventListener("click", () => openConfigModal());
  }
  // Account menu (multi-account switcher). The ellipsis button toggles the
  // menu; the standalone sign-in button is shown only when signed out.
  const accountMenuBtn = document.getElementById("account-menu-btn");
  if (accountMenuBtn) {
    accountMenuBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleAccountMenu();
    });
  }
  const accountSignin = document.getElementById("account-signin");
  if (accountSignin) {
    accountSignin.addEventListener("click", () => {
      if (state.auth.needsReconnect) reconnectAccount(activeSessionIdInState());
      else { beginAuth(); openAuthDialog(); }
    });
  }
  const accountMenu = document.getElementById("account-menu");
  if (accountMenu) {
    accountMenu.addEventListener("click", (e) => {
      const item = e.target.closest("[data-act]");
      if (!item) return;
      const act = item.dataset.act;
      if (act === "logout") logoutActiveAccount().catch(() => {});
      else if (act === "switch") switchAccount(item.dataset.id).catch(() => {});
      else if (act === "add") addAccount();
      else if (act === "reconnect") reconnectAccount(item.dataset.id);
    });
  }
  // Click outside the account menu closes it.
  document.addEventListener("click", (e) => {
    if (e.target.closest("#account-menu-btn") || e.target.closest("#account-menu")) return;
    closeAccountMenu();
  });
  document.addEventListener("click", (e) => {
    if (e.target.closest(".post-link-menu-wrap")) return;
    closePostLinkMenus();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closePostLinkMenus();
  });
  wireConfigModal();
  wireCreateBoardModal();
  wireSubscribeModal();
  // Auth dialog wiring
  const instanceInput = document.getElementById("auth-instance");
  const instanceLink = document.getElementById("auth-instance-link");
  if (instanceInput) instanceInput.value = OAUTH_CONFIG.defaultInstance;
  // Update link as user types
  if (instanceInput && instanceLink) {
    instanceLink.href = `https://${OAUTH_CONFIG.defaultInstance}/`;
    instanceInput.addEventListener("input", () => {
      const inst = instanceInput.value.trim();
      instanceLink.href = inst ? `https://${inst}/` : "#";
      refreshClearCacheButton();
    });
  }
  const openBtn = document.getElementById("auth-open");
  if (openBtn) {
    openBtn.addEventListener("click", async () => {
      const instance = (instanceInput?.value || "").trim().replace(/\/$/, "");
      if (!instance) {
        const err = document.getElementById("auth-error");
        if (err) err.textContent = "Enter an instance domain (e.g. example.tld)";
        return;
      }
      // The instance string is interpolated into the API base, the authorize
      // URL, and `https://${instance}`, so it must be a bare hostname. Anything
      // with a scheme, userinfo (@), path, or stray characters could redirect
      // our token/requests to another host (e.g. `good.tld@evil.tld`).
      if (!isValidInstanceDomain(instance)) {
        const err = document.getElementById("auth-error");
        if (err) err.textContent = "Enter a valid instance domain (e.g. example.tld)";
        return;
      }
      const flow = pendingAuth;
      openBtn.disabled = true;
      openBtn.textContent = "Registering…";
      const err = document.getElementById("auth-error");
      if (err) err.textContent = "";
      try {
        // Get or create app credentials for this instance
        let creds = getRegisteredApp(instance);
        if (!creds) {
          creds = await registerApp(instance);
          saveRegisteredApp(instance, creds.clientId, creds.clientSecret);
        }
        if (pendingAuth !== flow) return;
        flow.currentInstance = instance;
        flow.appCredentials = creds;
        // API base: same-origin for the proxied instance, cross-origin for others.
        // Mitra sends Access-Control-Allow-Origin: * by default, so direct
        // cross-origin requests work for any Mitra instance.
        if (instance === OAUTH_CONFIG.defaultInstance) {
          flow.apiBase = "";  // same-origin via Caddy
        } else {
          flow.apiBase = `https://${instance}`;
        }
        // Build authorize URL
        const url = new URL(`https://${instance}/oauth/authorize`);
        url.searchParams.set("client_id",     creds.clientId);
        url.searchParams.set("response_type",  "code");
        url.searchParams.set("redirect_uri",   OAUTH_CONFIG.redirectUri);
        url.searchParams.set("scope",          OAUTH_CONFIG.scopes);
        window.open(url.toString(), "_blank", "noopener,noreferrer");
        setTimeout(() => $("#auth-code")?.focus(), 100);
      } catch (e) {
        if (err) err.textContent = e.message || String(e);
        // Clear in-memory creds so retry re-registers
        delete flow.currentInstance;
        delete flow.apiBase;
        delete flow.appCredentials;
      } finally {
        openBtn.disabled = false;
        openBtn.textContent = "Open authorization page";
        refreshClearCacheButton();
      }
    });
  }
  const form = document.getElementById("auth-form");
  if (form) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const code = $("#auth-code").value.trim();
      if (code) exchangeCode(code);
    });
  }
  const cancelBtn = document.querySelector(".auth-cancel");
  if (cancelBtn) cancelBtn.addEventListener("click", () => dismissAuthDialog());
  // "Clear instance cache": drops the cached OAuth app for the typed instance
  // so the next sign-in re-registers. Handy for stale/purged-client auth errors.
  const clearBtn = document.getElementById("auth-clear-cache");
  if (clearBtn) {
    clearBtn.addEventListener("click", () => {
      const inst = (document.getElementById("auth-instance")?.value || "").trim().replace(/\/$/, "");
      if (!inst) return;
      clearRegisteredApp(inst);
      // Drop in-memory creds for the same instance so a retry re-registers
      // instead of reusing the client we just cleared.
      if (pendingAuth.currentInstance === inst) delete pendingAuth.appCredentials;
      refreshClearCacheButton();
    });
  }
  // Click on backdrop closes the dialog (but not a drag from inside; see
  // addBackdropClose).
  const dialog = document.getElementById("auth-dialog");
  if (dialog) {
    addBackdropClose(dialog, dismissAuthDialog);
  }
  // Escape closes the auth dialog or the account menu.
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (dialog?.classList.contains("is-open")) { dismissAuthDialog(); return; }
    const acctMenu = document.getElementById("account-menu");
    if (acctMenu && !acctMenu.hidden) { closeAccountMenu(); return; }
    if (isWatcherPanelOpen()) { closeWatcherPanel(); }
  });
}

// ---- init -------------------------------------------------------------------

async function init() {
  // As Mitra's default frontend, /objects/<id> redirects here as /post/<id>.
  // Preserve explicit hash routes, but otherwise adopt that server route and
  // move back to the app root so future permalinks cannot inherit /post/<id>.
  const serverPathHash = !location.hash && hashRouteForServerPath(location.pathname);
  if (serverPathHash) history.replaceState(history.state, "", `/${serverPathHash}`);

  window.addEventListener("hashchange", () => navigateToCurrentHash());

  // Turn the panel-style modals into floating, draggable, non-blocking
  // panels (compose, config, subscribe, board-create, emoji picker, poll
  // composer). The gallery and auth modals stay genuinely modal.
  for (const id of [
    "compose-modal", "config-modal", "subscribe-modal",
    "board-create", "emoji-picker", "poll-composer",
  ]) makePanelModal(document.getElementById(id));

  try {
    state.instance = await loadInstance(state);
  } catch (e) {
    state.error = "couldn't load instance: " + e.message;
    render();
    renderAuth();
    return;
  }

  wireControls();

  // Start the auto-refresh timer if the user enabled it. Settings are
  // loaded eagerly at the top of the file so the in-memory snapshot is
  // available by the time we get here.
  applyAutoRefreshFromSettings();

  // Restore notification store early (before first sync) so watched-thread
  // snapshots and read-state survive reloads.
  restoreNotificationsStore();
  window.addEventListener("storage", syncNotificationStateFromStorage);
  window.addEventListener("storage", (e) => {
    if (e.storageArea === localStorage && e.key?.startsWith(`${USER_POST_WATCHER_STORAGE_KEY}:`)) {
      syncUserPostNotifications();
    }
  });
  window.addEventListener("storage", (e) => { syncActiveAccountFromStorage(e).catch(() => {}); });

  // Restore saved auth token (if any) and verify it still works.
  if (restoreAuth()) {
    // Verify in the background; don't block the page load.
    verifyCredentials().catch(() => {});
  }
  renderAuth();

  await navigateToCurrentHash();

  // Initial + periodic notification sync.
  await syncNotifications();
  startNotificationPolling();
}

init();
